import vueRouter from 'vue-router';
import Vue from 'vue';
import Vuex from 'vuex';
import { watch, getCurrentInstance, reactive, toRefs } from '@vue/composition-api';
import axios from 'axios';
import { useWindowSize } from '@vueuse/core';
import VueI18n from 'vue-i18n';

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by rollup-plugin-commonjs');
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var moment = createCommonjsModule(function (module, exports) {

(function (global, factory) {
  module.exports = factory() ;
})(commonjsGlobal, function () {

  var hookCallback;

  function hooks() {
    return hookCallback.apply(null, arguments);
  } // This is done to register the method called with moment()
  // without creating circular dependencies.


  function setHookCallback(callback) {
    hookCallback = callback;
  }

  function isArray(input) {
    return input instanceof Array || Object.prototype.toString.call(input) === '[object Array]';
  }

  function isObject(input) {
    // IE8 will treat undefined and null as object if it wasn't for
    // input != null
    return input != null && Object.prototype.toString.call(input) === '[object Object]';
  }

  function hasOwnProp(a, b) {
    return Object.prototype.hasOwnProperty.call(a, b);
  }

  function isObjectEmpty(obj) {
    if (Object.getOwnPropertyNames) {
      return Object.getOwnPropertyNames(obj).length === 0;
    } else {
      var k;

      for (k in obj) {
        if (hasOwnProp(obj, k)) {
          return false;
        }
      }

      return true;
    }
  }

  function isUndefined(input) {
    return input === void 0;
  }

  function isNumber(input) {
    return typeof input === 'number' || Object.prototype.toString.call(input) === '[object Number]';
  }

  function isDate(input) {
    return input instanceof Date || Object.prototype.toString.call(input) === '[object Date]';
  }

  function map(arr, fn) {
    var res = [],
        i,
        arrLen = arr.length;

    for (i = 0; i < arrLen; ++i) {
      res.push(fn(arr[i], i));
    }

    return res;
  }

  function extend(a, b) {
    for (var i in b) {
      if (hasOwnProp(b, i)) {
        a[i] = b[i];
      }
    }

    if (hasOwnProp(b, 'toString')) {
      a.toString = b.toString;
    }

    if (hasOwnProp(b, 'valueOf')) {
      a.valueOf = b.valueOf;
    }

    return a;
  }

  function createUTC(input, format, locale, strict) {
    return createLocalOrUTC(input, format, locale, strict, true).utc();
  }

  function defaultParsingFlags() {
    // We need to deep clone this object.
    return {
      empty: false,
      unusedTokens: [],
      unusedInput: [],
      overflow: -2,
      charsLeftOver: 0,
      nullInput: false,
      invalidEra: null,
      invalidMonth: null,
      invalidFormat: false,
      userInvalidated: false,
      iso: false,
      parsedDateParts: [],
      era: null,
      meridiem: null,
      rfc2822: false,
      weekdayMismatch: false
    };
  }

  function getParsingFlags(m) {
    if (m._pf == null) {
      m._pf = defaultParsingFlags();
    }

    return m._pf;
  }

  var some;

  if (Array.prototype.some) {
    some = Array.prototype.some;
  } else {
    some = function (fun) {
      var t = Object(this),
          len = t.length >>> 0,
          i;

      for (i = 0; i < len; i++) {
        if (i in t && fun.call(this, t[i], i, t)) {
          return true;
        }
      }

      return false;
    };
  }

  function isValid(m) {
    if (m._isValid == null) {
      var flags = getParsingFlags(m),
          parsedParts = some.call(flags.parsedDateParts, function (i) {
        return i != null;
      }),
          isNowValid = !isNaN(m._d.getTime()) && flags.overflow < 0 && !flags.empty && !flags.invalidEra && !flags.invalidMonth && !flags.invalidWeekday && !flags.weekdayMismatch && !flags.nullInput && !flags.invalidFormat && !flags.userInvalidated && (!flags.meridiem || flags.meridiem && parsedParts);

      if (m._strict) {
        isNowValid = isNowValid && flags.charsLeftOver === 0 && flags.unusedTokens.length === 0 && flags.bigHour === undefined;
      }

      if (Object.isFrozen == null || !Object.isFrozen(m)) {
        m._isValid = isNowValid;
      } else {
        return isNowValid;
      }
    }

    return m._isValid;
  }

  function createInvalid(flags) {
    var m = createUTC(NaN);

    if (flags != null) {
      extend(getParsingFlags(m), flags);
    } else {
      getParsingFlags(m).userInvalidated = true;
    }

    return m;
  } // Plugins that add properties should also add the key here (null value),
  // so we can properly clone ourselves.


  var momentProperties = hooks.momentProperties = [],
      updateInProgress = false;

  function copyConfig(to, from) {
    var i,
        prop,
        val,
        momentPropertiesLen = momentProperties.length;

    if (!isUndefined(from._isAMomentObject)) {
      to._isAMomentObject = from._isAMomentObject;
    }

    if (!isUndefined(from._i)) {
      to._i = from._i;
    }

    if (!isUndefined(from._f)) {
      to._f = from._f;
    }

    if (!isUndefined(from._l)) {
      to._l = from._l;
    }

    if (!isUndefined(from._strict)) {
      to._strict = from._strict;
    }

    if (!isUndefined(from._tzm)) {
      to._tzm = from._tzm;
    }

    if (!isUndefined(from._isUTC)) {
      to._isUTC = from._isUTC;
    }

    if (!isUndefined(from._offset)) {
      to._offset = from._offset;
    }

    if (!isUndefined(from._pf)) {
      to._pf = getParsingFlags(from);
    }

    if (!isUndefined(from._locale)) {
      to._locale = from._locale;
    }

    if (momentPropertiesLen > 0) {
      for (i = 0; i < momentPropertiesLen; i++) {
        prop = momentProperties[i];
        val = from[prop];

        if (!isUndefined(val)) {
          to[prop] = val;
        }
      }
    }

    return to;
  } // Moment prototype object


  function Moment(config) {
    copyConfig(this, config);
    this._d = new Date(config._d != null ? config._d.getTime() : NaN);

    if (!this.isValid()) {
      this._d = new Date(NaN);
    } // Prevent infinite loop in case updateOffset creates new moment
    // objects.


    if (updateInProgress === false) {
      updateInProgress = true;
      hooks.updateOffset(this);
      updateInProgress = false;
    }
  }

  function isMoment(obj) {
    return obj instanceof Moment || obj != null && obj._isAMomentObject != null;
  }

  function warn(msg) {
    if (hooks.suppressDeprecationWarnings === false && typeof console !== 'undefined' && console.warn) {
      console.warn('Deprecation warning: ' + msg);
    }
  }

  function deprecate(msg, fn) {
    var firstTime = true;
    return extend(function () {
      if (hooks.deprecationHandler != null) {
        hooks.deprecationHandler(null, msg);
      }

      if (firstTime) {
        var args = [],
            arg,
            i,
            key,
            argLen = arguments.length;

        for (i = 0; i < argLen; i++) {
          arg = '';

          if (typeof arguments[i] === 'object') {
            arg += '\n[' + i + '] ';

            for (key in arguments[0]) {
              if (hasOwnProp(arguments[0], key)) {
                arg += key + ': ' + arguments[0][key] + ', ';
              }
            }

            arg = arg.slice(0, -2); // Remove trailing comma and space
          } else {
            arg = arguments[i];
          }

          args.push(arg);
        }

        warn(msg + '\nArguments: ' + Array.prototype.slice.call(args).join('') + '\n' + new Error().stack);
        firstTime = false;
      }

      return fn.apply(this, arguments);
    }, fn);
  }

  var deprecations = {};

  function deprecateSimple(name, msg) {
    if (hooks.deprecationHandler != null) {
      hooks.deprecationHandler(name, msg);
    }

    if (!deprecations[name]) {
      warn(msg);
      deprecations[name] = true;
    }
  }

  hooks.suppressDeprecationWarnings = false;
  hooks.deprecationHandler = null;

  function isFunction(input) {
    return typeof Function !== 'undefined' && input instanceof Function || Object.prototype.toString.call(input) === '[object Function]';
  }

  function set(config) {
    var prop, i;

    for (i in config) {
      if (hasOwnProp(config, i)) {
        prop = config[i];

        if (isFunction(prop)) {
          this[i] = prop;
        } else {
          this['_' + i] = prop;
        }
      }
    }

    this._config = config; // Lenient ordinal parsing accepts just a number in addition to
    // number + (possibly) stuff coming from _dayOfMonthOrdinalParse.
    // TODO: Remove "ordinalParse" fallback in next major release.

    this._dayOfMonthOrdinalParseLenient = new RegExp((this._dayOfMonthOrdinalParse.source || this._ordinalParse.source) + '|' + /\d{1,2}/.source);
  }

  function mergeConfigs(parentConfig, childConfig) {
    var res = extend({}, parentConfig),
        prop;

    for (prop in childConfig) {
      if (hasOwnProp(childConfig, prop)) {
        if (isObject(parentConfig[prop]) && isObject(childConfig[prop])) {
          res[prop] = {};
          extend(res[prop], parentConfig[prop]);
          extend(res[prop], childConfig[prop]);
        } else if (childConfig[prop] != null) {
          res[prop] = childConfig[prop];
        } else {
          delete res[prop];
        }
      }
    }

    for (prop in parentConfig) {
      if (hasOwnProp(parentConfig, prop) && !hasOwnProp(childConfig, prop) && isObject(parentConfig[prop])) {
        // make sure changes to properties don't modify parent config
        res[prop] = extend({}, res[prop]);
      }
    }

    return res;
  }

  function Locale(config) {
    if (config != null) {
      this.set(config);
    }
  }

  var keys;

  if (Object.keys) {
    keys = Object.keys;
  } else {
    keys = function (obj) {
      var i,
          res = [];

      for (i in obj) {
        if (hasOwnProp(obj, i)) {
          res.push(i);
        }
      }

      return res;
    };
  }

  var defaultCalendar = {
    sameDay: '[Today at] LT',
    nextDay: '[Tomorrow at] LT',
    nextWeek: 'dddd [at] LT',
    lastDay: '[Yesterday at] LT',
    lastWeek: '[Last] dddd [at] LT',
    sameElse: 'L'
  };

  function calendar(key, mom, now) {
    var output = this._calendar[key] || this._calendar['sameElse'];
    return isFunction(output) ? output.call(mom, now) : output;
  }

  function zeroFill(number, targetLength, forceSign) {
    var absNumber = '' + Math.abs(number),
        zerosToFill = targetLength - absNumber.length,
        sign = number >= 0;
    return (sign ? forceSign ? '+' : '' : '-') + Math.pow(10, Math.max(0, zerosToFill)).toString().substr(1) + absNumber;
  }

  var formattingTokens = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|N{1,5}|YYYYYY|YYYYY|YYYY|YY|y{2,4}|yo?|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g,
      localFormattingTokens = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g,
      formatFunctions = {},
      formatTokenFunctions = {}; // token:    'M'
  // padded:   ['MM', 2]
  // ordinal:  'Mo'
  // callback: function () { this.month() + 1 }

  function addFormatToken(token, padded, ordinal, callback) {
    var func = callback;

    if (typeof callback === 'string') {
      func = function () {
        return this[callback]();
      };
    }

    if (token) {
      formatTokenFunctions[token] = func;
    }

    if (padded) {
      formatTokenFunctions[padded[0]] = function () {
        return zeroFill(func.apply(this, arguments), padded[1], padded[2]);
      };
    }

    if (ordinal) {
      formatTokenFunctions[ordinal] = function () {
        return this.localeData().ordinal(func.apply(this, arguments), token);
      };
    }
  }

  function removeFormattingTokens(input) {
    if (input.match(/\[[\s\S]/)) {
      return input.replace(/^\[|\]$/g, '');
    }

    return input.replace(/\\/g, '');
  }

  function makeFormatFunction(format) {
    var array = format.match(formattingTokens),
        i,
        length;

    for (i = 0, length = array.length; i < length; i++) {
      if (formatTokenFunctions[array[i]]) {
        array[i] = formatTokenFunctions[array[i]];
      } else {
        array[i] = removeFormattingTokens(array[i]);
      }
    }

    return function (mom) {
      var output = '',
          i;

      for (i = 0; i < length; i++) {
        output += isFunction(array[i]) ? array[i].call(mom, format) : array[i];
      }

      return output;
    };
  } // format date using native date object


  function formatMoment(m, format) {
    if (!m.isValid()) {
      return m.localeData().invalidDate();
    }

    format = expandFormat(format, m.localeData());
    formatFunctions[format] = formatFunctions[format] || makeFormatFunction(format);
    return formatFunctions[format](m);
  }

  function expandFormat(format, locale) {
    var i = 5;

    function replaceLongDateFormatTokens(input) {
      return locale.longDateFormat(input) || input;
    }

    localFormattingTokens.lastIndex = 0;

    while (i >= 0 && localFormattingTokens.test(format)) {
      format = format.replace(localFormattingTokens, replaceLongDateFormatTokens);
      localFormattingTokens.lastIndex = 0;
      i -= 1;
    }

    return format;
  }

  var defaultLongDateFormat = {
    LTS: 'h:mm:ss A',
    LT: 'h:mm A',
    L: 'MM/DD/YYYY',
    LL: 'MMMM D, YYYY',
    LLL: 'MMMM D, YYYY h:mm A',
    LLLL: 'dddd, MMMM D, YYYY h:mm A'
  };

  function longDateFormat(key) {
    var format = this._longDateFormat[key],
        formatUpper = this._longDateFormat[key.toUpperCase()];

    if (format || !formatUpper) {
      return format;
    }

    this._longDateFormat[key] = formatUpper.match(formattingTokens).map(function (tok) {
      if (tok === 'MMMM' || tok === 'MM' || tok === 'DD' || tok === 'dddd') {
        return tok.slice(1);
      }

      return tok;
    }).join('');
    return this._longDateFormat[key];
  }

  var defaultInvalidDate = 'Invalid date';

  function invalidDate() {
    return this._invalidDate;
  }

  var defaultOrdinal = '%d',
      defaultDayOfMonthOrdinalParse = /\d{1,2}/;

  function ordinal(number) {
    return this._ordinal.replace('%d', number);
  }

  var defaultRelativeTime = {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    w: 'a week',
    ww: '%d weeks',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years'
  };

  function relativeTime(number, withoutSuffix, string, isFuture) {
    var output = this._relativeTime[string];
    return isFunction(output) ? output(number, withoutSuffix, string, isFuture) : output.replace(/%d/i, number);
  }

  function pastFuture(diff, output) {
    var format = this._relativeTime[diff > 0 ? 'future' : 'past'];
    return isFunction(format) ? format(output) : format.replace(/%s/i, output);
  }

  var aliases = {};

  function addUnitAlias(unit, shorthand) {
    var lowerCase = unit.toLowerCase();
    aliases[lowerCase] = aliases[lowerCase + 's'] = aliases[shorthand] = unit;
  }

  function normalizeUnits(units) {
    return typeof units === 'string' ? aliases[units] || aliases[units.toLowerCase()] : undefined;
  }

  function normalizeObjectUnits(inputObject) {
    var normalizedInput = {},
        normalizedProp,
        prop;

    for (prop in inputObject) {
      if (hasOwnProp(inputObject, prop)) {
        normalizedProp = normalizeUnits(prop);

        if (normalizedProp) {
          normalizedInput[normalizedProp] = inputObject[prop];
        }
      }
    }

    return normalizedInput;
  }

  var priorities = {};

  function addUnitPriority(unit, priority) {
    priorities[unit] = priority;
  }

  function getPrioritizedUnits(unitsObj) {
    var units = [],
        u;

    for (u in unitsObj) {
      if (hasOwnProp(unitsObj, u)) {
        units.push({
          unit: u,
          priority: priorities[u]
        });
      }
    }

    units.sort(function (a, b) {
      return a.priority - b.priority;
    });
    return units;
  }

  function isLeapYear(year) {
    return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
  }

  function absFloor(number) {
    if (number < 0) {
      // -0 -> 0
      return Math.ceil(number) || 0;
    } else {
      return Math.floor(number);
    }
  }

  function toInt(argumentForCoercion) {
    var coercedNumber = +argumentForCoercion,
        value = 0;

    if (coercedNumber !== 0 && isFinite(coercedNumber)) {
      value = absFloor(coercedNumber);
    }

    return value;
  }

  function makeGetSet(unit, keepTime) {
    return function (value) {
      if (value != null) {
        set$1(this, unit, value);
        hooks.updateOffset(this, keepTime);
        return this;
      } else {
        return get(this, unit);
      }
    };
  }

  function get(mom, unit) {
    return mom.isValid() ? mom._d['get' + (mom._isUTC ? 'UTC' : '') + unit]() : NaN;
  }

  function set$1(mom, unit, value) {
    if (mom.isValid() && !isNaN(value)) {
      if (unit === 'FullYear' && isLeapYear(mom.year()) && mom.month() === 1 && mom.date() === 29) {
        value = toInt(value);

        mom._d['set' + (mom._isUTC ? 'UTC' : '') + unit](value, mom.month(), daysInMonth(value, mom.month()));
      } else {
        mom._d['set' + (mom._isUTC ? 'UTC' : '') + unit](value);
      }
    }
  } // MOMENTS


  function stringGet(units) {
    units = normalizeUnits(units);

    if (isFunction(this[units])) {
      return this[units]();
    }

    return this;
  }

  function stringSet(units, value) {
    if (typeof units === 'object') {
      units = normalizeObjectUnits(units);
      var prioritized = getPrioritizedUnits(units),
          i,
          prioritizedLen = prioritized.length;

      for (i = 0; i < prioritizedLen; i++) {
        this[prioritized[i].unit](units[prioritized[i].unit]);
      }
    } else {
      units = normalizeUnits(units);

      if (isFunction(this[units])) {
        return this[units](value);
      }
    }

    return this;
  }

  var match1 = /\d/,
      //       0 - 9
  match2 = /\d\d/,
      //      00 - 99
  match3 = /\d{3}/,
      //     000 - 999
  match4 = /\d{4}/,
      //    0000 - 9999
  match6 = /[+-]?\d{6}/,
      // -999999 - 999999
  match1to2 = /\d\d?/,
      //       0 - 99
  match3to4 = /\d\d\d\d?/,
      //     999 - 9999
  match5to6 = /\d\d\d\d\d\d?/,
      //   99999 - 999999
  match1to3 = /\d{1,3}/,
      //       0 - 999
  match1to4 = /\d{1,4}/,
      //       0 - 9999
  match1to6 = /[+-]?\d{1,6}/,
      // -999999 - 999999
  matchUnsigned = /\d+/,
      //       0 - inf
  matchSigned = /[+-]?\d+/,
      //    -inf - inf
  matchOffset = /Z|[+-]\d\d:?\d\d/gi,
      // +00:00 -00:00 +0000 -0000 or Z
  matchShortOffset = /Z|[+-]\d\d(?::?\d\d)?/gi,
      // +00 -00 +00:00 -00:00 +0000 -0000 or Z
  matchTimestamp = /[+-]?\d+(\.\d{1,3})?/,
      // 123456789 123456789.123
  // any word (or two) characters or numbers including two/three word month in arabic.
  // includes scottish gaelic two word and hyphenated months
  matchWord = /[0-9]{0,256}['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFF07\uFF10-\uFFEF]{1,256}|[\u0600-\u06FF\/]{1,256}(\s*?[\u0600-\u06FF]{1,256}){1,2}/i,
      regexes;
  regexes = {};

  function addRegexToken(token, regex, strictRegex) {
    regexes[token] = isFunction(regex) ? regex : function (isStrict, localeData) {
      return isStrict && strictRegex ? strictRegex : regex;
    };
  }

  function getParseRegexForToken(token, config) {
    if (!hasOwnProp(regexes, token)) {
      return new RegExp(unescapeFormat(token));
    }

    return regexes[token](config._strict, config._locale);
  } // Code from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript


  function unescapeFormat(s) {
    return regexEscape(s.replace('\\', '').replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (matched, p1, p2, p3, p4) {
      return p1 || p2 || p3 || p4;
    }));
  }

  function regexEscape(s) {
    return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  var tokens = {};

  function addParseToken(token, callback) {
    var i,
        func = callback,
        tokenLen;

    if (typeof token === 'string') {
      token = [token];
    }

    if (isNumber(callback)) {
      func = function (input, array) {
        array[callback] = toInt(input);
      };
    }

    tokenLen = token.length;

    for (i = 0; i < tokenLen; i++) {
      tokens[token[i]] = func;
    }
  }

  function addWeekParseToken(token, callback) {
    addParseToken(token, function (input, array, config, token) {
      config._w = config._w || {};
      callback(input, config._w, config, token);
    });
  }

  function addTimeToArrayFromToken(token, input, config) {
    if (input != null && hasOwnProp(tokens, token)) {
      tokens[token](input, config._a, config, token);
    }
  }

  var YEAR = 0,
      MONTH = 1,
      DATE = 2,
      HOUR = 3,
      MINUTE = 4,
      SECOND = 5,
      MILLISECOND = 6,
      WEEK = 7,
      WEEKDAY = 8;

  function mod(n, x) {
    return (n % x + x) % x;
  }

  var indexOf;

  if (Array.prototype.indexOf) {
    indexOf = Array.prototype.indexOf;
  } else {
    indexOf = function (o) {
      // I know
      var i;

      for (i = 0; i < this.length; ++i) {
        if (this[i] === o) {
          return i;
        }
      }

      return -1;
    };
  }

  function daysInMonth(year, month) {
    if (isNaN(year) || isNaN(month)) {
      return NaN;
    }

    var modMonth = mod(month, 12);
    year += (month - modMonth) / 12;
    return modMonth === 1 ? isLeapYear(year) ? 29 : 28 : 31 - modMonth % 7 % 2;
  } // FORMATTING


  addFormatToken('M', ['MM', 2], 'Mo', function () {
    return this.month() + 1;
  });
  addFormatToken('MMM', 0, 0, function (format) {
    return this.localeData().monthsShort(this, format);
  });
  addFormatToken('MMMM', 0, 0, function (format) {
    return this.localeData().months(this, format);
  }); // ALIASES

  addUnitAlias('month', 'M'); // PRIORITY

  addUnitPriority('month', 8); // PARSING

  addRegexToken('M', match1to2);
  addRegexToken('MM', match1to2, match2);
  addRegexToken('MMM', function (isStrict, locale) {
    return locale.monthsShortRegex(isStrict);
  });
  addRegexToken('MMMM', function (isStrict, locale) {
    return locale.monthsRegex(isStrict);
  });
  addParseToken(['M', 'MM'], function (input, array) {
    array[MONTH] = toInt(input) - 1;
  });
  addParseToken(['MMM', 'MMMM'], function (input, array, config, token) {
    var month = config._locale.monthsParse(input, token, config._strict); // if we didn't find a month name, mark the date as invalid.


    if (month != null) {
      array[MONTH] = month;
    } else {
      getParsingFlags(config).invalidMonth = input;
    }
  }); // LOCALES

  var defaultLocaleMonths = 'January_February_March_April_May_June_July_August_September_October_November_December'.split('_'),
      defaultLocaleMonthsShort = 'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_'),
      MONTHS_IN_FORMAT = /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/,
      defaultMonthsShortRegex = matchWord,
      defaultMonthsRegex = matchWord;

  function localeMonths(m, format) {
    if (!m) {
      return isArray(this._months) ? this._months : this._months['standalone'];
    }

    return isArray(this._months) ? this._months[m.month()] : this._months[(this._months.isFormat || MONTHS_IN_FORMAT).test(format) ? 'format' : 'standalone'][m.month()];
  }

  function localeMonthsShort(m, format) {
    if (!m) {
      return isArray(this._monthsShort) ? this._monthsShort : this._monthsShort['standalone'];
    }

    return isArray(this._monthsShort) ? this._monthsShort[m.month()] : this._monthsShort[MONTHS_IN_FORMAT.test(format) ? 'format' : 'standalone'][m.month()];
  }

  function handleStrictParse(monthName, format, strict) {
    var i,
        ii,
        mom,
        llc = monthName.toLocaleLowerCase();

    if (!this._monthsParse) {
      // this is not used
      this._monthsParse = [];
      this._longMonthsParse = [];
      this._shortMonthsParse = [];

      for (i = 0; i < 12; ++i) {
        mom = createUTC([2000, i]);
        this._shortMonthsParse[i] = this.monthsShort(mom, '').toLocaleLowerCase();
        this._longMonthsParse[i] = this.months(mom, '').toLocaleLowerCase();
      }
    }

    if (strict) {
      if (format === 'MMM') {
        ii = indexOf.call(this._shortMonthsParse, llc);
        return ii !== -1 ? ii : null;
      } else {
        ii = indexOf.call(this._longMonthsParse, llc);
        return ii !== -1 ? ii : null;
      }
    } else {
      if (format === 'MMM') {
        ii = indexOf.call(this._shortMonthsParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._longMonthsParse, llc);
        return ii !== -1 ? ii : null;
      } else {
        ii = indexOf.call(this._longMonthsParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._shortMonthsParse, llc);
        return ii !== -1 ? ii : null;
      }
    }
  }

  function localeMonthsParse(monthName, format, strict) {
    var i, mom, regex;

    if (this._monthsParseExact) {
      return handleStrictParse.call(this, monthName, format, strict);
    }

    if (!this._monthsParse) {
      this._monthsParse = [];
      this._longMonthsParse = [];
      this._shortMonthsParse = [];
    } // TODO: add sorting
    // Sorting makes sure if one month (or abbr) is a prefix of another
    // see sorting in computeMonthsParse


    for (i = 0; i < 12; i++) {
      // make the regex if we don't have it already
      mom = createUTC([2000, i]);

      if (strict && !this._longMonthsParse[i]) {
        this._longMonthsParse[i] = new RegExp('^' + this.months(mom, '').replace('.', '') + '$', 'i');
        this._shortMonthsParse[i] = new RegExp('^' + this.monthsShort(mom, '').replace('.', '') + '$', 'i');
      }

      if (!strict && !this._monthsParse[i]) {
        regex = '^' + this.months(mom, '') + '|^' + this.monthsShort(mom, '');
        this._monthsParse[i] = new RegExp(regex.replace('.', ''), 'i');
      } // test the regex


      if (strict && format === 'MMMM' && this._longMonthsParse[i].test(monthName)) {
        return i;
      } else if (strict && format === 'MMM' && this._shortMonthsParse[i].test(monthName)) {
        return i;
      } else if (!strict && this._monthsParse[i].test(monthName)) {
        return i;
      }
    }
  } // MOMENTS


  function setMonth(mom, value) {
    var dayOfMonth;

    if (!mom.isValid()) {
      // No op
      return mom;
    }

    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) {
        value = toInt(value);
      } else {
        value = mom.localeData().monthsParse(value); // TODO: Another silent failure?

        if (!isNumber(value)) {
          return mom;
        }
      }
    }

    dayOfMonth = Math.min(mom.date(), daysInMonth(mom.year(), value));

    mom._d['set' + (mom._isUTC ? 'UTC' : '') + 'Month'](value, dayOfMonth);

    return mom;
  }

  function getSetMonth(value) {
    if (value != null) {
      setMonth(this, value);
      hooks.updateOffset(this, true);
      return this;
    } else {
      return get(this, 'Month');
    }
  }

  function getDaysInMonth() {
    return daysInMonth(this.year(), this.month());
  }

  function monthsShortRegex(isStrict) {
    if (this._monthsParseExact) {
      if (!hasOwnProp(this, '_monthsRegex')) {
        computeMonthsParse.call(this);
      }

      if (isStrict) {
        return this._monthsShortStrictRegex;
      } else {
        return this._monthsShortRegex;
      }
    } else {
      if (!hasOwnProp(this, '_monthsShortRegex')) {
        this._monthsShortRegex = defaultMonthsShortRegex;
      }

      return this._monthsShortStrictRegex && isStrict ? this._monthsShortStrictRegex : this._monthsShortRegex;
    }
  }

  function monthsRegex(isStrict) {
    if (this._monthsParseExact) {
      if (!hasOwnProp(this, '_monthsRegex')) {
        computeMonthsParse.call(this);
      }

      if (isStrict) {
        return this._monthsStrictRegex;
      } else {
        return this._monthsRegex;
      }
    } else {
      if (!hasOwnProp(this, '_monthsRegex')) {
        this._monthsRegex = defaultMonthsRegex;
      }

      return this._monthsStrictRegex && isStrict ? this._monthsStrictRegex : this._monthsRegex;
    }
  }

  function computeMonthsParse() {
    function cmpLenRev(a, b) {
      return b.length - a.length;
    }

    var shortPieces = [],
        longPieces = [],
        mixedPieces = [],
        i,
        mom;

    for (i = 0; i < 12; i++) {
      // make the regex if we don't have it already
      mom = createUTC([2000, i]);
      shortPieces.push(this.monthsShort(mom, ''));
      longPieces.push(this.months(mom, ''));
      mixedPieces.push(this.months(mom, ''));
      mixedPieces.push(this.monthsShort(mom, ''));
    } // Sorting makes sure if one month (or abbr) is a prefix of another it
    // will match the longer piece.


    shortPieces.sort(cmpLenRev);
    longPieces.sort(cmpLenRev);
    mixedPieces.sort(cmpLenRev);

    for (i = 0; i < 12; i++) {
      shortPieces[i] = regexEscape(shortPieces[i]);
      longPieces[i] = regexEscape(longPieces[i]);
    }

    for (i = 0; i < 24; i++) {
      mixedPieces[i] = regexEscape(mixedPieces[i]);
    }

    this._monthsRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
    this._monthsShortRegex = this._monthsRegex;
    this._monthsStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
    this._monthsShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
  } // FORMATTING


  addFormatToken('Y', 0, 0, function () {
    var y = this.year();
    return y <= 9999 ? zeroFill(y, 4) : '+' + y;
  });
  addFormatToken(0, ['YY', 2], 0, function () {
    return this.year() % 100;
  });
  addFormatToken(0, ['YYYY', 4], 0, 'year');
  addFormatToken(0, ['YYYYY', 5], 0, 'year');
  addFormatToken(0, ['YYYYYY', 6, true], 0, 'year'); // ALIASES

  addUnitAlias('year', 'y'); // PRIORITIES

  addUnitPriority('year', 1); // PARSING

  addRegexToken('Y', matchSigned);
  addRegexToken('YY', match1to2, match2);
  addRegexToken('YYYY', match1to4, match4);
  addRegexToken('YYYYY', match1to6, match6);
  addRegexToken('YYYYYY', match1to6, match6);
  addParseToken(['YYYYY', 'YYYYYY'], YEAR);
  addParseToken('YYYY', function (input, array) {
    array[YEAR] = input.length === 2 ? hooks.parseTwoDigitYear(input) : toInt(input);
  });
  addParseToken('YY', function (input, array) {
    array[YEAR] = hooks.parseTwoDigitYear(input);
  });
  addParseToken('Y', function (input, array) {
    array[YEAR] = parseInt(input, 10);
  }); // HELPERS

  function daysInYear(year) {
    return isLeapYear(year) ? 366 : 365;
  } // HOOKS


  hooks.parseTwoDigitYear = function (input) {
    return toInt(input) + (toInt(input) > 68 ? 1900 : 2000);
  }; // MOMENTS


  var getSetYear = makeGetSet('FullYear', true);

  function getIsLeapYear() {
    return isLeapYear(this.year());
  }

  function createDate(y, m, d, h, M, s, ms) {
    // can't just apply() to create a date:
    // https://stackoverflow.com/q/181348
    var date; // the date constructor remaps years 0-99 to 1900-1999

    if (y < 100 && y >= 0) {
      // preserve leap years using a full 400 year cycle, then reset
      date = new Date(y + 400, m, d, h, M, s, ms);

      if (isFinite(date.getFullYear())) {
        date.setFullYear(y);
      }
    } else {
      date = new Date(y, m, d, h, M, s, ms);
    }

    return date;
  }

  function createUTCDate(y) {
    var date, args; // the Date.UTC function remaps years 0-99 to 1900-1999

    if (y < 100 && y >= 0) {
      args = Array.prototype.slice.call(arguments); // preserve leap years using a full 400 year cycle, then reset

      args[0] = y + 400;
      date = new Date(Date.UTC.apply(null, args));

      if (isFinite(date.getUTCFullYear())) {
        date.setUTCFullYear(y);
      }
    } else {
      date = new Date(Date.UTC.apply(null, arguments));
    }

    return date;
  } // start-of-first-week - start-of-year


  function firstWeekOffset(year, dow, doy) {
    var // first-week day -- which january is always in the first week (4 for iso, 1 for other)
    fwd = 7 + dow - doy,
        // first-week day local weekday -- which local weekday is fwd
    fwdlw = (7 + createUTCDate(year, 0, fwd).getUTCDay() - dow) % 7;
    return -fwdlw + fwd - 1;
  } // https://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year.2C_week_number_and_weekday


  function dayOfYearFromWeeks(year, week, weekday, dow, doy) {
    var localWeekday = (7 + weekday - dow) % 7,
        weekOffset = firstWeekOffset(year, dow, doy),
        dayOfYear = 1 + 7 * (week - 1) + localWeekday + weekOffset,
        resYear,
        resDayOfYear;

    if (dayOfYear <= 0) {
      resYear = year - 1;
      resDayOfYear = daysInYear(resYear) + dayOfYear;
    } else if (dayOfYear > daysInYear(year)) {
      resYear = year + 1;
      resDayOfYear = dayOfYear - daysInYear(year);
    } else {
      resYear = year;
      resDayOfYear = dayOfYear;
    }

    return {
      year: resYear,
      dayOfYear: resDayOfYear
    };
  }

  function weekOfYear(mom, dow, doy) {
    var weekOffset = firstWeekOffset(mom.year(), dow, doy),
        week = Math.floor((mom.dayOfYear() - weekOffset - 1) / 7) + 1,
        resWeek,
        resYear;

    if (week < 1) {
      resYear = mom.year() - 1;
      resWeek = week + weeksInYear(resYear, dow, doy);
    } else if (week > weeksInYear(mom.year(), dow, doy)) {
      resWeek = week - weeksInYear(mom.year(), dow, doy);
      resYear = mom.year() + 1;
    } else {
      resYear = mom.year();
      resWeek = week;
    }

    return {
      week: resWeek,
      year: resYear
    };
  }

  function weeksInYear(year, dow, doy) {
    var weekOffset = firstWeekOffset(year, dow, doy),
        weekOffsetNext = firstWeekOffset(year + 1, dow, doy);
    return (daysInYear(year) - weekOffset + weekOffsetNext) / 7;
  } // FORMATTING


  addFormatToken('w', ['ww', 2], 'wo', 'week');
  addFormatToken('W', ['WW', 2], 'Wo', 'isoWeek'); // ALIASES

  addUnitAlias('week', 'w');
  addUnitAlias('isoWeek', 'W'); // PRIORITIES

  addUnitPriority('week', 5);
  addUnitPriority('isoWeek', 5); // PARSING

  addRegexToken('w', match1to2);
  addRegexToken('ww', match1to2, match2);
  addRegexToken('W', match1to2);
  addRegexToken('WW', match1to2, match2);
  addWeekParseToken(['w', 'ww', 'W', 'WW'], function (input, week, config, token) {
    week[token.substr(0, 1)] = toInt(input);
  }); // HELPERS
  // LOCALES

  function localeWeek(mom) {
    return weekOfYear(mom, this._week.dow, this._week.doy).week;
  }

  var defaultLocaleWeek = {
    dow: 0,
    // Sunday is the first day of the week.
    doy: 6 // The week that contains Jan 6th is the first week of the year.

  };

  function localeFirstDayOfWeek() {
    return this._week.dow;
  }

  function localeFirstDayOfYear() {
    return this._week.doy;
  } // MOMENTS


  function getSetWeek(input) {
    var week = this.localeData().week(this);
    return input == null ? week : this.add((input - week) * 7, 'd');
  }

  function getSetISOWeek(input) {
    var week = weekOfYear(this, 1, 4).week;
    return input == null ? week : this.add((input - week) * 7, 'd');
  } // FORMATTING


  addFormatToken('d', 0, 'do', 'day');
  addFormatToken('dd', 0, 0, function (format) {
    return this.localeData().weekdaysMin(this, format);
  });
  addFormatToken('ddd', 0, 0, function (format) {
    return this.localeData().weekdaysShort(this, format);
  });
  addFormatToken('dddd', 0, 0, function (format) {
    return this.localeData().weekdays(this, format);
  });
  addFormatToken('e', 0, 0, 'weekday');
  addFormatToken('E', 0, 0, 'isoWeekday'); // ALIASES

  addUnitAlias('day', 'd');
  addUnitAlias('weekday', 'e');
  addUnitAlias('isoWeekday', 'E'); // PRIORITY

  addUnitPriority('day', 11);
  addUnitPriority('weekday', 11);
  addUnitPriority('isoWeekday', 11); // PARSING

  addRegexToken('d', match1to2);
  addRegexToken('e', match1to2);
  addRegexToken('E', match1to2);
  addRegexToken('dd', function (isStrict, locale) {
    return locale.weekdaysMinRegex(isStrict);
  });
  addRegexToken('ddd', function (isStrict, locale) {
    return locale.weekdaysShortRegex(isStrict);
  });
  addRegexToken('dddd', function (isStrict, locale) {
    return locale.weekdaysRegex(isStrict);
  });
  addWeekParseToken(['dd', 'ddd', 'dddd'], function (input, week, config, token) {
    var weekday = config._locale.weekdaysParse(input, token, config._strict); // if we didn't get a weekday name, mark the date as invalid


    if (weekday != null) {
      week.d = weekday;
    } else {
      getParsingFlags(config).invalidWeekday = input;
    }
  });
  addWeekParseToken(['d', 'e', 'E'], function (input, week, config, token) {
    week[token] = toInt(input);
  }); // HELPERS

  function parseWeekday(input, locale) {
    if (typeof input !== 'string') {
      return input;
    }

    if (!isNaN(input)) {
      return parseInt(input, 10);
    }

    input = locale.weekdaysParse(input);

    if (typeof input === 'number') {
      return input;
    }

    return null;
  }

  function parseIsoWeekday(input, locale) {
    if (typeof input === 'string') {
      return locale.weekdaysParse(input) % 7 || 7;
    }

    return isNaN(input) ? null : input;
  } // LOCALES


  function shiftWeekdays(ws, n) {
    return ws.slice(n, 7).concat(ws.slice(0, n));
  }

  var defaultLocaleWeekdays = 'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_'),
      defaultLocaleWeekdaysShort = 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_'),
      defaultLocaleWeekdaysMin = 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_'),
      defaultWeekdaysRegex = matchWord,
      defaultWeekdaysShortRegex = matchWord,
      defaultWeekdaysMinRegex = matchWord;

  function localeWeekdays(m, format) {
    var weekdays = isArray(this._weekdays) ? this._weekdays : this._weekdays[m && m !== true && this._weekdays.isFormat.test(format) ? 'format' : 'standalone'];
    return m === true ? shiftWeekdays(weekdays, this._week.dow) : m ? weekdays[m.day()] : weekdays;
  }

  function localeWeekdaysShort(m) {
    return m === true ? shiftWeekdays(this._weekdaysShort, this._week.dow) : m ? this._weekdaysShort[m.day()] : this._weekdaysShort;
  }

  function localeWeekdaysMin(m) {
    return m === true ? shiftWeekdays(this._weekdaysMin, this._week.dow) : m ? this._weekdaysMin[m.day()] : this._weekdaysMin;
  }

  function handleStrictParse$1(weekdayName, format, strict) {
    var i,
        ii,
        mom,
        llc = weekdayName.toLocaleLowerCase();

    if (!this._weekdaysParse) {
      this._weekdaysParse = [];
      this._shortWeekdaysParse = [];
      this._minWeekdaysParse = [];

      for (i = 0; i < 7; ++i) {
        mom = createUTC([2000, 1]).day(i);
        this._minWeekdaysParse[i] = this.weekdaysMin(mom, '').toLocaleLowerCase();
        this._shortWeekdaysParse[i] = this.weekdaysShort(mom, '').toLocaleLowerCase();
        this._weekdaysParse[i] = this.weekdays(mom, '').toLocaleLowerCase();
      }
    }

    if (strict) {
      if (format === 'dddd') {
        ii = indexOf.call(this._weekdaysParse, llc);
        return ii !== -1 ? ii : null;
      } else if (format === 'ddd') {
        ii = indexOf.call(this._shortWeekdaysParse, llc);
        return ii !== -1 ? ii : null;
      } else {
        ii = indexOf.call(this._minWeekdaysParse, llc);
        return ii !== -1 ? ii : null;
      }
    } else {
      if (format === 'dddd') {
        ii = indexOf.call(this._weekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._shortWeekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._minWeekdaysParse, llc);
        return ii !== -1 ? ii : null;
      } else if (format === 'ddd') {
        ii = indexOf.call(this._shortWeekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._weekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._minWeekdaysParse, llc);
        return ii !== -1 ? ii : null;
      } else {
        ii = indexOf.call(this._minWeekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._weekdaysParse, llc);

        if (ii !== -1) {
          return ii;
        }

        ii = indexOf.call(this._shortWeekdaysParse, llc);
        return ii !== -1 ? ii : null;
      }
    }
  }

  function localeWeekdaysParse(weekdayName, format, strict) {
    var i, mom, regex;

    if (this._weekdaysParseExact) {
      return handleStrictParse$1.call(this, weekdayName, format, strict);
    }

    if (!this._weekdaysParse) {
      this._weekdaysParse = [];
      this._minWeekdaysParse = [];
      this._shortWeekdaysParse = [];
      this._fullWeekdaysParse = [];
    }

    for (i = 0; i < 7; i++) {
      // make the regex if we don't have it already
      mom = createUTC([2000, 1]).day(i);

      if (strict && !this._fullWeekdaysParse[i]) {
        this._fullWeekdaysParse[i] = new RegExp('^' + this.weekdays(mom, '').replace('.', '\\.?') + '$', 'i');
        this._shortWeekdaysParse[i] = new RegExp('^' + this.weekdaysShort(mom, '').replace('.', '\\.?') + '$', 'i');
        this._minWeekdaysParse[i] = new RegExp('^' + this.weekdaysMin(mom, '').replace('.', '\\.?') + '$', 'i');
      }

      if (!this._weekdaysParse[i]) {
        regex = '^' + this.weekdays(mom, '') + '|^' + this.weekdaysShort(mom, '') + '|^' + this.weekdaysMin(mom, '');
        this._weekdaysParse[i] = new RegExp(regex.replace('.', ''), 'i');
      } // test the regex


      if (strict && format === 'dddd' && this._fullWeekdaysParse[i].test(weekdayName)) {
        return i;
      } else if (strict && format === 'ddd' && this._shortWeekdaysParse[i].test(weekdayName)) {
        return i;
      } else if (strict && format === 'dd' && this._minWeekdaysParse[i].test(weekdayName)) {
        return i;
      } else if (!strict && this._weekdaysParse[i].test(weekdayName)) {
        return i;
      }
    }
  } // MOMENTS


  function getSetDayOfWeek(input) {
    if (!this.isValid()) {
      return input != null ? this : NaN;
    }

    var day = this._isUTC ? this._d.getUTCDay() : this._d.getDay();

    if (input != null) {
      input = parseWeekday(input, this.localeData());
      return this.add(input - day, 'd');
    } else {
      return day;
    }
  }

  function getSetLocaleDayOfWeek(input) {
    if (!this.isValid()) {
      return input != null ? this : NaN;
    }

    var weekday = (this.day() + 7 - this.localeData()._week.dow) % 7;
    return input == null ? weekday : this.add(input - weekday, 'd');
  }

  function getSetISODayOfWeek(input) {
    if (!this.isValid()) {
      return input != null ? this : NaN;
    } // behaves the same as moment#day except
    // as a getter, returns 7 instead of 0 (1-7 range instead of 0-6)
    // as a setter, sunday should belong to the previous week.


    if (input != null) {
      var weekday = parseIsoWeekday(input, this.localeData());
      return this.day(this.day() % 7 ? weekday : weekday - 7);
    } else {
      return this.day() || 7;
    }
  }

  function weekdaysRegex(isStrict) {
    if (this._weekdaysParseExact) {
      if (!hasOwnProp(this, '_weekdaysRegex')) {
        computeWeekdaysParse.call(this);
      }

      if (isStrict) {
        return this._weekdaysStrictRegex;
      } else {
        return this._weekdaysRegex;
      }
    } else {
      if (!hasOwnProp(this, '_weekdaysRegex')) {
        this._weekdaysRegex = defaultWeekdaysRegex;
      }

      return this._weekdaysStrictRegex && isStrict ? this._weekdaysStrictRegex : this._weekdaysRegex;
    }
  }

  function weekdaysShortRegex(isStrict) {
    if (this._weekdaysParseExact) {
      if (!hasOwnProp(this, '_weekdaysRegex')) {
        computeWeekdaysParse.call(this);
      }

      if (isStrict) {
        return this._weekdaysShortStrictRegex;
      } else {
        return this._weekdaysShortRegex;
      }
    } else {
      if (!hasOwnProp(this, '_weekdaysShortRegex')) {
        this._weekdaysShortRegex = defaultWeekdaysShortRegex;
      }

      return this._weekdaysShortStrictRegex && isStrict ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex;
    }
  }

  function weekdaysMinRegex(isStrict) {
    if (this._weekdaysParseExact) {
      if (!hasOwnProp(this, '_weekdaysRegex')) {
        computeWeekdaysParse.call(this);
      }

      if (isStrict) {
        return this._weekdaysMinStrictRegex;
      } else {
        return this._weekdaysMinRegex;
      }
    } else {
      if (!hasOwnProp(this, '_weekdaysMinRegex')) {
        this._weekdaysMinRegex = defaultWeekdaysMinRegex;
      }

      return this._weekdaysMinStrictRegex && isStrict ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex;
    }
  }

  function computeWeekdaysParse() {
    function cmpLenRev(a, b) {
      return b.length - a.length;
    }

    var minPieces = [],
        shortPieces = [],
        longPieces = [],
        mixedPieces = [],
        i,
        mom,
        minp,
        shortp,
        longp;

    for (i = 0; i < 7; i++) {
      // make the regex if we don't have it already
      mom = createUTC([2000, 1]).day(i);
      minp = regexEscape(this.weekdaysMin(mom, ''));
      shortp = regexEscape(this.weekdaysShort(mom, ''));
      longp = regexEscape(this.weekdays(mom, ''));
      minPieces.push(minp);
      shortPieces.push(shortp);
      longPieces.push(longp);
      mixedPieces.push(minp);
      mixedPieces.push(shortp);
      mixedPieces.push(longp);
    } // Sorting makes sure if one weekday (or abbr) is a prefix of another it
    // will match the longer piece.


    minPieces.sort(cmpLenRev);
    shortPieces.sort(cmpLenRev);
    longPieces.sort(cmpLenRev);
    mixedPieces.sort(cmpLenRev);
    this._weekdaysRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
    this._weekdaysShortRegex = this._weekdaysRegex;
    this._weekdaysMinRegex = this._weekdaysRegex;
    this._weekdaysStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
    this._weekdaysShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
    this._weekdaysMinStrictRegex = new RegExp('^(' + minPieces.join('|') + ')', 'i');
  } // FORMATTING


  function hFormat() {
    return this.hours() % 12 || 12;
  }

  function kFormat() {
    return this.hours() || 24;
  }

  addFormatToken('H', ['HH', 2], 0, 'hour');
  addFormatToken('h', ['hh', 2], 0, hFormat);
  addFormatToken('k', ['kk', 2], 0, kFormat);
  addFormatToken('hmm', 0, 0, function () {
    return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2);
  });
  addFormatToken('hmmss', 0, 0, function () {
    return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2) + zeroFill(this.seconds(), 2);
  });
  addFormatToken('Hmm', 0, 0, function () {
    return '' + this.hours() + zeroFill(this.minutes(), 2);
  });
  addFormatToken('Hmmss', 0, 0, function () {
    return '' + this.hours() + zeroFill(this.minutes(), 2) + zeroFill(this.seconds(), 2);
  });

  function meridiem(token, lowercase) {
    addFormatToken(token, 0, 0, function () {
      return this.localeData().meridiem(this.hours(), this.minutes(), lowercase);
    });
  }

  meridiem('a', true);
  meridiem('A', false); // ALIASES

  addUnitAlias('hour', 'h'); // PRIORITY

  addUnitPriority('hour', 13); // PARSING

  function matchMeridiem(isStrict, locale) {
    return locale._meridiemParse;
  }

  addRegexToken('a', matchMeridiem);
  addRegexToken('A', matchMeridiem);
  addRegexToken('H', match1to2);
  addRegexToken('h', match1to2);
  addRegexToken('k', match1to2);
  addRegexToken('HH', match1to2, match2);
  addRegexToken('hh', match1to2, match2);
  addRegexToken('kk', match1to2, match2);
  addRegexToken('hmm', match3to4);
  addRegexToken('hmmss', match5to6);
  addRegexToken('Hmm', match3to4);
  addRegexToken('Hmmss', match5to6);
  addParseToken(['H', 'HH'], HOUR);
  addParseToken(['k', 'kk'], function (input, array, config) {
    var kInput = toInt(input);
    array[HOUR] = kInput === 24 ? 0 : kInput;
  });
  addParseToken(['a', 'A'], function (input, array, config) {
    config._isPm = config._locale.isPM(input);
    config._meridiem = input;
  });
  addParseToken(['h', 'hh'], function (input, array, config) {
    array[HOUR] = toInt(input);
    getParsingFlags(config).bigHour = true;
  });
  addParseToken('hmm', function (input, array, config) {
    var pos = input.length - 2;
    array[HOUR] = toInt(input.substr(0, pos));
    array[MINUTE] = toInt(input.substr(pos));
    getParsingFlags(config).bigHour = true;
  });
  addParseToken('hmmss', function (input, array, config) {
    var pos1 = input.length - 4,
        pos2 = input.length - 2;
    array[HOUR] = toInt(input.substr(0, pos1));
    array[MINUTE] = toInt(input.substr(pos1, 2));
    array[SECOND] = toInt(input.substr(pos2));
    getParsingFlags(config).bigHour = true;
  });
  addParseToken('Hmm', function (input, array, config) {
    var pos = input.length - 2;
    array[HOUR] = toInt(input.substr(0, pos));
    array[MINUTE] = toInt(input.substr(pos));
  });
  addParseToken('Hmmss', function (input, array, config) {
    var pos1 = input.length - 4,
        pos2 = input.length - 2;
    array[HOUR] = toInt(input.substr(0, pos1));
    array[MINUTE] = toInt(input.substr(pos1, 2));
    array[SECOND] = toInt(input.substr(pos2));
  }); // LOCALES

  function localeIsPM(input) {
    // IE8 Quirks Mode & IE7 Standards Mode do not allow accessing strings like arrays
    // Using charAt should be more compatible.
    return (input + '').toLowerCase().charAt(0) === 'p';
  }

  var defaultLocaleMeridiemParse = /[ap]\.?m?\.?/i,
      // Setting the hour should keep the time, because the user explicitly
  // specified which hour they want. So trying to maintain the same hour (in
  // a new timezone) makes sense. Adding/subtracting hours does not follow
  // this rule.
  getSetHour = makeGetSet('Hours', true);

  function localeMeridiem(hours, minutes, isLower) {
    if (hours > 11) {
      return isLower ? 'pm' : 'PM';
    } else {
      return isLower ? 'am' : 'AM';
    }
  }

  var baseConfig = {
    calendar: defaultCalendar,
    longDateFormat: defaultLongDateFormat,
    invalidDate: defaultInvalidDate,
    ordinal: defaultOrdinal,
    dayOfMonthOrdinalParse: defaultDayOfMonthOrdinalParse,
    relativeTime: defaultRelativeTime,
    months: defaultLocaleMonths,
    monthsShort: defaultLocaleMonthsShort,
    week: defaultLocaleWeek,
    weekdays: defaultLocaleWeekdays,
    weekdaysMin: defaultLocaleWeekdaysMin,
    weekdaysShort: defaultLocaleWeekdaysShort,
    meridiemParse: defaultLocaleMeridiemParse
  }; // internal storage for locale config files

  var locales = {},
      localeFamilies = {},
      globalLocale;

  function commonPrefix(arr1, arr2) {
    var i,
        minl = Math.min(arr1.length, arr2.length);

    for (i = 0; i < minl; i += 1) {
      if (arr1[i] !== arr2[i]) {
        return i;
      }
    }

    return minl;
  }

  function normalizeLocale(key) {
    return key ? key.toLowerCase().replace('_', '-') : key;
  } // pick the locale from the array
  // try ['en-au', 'en-gb'] as 'en-au', 'en-gb', 'en', as in move through the list trying each
  // substring from most specific to least, but move to the next array item if it's a more specific variant than the current root


  function chooseLocale(names) {
    var i = 0,
        j,
        next,
        locale,
        split;

    while (i < names.length) {
      split = normalizeLocale(names[i]).split('-');
      j = split.length;
      next = normalizeLocale(names[i + 1]);
      next = next ? next.split('-') : null;

      while (j > 0) {
        locale = loadLocale(split.slice(0, j).join('-'));

        if (locale) {
          return locale;
        }

        if (next && next.length >= j && commonPrefix(split, next) >= j - 1) {
          //the next array item is better than a shallower substring of this one
          break;
        }

        j--;
      }

      i++;
    }

    return globalLocale;
  }

  function isLocaleNameSane(name) {
    // Prevent names that look like filesystem paths, i.e contain '/' or '\'
    return name.match('^[^/\\\\]*$') != null;
  }

  function loadLocale(name) {
    var oldLocale = null,
        aliasedRequire; // TODO: Find a better way to register and load all the locales in Node

    if (locales[name] === undefined && 'object' !== 'undefined' && module && module.exports && isLocaleNameSane(name)) {
      try {
        oldLocale = globalLocale._abbr;
        aliasedRequire = commonjsRequire;
        aliasedRequire('./locale/' + name);
        getSetGlobalLocale(oldLocale);
      } catch (e) {
        // mark as not found to avoid repeating expensive file require call causing high CPU
        // when trying to find en-US, en_US, en-us for every format call
        locales[name] = null; // null means not found
      }
    }

    return locales[name];
  } // This function will load locale and then set the global locale.  If
  // no arguments are passed in, it will simply return the current global
  // locale key.


  function getSetGlobalLocale(key, values) {
    var data;

    if (key) {
      if (isUndefined(values)) {
        data = getLocale(key);
      } else {
        data = defineLocale(key, values);
      }

      if (data) {
        // moment.duration._locale = moment._locale = data;
        globalLocale = data;
      } else {
        if (typeof console !== 'undefined' && console.warn) {
          //warn user if arguments are passed but the locale could not be set
          console.warn('Locale ' + key + ' not found. Did you forget to load it?');
        }
      }
    }

    return globalLocale._abbr;
  }

  function defineLocale(name, config) {
    if (config !== null) {
      var locale,
          parentConfig = baseConfig;
      config.abbr = name;

      if (locales[name] != null) {
        deprecateSimple('defineLocaleOverride', 'use moment.updateLocale(localeName, config) to change ' + 'an existing locale. moment.defineLocale(localeName, ' + 'config) should only be used for creating a new locale ' + 'See http://momentjs.com/guides/#/warnings/define-locale/ for more info.');
        parentConfig = locales[name]._config;
      } else if (config.parentLocale != null) {
        if (locales[config.parentLocale] != null) {
          parentConfig = locales[config.parentLocale]._config;
        } else {
          locale = loadLocale(config.parentLocale);

          if (locale != null) {
            parentConfig = locale._config;
          } else {
            if (!localeFamilies[config.parentLocale]) {
              localeFamilies[config.parentLocale] = [];
            }

            localeFamilies[config.parentLocale].push({
              name: name,
              config: config
            });
            return null;
          }
        }
      }

      locales[name] = new Locale(mergeConfigs(parentConfig, config));

      if (localeFamilies[name]) {
        localeFamilies[name].forEach(function (x) {
          defineLocale(x.name, x.config);
        });
      } // backwards compat for now: also set the locale
      // make sure we set the locale AFTER all child locales have been
      // created, so we won't end up with the child locale set.


      getSetGlobalLocale(name);
      return locales[name];
    } else {
      // useful for testing
      delete locales[name];
      return null;
    }
  }

  function updateLocale(name, config) {
    if (config != null) {
      var locale,
          tmpLocale,
          parentConfig = baseConfig;

      if (locales[name] != null && locales[name].parentLocale != null) {
        // Update existing child locale in-place to avoid memory-leaks
        locales[name].set(mergeConfigs(locales[name]._config, config));
      } else {
        // MERGE
        tmpLocale = loadLocale(name);

        if (tmpLocale != null) {
          parentConfig = tmpLocale._config;
        }

        config = mergeConfigs(parentConfig, config);

        if (tmpLocale == null) {
          // updateLocale is called for creating a new locale
          // Set abbr so it will have a name (getters return
          // undefined otherwise).
          config.abbr = name;
        }

        locale = new Locale(config);
        locale.parentLocale = locales[name];
        locales[name] = locale;
      } // backwards compat for now: also set the locale


      getSetGlobalLocale(name);
    } else {
      // pass null for config to unupdate, useful for tests
      if (locales[name] != null) {
        if (locales[name].parentLocale != null) {
          locales[name] = locales[name].parentLocale;

          if (name === getSetGlobalLocale()) {
            getSetGlobalLocale(name);
          }
        } else if (locales[name] != null) {
          delete locales[name];
        }
      }
    }

    return locales[name];
  } // returns locale data


  function getLocale(key) {
    var locale;

    if (key && key._locale && key._locale._abbr) {
      key = key._locale._abbr;
    }

    if (!key) {
      return globalLocale;
    }

    if (!isArray(key)) {
      //short-circuit everything else
      locale = loadLocale(key);

      if (locale) {
        return locale;
      }

      key = [key];
    }

    return chooseLocale(key);
  }

  function listLocales() {
    return keys(locales);
  }

  function checkOverflow(m) {
    var overflow,
        a = m._a;

    if (a && getParsingFlags(m).overflow === -2) {
      overflow = a[MONTH] < 0 || a[MONTH] > 11 ? MONTH : a[DATE] < 1 || a[DATE] > daysInMonth(a[YEAR], a[MONTH]) ? DATE : a[HOUR] < 0 || a[HOUR] > 24 || a[HOUR] === 24 && (a[MINUTE] !== 0 || a[SECOND] !== 0 || a[MILLISECOND] !== 0) ? HOUR : a[MINUTE] < 0 || a[MINUTE] > 59 ? MINUTE : a[SECOND] < 0 || a[SECOND] > 59 ? SECOND : a[MILLISECOND] < 0 || a[MILLISECOND] > 999 ? MILLISECOND : -1;

      if (getParsingFlags(m)._overflowDayOfYear && (overflow < YEAR || overflow > DATE)) {
        overflow = DATE;
      }

      if (getParsingFlags(m)._overflowWeeks && overflow === -1) {
        overflow = WEEK;
      }

      if (getParsingFlags(m)._overflowWeekday && overflow === -1) {
        overflow = WEEKDAY;
      }

      getParsingFlags(m).overflow = overflow;
    }

    return m;
  } // iso 8601 regex
  // 0000-00-00 0000-W00 or 0000-W00-0 + T + 00 or 00:00 or 00:00:00 or 00:00:00.000 + +00:00 or +0000 or +00)


  var extendedIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,
      basicIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d|))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,
      tzRegex = /Z|[+-]\d\d(?::?\d\d)?/,
      isoDates = [['YYYYYY-MM-DD', /[+-]\d{6}-\d\d-\d\d/], ['YYYY-MM-DD', /\d{4}-\d\d-\d\d/], ['GGGG-[W]WW-E', /\d{4}-W\d\d-\d/], ['GGGG-[W]WW', /\d{4}-W\d\d/, false], ['YYYY-DDD', /\d{4}-\d{3}/], ['YYYY-MM', /\d{4}-\d\d/, false], ['YYYYYYMMDD', /[+-]\d{10}/], ['YYYYMMDD', /\d{8}/], ['GGGG[W]WWE', /\d{4}W\d{3}/], ['GGGG[W]WW', /\d{4}W\d{2}/, false], ['YYYYDDD', /\d{7}/], ['YYYYMM', /\d{6}/, false], ['YYYY', /\d{4}/, false]],
      // iso time formats and regexes
  isoTimes = [['HH:mm:ss.SSSS', /\d\d:\d\d:\d\d\.\d+/], ['HH:mm:ss,SSSS', /\d\d:\d\d:\d\d,\d+/], ['HH:mm:ss', /\d\d:\d\d:\d\d/], ['HH:mm', /\d\d:\d\d/], ['HHmmss.SSSS', /\d\d\d\d\d\d\.\d+/], ['HHmmss,SSSS', /\d\d\d\d\d\d,\d+/], ['HHmmss', /\d\d\d\d\d\d/], ['HHmm', /\d\d\d\d/], ['HH', /\d\d/]],
      aspNetJsonRegex = /^\/?Date\((-?\d+)/i,
      // RFC 2822 regex: For details see https://tools.ietf.org/html/rfc2822#section-3.3
  rfc2822 = /^(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{2,4})\s(\d\d):(\d\d)(?::(\d\d))?\s(?:(UT|GMT|[ECMP][SD]T)|([Zz])|([+-]\d{4}))$/,
      obsOffsets = {
    UT: 0,
    GMT: 0,
    EDT: -4 * 60,
    EST: -5 * 60,
    CDT: -5 * 60,
    CST: -6 * 60,
    MDT: -6 * 60,
    MST: -7 * 60,
    PDT: -7 * 60,
    PST: -8 * 60
  }; // date from iso format

  function configFromISO(config) {
    var i,
        l,
        string = config._i,
        match = extendedIsoRegex.exec(string) || basicIsoRegex.exec(string),
        allowTime,
        dateFormat,
        timeFormat,
        tzFormat,
        isoDatesLen = isoDates.length,
        isoTimesLen = isoTimes.length;

    if (match) {
      getParsingFlags(config).iso = true;

      for (i = 0, l = isoDatesLen; i < l; i++) {
        if (isoDates[i][1].exec(match[1])) {
          dateFormat = isoDates[i][0];
          allowTime = isoDates[i][2] !== false;
          break;
        }
      }

      if (dateFormat == null) {
        config._isValid = false;
        return;
      }

      if (match[3]) {
        for (i = 0, l = isoTimesLen; i < l; i++) {
          if (isoTimes[i][1].exec(match[3])) {
            // match[2] should be 'T' or space
            timeFormat = (match[2] || ' ') + isoTimes[i][0];
            break;
          }
        }

        if (timeFormat == null) {
          config._isValid = false;
          return;
        }
      }

      if (!allowTime && timeFormat != null) {
        config._isValid = false;
        return;
      }

      if (match[4]) {
        if (tzRegex.exec(match[4])) {
          tzFormat = 'Z';
        } else {
          config._isValid = false;
          return;
        }
      }

      config._f = dateFormat + (timeFormat || '') + (tzFormat || '');
      configFromStringAndFormat(config);
    } else {
      config._isValid = false;
    }
  }

  function extractFromRFC2822Strings(yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr) {
    var result = [untruncateYear(yearStr), defaultLocaleMonthsShort.indexOf(monthStr), parseInt(dayStr, 10), parseInt(hourStr, 10), parseInt(minuteStr, 10)];

    if (secondStr) {
      result.push(parseInt(secondStr, 10));
    }

    return result;
  }

  function untruncateYear(yearStr) {
    var year = parseInt(yearStr, 10);

    if (year <= 49) {
      return 2000 + year;
    } else if (year <= 999) {
      return 1900 + year;
    }

    return year;
  }

  function preprocessRFC2822(s) {
    // Remove comments and folding whitespace and replace multiple-spaces with a single space
    return s.replace(/\([^()]*\)|[\n\t]/g, ' ').replace(/(\s\s+)/g, ' ').replace(/^\s\s*/, '').replace(/\s\s*$/, '');
  }

  function checkWeekday(weekdayStr, parsedInput, config) {
    if (weekdayStr) {
      // TODO: Replace the vanilla JS Date object with an independent day-of-week check.
      var weekdayProvided = defaultLocaleWeekdaysShort.indexOf(weekdayStr),
          weekdayActual = new Date(parsedInput[0], parsedInput[1], parsedInput[2]).getDay();

      if (weekdayProvided !== weekdayActual) {
        getParsingFlags(config).weekdayMismatch = true;
        config._isValid = false;
        return false;
      }
    }

    return true;
  }

  function calculateOffset(obsOffset, militaryOffset, numOffset) {
    if (obsOffset) {
      return obsOffsets[obsOffset];
    } else if (militaryOffset) {
      // the only allowed military tz is Z
      return 0;
    } else {
      var hm = parseInt(numOffset, 10),
          m = hm % 100,
          h = (hm - m) / 100;
      return h * 60 + m;
    }
  } // date and time from ref 2822 format


  function configFromRFC2822(config) {
    var match = rfc2822.exec(preprocessRFC2822(config._i)),
        parsedArray;

    if (match) {
      parsedArray = extractFromRFC2822Strings(match[4], match[3], match[2], match[5], match[6], match[7]);

      if (!checkWeekday(match[1], parsedArray, config)) {
        return;
      }

      config._a = parsedArray;
      config._tzm = calculateOffset(match[8], match[9], match[10]);
      config._d = createUTCDate.apply(null, config._a);

      config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);

      getParsingFlags(config).rfc2822 = true;
    } else {
      config._isValid = false;
    }
  } // date from 1) ASP.NET, 2) ISO, 3) RFC 2822 formats, or 4) optional fallback if parsing isn't strict


  function configFromString(config) {
    var matched = aspNetJsonRegex.exec(config._i);

    if (matched !== null) {
      config._d = new Date(+matched[1]);
      return;
    }

    configFromISO(config);

    if (config._isValid === false) {
      delete config._isValid;
    } else {
      return;
    }

    configFromRFC2822(config);

    if (config._isValid === false) {
      delete config._isValid;
    } else {
      return;
    }

    if (config._strict) {
      config._isValid = false;
    } else {
      // Final attempt, use Input Fallback
      hooks.createFromInputFallback(config);
    }
  }

  hooks.createFromInputFallback = deprecate('value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), ' + 'which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are ' + 'discouraged. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.', function (config) {
    config._d = new Date(config._i + (config._useUTC ? ' UTC' : ''));
  }); // Pick the first defined of two or three arguments.

  function defaults(a, b, c) {
    if (a != null) {
      return a;
    }

    if (b != null) {
      return b;
    }

    return c;
  }

  function currentDateArray(config) {
    // hooks is actually the exported moment object
    var nowValue = new Date(hooks.now());

    if (config._useUTC) {
      return [nowValue.getUTCFullYear(), nowValue.getUTCMonth(), nowValue.getUTCDate()];
    }

    return [nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()];
  } // convert an array to a date.
  // the array should mirror the parameters below
  // note: all values past the year are optional and will default to the lowest possible value.
  // [year, month, day , hour, minute, second, millisecond]


  function configFromArray(config) {
    var i,
        date,
        input = [],
        currentDate,
        expectedWeekday,
        yearToUse;

    if (config._d) {
      return;
    }

    currentDate = currentDateArray(config); //compute day of the year from weeks and weekdays

    if (config._w && config._a[DATE] == null && config._a[MONTH] == null) {
      dayOfYearFromWeekInfo(config);
    } //if the day of the year is set, figure out what it is


    if (config._dayOfYear != null) {
      yearToUse = defaults(config._a[YEAR], currentDate[YEAR]);

      if (config._dayOfYear > daysInYear(yearToUse) || config._dayOfYear === 0) {
        getParsingFlags(config)._overflowDayOfYear = true;
      }

      date = createUTCDate(yearToUse, 0, config._dayOfYear);
      config._a[MONTH] = date.getUTCMonth();
      config._a[DATE] = date.getUTCDate();
    } // Default to current date.
    // * if no year, month, day of month are given, default to today
    // * if day of month is given, default month and year
    // * if month is given, default only year
    // * if year is given, don't default anything


    for (i = 0; i < 3 && config._a[i] == null; ++i) {
      config._a[i] = input[i] = currentDate[i];
    } // Zero out whatever was not defaulted, including time


    for (; i < 7; i++) {
      config._a[i] = input[i] = config._a[i] == null ? i === 2 ? 1 : 0 : config._a[i];
    } // Check for 24:00:00.000


    if (config._a[HOUR] === 24 && config._a[MINUTE] === 0 && config._a[SECOND] === 0 && config._a[MILLISECOND] === 0) {
      config._nextDay = true;
      config._a[HOUR] = 0;
    }

    config._d = (config._useUTC ? createUTCDate : createDate).apply(null, input);
    expectedWeekday = config._useUTC ? config._d.getUTCDay() : config._d.getDay(); // Apply timezone offset from input. The actual utcOffset can be changed
    // with parseZone.

    if (config._tzm != null) {
      config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);
    }

    if (config._nextDay) {
      config._a[HOUR] = 24;
    } // check for mismatching day of week


    if (config._w && typeof config._w.d !== 'undefined' && config._w.d !== expectedWeekday) {
      getParsingFlags(config).weekdayMismatch = true;
    }
  }

  function dayOfYearFromWeekInfo(config) {
    var w, weekYear, week, weekday, dow, doy, temp, weekdayOverflow, curWeek;
    w = config._w;

    if (w.GG != null || w.W != null || w.E != null) {
      dow = 1;
      doy = 4; // TODO: We need to take the current isoWeekYear, but that depends on
      // how we interpret now (local, utc, fixed offset). So create
      // a now version of current config (take local/utc/offset flags, and
      // create now).

      weekYear = defaults(w.GG, config._a[YEAR], weekOfYear(createLocal(), 1, 4).year);
      week = defaults(w.W, 1);
      weekday = defaults(w.E, 1);

      if (weekday < 1 || weekday > 7) {
        weekdayOverflow = true;
      }
    } else {
      dow = config._locale._week.dow;
      doy = config._locale._week.doy;
      curWeek = weekOfYear(createLocal(), dow, doy);
      weekYear = defaults(w.gg, config._a[YEAR], curWeek.year); // Default to current week.

      week = defaults(w.w, curWeek.week);

      if (w.d != null) {
        // weekday -- low day numbers are considered next week
        weekday = w.d;

        if (weekday < 0 || weekday > 6) {
          weekdayOverflow = true;
        }
      } else if (w.e != null) {
        // local weekday -- counting starts from beginning of week
        weekday = w.e + dow;

        if (w.e < 0 || w.e > 6) {
          weekdayOverflow = true;
        }
      } else {
        // default to beginning of week
        weekday = dow;
      }
    }

    if (week < 1 || week > weeksInYear(weekYear, dow, doy)) {
      getParsingFlags(config)._overflowWeeks = true;
    } else if (weekdayOverflow != null) {
      getParsingFlags(config)._overflowWeekday = true;
    } else {
      temp = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy);
      config._a[YEAR] = temp.year;
      config._dayOfYear = temp.dayOfYear;
    }
  } // constant that refers to the ISO standard


  hooks.ISO_8601 = function () {}; // constant that refers to the RFC 2822 form


  hooks.RFC_2822 = function () {}; // date from string and format string


  function configFromStringAndFormat(config) {
    // TODO: Move this to another part of the creation flow to prevent circular deps
    if (config._f === hooks.ISO_8601) {
      configFromISO(config);
      return;
    }

    if (config._f === hooks.RFC_2822) {
      configFromRFC2822(config);
      return;
    }

    config._a = [];
    getParsingFlags(config).empty = true; // This array is used to make a Date, either with `new Date` or `Date.UTC`

    var string = '' + config._i,
        i,
        parsedInput,
        tokens,
        token,
        skipped,
        stringLength = string.length,
        totalParsedInputLength = 0,
        era,
        tokenLen;
    tokens = expandFormat(config._f, config._locale).match(formattingTokens) || [];
    tokenLen = tokens.length;

    for (i = 0; i < tokenLen; i++) {
      token = tokens[i];
      parsedInput = (string.match(getParseRegexForToken(token, config)) || [])[0];

      if (parsedInput) {
        skipped = string.substr(0, string.indexOf(parsedInput));

        if (skipped.length > 0) {
          getParsingFlags(config).unusedInput.push(skipped);
        }

        string = string.slice(string.indexOf(parsedInput) + parsedInput.length);
        totalParsedInputLength += parsedInput.length;
      } // don't parse if it's not a known token


      if (formatTokenFunctions[token]) {
        if (parsedInput) {
          getParsingFlags(config).empty = false;
        } else {
          getParsingFlags(config).unusedTokens.push(token);
        }

        addTimeToArrayFromToken(token, parsedInput, config);
      } else if (config._strict && !parsedInput) {
        getParsingFlags(config).unusedTokens.push(token);
      }
    } // add remaining unparsed input length to the string


    getParsingFlags(config).charsLeftOver = stringLength - totalParsedInputLength;

    if (string.length > 0) {
      getParsingFlags(config).unusedInput.push(string);
    } // clear _12h flag if hour is <= 12


    if (config._a[HOUR] <= 12 && getParsingFlags(config).bigHour === true && config._a[HOUR] > 0) {
      getParsingFlags(config).bigHour = undefined;
    }

    getParsingFlags(config).parsedDateParts = config._a.slice(0);
    getParsingFlags(config).meridiem = config._meridiem; // handle meridiem

    config._a[HOUR] = meridiemFixWrap(config._locale, config._a[HOUR], config._meridiem); // handle era

    era = getParsingFlags(config).era;

    if (era !== null) {
      config._a[YEAR] = config._locale.erasConvertYear(era, config._a[YEAR]);
    }

    configFromArray(config);
    checkOverflow(config);
  }

  function meridiemFixWrap(locale, hour, meridiem) {
    var isPm;

    if (meridiem == null) {
      // nothing to do
      return hour;
    }

    if (locale.meridiemHour != null) {
      return locale.meridiemHour(hour, meridiem);
    } else if (locale.isPM != null) {
      // Fallback
      isPm = locale.isPM(meridiem);

      if (isPm && hour < 12) {
        hour += 12;
      }

      if (!isPm && hour === 12) {
        hour = 0;
      }

      return hour;
    } else {
      // this is not supposed to happen
      return hour;
    }
  } // date from string and array of format strings


  function configFromStringAndArray(config) {
    var tempConfig,
        bestMoment,
        scoreToBeat,
        i,
        currentScore,
        validFormatFound,
        bestFormatIsValid = false,
        configfLen = config._f.length;

    if (configfLen === 0) {
      getParsingFlags(config).invalidFormat = true;
      config._d = new Date(NaN);
      return;
    }

    for (i = 0; i < configfLen; i++) {
      currentScore = 0;
      validFormatFound = false;
      tempConfig = copyConfig({}, config);

      if (config._useUTC != null) {
        tempConfig._useUTC = config._useUTC;
      }

      tempConfig._f = config._f[i];
      configFromStringAndFormat(tempConfig);

      if (isValid(tempConfig)) {
        validFormatFound = true;
      } // if there is any input that was not parsed add a penalty for that format


      currentScore += getParsingFlags(tempConfig).charsLeftOver; //or tokens

      currentScore += getParsingFlags(tempConfig).unusedTokens.length * 10;
      getParsingFlags(tempConfig).score = currentScore;

      if (!bestFormatIsValid) {
        if (scoreToBeat == null || currentScore < scoreToBeat || validFormatFound) {
          scoreToBeat = currentScore;
          bestMoment = tempConfig;

          if (validFormatFound) {
            bestFormatIsValid = true;
          }
        }
      } else {
        if (currentScore < scoreToBeat) {
          scoreToBeat = currentScore;
          bestMoment = tempConfig;
        }
      }
    }

    extend(config, bestMoment || tempConfig);
  }

  function configFromObject(config) {
    if (config._d) {
      return;
    }

    var i = normalizeObjectUnits(config._i),
        dayOrDate = i.day === undefined ? i.date : i.day;
    config._a = map([i.year, i.month, dayOrDate, i.hour, i.minute, i.second, i.millisecond], function (obj) {
      return obj && parseInt(obj, 10);
    });
    configFromArray(config);
  }

  function createFromConfig(config) {
    var res = new Moment(checkOverflow(prepareConfig(config)));

    if (res._nextDay) {
      // Adding is smart enough around DST
      res.add(1, 'd');
      res._nextDay = undefined;
    }

    return res;
  }

  function prepareConfig(config) {
    var input = config._i,
        format = config._f;
    config._locale = config._locale || getLocale(config._l);

    if (input === null || format === undefined && input === '') {
      return createInvalid({
        nullInput: true
      });
    }

    if (typeof input === 'string') {
      config._i = input = config._locale.preparse(input);
    }

    if (isMoment(input)) {
      return new Moment(checkOverflow(input));
    } else if (isDate(input)) {
      config._d = input;
    } else if (isArray(format)) {
      configFromStringAndArray(config);
    } else if (format) {
      configFromStringAndFormat(config);
    } else {
      configFromInput(config);
    }

    if (!isValid(config)) {
      config._d = null;
    }

    return config;
  }

  function configFromInput(config) {
    var input = config._i;

    if (isUndefined(input)) {
      config._d = new Date(hooks.now());
    } else if (isDate(input)) {
      config._d = new Date(input.valueOf());
    } else if (typeof input === 'string') {
      configFromString(config);
    } else if (isArray(input)) {
      config._a = map(input.slice(0), function (obj) {
        return parseInt(obj, 10);
      });
      configFromArray(config);
    } else if (isObject(input)) {
      configFromObject(config);
    } else if (isNumber(input)) {
      // from milliseconds
      config._d = new Date(input);
    } else {
      hooks.createFromInputFallback(config);
    }
  }

  function createLocalOrUTC(input, format, locale, strict, isUTC) {
    var c = {};

    if (format === true || format === false) {
      strict = format;
      format = undefined;
    }

    if (locale === true || locale === false) {
      strict = locale;
      locale = undefined;
    }

    if (isObject(input) && isObjectEmpty(input) || isArray(input) && input.length === 0) {
      input = undefined;
    } // object construction must be done this way.
    // https://github.com/moment/moment/issues/1423


    c._isAMomentObject = true;
    c._useUTC = c._isUTC = isUTC;
    c._l = locale;
    c._i = input;
    c._f = format;
    c._strict = strict;
    return createFromConfig(c);
  }

  function createLocal(input, format, locale, strict) {
    return createLocalOrUTC(input, format, locale, strict, false);
  }

  var prototypeMin = deprecate('moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/', function () {
    var other = createLocal.apply(null, arguments);

    if (this.isValid() && other.isValid()) {
      return other < this ? this : other;
    } else {
      return createInvalid();
    }
  }),
      prototypeMax = deprecate('moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/', function () {
    var other = createLocal.apply(null, arguments);

    if (this.isValid() && other.isValid()) {
      return other > this ? this : other;
    } else {
      return createInvalid();
    }
  }); // Pick a moment m from moments so that m[fn](other) is true for all
  // other. This relies on the function fn to be transitive.
  //
  // moments should either be an array of moment objects or an array, whose
  // first element is an array of moment objects.

  function pickBy(fn, moments) {
    var res, i;

    if (moments.length === 1 && isArray(moments[0])) {
      moments = moments[0];
    }

    if (!moments.length) {
      return createLocal();
    }

    res = moments[0];

    for (i = 1; i < moments.length; ++i) {
      if (!moments[i].isValid() || moments[i][fn](res)) {
        res = moments[i];
      }
    }

    return res;
  } // TODO: Use [].sort instead?


  function min() {
    var args = [].slice.call(arguments, 0);
    return pickBy('isBefore', args);
  }

  function max() {
    var args = [].slice.call(arguments, 0);
    return pickBy('isAfter', args);
  }

  var now = function () {
    return Date.now ? Date.now() : +new Date();
  };

  var ordering = ['year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second', 'millisecond'];

  function isDurationValid(m) {
    var key,
        unitHasDecimal = false,
        i,
        orderLen = ordering.length;

    for (key in m) {
      if (hasOwnProp(m, key) && !(indexOf.call(ordering, key) !== -1 && (m[key] == null || !isNaN(m[key])))) {
        return false;
      }
    }

    for (i = 0; i < orderLen; ++i) {
      if (m[ordering[i]]) {
        if (unitHasDecimal) {
          return false; // only allow non-integers for smallest unit
        }

        if (parseFloat(m[ordering[i]]) !== toInt(m[ordering[i]])) {
          unitHasDecimal = true;
        }
      }
    }

    return true;
  }

  function isValid$1() {
    return this._isValid;
  }

  function createInvalid$1() {
    return createDuration(NaN);
  }

  function Duration(duration) {
    var normalizedInput = normalizeObjectUnits(duration),
        years = normalizedInput.year || 0,
        quarters = normalizedInput.quarter || 0,
        months = normalizedInput.month || 0,
        weeks = normalizedInput.week || normalizedInput.isoWeek || 0,
        days = normalizedInput.day || 0,
        hours = normalizedInput.hour || 0,
        minutes = normalizedInput.minute || 0,
        seconds = normalizedInput.second || 0,
        milliseconds = normalizedInput.millisecond || 0;
    this._isValid = isDurationValid(normalizedInput); // representation for dateAddRemove

    this._milliseconds = +milliseconds + seconds * 1e3 + // 1000
    minutes * 6e4 + // 1000 * 60
    hours * 1000 * 60 * 60; //using 1000 * 60 * 60 instead of 36e5 to avoid floating point rounding errors https://github.com/moment/moment/issues/2978
    // Because of dateAddRemove treats 24 hours as different from a
    // day when working around DST, we need to store them separately

    this._days = +days + weeks * 7; // It is impossible to translate months into days without knowing
    // which months you are are talking about, so we have to store
    // it separately.

    this._months = +months + quarters * 3 + years * 12;
    this._data = {};
    this._locale = getLocale();

    this._bubble();
  }

  function isDuration(obj) {
    return obj instanceof Duration;
  }

  function absRound(number) {
    if (number < 0) {
      return Math.round(-1 * number) * -1;
    } else {
      return Math.round(number);
    }
  } // compare two arrays, return the number of differences


  function compareArrays(array1, array2, dontConvert) {
    var len = Math.min(array1.length, array2.length),
        lengthDiff = Math.abs(array1.length - array2.length),
        diffs = 0,
        i;

    for (i = 0; i < len; i++) {
      if (dontConvert && array1[i] !== array2[i] || !dontConvert && toInt(array1[i]) !== toInt(array2[i])) {
        diffs++;
      }
    }

    return diffs + lengthDiff;
  } // FORMATTING


  function offset(token, separator) {
    addFormatToken(token, 0, 0, function () {
      var offset = this.utcOffset(),
          sign = '+';

      if (offset < 0) {
        offset = -offset;
        sign = '-';
      }

      return sign + zeroFill(~~(offset / 60), 2) + separator + zeroFill(~~offset % 60, 2);
    });
  }

  offset('Z', ':');
  offset('ZZ', ''); // PARSING

  addRegexToken('Z', matchShortOffset);
  addRegexToken('ZZ', matchShortOffset);
  addParseToken(['Z', 'ZZ'], function (input, array, config) {
    config._useUTC = true;
    config._tzm = offsetFromString(matchShortOffset, input);
  }); // HELPERS
  // timezone chunker
  // '+10:00' > ['10',  '00']
  // '-1530'  > ['-15', '30']

  var chunkOffset = /([\+\-]|\d\d)/gi;

  function offsetFromString(matcher, string) {
    var matches = (string || '').match(matcher),
        chunk,
        parts,
        minutes;

    if (matches === null) {
      return null;
    }

    chunk = matches[matches.length - 1] || [];
    parts = (chunk + '').match(chunkOffset) || ['-', 0, 0];
    minutes = +(parts[1] * 60) + toInt(parts[2]);
    return minutes === 0 ? 0 : parts[0] === '+' ? minutes : -minutes;
  } // Return a moment from input, that is local/utc/zone equivalent to model.


  function cloneWithOffset(input, model) {
    var res, diff;

    if (model._isUTC) {
      res = model.clone();
      diff = (isMoment(input) || isDate(input) ? input.valueOf() : createLocal(input).valueOf()) - res.valueOf(); // Use low-level api, because this fn is low-level api.

      res._d.setTime(res._d.valueOf() + diff);

      hooks.updateOffset(res, false);
      return res;
    } else {
      return createLocal(input).local();
    }
  }

  function getDateOffset(m) {
    // On Firefox.24 Date#getTimezoneOffset returns a floating point.
    // https://github.com/moment/moment/pull/1871
    return -Math.round(m._d.getTimezoneOffset());
  } // HOOKS
  // This function will be called whenever a moment is mutated.
  // It is intended to keep the offset in sync with the timezone.


  hooks.updateOffset = function () {}; // MOMENTS
  // keepLocalTime = true means only change the timezone, without
  // affecting the local hour. So 5:31:26 +0300 --[utcOffset(2, true)]-->
  // 5:31:26 +0200 It is possible that 5:31:26 doesn't exist with offset
  // +0200, so we adjust the time as needed, to be valid.
  //
  // Keeping the time actually adds/subtracts (one hour)
  // from the actual represented time. That is why we call updateOffset
  // a second time. In case it wants us to change the offset again
  // _changeInProgress == true case, then we have to adjust, because
  // there is no such time in the given timezone.


  function getSetOffset(input, keepLocalTime, keepMinutes) {
    var offset = this._offset || 0,
        localAdjust;

    if (!this.isValid()) {
      return input != null ? this : NaN;
    }

    if (input != null) {
      if (typeof input === 'string') {
        input = offsetFromString(matchShortOffset, input);

        if (input === null) {
          return this;
        }
      } else if (Math.abs(input) < 16 && !keepMinutes) {
        input = input * 60;
      }

      if (!this._isUTC && keepLocalTime) {
        localAdjust = getDateOffset(this);
      }

      this._offset = input;
      this._isUTC = true;

      if (localAdjust != null) {
        this.add(localAdjust, 'm');
      }

      if (offset !== input) {
        if (!keepLocalTime || this._changeInProgress) {
          addSubtract(this, createDuration(input - offset, 'm'), 1, false);
        } else if (!this._changeInProgress) {
          this._changeInProgress = true;
          hooks.updateOffset(this, true);
          this._changeInProgress = null;
        }
      }

      return this;
    } else {
      return this._isUTC ? offset : getDateOffset(this);
    }
  }

  function getSetZone(input, keepLocalTime) {
    if (input != null) {
      if (typeof input !== 'string') {
        input = -input;
      }

      this.utcOffset(input, keepLocalTime);
      return this;
    } else {
      return -this.utcOffset();
    }
  }

  function setOffsetToUTC(keepLocalTime) {
    return this.utcOffset(0, keepLocalTime);
  }

  function setOffsetToLocal(keepLocalTime) {
    if (this._isUTC) {
      this.utcOffset(0, keepLocalTime);
      this._isUTC = false;

      if (keepLocalTime) {
        this.subtract(getDateOffset(this), 'm');
      }
    }

    return this;
  }

  function setOffsetToParsedOffset() {
    if (this._tzm != null) {
      this.utcOffset(this._tzm, false, true);
    } else if (typeof this._i === 'string') {
      var tZone = offsetFromString(matchOffset, this._i);

      if (tZone != null) {
        this.utcOffset(tZone);
      } else {
        this.utcOffset(0, true);
      }
    }

    return this;
  }

  function hasAlignedHourOffset(input) {
    if (!this.isValid()) {
      return false;
    }

    input = input ? createLocal(input).utcOffset() : 0;
    return (this.utcOffset() - input) % 60 === 0;
  }

  function isDaylightSavingTime() {
    return this.utcOffset() > this.clone().month(0).utcOffset() || this.utcOffset() > this.clone().month(5).utcOffset();
  }

  function isDaylightSavingTimeShifted() {
    if (!isUndefined(this._isDSTShifted)) {
      return this._isDSTShifted;
    }

    var c = {},
        other;
    copyConfig(c, this);
    c = prepareConfig(c);

    if (c._a) {
      other = c._isUTC ? createUTC(c._a) : createLocal(c._a);
      this._isDSTShifted = this.isValid() && compareArrays(c._a, other.toArray()) > 0;
    } else {
      this._isDSTShifted = false;
    }

    return this._isDSTShifted;
  }

  function isLocal() {
    return this.isValid() ? !this._isUTC : false;
  }

  function isUtcOffset() {
    return this.isValid() ? this._isUTC : false;
  }

  function isUtc() {
    return this.isValid() ? this._isUTC && this._offset === 0 : false;
  } // ASP.NET json date format regex


  var aspNetRegex = /^(-|\+)?(?:(\d*)[. ])?(\d+):(\d+)(?::(\d+)(\.\d*)?)?$/,
      // from http://docs.closure-library.googlecode.com/git/closure_goog_date_date.js.source.html
  // somewhat more in line with 4.4.3.2 2004 spec, but allows decimal anywhere
  // and further modified to allow for strings containing both week and day
  isoRegex = /^(-|\+)?P(?:([-+]?[0-9,.]*)Y)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)W)?(?:([-+]?[0-9,.]*)D)?(?:T(?:([-+]?[0-9,.]*)H)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)S)?)?$/;

  function createDuration(input, key) {
    var duration = input,
        // matching against regexp is expensive, do it on demand
    match = null,
        sign,
        ret,
        diffRes;

    if (isDuration(input)) {
      duration = {
        ms: input._milliseconds,
        d: input._days,
        M: input._months
      };
    } else if (isNumber(input) || !isNaN(+input)) {
      duration = {};

      if (key) {
        duration[key] = +input;
      } else {
        duration.milliseconds = +input;
      }
    } else if (match = aspNetRegex.exec(input)) {
      sign = match[1] === '-' ? -1 : 1;
      duration = {
        y: 0,
        d: toInt(match[DATE]) * sign,
        h: toInt(match[HOUR]) * sign,
        m: toInt(match[MINUTE]) * sign,
        s: toInt(match[SECOND]) * sign,
        ms: toInt(absRound(match[MILLISECOND] * 1000)) * sign // the millisecond decimal point is included in the match

      };
    } else if (match = isoRegex.exec(input)) {
      sign = match[1] === '-' ? -1 : 1;
      duration = {
        y: parseIso(match[2], sign),
        M: parseIso(match[3], sign),
        w: parseIso(match[4], sign),
        d: parseIso(match[5], sign),
        h: parseIso(match[6], sign),
        m: parseIso(match[7], sign),
        s: parseIso(match[8], sign)
      };
    } else if (duration == null) {
      // checks for null or undefined
      duration = {};
    } else if (typeof duration === 'object' && ('from' in duration || 'to' in duration)) {
      diffRes = momentsDifference(createLocal(duration.from), createLocal(duration.to));
      duration = {};
      duration.ms = diffRes.milliseconds;
      duration.M = diffRes.months;
    }

    ret = new Duration(duration);

    if (isDuration(input) && hasOwnProp(input, '_locale')) {
      ret._locale = input._locale;
    }

    if (isDuration(input) && hasOwnProp(input, '_isValid')) {
      ret._isValid = input._isValid;
    }

    return ret;
  }

  createDuration.fn = Duration.prototype;
  createDuration.invalid = createInvalid$1;

  function parseIso(inp, sign) {
    // We'd normally use ~~inp for this, but unfortunately it also
    // converts floats to ints.
    // inp may be undefined, so careful calling replace on it.
    var res = inp && parseFloat(inp.replace(',', '.')); // apply sign while we're at it

    return (isNaN(res) ? 0 : res) * sign;
  }

  function positiveMomentsDifference(base, other) {
    var res = {};
    res.months = other.month() - base.month() + (other.year() - base.year()) * 12;

    if (base.clone().add(res.months, 'M').isAfter(other)) {
      --res.months;
    }

    res.milliseconds = +other - +base.clone().add(res.months, 'M');
    return res;
  }

  function momentsDifference(base, other) {
    var res;

    if (!(base.isValid() && other.isValid())) {
      return {
        milliseconds: 0,
        months: 0
      };
    }

    other = cloneWithOffset(other, base);

    if (base.isBefore(other)) {
      res = positiveMomentsDifference(base, other);
    } else {
      res = positiveMomentsDifference(other, base);
      res.milliseconds = -res.milliseconds;
      res.months = -res.months;
    }

    return res;
  } // TODO: remove 'name' arg after deprecation is removed


  function createAdder(direction, name) {
    return function (val, period) {
      var dur, tmp; //invert the arguments, but complain about it

      if (period !== null && !isNaN(+period)) {
        deprecateSimple(name, 'moment().' + name + '(period, number) is deprecated. Please use moment().' + name + '(number, period). ' + 'See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info.');
        tmp = val;
        val = period;
        period = tmp;
      }

      dur = createDuration(val, period);
      addSubtract(this, dur, direction);
      return this;
    };
  }

  function addSubtract(mom, duration, isAdding, updateOffset) {
    var milliseconds = duration._milliseconds,
        days = absRound(duration._days),
        months = absRound(duration._months);

    if (!mom.isValid()) {
      // No op
      return;
    }

    updateOffset = updateOffset == null ? true : updateOffset;

    if (months) {
      setMonth(mom, get(mom, 'Month') + months * isAdding);
    }

    if (days) {
      set$1(mom, 'Date', get(mom, 'Date') + days * isAdding);
    }

    if (milliseconds) {
      mom._d.setTime(mom._d.valueOf() + milliseconds * isAdding);
    }

    if (updateOffset) {
      hooks.updateOffset(mom, days || months);
    }
  }

  var add = createAdder(1, 'add'),
      subtract = createAdder(-1, 'subtract');

  function isString(input) {
    return typeof input === 'string' || input instanceof String;
  } // type MomentInput = Moment | Date | string | number | (number | string)[] | MomentInputObject | void; // null | undefined


  function isMomentInput(input) {
    return isMoment(input) || isDate(input) || isString(input) || isNumber(input) || isNumberOrStringArray(input) || isMomentInputObject(input) || input === null || input === undefined;
  }

  function isMomentInputObject(input) {
    var objectTest = isObject(input) && !isObjectEmpty(input),
        propertyTest = false,
        properties = ['years', 'year', 'y', 'months', 'month', 'M', 'days', 'day', 'd', 'dates', 'date', 'D', 'hours', 'hour', 'h', 'minutes', 'minute', 'm', 'seconds', 'second', 's', 'milliseconds', 'millisecond', 'ms'],
        i,
        property,
        propertyLen = properties.length;

    for (i = 0; i < propertyLen; i += 1) {
      property = properties[i];
      propertyTest = propertyTest || hasOwnProp(input, property);
    }

    return objectTest && propertyTest;
  }

  function isNumberOrStringArray(input) {
    var arrayTest = isArray(input),
        dataTypeTest = false;

    if (arrayTest) {
      dataTypeTest = input.filter(function (item) {
        return !isNumber(item) && isString(input);
      }).length === 0;
    }

    return arrayTest && dataTypeTest;
  }

  function isCalendarSpec(input) {
    var objectTest = isObject(input) && !isObjectEmpty(input),
        propertyTest = false,
        properties = ['sameDay', 'nextDay', 'lastDay', 'nextWeek', 'lastWeek', 'sameElse'],
        i,
        property;

    for (i = 0; i < properties.length; i += 1) {
      property = properties[i];
      propertyTest = propertyTest || hasOwnProp(input, property);
    }

    return objectTest && propertyTest;
  }

  function getCalendarFormat(myMoment, now) {
    var diff = myMoment.diff(now, 'days', true);
    return diff < -6 ? 'sameElse' : diff < -1 ? 'lastWeek' : diff < 0 ? 'lastDay' : diff < 1 ? 'sameDay' : diff < 2 ? 'nextDay' : diff < 7 ? 'nextWeek' : 'sameElse';
  }

  function calendar$1(time, formats) {
    // Support for single parameter, formats only overload to the calendar function
    if (arguments.length === 1) {
      if (!arguments[0]) {
        time = undefined;
        formats = undefined;
      } else if (isMomentInput(arguments[0])) {
        time = arguments[0];
        formats = undefined;
      } else if (isCalendarSpec(arguments[0])) {
        formats = arguments[0];
        time = undefined;
      }
    } // We want to compare the start of today, vs this.
    // Getting start-of-today depends on whether we're local/utc/offset or not.


    var now = time || createLocal(),
        sod = cloneWithOffset(now, this).startOf('day'),
        format = hooks.calendarFormat(this, sod) || 'sameElse',
        output = formats && (isFunction(formats[format]) ? formats[format].call(this, now) : formats[format]);
    return this.format(output || this.localeData().calendar(format, this, createLocal(now)));
  }

  function clone() {
    return new Moment(this);
  }

  function isAfter(input, units) {
    var localInput = isMoment(input) ? input : createLocal(input);

    if (!(this.isValid() && localInput.isValid())) {
      return false;
    }

    units = normalizeUnits(units) || 'millisecond';

    if (units === 'millisecond') {
      return this.valueOf() > localInput.valueOf();
    } else {
      return localInput.valueOf() < this.clone().startOf(units).valueOf();
    }
  }

  function isBefore(input, units) {
    var localInput = isMoment(input) ? input : createLocal(input);

    if (!(this.isValid() && localInput.isValid())) {
      return false;
    }

    units = normalizeUnits(units) || 'millisecond';

    if (units === 'millisecond') {
      return this.valueOf() < localInput.valueOf();
    } else {
      return this.clone().endOf(units).valueOf() < localInput.valueOf();
    }
  }

  function isBetween(from, to, units, inclusivity) {
    var localFrom = isMoment(from) ? from : createLocal(from),
        localTo = isMoment(to) ? to : createLocal(to);

    if (!(this.isValid() && localFrom.isValid() && localTo.isValid())) {
      return false;
    }

    inclusivity = inclusivity || '()';
    return (inclusivity[0] === '(' ? this.isAfter(localFrom, units) : !this.isBefore(localFrom, units)) && (inclusivity[1] === ')' ? this.isBefore(localTo, units) : !this.isAfter(localTo, units));
  }

  function isSame(input, units) {
    var localInput = isMoment(input) ? input : createLocal(input),
        inputMs;

    if (!(this.isValid() && localInput.isValid())) {
      return false;
    }

    units = normalizeUnits(units) || 'millisecond';

    if (units === 'millisecond') {
      return this.valueOf() === localInput.valueOf();
    } else {
      inputMs = localInput.valueOf();
      return this.clone().startOf(units).valueOf() <= inputMs && inputMs <= this.clone().endOf(units).valueOf();
    }
  }

  function isSameOrAfter(input, units) {
    return this.isSame(input, units) || this.isAfter(input, units);
  }

  function isSameOrBefore(input, units) {
    return this.isSame(input, units) || this.isBefore(input, units);
  }

  function diff(input, units, asFloat) {
    var that, zoneDelta, output;

    if (!this.isValid()) {
      return NaN;
    }

    that = cloneWithOffset(input, this);

    if (!that.isValid()) {
      return NaN;
    }

    zoneDelta = (that.utcOffset() - this.utcOffset()) * 6e4;
    units = normalizeUnits(units);

    switch (units) {
      case 'year':
        output = monthDiff(this, that) / 12;
        break;

      case 'month':
        output = monthDiff(this, that);
        break;

      case 'quarter':
        output = monthDiff(this, that) / 3;
        break;

      case 'second':
        output = (this - that) / 1e3;
        break;
      // 1000

      case 'minute':
        output = (this - that) / 6e4;
        break;
      // 1000 * 60

      case 'hour':
        output = (this - that) / 36e5;
        break;
      // 1000 * 60 * 60

      case 'day':
        output = (this - that - zoneDelta) / 864e5;
        break;
      // 1000 * 60 * 60 * 24, negate dst

      case 'week':
        output = (this - that - zoneDelta) / 6048e5;
        break;
      // 1000 * 60 * 60 * 24 * 7, negate dst

      default:
        output = this - that;
    }

    return asFloat ? output : absFloor(output);
  }

  function monthDiff(a, b) {
    if (a.date() < b.date()) {
      // end-of-month calculations work correct when the start month has more
      // days than the end month.
      return -monthDiff(b, a);
    } // difference in months


    var wholeMonthDiff = (b.year() - a.year()) * 12 + (b.month() - a.month()),
        // b is in (anchor - 1 month, anchor + 1 month)
    anchor = a.clone().add(wholeMonthDiff, 'months'),
        anchor2,
        adjust;

    if (b - anchor < 0) {
      anchor2 = a.clone().add(wholeMonthDiff - 1, 'months'); // linear across the month

      adjust = (b - anchor) / (anchor - anchor2);
    } else {
      anchor2 = a.clone().add(wholeMonthDiff + 1, 'months'); // linear across the month

      adjust = (b - anchor) / (anchor2 - anchor);
    } //check for negative zero, return zero if negative zero


    return -(wholeMonthDiff + adjust) || 0;
  }

  hooks.defaultFormat = 'YYYY-MM-DDTHH:mm:ssZ';
  hooks.defaultFormatUtc = 'YYYY-MM-DDTHH:mm:ss[Z]';

  function toString() {
    return this.clone().locale('en').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
  }

  function toISOString(keepOffset) {
    if (!this.isValid()) {
      return null;
    }

    var utc = keepOffset !== true,
        m = utc ? this.clone().utc() : this;

    if (m.year() < 0 || m.year() > 9999) {
      return formatMoment(m, utc ? 'YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]' : 'YYYYYY-MM-DD[T]HH:mm:ss.SSSZ');
    }

    if (isFunction(Date.prototype.toISOString)) {
      // native implementation is ~50x faster, use it when we can
      if (utc) {
        return this.toDate().toISOString();
      } else {
        return new Date(this.valueOf() + this.utcOffset() * 60 * 1000).toISOString().replace('Z', formatMoment(m, 'Z'));
      }
    }

    return formatMoment(m, utc ? 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]' : 'YYYY-MM-DD[T]HH:mm:ss.SSSZ');
  }
  /**
   * Return a human readable representation of a moment that can
   * also be evaluated to get a new moment which is the same
   *
   * @link https://nodejs.org/dist/latest/docs/api/util.html#util_custom_inspect_function_on_objects
   */


  function inspect() {
    if (!this.isValid()) {
      return 'moment.invalid(/* ' + this._i + ' */)';
    }

    var func = 'moment',
        zone = '',
        prefix,
        year,
        datetime,
        suffix;

    if (!this.isLocal()) {
      func = this.utcOffset() === 0 ? 'moment.utc' : 'moment.parseZone';
      zone = 'Z';
    }

    prefix = '[' + func + '("]';
    year = 0 <= this.year() && this.year() <= 9999 ? 'YYYY' : 'YYYYYY';
    datetime = '-MM-DD[T]HH:mm:ss.SSS';
    suffix = zone + '[")]';
    return this.format(prefix + year + datetime + suffix);
  }

  function format(inputString) {
    if (!inputString) {
      inputString = this.isUtc() ? hooks.defaultFormatUtc : hooks.defaultFormat;
    }

    var output = formatMoment(this, inputString);
    return this.localeData().postformat(output);
  }

  function from(time, withoutSuffix) {
    if (this.isValid() && (isMoment(time) && time.isValid() || createLocal(time).isValid())) {
      return createDuration({
        to: this,
        from: time
      }).locale(this.locale()).humanize(!withoutSuffix);
    } else {
      return this.localeData().invalidDate();
    }
  }

  function fromNow(withoutSuffix) {
    return this.from(createLocal(), withoutSuffix);
  }

  function to(time, withoutSuffix) {
    if (this.isValid() && (isMoment(time) && time.isValid() || createLocal(time).isValid())) {
      return createDuration({
        from: this,
        to: time
      }).locale(this.locale()).humanize(!withoutSuffix);
    } else {
      return this.localeData().invalidDate();
    }
  }

  function toNow(withoutSuffix) {
    return this.to(createLocal(), withoutSuffix);
  } // If passed a locale key, it will set the locale for this
  // instance.  Otherwise, it will return the locale configuration
  // variables for this instance.


  function locale(key) {
    var newLocaleData;

    if (key === undefined) {
      return this._locale._abbr;
    } else {
      newLocaleData = getLocale(key);

      if (newLocaleData != null) {
        this._locale = newLocaleData;
      }

      return this;
    }
  }

  var lang = deprecate('moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.', function (key) {
    if (key === undefined) {
      return this.localeData();
    } else {
      return this.locale(key);
    }
  });

  function localeData() {
    return this._locale;
  }

  var MS_PER_SECOND = 1000,
      MS_PER_MINUTE = 60 * MS_PER_SECOND,
      MS_PER_HOUR = 60 * MS_PER_MINUTE,
      MS_PER_400_YEARS = (365 * 400 + 97) * 24 * MS_PER_HOUR; // actual modulo - handles negative numbers (for dates before 1970):

  function mod$1(dividend, divisor) {
    return (dividend % divisor + divisor) % divisor;
  }

  function localStartOfDate(y, m, d) {
    // the date constructor remaps years 0-99 to 1900-1999
    if (y < 100 && y >= 0) {
      // preserve leap years using a full 400 year cycle, then reset
      return new Date(y + 400, m, d) - MS_PER_400_YEARS;
    } else {
      return new Date(y, m, d).valueOf();
    }
  }

  function utcStartOfDate(y, m, d) {
    // Date.UTC remaps years 0-99 to 1900-1999
    if (y < 100 && y >= 0) {
      // preserve leap years using a full 400 year cycle, then reset
      return Date.UTC(y + 400, m, d) - MS_PER_400_YEARS;
    } else {
      return Date.UTC(y, m, d);
    }
  }

  function startOf(units) {
    var time, startOfDate;
    units = normalizeUnits(units);

    if (units === undefined || units === 'millisecond' || !this.isValid()) {
      return this;
    }

    startOfDate = this._isUTC ? utcStartOfDate : localStartOfDate;

    switch (units) {
      case 'year':
        time = startOfDate(this.year(), 0, 1);
        break;

      case 'quarter':
        time = startOfDate(this.year(), this.month() - this.month() % 3, 1);
        break;

      case 'month':
        time = startOfDate(this.year(), this.month(), 1);
        break;

      case 'week':
        time = startOfDate(this.year(), this.month(), this.date() - this.weekday());
        break;

      case 'isoWeek':
        time = startOfDate(this.year(), this.month(), this.date() - (this.isoWeekday() - 1));
        break;

      case 'day':
      case 'date':
        time = startOfDate(this.year(), this.month(), this.date());
        break;

      case 'hour':
        time = this._d.valueOf();
        time -= mod$1(time + (this._isUTC ? 0 : this.utcOffset() * MS_PER_MINUTE), MS_PER_HOUR);
        break;

      case 'minute':
        time = this._d.valueOf();
        time -= mod$1(time, MS_PER_MINUTE);
        break;

      case 'second':
        time = this._d.valueOf();
        time -= mod$1(time, MS_PER_SECOND);
        break;
    }

    this._d.setTime(time);

    hooks.updateOffset(this, true);
    return this;
  }

  function endOf(units) {
    var time, startOfDate;
    units = normalizeUnits(units);

    if (units === undefined || units === 'millisecond' || !this.isValid()) {
      return this;
    }

    startOfDate = this._isUTC ? utcStartOfDate : localStartOfDate;

    switch (units) {
      case 'year':
        time = startOfDate(this.year() + 1, 0, 1) - 1;
        break;

      case 'quarter':
        time = startOfDate(this.year(), this.month() - this.month() % 3 + 3, 1) - 1;
        break;

      case 'month':
        time = startOfDate(this.year(), this.month() + 1, 1) - 1;
        break;

      case 'week':
        time = startOfDate(this.year(), this.month(), this.date() - this.weekday() + 7) - 1;
        break;

      case 'isoWeek':
        time = startOfDate(this.year(), this.month(), this.date() - (this.isoWeekday() - 1) + 7) - 1;
        break;

      case 'day':
      case 'date':
        time = startOfDate(this.year(), this.month(), this.date() + 1) - 1;
        break;

      case 'hour':
        time = this._d.valueOf();
        time += MS_PER_HOUR - mod$1(time + (this._isUTC ? 0 : this.utcOffset() * MS_PER_MINUTE), MS_PER_HOUR) - 1;
        break;

      case 'minute':
        time = this._d.valueOf();
        time += MS_PER_MINUTE - mod$1(time, MS_PER_MINUTE) - 1;
        break;

      case 'second':
        time = this._d.valueOf();
        time += MS_PER_SECOND - mod$1(time, MS_PER_SECOND) - 1;
        break;
    }

    this._d.setTime(time);

    hooks.updateOffset(this, true);
    return this;
  }

  function valueOf() {
    return this._d.valueOf() - (this._offset || 0) * 60000;
  }

  function unix() {
    return Math.floor(this.valueOf() / 1000);
  }

  function toDate() {
    return new Date(this.valueOf());
  }

  function toArray() {
    var m = this;
    return [m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond()];
  }

  function toObject() {
    var m = this;
    return {
      years: m.year(),
      months: m.month(),
      date: m.date(),
      hours: m.hours(),
      minutes: m.minutes(),
      seconds: m.seconds(),
      milliseconds: m.milliseconds()
    };
  }

  function toJSON() {
    // new Date(NaN).toJSON() === null
    return this.isValid() ? this.toISOString() : null;
  }

  function isValid$2() {
    return isValid(this);
  }

  function parsingFlags() {
    return extend({}, getParsingFlags(this));
  }

  function invalidAt() {
    return getParsingFlags(this).overflow;
  }

  function creationData() {
    return {
      input: this._i,
      format: this._f,
      locale: this._locale,
      isUTC: this._isUTC,
      strict: this._strict
    };
  }

  addFormatToken('N', 0, 0, 'eraAbbr');
  addFormatToken('NN', 0, 0, 'eraAbbr');
  addFormatToken('NNN', 0, 0, 'eraAbbr');
  addFormatToken('NNNN', 0, 0, 'eraName');
  addFormatToken('NNNNN', 0, 0, 'eraNarrow');
  addFormatToken('y', ['y', 1], 'yo', 'eraYear');
  addFormatToken('y', ['yy', 2], 0, 'eraYear');
  addFormatToken('y', ['yyy', 3], 0, 'eraYear');
  addFormatToken('y', ['yyyy', 4], 0, 'eraYear');
  addRegexToken('N', matchEraAbbr);
  addRegexToken('NN', matchEraAbbr);
  addRegexToken('NNN', matchEraAbbr);
  addRegexToken('NNNN', matchEraName);
  addRegexToken('NNNNN', matchEraNarrow);
  addParseToken(['N', 'NN', 'NNN', 'NNNN', 'NNNNN'], function (input, array, config, token) {
    var era = config._locale.erasParse(input, token, config._strict);

    if (era) {
      getParsingFlags(config).era = era;
    } else {
      getParsingFlags(config).invalidEra = input;
    }
  });
  addRegexToken('y', matchUnsigned);
  addRegexToken('yy', matchUnsigned);
  addRegexToken('yyy', matchUnsigned);
  addRegexToken('yyyy', matchUnsigned);
  addRegexToken('yo', matchEraYearOrdinal);
  addParseToken(['y', 'yy', 'yyy', 'yyyy'], YEAR);
  addParseToken(['yo'], function (input, array, config, token) {
    var match;

    if (config._locale._eraYearOrdinalRegex) {
      match = input.match(config._locale._eraYearOrdinalRegex);
    }

    if (config._locale.eraYearOrdinalParse) {
      array[YEAR] = config._locale.eraYearOrdinalParse(input, match);
    } else {
      array[YEAR] = parseInt(input, 10);
    }
  });

  function localeEras(m, format) {
    var i,
        l,
        date,
        eras = this._eras || getLocale('en')._eras;

    for (i = 0, l = eras.length; i < l; ++i) {
      switch (typeof eras[i].since) {
        case 'string':
          // truncate time
          date = hooks(eras[i].since).startOf('day');
          eras[i].since = date.valueOf();
          break;
      }

      switch (typeof eras[i].until) {
        case 'undefined':
          eras[i].until = +Infinity;
          break;

        case 'string':
          // truncate time
          date = hooks(eras[i].until).startOf('day').valueOf();
          eras[i].until = date.valueOf();
          break;
      }
    }

    return eras;
  }

  function localeErasParse(eraName, format, strict) {
    var i,
        l,
        eras = this.eras(),
        name,
        abbr,
        narrow;
    eraName = eraName.toUpperCase();

    for (i = 0, l = eras.length; i < l; ++i) {
      name = eras[i].name.toUpperCase();
      abbr = eras[i].abbr.toUpperCase();
      narrow = eras[i].narrow.toUpperCase();

      if (strict) {
        switch (format) {
          case 'N':
          case 'NN':
          case 'NNN':
            if (abbr === eraName) {
              return eras[i];
            }

            break;

          case 'NNNN':
            if (name === eraName) {
              return eras[i];
            }

            break;

          case 'NNNNN':
            if (narrow === eraName) {
              return eras[i];
            }

            break;
        }
      } else if ([name, abbr, narrow].indexOf(eraName) >= 0) {
        return eras[i];
      }
    }
  }

  function localeErasConvertYear(era, year) {
    var dir = era.since <= era.until ? +1 : -1;

    if (year === undefined) {
      return hooks(era.since).year();
    } else {
      return hooks(era.since).year() + (year - era.offset) * dir;
    }
  }

  function getEraName() {
    var i,
        l,
        val,
        eras = this.localeData().eras();

    for (i = 0, l = eras.length; i < l; ++i) {
      // truncate time
      val = this.clone().startOf('day').valueOf();

      if (eras[i].since <= val && val <= eras[i].until) {
        return eras[i].name;
      }

      if (eras[i].until <= val && val <= eras[i].since) {
        return eras[i].name;
      }
    }

    return '';
  }

  function getEraNarrow() {
    var i,
        l,
        val,
        eras = this.localeData().eras();

    for (i = 0, l = eras.length; i < l; ++i) {
      // truncate time
      val = this.clone().startOf('day').valueOf();

      if (eras[i].since <= val && val <= eras[i].until) {
        return eras[i].narrow;
      }

      if (eras[i].until <= val && val <= eras[i].since) {
        return eras[i].narrow;
      }
    }

    return '';
  }

  function getEraAbbr() {
    var i,
        l,
        val,
        eras = this.localeData().eras();

    for (i = 0, l = eras.length; i < l; ++i) {
      // truncate time
      val = this.clone().startOf('day').valueOf();

      if (eras[i].since <= val && val <= eras[i].until) {
        return eras[i].abbr;
      }

      if (eras[i].until <= val && val <= eras[i].since) {
        return eras[i].abbr;
      }
    }

    return '';
  }

  function getEraYear() {
    var i,
        l,
        dir,
        val,
        eras = this.localeData().eras();

    for (i = 0, l = eras.length; i < l; ++i) {
      dir = eras[i].since <= eras[i].until ? +1 : -1; // truncate time

      val = this.clone().startOf('day').valueOf();

      if (eras[i].since <= val && val <= eras[i].until || eras[i].until <= val && val <= eras[i].since) {
        return (this.year() - hooks(eras[i].since).year()) * dir + eras[i].offset;
      }
    }

    return this.year();
  }

  function erasNameRegex(isStrict) {
    if (!hasOwnProp(this, '_erasNameRegex')) {
      computeErasParse.call(this);
    }

    return isStrict ? this._erasNameRegex : this._erasRegex;
  }

  function erasAbbrRegex(isStrict) {
    if (!hasOwnProp(this, '_erasAbbrRegex')) {
      computeErasParse.call(this);
    }

    return isStrict ? this._erasAbbrRegex : this._erasRegex;
  }

  function erasNarrowRegex(isStrict) {
    if (!hasOwnProp(this, '_erasNarrowRegex')) {
      computeErasParse.call(this);
    }

    return isStrict ? this._erasNarrowRegex : this._erasRegex;
  }

  function matchEraAbbr(isStrict, locale) {
    return locale.erasAbbrRegex(isStrict);
  }

  function matchEraName(isStrict, locale) {
    return locale.erasNameRegex(isStrict);
  }

  function matchEraNarrow(isStrict, locale) {
    return locale.erasNarrowRegex(isStrict);
  }

  function matchEraYearOrdinal(isStrict, locale) {
    return locale._eraYearOrdinalRegex || matchUnsigned;
  }

  function computeErasParse() {
    var abbrPieces = [],
        namePieces = [],
        narrowPieces = [],
        mixedPieces = [],
        i,
        l,
        eras = this.eras();

    for (i = 0, l = eras.length; i < l; ++i) {
      namePieces.push(regexEscape(eras[i].name));
      abbrPieces.push(regexEscape(eras[i].abbr));
      narrowPieces.push(regexEscape(eras[i].narrow));
      mixedPieces.push(regexEscape(eras[i].name));
      mixedPieces.push(regexEscape(eras[i].abbr));
      mixedPieces.push(regexEscape(eras[i].narrow));
    }

    this._erasRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
    this._erasNameRegex = new RegExp('^(' + namePieces.join('|') + ')', 'i');
    this._erasAbbrRegex = new RegExp('^(' + abbrPieces.join('|') + ')', 'i');
    this._erasNarrowRegex = new RegExp('^(' + narrowPieces.join('|') + ')', 'i');
  } // FORMATTING


  addFormatToken(0, ['gg', 2], 0, function () {
    return this.weekYear() % 100;
  });
  addFormatToken(0, ['GG', 2], 0, function () {
    return this.isoWeekYear() % 100;
  });

  function addWeekYearFormatToken(token, getter) {
    addFormatToken(0, [token, token.length], 0, getter);
  }

  addWeekYearFormatToken('gggg', 'weekYear');
  addWeekYearFormatToken('ggggg', 'weekYear');
  addWeekYearFormatToken('GGGG', 'isoWeekYear');
  addWeekYearFormatToken('GGGGG', 'isoWeekYear'); // ALIASES

  addUnitAlias('weekYear', 'gg');
  addUnitAlias('isoWeekYear', 'GG'); // PRIORITY

  addUnitPriority('weekYear', 1);
  addUnitPriority('isoWeekYear', 1); // PARSING

  addRegexToken('G', matchSigned);
  addRegexToken('g', matchSigned);
  addRegexToken('GG', match1to2, match2);
  addRegexToken('gg', match1to2, match2);
  addRegexToken('GGGG', match1to4, match4);
  addRegexToken('gggg', match1to4, match4);
  addRegexToken('GGGGG', match1to6, match6);
  addRegexToken('ggggg', match1to6, match6);
  addWeekParseToken(['gggg', 'ggggg', 'GGGG', 'GGGGG'], function (input, week, config, token) {
    week[token.substr(0, 2)] = toInt(input);
  });
  addWeekParseToken(['gg', 'GG'], function (input, week, config, token) {
    week[token] = hooks.parseTwoDigitYear(input);
  }); // MOMENTS

  function getSetWeekYear(input) {
    return getSetWeekYearHelper.call(this, input, this.week(), this.weekday(), this.localeData()._week.dow, this.localeData()._week.doy);
  }

  function getSetISOWeekYear(input) {
    return getSetWeekYearHelper.call(this, input, this.isoWeek(), this.isoWeekday(), 1, 4);
  }

  function getISOWeeksInYear() {
    return weeksInYear(this.year(), 1, 4);
  }

  function getISOWeeksInISOWeekYear() {
    return weeksInYear(this.isoWeekYear(), 1, 4);
  }

  function getWeeksInYear() {
    var weekInfo = this.localeData()._week;

    return weeksInYear(this.year(), weekInfo.dow, weekInfo.doy);
  }

  function getWeeksInWeekYear() {
    var weekInfo = this.localeData()._week;

    return weeksInYear(this.weekYear(), weekInfo.dow, weekInfo.doy);
  }

  function getSetWeekYearHelper(input, week, weekday, dow, doy) {
    var weeksTarget;

    if (input == null) {
      return weekOfYear(this, dow, doy).year;
    } else {
      weeksTarget = weeksInYear(input, dow, doy);

      if (week > weeksTarget) {
        week = weeksTarget;
      }

      return setWeekAll.call(this, input, week, weekday, dow, doy);
    }
  }

  function setWeekAll(weekYear, week, weekday, dow, doy) {
    var dayOfYearData = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy),
        date = createUTCDate(dayOfYearData.year, 0, dayOfYearData.dayOfYear);
    this.year(date.getUTCFullYear());
    this.month(date.getUTCMonth());
    this.date(date.getUTCDate());
    return this;
  } // FORMATTING


  addFormatToken('Q', 0, 'Qo', 'quarter'); // ALIASES

  addUnitAlias('quarter', 'Q'); // PRIORITY

  addUnitPriority('quarter', 7); // PARSING

  addRegexToken('Q', match1);
  addParseToken('Q', function (input, array) {
    array[MONTH] = (toInt(input) - 1) * 3;
  }); // MOMENTS

  function getSetQuarter(input) {
    return input == null ? Math.ceil((this.month() + 1) / 3) : this.month((input - 1) * 3 + this.month() % 3);
  } // FORMATTING


  addFormatToken('D', ['DD', 2], 'Do', 'date'); // ALIASES

  addUnitAlias('date', 'D'); // PRIORITY

  addUnitPriority('date', 9); // PARSING

  addRegexToken('D', match1to2);
  addRegexToken('DD', match1to2, match2);
  addRegexToken('Do', function (isStrict, locale) {
    // TODO: Remove "ordinalParse" fallback in next major release.
    return isStrict ? locale._dayOfMonthOrdinalParse || locale._ordinalParse : locale._dayOfMonthOrdinalParseLenient;
  });
  addParseToken(['D', 'DD'], DATE);
  addParseToken('Do', function (input, array) {
    array[DATE] = toInt(input.match(match1to2)[0]);
  }); // MOMENTS

  var getSetDayOfMonth = makeGetSet('Date', true); // FORMATTING

  addFormatToken('DDD', ['DDDD', 3], 'DDDo', 'dayOfYear'); // ALIASES

  addUnitAlias('dayOfYear', 'DDD'); // PRIORITY

  addUnitPriority('dayOfYear', 4); // PARSING

  addRegexToken('DDD', match1to3);
  addRegexToken('DDDD', match3);
  addParseToken(['DDD', 'DDDD'], function (input, array, config) {
    config._dayOfYear = toInt(input);
  }); // HELPERS
  // MOMENTS

  function getSetDayOfYear(input) {
    var dayOfYear = Math.round((this.clone().startOf('day') - this.clone().startOf('year')) / 864e5) + 1;
    return input == null ? dayOfYear : this.add(input - dayOfYear, 'd');
  } // FORMATTING


  addFormatToken('m', ['mm', 2], 0, 'minute'); // ALIASES

  addUnitAlias('minute', 'm'); // PRIORITY

  addUnitPriority('minute', 14); // PARSING

  addRegexToken('m', match1to2);
  addRegexToken('mm', match1to2, match2);
  addParseToken(['m', 'mm'], MINUTE); // MOMENTS

  var getSetMinute = makeGetSet('Minutes', false); // FORMATTING

  addFormatToken('s', ['ss', 2], 0, 'second'); // ALIASES

  addUnitAlias('second', 's'); // PRIORITY

  addUnitPriority('second', 15); // PARSING

  addRegexToken('s', match1to2);
  addRegexToken('ss', match1to2, match2);
  addParseToken(['s', 'ss'], SECOND); // MOMENTS

  var getSetSecond = makeGetSet('Seconds', false); // FORMATTING

  addFormatToken('S', 0, 0, function () {
    return ~~(this.millisecond() / 100);
  });
  addFormatToken(0, ['SS', 2], 0, function () {
    return ~~(this.millisecond() / 10);
  });
  addFormatToken(0, ['SSS', 3], 0, 'millisecond');
  addFormatToken(0, ['SSSS', 4], 0, function () {
    return this.millisecond() * 10;
  });
  addFormatToken(0, ['SSSSS', 5], 0, function () {
    return this.millisecond() * 100;
  });
  addFormatToken(0, ['SSSSSS', 6], 0, function () {
    return this.millisecond() * 1000;
  });
  addFormatToken(0, ['SSSSSSS', 7], 0, function () {
    return this.millisecond() * 10000;
  });
  addFormatToken(0, ['SSSSSSSS', 8], 0, function () {
    return this.millisecond() * 100000;
  });
  addFormatToken(0, ['SSSSSSSSS', 9], 0, function () {
    return this.millisecond() * 1000000;
  }); // ALIASES

  addUnitAlias('millisecond', 'ms'); // PRIORITY

  addUnitPriority('millisecond', 16); // PARSING

  addRegexToken('S', match1to3, match1);
  addRegexToken('SS', match1to3, match2);
  addRegexToken('SSS', match1to3, match3);
  var token, getSetMillisecond;

  for (token = 'SSSS'; token.length <= 9; token += 'S') {
    addRegexToken(token, matchUnsigned);
  }

  function parseMs(input, array) {
    array[MILLISECOND] = toInt(('0.' + input) * 1000);
  }

  for (token = 'S'; token.length <= 9; token += 'S') {
    addParseToken(token, parseMs);
  }

  getSetMillisecond = makeGetSet('Milliseconds', false); // FORMATTING

  addFormatToken('z', 0, 0, 'zoneAbbr');
  addFormatToken('zz', 0, 0, 'zoneName'); // MOMENTS

  function getZoneAbbr() {
    return this._isUTC ? 'UTC' : '';
  }

  function getZoneName() {
    return this._isUTC ? 'Coordinated Universal Time' : '';
  }

  var proto = Moment.prototype;
  proto.add = add;
  proto.calendar = calendar$1;
  proto.clone = clone;
  proto.diff = diff;
  proto.endOf = endOf;
  proto.format = format;
  proto.from = from;
  proto.fromNow = fromNow;
  proto.to = to;
  proto.toNow = toNow;
  proto.get = stringGet;
  proto.invalidAt = invalidAt;
  proto.isAfter = isAfter;
  proto.isBefore = isBefore;
  proto.isBetween = isBetween;
  proto.isSame = isSame;
  proto.isSameOrAfter = isSameOrAfter;
  proto.isSameOrBefore = isSameOrBefore;
  proto.isValid = isValid$2;
  proto.lang = lang;
  proto.locale = locale;
  proto.localeData = localeData;
  proto.max = prototypeMax;
  proto.min = prototypeMin;
  proto.parsingFlags = parsingFlags;
  proto.set = stringSet;
  proto.startOf = startOf;
  proto.subtract = subtract;
  proto.toArray = toArray;
  proto.toObject = toObject;
  proto.toDate = toDate;
  proto.toISOString = toISOString;
  proto.inspect = inspect;

  if (typeof Symbol !== 'undefined' && Symbol.for != null) {
    proto[Symbol.for('nodejs.util.inspect.custom')] = function () {
      return 'Moment<' + this.format() + '>';
    };
  }

  proto.toJSON = toJSON;
  proto.toString = toString;
  proto.unix = unix;
  proto.valueOf = valueOf;
  proto.creationData = creationData;
  proto.eraName = getEraName;
  proto.eraNarrow = getEraNarrow;
  proto.eraAbbr = getEraAbbr;
  proto.eraYear = getEraYear;
  proto.year = getSetYear;
  proto.isLeapYear = getIsLeapYear;
  proto.weekYear = getSetWeekYear;
  proto.isoWeekYear = getSetISOWeekYear;
  proto.quarter = proto.quarters = getSetQuarter;
  proto.month = getSetMonth;
  proto.daysInMonth = getDaysInMonth;
  proto.week = proto.weeks = getSetWeek;
  proto.isoWeek = proto.isoWeeks = getSetISOWeek;
  proto.weeksInYear = getWeeksInYear;
  proto.weeksInWeekYear = getWeeksInWeekYear;
  proto.isoWeeksInYear = getISOWeeksInYear;
  proto.isoWeeksInISOWeekYear = getISOWeeksInISOWeekYear;
  proto.date = getSetDayOfMonth;
  proto.day = proto.days = getSetDayOfWeek;
  proto.weekday = getSetLocaleDayOfWeek;
  proto.isoWeekday = getSetISODayOfWeek;
  proto.dayOfYear = getSetDayOfYear;
  proto.hour = proto.hours = getSetHour;
  proto.minute = proto.minutes = getSetMinute;
  proto.second = proto.seconds = getSetSecond;
  proto.millisecond = proto.milliseconds = getSetMillisecond;
  proto.utcOffset = getSetOffset;
  proto.utc = setOffsetToUTC;
  proto.local = setOffsetToLocal;
  proto.parseZone = setOffsetToParsedOffset;
  proto.hasAlignedHourOffset = hasAlignedHourOffset;
  proto.isDST = isDaylightSavingTime;
  proto.isLocal = isLocal;
  proto.isUtcOffset = isUtcOffset;
  proto.isUtc = isUtc;
  proto.isUTC = isUtc;
  proto.zoneAbbr = getZoneAbbr;
  proto.zoneName = getZoneName;
  proto.dates = deprecate('dates accessor is deprecated. Use date instead.', getSetDayOfMonth);
  proto.months = deprecate('months accessor is deprecated. Use month instead', getSetMonth);
  proto.years = deprecate('years accessor is deprecated. Use year instead', getSetYear);
  proto.zone = deprecate('moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/', getSetZone);
  proto.isDSTShifted = deprecate('isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information', isDaylightSavingTimeShifted);

  function createUnix(input) {
    return createLocal(input * 1000);
  }

  function createInZone() {
    return createLocal.apply(null, arguments).parseZone();
  }

  function preParsePostFormat(string) {
    return string;
  }

  var proto$1 = Locale.prototype;
  proto$1.calendar = calendar;
  proto$1.longDateFormat = longDateFormat;
  proto$1.invalidDate = invalidDate;
  proto$1.ordinal = ordinal;
  proto$1.preparse = preParsePostFormat;
  proto$1.postformat = preParsePostFormat;
  proto$1.relativeTime = relativeTime;
  proto$1.pastFuture = pastFuture;
  proto$1.set = set;
  proto$1.eras = localeEras;
  proto$1.erasParse = localeErasParse;
  proto$1.erasConvertYear = localeErasConvertYear;
  proto$1.erasAbbrRegex = erasAbbrRegex;
  proto$1.erasNameRegex = erasNameRegex;
  proto$1.erasNarrowRegex = erasNarrowRegex;
  proto$1.months = localeMonths;
  proto$1.monthsShort = localeMonthsShort;
  proto$1.monthsParse = localeMonthsParse;
  proto$1.monthsRegex = monthsRegex;
  proto$1.monthsShortRegex = monthsShortRegex;
  proto$1.week = localeWeek;
  proto$1.firstDayOfYear = localeFirstDayOfYear;
  proto$1.firstDayOfWeek = localeFirstDayOfWeek;
  proto$1.weekdays = localeWeekdays;
  proto$1.weekdaysMin = localeWeekdaysMin;
  proto$1.weekdaysShort = localeWeekdaysShort;
  proto$1.weekdaysParse = localeWeekdaysParse;
  proto$1.weekdaysRegex = weekdaysRegex;
  proto$1.weekdaysShortRegex = weekdaysShortRegex;
  proto$1.weekdaysMinRegex = weekdaysMinRegex;
  proto$1.isPM = localeIsPM;
  proto$1.meridiem = localeMeridiem;

  function get$1(format, index, field, setter) {
    var locale = getLocale(),
        utc = createUTC().set(setter, index);
    return locale[field](utc, format);
  }

  function listMonthsImpl(format, index, field) {
    if (isNumber(format)) {
      index = format;
      format = undefined;
    }

    format = format || '';

    if (index != null) {
      return get$1(format, index, field, 'month');
    }

    var i,
        out = [];

    for (i = 0; i < 12; i++) {
      out[i] = get$1(format, i, field, 'month');
    }

    return out;
  } // ()
  // (5)
  // (fmt, 5)
  // (fmt)
  // (true)
  // (true, 5)
  // (true, fmt, 5)
  // (true, fmt)


  function listWeekdaysImpl(localeSorted, format, index, field) {
    if (typeof localeSorted === 'boolean') {
      if (isNumber(format)) {
        index = format;
        format = undefined;
      }

      format = format || '';
    } else {
      format = localeSorted;
      index = format;
      localeSorted = false;

      if (isNumber(format)) {
        index = format;
        format = undefined;
      }

      format = format || '';
    }

    var locale = getLocale(),
        shift = localeSorted ? locale._week.dow : 0,
        i,
        out = [];

    if (index != null) {
      return get$1(format, (index + shift) % 7, field, 'day');
    }

    for (i = 0; i < 7; i++) {
      out[i] = get$1(format, (i + shift) % 7, field, 'day');
    }

    return out;
  }

  function listMonths(format, index) {
    return listMonthsImpl(format, index, 'months');
  }

  function listMonthsShort(format, index) {
    return listMonthsImpl(format, index, 'monthsShort');
  }

  function listWeekdays(localeSorted, format, index) {
    return listWeekdaysImpl(localeSorted, format, index, 'weekdays');
  }

  function listWeekdaysShort(localeSorted, format, index) {
    return listWeekdaysImpl(localeSorted, format, index, 'weekdaysShort');
  }

  function listWeekdaysMin(localeSorted, format, index) {
    return listWeekdaysImpl(localeSorted, format, index, 'weekdaysMin');
  }

  getSetGlobalLocale('en', {
    eras: [{
      since: '0001-01-01',
      until: +Infinity,
      offset: 1,
      name: 'Anno Domini',
      narrow: 'AD',
      abbr: 'AD'
    }, {
      since: '0000-12-31',
      until: -Infinity,
      offset: 1,
      name: 'Before Christ',
      narrow: 'BC',
      abbr: 'BC'
    }],
    dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/,
    ordinal: function (number) {
      var b = number % 10,
          output = toInt(number % 100 / 10) === 1 ? 'th' : b === 1 ? 'st' : b === 2 ? 'nd' : b === 3 ? 'rd' : 'th';
      return number + output;
    }
  }); // Side effect imports

  hooks.lang = deprecate('moment.lang is deprecated. Use moment.locale instead.', getSetGlobalLocale);
  hooks.langData = deprecate('moment.langData is deprecated. Use moment.localeData instead.', getLocale);
  var mathAbs = Math.abs;

  function abs() {
    var data = this._data;
    this._milliseconds = mathAbs(this._milliseconds);
    this._days = mathAbs(this._days);
    this._months = mathAbs(this._months);
    data.milliseconds = mathAbs(data.milliseconds);
    data.seconds = mathAbs(data.seconds);
    data.minutes = mathAbs(data.minutes);
    data.hours = mathAbs(data.hours);
    data.months = mathAbs(data.months);
    data.years = mathAbs(data.years);
    return this;
  }

  function addSubtract$1(duration, input, value, direction) {
    var other = createDuration(input, value);
    duration._milliseconds += direction * other._milliseconds;
    duration._days += direction * other._days;
    duration._months += direction * other._months;
    return duration._bubble();
  } // supports only 2.0-style add(1, 's') or add(duration)


  function add$1(input, value) {
    return addSubtract$1(this, input, value, 1);
  } // supports only 2.0-style subtract(1, 's') or subtract(duration)


  function subtract$1(input, value) {
    return addSubtract$1(this, input, value, -1);
  }

  function absCeil(number) {
    if (number < 0) {
      return Math.floor(number);
    } else {
      return Math.ceil(number);
    }
  }

  function bubble() {
    var milliseconds = this._milliseconds,
        days = this._days,
        months = this._months,
        data = this._data,
        seconds,
        minutes,
        hours,
        years,
        monthsFromDays; // if we have a mix of positive and negative values, bubble down first
    // check: https://github.com/moment/moment/issues/2166

    if (!(milliseconds >= 0 && days >= 0 && months >= 0 || milliseconds <= 0 && days <= 0 && months <= 0)) {
      milliseconds += absCeil(monthsToDays(months) + days) * 864e5;
      days = 0;
      months = 0;
    } // The following code bubbles up values, see the tests for
    // examples of what that means.


    data.milliseconds = milliseconds % 1000;
    seconds = absFloor(milliseconds / 1000);
    data.seconds = seconds % 60;
    minutes = absFloor(seconds / 60);
    data.minutes = minutes % 60;
    hours = absFloor(minutes / 60);
    data.hours = hours % 24;
    days += absFloor(hours / 24); // convert days to months

    monthsFromDays = absFloor(daysToMonths(days));
    months += monthsFromDays;
    days -= absCeil(monthsToDays(monthsFromDays)); // 12 months -> 1 year

    years = absFloor(months / 12);
    months %= 12;
    data.days = days;
    data.months = months;
    data.years = years;
    return this;
  }

  function daysToMonths(days) {
    // 400 years have 146097 days (taking into account leap year rules)
    // 400 years have 12 months === 4800
    return days * 4800 / 146097;
  }

  function monthsToDays(months) {
    // the reverse of daysToMonths
    return months * 146097 / 4800;
  }

  function as(units) {
    if (!this.isValid()) {
      return NaN;
    }

    var days,
        months,
        milliseconds = this._milliseconds;
    units = normalizeUnits(units);

    if (units === 'month' || units === 'quarter' || units === 'year') {
      days = this._days + milliseconds / 864e5;
      months = this._months + daysToMonths(days);

      switch (units) {
        case 'month':
          return months;

        case 'quarter':
          return months / 3;

        case 'year':
          return months / 12;
      }
    } else {
      // handle milliseconds separately because of floating point math errors (issue #1867)
      days = this._days + Math.round(monthsToDays(this._months));

      switch (units) {
        case 'week':
          return days / 7 + milliseconds / 6048e5;

        case 'day':
          return days + milliseconds / 864e5;

        case 'hour':
          return days * 24 + milliseconds / 36e5;

        case 'minute':
          return days * 1440 + milliseconds / 6e4;

        case 'second':
          return days * 86400 + milliseconds / 1000;
        // Math.floor prevents floating point math errors here

        case 'millisecond':
          return Math.floor(days * 864e5) + milliseconds;

        default:
          throw new Error('Unknown unit ' + units);
      }
    }
  } // TODO: Use this.as('ms')?


  function valueOf$1() {
    if (!this.isValid()) {
      return NaN;
    }

    return this._milliseconds + this._days * 864e5 + this._months % 12 * 2592e6 + toInt(this._months / 12) * 31536e6;
  }

  function makeAs(alias) {
    return function () {
      return this.as(alias);
    };
  }

  var asMilliseconds = makeAs('ms'),
      asSeconds = makeAs('s'),
      asMinutes = makeAs('m'),
      asHours = makeAs('h'),
      asDays = makeAs('d'),
      asWeeks = makeAs('w'),
      asMonths = makeAs('M'),
      asQuarters = makeAs('Q'),
      asYears = makeAs('y');

  function clone$1() {
    return createDuration(this);
  }

  function get$2(units) {
    units = normalizeUnits(units);
    return this.isValid() ? this[units + 's']() : NaN;
  }

  function makeGetter(name) {
    return function () {
      return this.isValid() ? this._data[name] : NaN;
    };
  }

  var milliseconds = makeGetter('milliseconds'),
      seconds = makeGetter('seconds'),
      minutes = makeGetter('minutes'),
      hours = makeGetter('hours'),
      days = makeGetter('days'),
      months = makeGetter('months'),
      years = makeGetter('years');

  function weeks() {
    return absFloor(this.days() / 7);
  }

  var round = Math.round,
      thresholds = {
    ss: 44,
    // a few seconds to seconds
    s: 45,
    // seconds to minute
    m: 45,
    // minutes to hour
    h: 22,
    // hours to day
    d: 26,
    // days to month/week
    w: null,
    // weeks to month
    M: 11 // months to year

  }; // helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize

  function substituteTimeAgo(string, number, withoutSuffix, isFuture, locale) {
    return locale.relativeTime(number || 1, !!withoutSuffix, string, isFuture);
  }

  function relativeTime$1(posNegDuration, withoutSuffix, thresholds, locale) {
    var duration = createDuration(posNegDuration).abs(),
        seconds = round(duration.as('s')),
        minutes = round(duration.as('m')),
        hours = round(duration.as('h')),
        days = round(duration.as('d')),
        months = round(duration.as('M')),
        weeks = round(duration.as('w')),
        years = round(duration.as('y')),
        a = seconds <= thresholds.ss && ['s', seconds] || seconds < thresholds.s && ['ss', seconds] || minutes <= 1 && ['m'] || minutes < thresholds.m && ['mm', minutes] || hours <= 1 && ['h'] || hours < thresholds.h && ['hh', hours] || days <= 1 && ['d'] || days < thresholds.d && ['dd', days];

    if (thresholds.w != null) {
      a = a || weeks <= 1 && ['w'] || weeks < thresholds.w && ['ww', weeks];
    }

    a = a || months <= 1 && ['M'] || months < thresholds.M && ['MM', months] || years <= 1 && ['y'] || ['yy', years];
    a[2] = withoutSuffix;
    a[3] = +posNegDuration > 0;
    a[4] = locale;
    return substituteTimeAgo.apply(null, a);
  } // This function allows you to set the rounding function for relative time strings


  function getSetRelativeTimeRounding(roundingFunction) {
    if (roundingFunction === undefined) {
      return round;
    }

    if (typeof roundingFunction === 'function') {
      round = roundingFunction;
      return true;
    }

    return false;
  } // This function allows you to set a threshold for relative time strings


  function getSetRelativeTimeThreshold(threshold, limit) {
    if (thresholds[threshold] === undefined) {
      return false;
    }

    if (limit === undefined) {
      return thresholds[threshold];
    }

    thresholds[threshold] = limit;

    if (threshold === 's') {
      thresholds.ss = limit - 1;
    }

    return true;
  }

  function humanize(argWithSuffix, argThresholds) {
    if (!this.isValid()) {
      return this.localeData().invalidDate();
    }

    var withSuffix = false,
        th = thresholds,
        locale,
        output;

    if (typeof argWithSuffix === 'object') {
      argThresholds = argWithSuffix;
      argWithSuffix = false;
    }

    if (typeof argWithSuffix === 'boolean') {
      withSuffix = argWithSuffix;
    }

    if (typeof argThresholds === 'object') {
      th = Object.assign({}, thresholds, argThresholds);

      if (argThresholds.s != null && argThresholds.ss == null) {
        th.ss = argThresholds.s - 1;
      }
    }

    locale = this.localeData();
    output = relativeTime$1(this, !withSuffix, th, locale);

    if (withSuffix) {
      output = locale.pastFuture(+this, output);
    }

    return locale.postformat(output);
  }

  var abs$1 = Math.abs;

  function sign(x) {
    return (x > 0) - (x < 0) || +x;
  }

  function toISOString$1() {
    // for ISO strings we do not use the normal bubbling rules:
    //  * milliseconds bubble up until they become hours
    //  * days do not bubble at all
    //  * months bubble up until they become years
    // This is because there is no context-free conversion between hours and days
    // (think of clock changes)
    // and also not between days and months (28-31 days per month)
    if (!this.isValid()) {
      return this.localeData().invalidDate();
    }

    var seconds = abs$1(this._milliseconds) / 1000,
        days = abs$1(this._days),
        months = abs$1(this._months),
        minutes,
        hours,
        years,
        s,
        total = this.asSeconds(),
        totalSign,
        ymSign,
        daysSign,
        hmsSign;

    if (!total) {
      // this is the same as C#'s (Noda) and python (isodate)...
      // but not other JS (goog.date)
      return 'P0D';
    } // 3600 seconds -> 60 minutes -> 1 hour


    minutes = absFloor(seconds / 60);
    hours = absFloor(minutes / 60);
    seconds %= 60;
    minutes %= 60; // 12 months -> 1 year

    years = absFloor(months / 12);
    months %= 12; // inspired by https://github.com/dordille/moment-isoduration/blob/master/moment.isoduration.js

    s = seconds ? seconds.toFixed(3).replace(/\.?0+$/, '') : '';
    totalSign = total < 0 ? '-' : '';
    ymSign = sign(this._months) !== sign(total) ? '-' : '';
    daysSign = sign(this._days) !== sign(total) ? '-' : '';
    hmsSign = sign(this._milliseconds) !== sign(total) ? '-' : '';
    return totalSign + 'P' + (years ? ymSign + years + 'Y' : '') + (months ? ymSign + months + 'M' : '') + (days ? daysSign + days + 'D' : '') + (hours || minutes || seconds ? 'T' : '') + (hours ? hmsSign + hours + 'H' : '') + (minutes ? hmsSign + minutes + 'M' : '') + (seconds ? hmsSign + s + 'S' : '');
  }

  var proto$2 = Duration.prototype;
  proto$2.isValid = isValid$1;
  proto$2.abs = abs;
  proto$2.add = add$1;
  proto$2.subtract = subtract$1;
  proto$2.as = as;
  proto$2.asMilliseconds = asMilliseconds;
  proto$2.asSeconds = asSeconds;
  proto$2.asMinutes = asMinutes;
  proto$2.asHours = asHours;
  proto$2.asDays = asDays;
  proto$2.asWeeks = asWeeks;
  proto$2.asMonths = asMonths;
  proto$2.asQuarters = asQuarters;
  proto$2.asYears = asYears;
  proto$2.valueOf = valueOf$1;
  proto$2._bubble = bubble;
  proto$2.clone = clone$1;
  proto$2.get = get$2;
  proto$2.milliseconds = milliseconds;
  proto$2.seconds = seconds;
  proto$2.minutes = minutes;
  proto$2.hours = hours;
  proto$2.days = days;
  proto$2.weeks = weeks;
  proto$2.months = months;
  proto$2.years = years;
  proto$2.humanize = humanize;
  proto$2.toISOString = toISOString$1;
  proto$2.toString = toISOString$1;
  proto$2.toJSON = toISOString$1;
  proto$2.locale = locale;
  proto$2.localeData = localeData;
  proto$2.toIsoString = deprecate('toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)', toISOString$1);
  proto$2.lang = lang; // FORMATTING

  addFormatToken('X', 0, 0, 'unix');
  addFormatToken('x', 0, 0, 'valueOf'); // PARSING

  addRegexToken('x', matchSigned);
  addRegexToken('X', matchTimestamp);
  addParseToken('X', function (input, array, config) {
    config._d = new Date(parseFloat(input) * 1000);
  });
  addParseToken('x', function (input, array, config) {
    config._d = new Date(toInt(input));
  }); //! moment.js

  hooks.version = '2.29.4';
  setHookCallback(createLocal);
  hooks.fn = proto;
  hooks.min = min;
  hooks.max = max;
  hooks.now = now;
  hooks.utc = createUTC;
  hooks.unix = createUnix;
  hooks.months = listMonths;
  hooks.isDate = isDate;
  hooks.locale = getSetGlobalLocale;
  hooks.invalid = createInvalid;
  hooks.duration = createDuration;
  hooks.isMoment = isMoment;
  hooks.weekdays = listWeekdays;
  hooks.parseZone = createInZone;
  hooks.localeData = getLocale;
  hooks.isDuration = isDuration;
  hooks.monthsShort = listMonthsShort;
  hooks.weekdaysMin = listWeekdaysMin;
  hooks.defineLocale = defineLocale;
  hooks.updateLocale = updateLocale;
  hooks.locales = listLocales;
  hooks.weekdaysShort = listWeekdaysShort;
  hooks.normalizeUnits = normalizeUnits;
  hooks.relativeTimeRounding = getSetRelativeTimeRounding;
  hooks.relativeTimeThreshold = getSetRelativeTimeThreshold;
  hooks.calendarFormat = getCalendarFormat;
  hooks.prototype = proto; // currently HTML5 input type only supports 24-hour formats

  hooks.HTML5_FMT = {
    DATETIME_LOCAL: 'YYYY-MM-DDTHH:mm',
    // <input type="datetime-local" />
    DATETIME_LOCAL_SECONDS: 'YYYY-MM-DDTHH:mm:ss',
    // <input type="datetime-local" step="1" />
    DATETIME_LOCAL_MS: 'YYYY-MM-DDTHH:mm:ss.SSS',
    // <input type="datetime-local" step="0.001" />
    DATE: 'YYYY-MM-DD',
    // <input type="date" />
    TIME: 'HH:mm',
    // <input type="time" />
    TIME_SECONDS: 'HH:mm:ss',
    // <input type="time" step="1" />
    TIME_MS: 'HH:mm:ss.SSS',
    // <input type="time" step="0.001" />
    WEEK: 'GGGG-[W]WW',
    // <input type="week" />
    MONTH: 'YYYY-MM' // <input type="month" />

  };
  return hooks;
});
});

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _regeneratorRuntime() {
  /*! regenerator-runtime -- Copyright (c) 2014-present, Facebook, Inc. -- license (MIT): https://github.com/facebook/regenerator/blob/main/LICENSE */

  _regeneratorRuntime = function () {
    return exports;
  };

  var exports = {},
      Op = Object.prototype,
      hasOwn = Op.hasOwnProperty,
      $Symbol = "function" == typeof Symbol ? Symbol : {},
      iteratorSymbol = $Symbol.iterator || "@@iterator",
      asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator",
      toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    return Object.defineProperty(obj, key, {
      value: value,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), obj[key];
  }

  try {
    define({}, "");
  } catch (err) {
    define = function (obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator,
        generator = Object.create(protoGenerator.prototype),
        context = new Context(tryLocsList || []);
    return generator._invoke = function (innerFn, self, context) {
      var state = "suspendedStart";
      return function (method, arg) {
        if ("executing" === state) throw new Error("Generator is already running");

        if ("completed" === state) {
          if ("throw" === method) throw arg;
          return doneResult();
        }

        for (context.method = method, context.arg = arg;;) {
          var delegate = context.delegate;

          if (delegate) {
            var delegateResult = maybeInvokeDelegate(delegate, context);

            if (delegateResult) {
              if (delegateResult === ContinueSentinel) continue;
              return delegateResult;
            }
          }

          if ("next" === context.method) context.sent = context._sent = context.arg;else if ("throw" === context.method) {
            if ("suspendedStart" === state) throw state = "completed", context.arg;
            context.dispatchException(context.arg);
          } else "return" === context.method && context.abrupt("return", context.arg);
          state = "executing";
          var record = tryCatch(innerFn, self, context);

          if ("normal" === record.type) {
            if (state = context.done ? "completed" : "suspendedYield", record.arg === ContinueSentinel) continue;
            return {
              value: record.arg,
              done: context.done
            };
          }

          "throw" === record.type && (state = "completed", context.method = "throw", context.arg = record.arg);
        }
      };
    }(innerFn, self, context), generator;
  }

  function tryCatch(fn, obj, arg) {
    try {
      return {
        type: "normal",
        arg: fn.call(obj, arg)
      };
    } catch (err) {
      return {
        type: "throw",
        arg: err
      };
    }
  }

  exports.wrap = wrap;
  var ContinueSentinel = {};

  function Generator() {}

  function GeneratorFunction() {}

  function GeneratorFunctionPrototype() {}

  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });
  var getProto = Object.getPrototypeOf,
      NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  NativeIteratorPrototype && NativeIteratorPrototype !== Op && hasOwn.call(NativeIteratorPrototype, iteratorSymbol) && (IteratorPrototype = NativeIteratorPrototype);
  var Gp = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(IteratorPrototype);

  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function (method) {
      define(prototype, method, function (arg) {
        return this._invoke(method, arg);
      });
    });
  }

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);

      if ("throw" !== record.type) {
        var result = record.arg,
            value = result.value;
        return value && "object" == typeof value && hasOwn.call(value, "__await") ? PromiseImpl.resolve(value.__await).then(function (value) {
          invoke("next", value, resolve, reject);
        }, function (err) {
          invoke("throw", err, resolve, reject);
        }) : PromiseImpl.resolve(value).then(function (unwrapped) {
          result.value = unwrapped, resolve(result);
        }, function (error) {
          return invoke("throw", error, resolve, reject);
        });
      }

      reject(record.arg);
    }

    var previousPromise;

    this._invoke = function (method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function (resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise = previousPromise ? previousPromise.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
    };
  }

  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];

    if (undefined === method) {
      if (context.delegate = null, "throw" === context.method) {
        if (delegate.iterator.return && (context.method = "return", context.arg = undefined, maybeInvokeDelegate(delegate, context), "throw" === context.method)) return ContinueSentinel;
        context.method = "throw", context.arg = new TypeError("The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);
    if ("throw" === record.type) return context.method = "throw", context.arg = record.arg, context.delegate = null, ContinueSentinel;
    var info = record.arg;
    return info ? info.done ? (context[delegate.resultName] = info.value, context.next = delegate.nextLoc, "return" !== context.method && (context.method = "next", context.arg = undefined), context.delegate = null, ContinueSentinel) : info : (context.method = "throw", context.arg = new TypeError("iterator result is not an object"), context.delegate = null, ContinueSentinel);
  }

  function pushTryEntry(locs) {
    var entry = {
      tryLoc: locs[0]
    };
    1 in locs && (entry.catchLoc = locs[1]), 2 in locs && (entry.finallyLoc = locs[2], entry.afterLoc = locs[3]), this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal", delete record.arg, entry.completion = record;
  }

  function Context(tryLocsList) {
    this.tryEntries = [{
      tryLoc: "root"
    }], tryLocsList.forEach(pushTryEntry, this), this.reset(!0);
  }

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) return iteratorMethod.call(iterable);
      if ("function" == typeof iterable.next) return iterable;

      if (!isNaN(iterable.length)) {
        var i = -1,
            next = function next() {
          for (; ++i < iterable.length;) if (hasOwn.call(iterable, i)) return next.value = iterable[i], next.done = !1, next;

          return next.value = undefined, next.done = !0, next;
        };

        return next.next = next;
      }
    }

    return {
      next: doneResult
    };
  }

  function doneResult() {
    return {
      value: undefined,
      done: !0
    };
  }

  return GeneratorFunction.prototype = GeneratorFunctionPrototype, define(Gp, "constructor", GeneratorFunctionPrototype), define(GeneratorFunctionPrototype, "constructor", GeneratorFunction), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, toStringTagSymbol, "GeneratorFunction"), exports.isGeneratorFunction = function (genFun) {
    var ctor = "function" == typeof genFun && genFun.constructor;
    return !!ctor && (ctor === GeneratorFunction || "GeneratorFunction" === (ctor.displayName || ctor.name));
  }, exports.mark = function (genFun) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(genFun, GeneratorFunctionPrototype) : (genFun.__proto__ = GeneratorFunctionPrototype, define(genFun, toStringTagSymbol, "GeneratorFunction")), genFun.prototype = Object.create(Gp), genFun;
  }, exports.awrap = function (arg) {
    return {
      __await: arg
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  }), exports.AsyncIterator = AsyncIterator, exports.async = function (innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    void 0 === PromiseImpl && (PromiseImpl = Promise);
    var iter = new AsyncIterator(wrap(innerFn, outerFn, self, tryLocsList), PromiseImpl);
    return exports.isGeneratorFunction(outerFn) ? iter : iter.next().then(function (result) {
      return result.done ? result.value : iter.next();
    });
  }, defineIteratorMethods(Gp), define(Gp, toStringTagSymbol, "Generator"), define(Gp, iteratorSymbol, function () {
    return this;
  }), define(Gp, "toString", function () {
    return "[object Generator]";
  }), exports.keys = function (object) {
    var keys = [];

    for (var key in object) keys.push(key);

    return keys.reverse(), function next() {
      for (; keys.length;) {
        var key = keys.pop();
        if (key in object) return next.value = key, next.done = !1, next;
      }

      return next.done = !0, next;
    };
  }, exports.values = values, Context.prototype = {
    constructor: Context,
    reset: function (skipTempReset) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = undefined, this.done = !1, this.delegate = null, this.method = "next", this.arg = undefined, this.tryEntries.forEach(resetTryEntry), !skipTempReset) for (var name in this) "t" === name.charAt(0) && hasOwn.call(this, name) && !isNaN(+name.slice(1)) && (this[name] = undefined);
    },
    stop: function () {
      this.done = !0;
      var rootRecord = this.tryEntries[0].completion;
      if ("throw" === rootRecord.type) throw rootRecord.arg;
      return this.rval;
    },
    dispatchException: function (exception) {
      if (this.done) throw exception;
      var context = this;

      function handle(loc, caught) {
        return record.type = "throw", record.arg = exception, context.next = loc, caught && (context.method = "next", context.arg = undefined), !!caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i],
            record = entry.completion;
        if ("root" === entry.tryLoc) return handle("end");

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc"),
              hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
          } else {
            if (!hasFinally) throw new Error("try statement without catch or finally");
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          }
        }
      }
    },
    abrupt: function (type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc <= this.prev && hasOwn.call(entry, "finallyLoc") && this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      finallyEntry && ("break" === type || "continue" === type) && finallyEntry.tryLoc <= arg && arg <= finallyEntry.finallyLoc && (finallyEntry = null);
      var record = finallyEntry ? finallyEntry.completion : {};
      return record.type = type, record.arg = arg, finallyEntry ? (this.method = "next", this.next = finallyEntry.finallyLoc, ContinueSentinel) : this.complete(record);
    },
    complete: function (record, afterLoc) {
      if ("throw" === record.type) throw record.arg;
      return "break" === record.type || "continue" === record.type ? this.next = record.arg : "return" === record.type ? (this.rval = this.arg = record.arg, this.method = "return", this.next = "end") : "normal" === record.type && afterLoc && (this.next = afterLoc), ContinueSentinel;
    },
    finish: function (finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) return this.complete(entry.completion, entry.afterLoc), resetTryEntry(entry), ContinueSentinel;
      }
    },
    catch: function (tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;

          if ("throw" === record.type) {
            var thrown = record.arg;
            resetTryEntry(entry);
          }

          return thrown;
        }
      }

      throw new Error("illegal catch attempt");
    },
    delegateYield: function (iterable, resultName, nextLoc) {
      return this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      }, "next" === this.method && (this.arg = undefined), ContinueSentinel;
    }
  }, exports;
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _objectDestructuringEmpty(obj) {
  if (obj == null) throw new TypeError("Cannot destructure undefined");
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];

  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;

      var F = function () {};

      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

/**
 * Created by xuyq on 2020/12/28.
 */
var module = /*#__PURE__*/function () {
  function module(app) {
    _classCallCheck(this, module);

    this._cacheModule = [];
    this._app = app;
  }
  /**
   * 添加模块
   * @param modules
   * @param name
   */


  _createClass(module, [{
    key: "addModule",
    value: function addModule(modules, name) {
      var bundle = [];

      if (!this._cacheModule[name]) {
        for (var _i = 0, _Object$keys = Object.keys(modules); _i < _Object$keys.length; _i++) {
          var key = _Object$keys[_i];
          var _module3 = {};
          _module3.key = key;
          _module3.creator = modules[key];
          _module3.bundle = name;
          this.loadModule(_module3);
          bundle.push(_module3);
        }

        this._cacheModule.push({
          name: name,
          bundle: bundle
        });
      }
    }
    /**
     * 加载module
     * @param module
     */

  }, {
    key: "loadModule",
    value: function loadModule(_module2) {
      var creator = _module2.creator,
          key = _module2.key,
          bundle = _module2.bundle;

      if (typeof creator !== "function") {
        throw new Error('loadModule error due to creator undefined!');
      }

      var _creator = creator(),
          menu = _creator.menu,
          lang = _creator.lang,
          store = _creator.store;

      if (Array.isArray(menu)) {
        lang && this._app.i18n.mergeMessage(lang); //创建菜单

        var _iterator = _createForOfIteratorHelper(menu),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var m = _step.value;

            this._app.createMenu(_objectSpread2(_objectSpread2({}, m), {}, {
              key: m.name,
              bundle: bundle
            }));
          } //创建store

        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }

        store && this._app.addStore(key, store);
      } else {
        menu = _objectSpread2(_objectSpread2({}, menu), {}, {
          key: key,
          bundle: bundle
        }); //合并多语言

        lang && this._app.i18n.mergeMessage(lang); //创建菜单

        this._app.createMenu(menu); //创建store


        store && this._app.addStore(key, store);
      }
    }
  }]);

  return module;
}();

var state$1 = {
  menu: [],
  showLoading: false,
  shallContentShowOverlay: false
};
var mutations$1 = {
  TOGGLE_CONTENT_OVERLAY: function TOGGLE_CONTENT_OVERLAY(state, value) {
    state.shallContentShowOverlay = value !== undefined ? value : !state.shallContentShowOverlay;
  },
  ADD_MENU: function ADD_MENU(state, menu) {
    state.menu = [].concat(_toConsumableArray(state.menu), [menu]);
  }
};
var app = _objectSpread2({}, createModule({
  state: state$1,
  mutations: mutations$1
}));

/**
 * Created by xuyq on 2021/1/28.
 */
var eventBus = new Vue();

/**
 * Created by xuyq on 2021/5/21.
 */
var _Group = /*#__PURE__*/function () {
  function Group() {
    _classCallCheck(this, Group);

    this.subItems = [];
    this.subGroups = [];
    this._logic = 'and';
  }

  _createClass(Group, [{
    key: "logic",
    value: function logic(_logic) {
      this._logic = _logic;
      return this;
    }
  }, {
    key: "And",
    value: function And() {
      this._logic = 'and';
      return this;
    }
  }, {
    key: "Or",
    value: function Or() {
      this._logic = 'or';
      return this;
    }
  }, {
    key: "addItem",
    value: function addItem(item) {
      this.subItems.push(item);
      return this;
    }
  }, {
    key: "addGroup",
    value: function addGroup(group) {
      this.subGroups.push(group);
      return this;
    }
  }, {
    key: "toJson",
    value: function toJson() {
      var json = {};

      if (this.subGroups.length > 0) {
        json.subGroups = this.subGroups.map(function (group) {
          return group.toJson();
        });
      }

      if (this.subItems.length > 0) {
        json.subItems = this.subItems.map(function (item) {
          return item.toJson();
        });
      }

      return _objectSpread2({
        logic: this._logic
      }, json);
    }
  }]);

  return Group;
}();

var _Item = /*#__PURE__*/function () {
  function Item(name) {
    _classCallCheck(this, Item);

    this.field = name;
    this._logic = 'eq';
    this.value = undefined;
  }

  _createClass(Item, [{
    key: "logic",
    value: function logic(_logic2) {
      this._logic = _logic2;
      return this;
    }
  }, {
    key: "eq",
    value: function eq() {
      this._logic = 'eq';
      return this;
    }
  }, {
    key: "in",
    value: function _in() {
      this._logic = 'in';
      return this;
    }
  }, {
    key: "ne",
    value: function ne() {
      this._logic = 'ne';
      return this;
    }
  }, {
    key: "gt",
    value: function gt() {
      this._logic = 'gt';
      return this;
    }
  }, {
    key: "ge",
    value: function ge() {
      this._logic = 'ge';
      return this;
    }
  }, {
    key: "lt",
    value: function lt() {
      this._logic = 'lt';
      return this;
    }
  }, {
    key: "le",
    value: function le() {
      this._logic = 'le';
      return this;
    }
  }, {
    key: "like",
    value: function like() {
      this._logic = 'like';
      return this;
    }
  }, {
    key: "isNull",
    value: function isNull() {
      this._logic = 'isNull';
      return this;
    }
  }, {
    key: "isNotNull",
    value: function isNotNull() {
      this._logic = 'isNotNull';
      return this;
    }
  }, {
    key: "isEmpty",
    value: function isEmpty() {
      this._logic = 'isEmpty';
      return this;
    }
  }, {
    key: "val",
    value: function val(_val) {
      this.value = _val;
      return this;
    }
  }, {
    key: "toJson",
    value: function toJson() {
      if (this._logic === 'isNull' || this._logic === 'isEmpty') {
        return {
          field: this.field,
          logic: this._logic
        };
      }

      return {
        field: this.field,
        logic: this._logic,
        value: this.value
      };
    }
  }]);

  return Item;
}();

var paramsUtil = /*#__PURE__*/function () {
  function paramsUtil() {
    _classCallCheck(this, paramsUtil);
  }

  _createClass(paramsUtil, null, [{
    key: "Group",
    value: function Group() {
      return new _Group();
    }
  }, {
    key: "Item",
    value: function Item(field) {
      return new _Item(field);
    }
  }]);

  return paramsUtil;
}();

// Material Design Icons v5.9.55
var mdiCheckboxBlankCircleOutline="M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z";

var Setting = /*#__PURE__*/_createClass(function Setting() {
  _classCallCheck(this, Setting);

  _defineProperty(this, "tokenLocation", 'localStorage');

  _defineProperty(this, "tokenKey", "ops.access.token");

  _defineProperty(this, "isESO", false);

  _defineProperty(this, "mode", "development");

  _defineProperty(this, "lang", 'en');

  _defineProperty(this, "routeLog", true);

  _defineProperty(this, "request", {
    // 请求设置
    tokenId: 'Authorization',
    // Header Token key
    timeout: 20 * 60 * 1000,
    // 请求超时时间
    sys_prefix: '',
    // 请求前缀
    base_url: './',
    suffix: null,
    // 请求后缀
    tokenType: 'LDAP',
    client: 'MYOPS_APP',
    message: {
      // 请求报错信息
      _403: 'Sorry, you have no permission to access this page.',
      _503: 'Something wrong with the server, please wait for a moment or contact the MyOps Team.'
    }
  });

  _defineProperty(this, "eso", {
    stage: {
      tenantId: 'f3211d0e-125b-42c3-86db-322b19a65a22',
      clientId: 'd44d9897-890c-4108-8714-4549d2bf8e22',
      redirectUri: 'https://dev.myops.cdc.accenture.com/login/'
    },
    prod: {
      tenantId: '8bf3ea15-8d0e-4c87-bfc6-7c086f2820f7',
      clientId: '1ac143c8-9b92-436a-9b46-5b4e5b7043b2',
      redirectUri: 'https://cssa.accenture.cn/'
    }
  });

  _defineProperty(this, "themeConfig", {
    app: {
      name: 'MATERIO',
      isDark: false,
      isRtl: false,
      contentLayoutNav: 'vertical',
      // vertical, horizontal
      routeTransition: 'scroll-x-transition',
      // https://vuetifyjs.com/en/styles/transitions/#api
      // ! `semi-dark` isn't available for horizontal nav menu
      skinVariant: 'default',
      // default, bordered, semi-dark
      contentWidth: 'boxed'
    },
    menu: {
      isMenuHidden: false,
      isVerticalNavMini: false,
      verticalMenuAccordion: true,
      groupChildIcon: mdiCheckboxBlankCircleOutline,
      horizontalNavMenuGroupOpenOnHover: true
    },
    appBar: {
      /*
      ! In Content Layout Horizontal Nav type `hidden` value won't work
      ! In Content Layout Horizontal Nav type value of `type` will affect both Appbar and Horizontal Navigation Menu
      */
      type: 'fixed',
      // fixed, static, hidden
      isBlurred: true
    },
    footer: {
      type: 'static' // fixed, static, hidden

    },
    themes: {
      light: {
        primary: '#9155FD',
        accent: '#0d6efd',
        secondary: '#8A8D93',
        success: '#56CA00',
        info: '#16B1FF',
        warning: '#FFB400',
        error: '#FF4C51'
      },
      dark: {
        primary: '#9155FD',
        accent: '#0d6efd',
        secondary: '#8A8D93',
        success: '#56CA00',
        info: '#16B1FF',
        warning: '#FFB400',
        error: '#FF4C51'
      }
    }
  });

  _defineProperty(this, "loginUrl", location.origin + '/#/login');

  _defineProperty(this, "noPermissionUrl", '');
});

var _setting = new Setting();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

/* global Reflect, Promise */

var extendStatics$1 = function (d, b) {
  extendStatics$1 = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
  };

  return extendStatics$1(d, b);
};

function __extends$1(d, b) {
  extendStatics$1(d, b);

  function __() {
    this.constructor = d;
  }

  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign$1 = function () {
  __assign$1 = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign$1.apply(this, arguments);
};

function __awaiter$1(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

function __generator$1(thisArg, body) {
  var _ = {
    label: 0,
    sent: function () {
      if (t[0] & 1) throw t[1];
      return t[1];
    },
    trys: [],
    ops: []
  },
      f,
      y,
      t,
      g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;

  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }

  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");

    while (_) try {
      if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
      if (y = 0, t) op = [op[0] & 2, t.value];

      switch (op[0]) {
        case 0:
        case 1:
          t = op;
          break;

        case 4:
          _.label++;
          return {
            value: op[1],
            done: false
          };

        case 5:
          _.label++;
          y = op[1];
          op = [0];
          continue;

        case 7:
          op = _.ops.pop();

          _.trys.pop();

          continue;

        default:
          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
            _ = 0;
            continue;
          }

          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
            _.label = op[1];
            break;
          }

          if (op[0] === 6 && _.label < t[1]) {
            _.label = t[1];
            t = op;
            break;
          }

          if (t && _.label < t[2]) {
            _.label = t[2];

            _.ops.push(op);

            break;
          }

          if (t[2]) _.ops.pop();

          _.trys.pop();

          continue;
      }

      op = body.call(thisArg, _);
    } catch (e) {
      op = [6, e];
      y = 0;
    } finally {
      f = t = 0;
    }

    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
}

function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
      r,
      ar = [],
      e;

  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }

  return ar;
}

function __spread() {
  for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));

  return ar;
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

/* global Reflect, Promise */

var extendStatics = function (d, b) {
  extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
  };

  return extendStatics(d, b);
};

function __extends(d, b) {
  extendStatics(d, b);

  function __() {
    this.constructor = d;
  }

  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

function __generator(thisArg, body) {
  var _ = {
    label: 0,
    sent: function () {
      if (t[0] & 1) throw t[1];
      return t[1];
    },
    trys: [],
    ops: []
  },
      f,
      y,
      t,
      g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;

  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }

  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");

    while (_) try {
      if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
      if (y = 0, t) op = [op[0] & 2, t.value];

      switch (op[0]) {
        case 0:
        case 1:
          t = op;
          break;

        case 4:
          _.label++;
          return {
            value: op[1],
            done: false
          };

        case 5:
          _.label++;
          y = op[1];
          op = [0];
          continue;

        case 7:
          op = _.ops.pop();

          _.trys.pop();

          continue;

        default:
          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
            _ = 0;
            continue;
          }

          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
            _.label = op[1];
            break;
          }

          if (op[0] === 6 && _.label < t[1]) {
            _.label = t[1];
            t = op;
            break;
          }

          if (t && _.label < t[2]) {
            _.label = t[2];

            _.ops.push(op);

            break;
          }

          if (t[2]) _.ops.pop();

          _.trys.pop();

          continue;
      }

      op = body.call(thisArg, _);
    } catch (e) {
      op = [6, e];
      y = 0;
    } finally {
      f = t = 0;
    }

    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
}

function __spreadArrays() {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;

  for (var r = Array(s), k = 0, i = 0; i < il; i++) for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) r[k] = a[j];

  return r;
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var Constants = {
  LIBRARY_NAME: "MSAL.JS",
  SKU: "msal.js.common",
  // Prefix for all library cache entries
  CACHE_PREFIX: "msal",
  // default authority
  DEFAULT_AUTHORITY: "https://login.microsoftonline.com/common/",
  DEFAULT_AUTHORITY_HOST: "login.microsoftonline.com",
  DEFAULT_COMMON_TENANT: "common",
  // ADFS String
  ADFS: "adfs",
  DSTS: "dstsv2",
  // Default AAD Instance Discovery Endpoint
  AAD_INSTANCE_DISCOVERY_ENDPT: "https://login.microsoftonline.com/common/discovery/instance?api-version=1.1&authorization_endpoint=",
  // Resource delimiter - used for certain cache entries
  RESOURCE_DELIM: "|",
  // Placeholder for non-existent account ids/objects
  NO_ACCOUNT: "NO_ACCOUNT",
  // Claims
  CLAIMS: "claims",
  // Consumer UTID
  CONSUMER_UTID: "9188040d-6c67-4c5b-b112-36a304b66dad",
  // Default scopes
  OPENID_SCOPE: "openid",
  PROFILE_SCOPE: "profile",
  OFFLINE_ACCESS_SCOPE: "offline_access",
  EMAIL_SCOPE: "email",
  // Default response type for authorization code flow
  CODE_RESPONSE_TYPE: "code",
  CODE_GRANT_TYPE: "authorization_code",
  RT_GRANT_TYPE: "refresh_token",
  FRAGMENT_RESPONSE_MODE: "fragment",
  S256_CODE_CHALLENGE_METHOD: "S256",
  URL_FORM_CONTENT_TYPE: "application/x-www-form-urlencoded;charset=utf-8",
  AUTHORIZATION_PENDING: "authorization_pending",
  NOT_DEFINED: "not_defined",
  EMPTY_STRING: "",
  FORWARD_SLASH: "/",
  IMDS_ENDPOINT: "http://169.254.169.254/metadata/instance/compute/location",
  IMDS_VERSION: "2020-06-01",
  IMDS_TIMEOUT: 2000,
  AZURE_REGION_AUTO_DISCOVER_FLAG: "TryAutoDetect",
  REGIONAL_AUTH_PUBLIC_CLOUD_SUFFIX: "login.microsoft.com",
  REGIONAL_AUTH_NON_MSI_QUERY_STRING: "allowestsrnonmsi=true",
  KNOWN_PUBLIC_CLOUDS: ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"],
  TOKEN_RESPONSE_TYPE: "token",
  ID_TOKEN_RESPONSE_TYPE: "id_token",
  SHR_NONCE_VALIDITY: 240
};
var OIDC_DEFAULT_SCOPES = [Constants.OPENID_SCOPE, Constants.PROFILE_SCOPE, Constants.OFFLINE_ACCESS_SCOPE];

var OIDC_SCOPES = __spreadArrays(OIDC_DEFAULT_SCOPES, [Constants.EMAIL_SCOPE]);
/**
 * Request header names
 */


var HeaderNames;

(function (HeaderNames) {
  HeaderNames["CONTENT_TYPE"] = "Content-Type";
  HeaderNames["RETRY_AFTER"] = "Retry-After";
  HeaderNames["CCS_HEADER"] = "X-AnchorMailbox";
  HeaderNames["WWWAuthenticate"] = "WWW-Authenticate";
  HeaderNames["AuthenticationInfo"] = "Authentication-Info";
  HeaderNames["X_MS_REQUEST_ID"] = "x-ms-request-id";
})(HeaderNames || (HeaderNames = {}));
/**
 * Persistent cache keys MSAL which stay while user is logged in.
 */


var PersistentCacheKeys;

(function (PersistentCacheKeys) {
  PersistentCacheKeys["ID_TOKEN"] = "idtoken";
  PersistentCacheKeys["CLIENT_INFO"] = "client.info";
  PersistentCacheKeys["ADAL_ID_TOKEN"] = "adal.idtoken";
  PersistentCacheKeys["ERROR"] = "error";
  PersistentCacheKeys["ERROR_DESC"] = "error.description";
  PersistentCacheKeys["ACTIVE_ACCOUNT"] = "active-account";
  PersistentCacheKeys["ACTIVE_ACCOUNT_FILTERS"] = "active-account-filters"; // new cache entry for active_account for a more robust version for browser
})(PersistentCacheKeys || (PersistentCacheKeys = {}));
/**
 * String constants related to AAD Authority
 */


var AADAuthorityConstants;

(function (AADAuthorityConstants) {
  AADAuthorityConstants["COMMON"] = "common";
  AADAuthorityConstants["ORGANIZATIONS"] = "organizations";
  AADAuthorityConstants["CONSUMERS"] = "consumers";
})(AADAuthorityConstants || (AADAuthorityConstants = {}));
/**
 * Keys in the hashParams sent by AAD Server
 */


var AADServerParamKeys;

(function (AADServerParamKeys) {
  AADServerParamKeys["CLIENT_ID"] = "client_id";
  AADServerParamKeys["REDIRECT_URI"] = "redirect_uri";
  AADServerParamKeys["RESPONSE_TYPE"] = "response_type";
  AADServerParamKeys["RESPONSE_MODE"] = "response_mode";
  AADServerParamKeys["GRANT_TYPE"] = "grant_type";
  AADServerParamKeys["CLAIMS"] = "claims";
  AADServerParamKeys["SCOPE"] = "scope";
  AADServerParamKeys["ERROR"] = "error";
  AADServerParamKeys["ERROR_DESCRIPTION"] = "error_description";
  AADServerParamKeys["ACCESS_TOKEN"] = "access_token";
  AADServerParamKeys["ID_TOKEN"] = "id_token";
  AADServerParamKeys["REFRESH_TOKEN"] = "refresh_token";
  AADServerParamKeys["EXPIRES_IN"] = "expires_in";
  AADServerParamKeys["STATE"] = "state";
  AADServerParamKeys["NONCE"] = "nonce";
  AADServerParamKeys["PROMPT"] = "prompt";
  AADServerParamKeys["SESSION_STATE"] = "session_state";
  AADServerParamKeys["CLIENT_INFO"] = "client_info";
  AADServerParamKeys["CODE"] = "code";
  AADServerParamKeys["CODE_CHALLENGE"] = "code_challenge";
  AADServerParamKeys["CODE_CHALLENGE_METHOD"] = "code_challenge_method";
  AADServerParamKeys["CODE_VERIFIER"] = "code_verifier";
  AADServerParamKeys["CLIENT_REQUEST_ID"] = "client-request-id";
  AADServerParamKeys["X_CLIENT_SKU"] = "x-client-SKU";
  AADServerParamKeys["X_CLIENT_VER"] = "x-client-VER";
  AADServerParamKeys["X_CLIENT_OS"] = "x-client-OS";
  AADServerParamKeys["X_CLIENT_CPU"] = "x-client-CPU";
  AADServerParamKeys["X_CLIENT_CURR_TELEM"] = "x-client-current-telemetry";
  AADServerParamKeys["X_CLIENT_LAST_TELEM"] = "x-client-last-telemetry";
  AADServerParamKeys["X_MS_LIB_CAPABILITY"] = "x-ms-lib-capability";
  AADServerParamKeys["X_APP_NAME"] = "x-app-name";
  AADServerParamKeys["X_APP_VER"] = "x-app-ver";
  AADServerParamKeys["POST_LOGOUT_URI"] = "post_logout_redirect_uri";
  AADServerParamKeys["ID_TOKEN_HINT"] = "id_token_hint";
  AADServerParamKeys["DEVICE_CODE"] = "device_code";
  AADServerParamKeys["CLIENT_SECRET"] = "client_secret";
  AADServerParamKeys["CLIENT_ASSERTION"] = "client_assertion";
  AADServerParamKeys["CLIENT_ASSERTION_TYPE"] = "client_assertion_type";
  AADServerParamKeys["TOKEN_TYPE"] = "token_type";
  AADServerParamKeys["REQ_CNF"] = "req_cnf";
  AADServerParamKeys["OBO_ASSERTION"] = "assertion";
  AADServerParamKeys["REQUESTED_TOKEN_USE"] = "requested_token_use";
  AADServerParamKeys["ON_BEHALF_OF"] = "on_behalf_of";
  AADServerParamKeys["FOCI"] = "foci";
  AADServerParamKeys["CCS_HEADER"] = "X-AnchorMailbox";
  AADServerParamKeys["RETURN_SPA_CODE"] = "return_spa_code";
  AADServerParamKeys["NATIVE_BROKER"] = "nativebroker";
  AADServerParamKeys["LOGOUT_HINT"] = "logout_hint";
})(AADServerParamKeys || (AADServerParamKeys = {}));
/**
 * Claims request keys
 */


var ClaimsRequestKeys;

(function (ClaimsRequestKeys) {
  ClaimsRequestKeys["ACCESS_TOKEN"] = "access_token";
  ClaimsRequestKeys["XMS_CC"] = "xms_cc";
})(ClaimsRequestKeys || (ClaimsRequestKeys = {}));
/**
 * we considered making this "enum" in the request instead of string, however it looks like the allowed list of
 * prompt values kept changing over past couple of years. There are some undocumented prompt values for some
 * internal partners too, hence the choice of generic "string" type instead of the "enum"
 */


var PromptValue = {
  LOGIN: "login",
  SELECT_ACCOUNT: "select_account",
  CONSENT: "consent",
  NONE: "none",
  CREATE: "create",
  NO_SESSION: "no_session"
};
/**
 * SSO Types - generated to populate hints
 */

var SSOTypes;

(function (SSOTypes) {
  SSOTypes["ACCOUNT"] = "account";
  SSOTypes["SID"] = "sid";
  SSOTypes["LOGIN_HINT"] = "login_hint";
  SSOTypes["ID_TOKEN"] = "id_token";
  SSOTypes["DOMAIN_HINT"] = "domain_hint";
  SSOTypes["ORGANIZATIONS"] = "organizations";
  SSOTypes["CONSUMERS"] = "consumers";
  SSOTypes["ACCOUNT_ID"] = "accountIdentifier";
  SSOTypes["HOMEACCOUNT_ID"] = "homeAccountIdentifier";
})(SSOTypes || (SSOTypes = {}));
/**
 * allowed values for codeVerifier
 */


var CodeChallengeMethodValues = {
  PLAIN: "plain",
  S256: "S256"
};
/**
 * allowed values for response_mode
 */

var ResponseMode;

(function (ResponseMode) {
  ResponseMode["QUERY"] = "query";
  ResponseMode["FRAGMENT"] = "fragment";
  ResponseMode["FORM_POST"] = "form_post";
})(ResponseMode || (ResponseMode = {}));
/**
 * allowed grant_type
 */


var GrantType;

(function (GrantType) {
  GrantType["IMPLICIT_GRANT"] = "implicit";
  GrantType["AUTHORIZATION_CODE_GRANT"] = "authorization_code";
  GrantType["CLIENT_CREDENTIALS_GRANT"] = "client_credentials";
  GrantType["RESOURCE_OWNER_PASSWORD_GRANT"] = "password";
  GrantType["REFRESH_TOKEN_GRANT"] = "refresh_token";
  GrantType["DEVICE_CODE_GRANT"] = "device_code";
  GrantType["JWT_BEARER"] = "urn:ietf:params:oauth:grant-type:jwt-bearer";
})(GrantType || (GrantType = {}));
/**
 * Account types in Cache
 */


var CacheAccountType;

(function (CacheAccountType) {
  CacheAccountType["MSSTS_ACCOUNT_TYPE"] = "MSSTS";
  CacheAccountType["ADFS_ACCOUNT_TYPE"] = "ADFS";
  CacheAccountType["MSAV1_ACCOUNT_TYPE"] = "MSA";
  CacheAccountType["GENERIC_ACCOUNT_TYPE"] = "Generic"; // NTLM, Kerberos, FBA, Basic etc
})(CacheAccountType || (CacheAccountType = {}));
/**
 * Separators used in cache
 */


var Separators;

(function (Separators) {
  Separators["CACHE_KEY_SEPARATOR"] = "-";
  Separators["CLIENT_INFO_SEPARATOR"] = ".";
})(Separators || (Separators = {}));
/**
 * Credential Type stored in the cache
 */


var CredentialType;

(function (CredentialType) {
  CredentialType["ID_TOKEN"] = "IdToken";
  CredentialType["ACCESS_TOKEN"] = "AccessToken";
  CredentialType["ACCESS_TOKEN_WITH_AUTH_SCHEME"] = "AccessToken_With_AuthScheme";
  CredentialType["REFRESH_TOKEN"] = "RefreshToken";
})(CredentialType || (CredentialType = {}));
/**
 * Credential Type stored in the cache
 */


var CacheSchemaType;

(function (CacheSchemaType) {
  CacheSchemaType["ACCOUNT"] = "Account";
  CacheSchemaType["CREDENTIAL"] = "Credential";
  CacheSchemaType["ID_TOKEN"] = "IdToken";
  CacheSchemaType["ACCESS_TOKEN"] = "AccessToken";
  CacheSchemaType["REFRESH_TOKEN"] = "RefreshToken";
  CacheSchemaType["APP_METADATA"] = "AppMetadata";
  CacheSchemaType["TEMPORARY"] = "TempCache";
  CacheSchemaType["TELEMETRY"] = "Telemetry";
  CacheSchemaType["UNDEFINED"] = "Undefined";
  CacheSchemaType["THROTTLING"] = "Throttling";
})(CacheSchemaType || (CacheSchemaType = {}));
/**
 * Combine all cache types
 */


var CacheType;

(function (CacheType) {
  CacheType[CacheType["ADFS"] = 1001] = "ADFS";
  CacheType[CacheType["MSA"] = 1002] = "MSA";
  CacheType[CacheType["MSSTS"] = 1003] = "MSSTS";
  CacheType[CacheType["GENERIC"] = 1004] = "GENERIC";
  CacheType[CacheType["ACCESS_TOKEN"] = 2001] = "ACCESS_TOKEN";
  CacheType[CacheType["REFRESH_TOKEN"] = 2002] = "REFRESH_TOKEN";
  CacheType[CacheType["ID_TOKEN"] = 2003] = "ID_TOKEN";
  CacheType[CacheType["APP_METADATA"] = 3001] = "APP_METADATA";
  CacheType[CacheType["UNDEFINED"] = 9999] = "UNDEFINED";
})(CacheType || (CacheType = {}));
/**
 * More Cache related constants
 */


var APP_METADATA = "appmetadata";
var CLIENT_INFO = "client_info";
var THE_FAMILY_ID = "1";
var AUTHORITY_METADATA_CONSTANTS = {
  CACHE_KEY: "authority-metadata",
  REFRESH_TIME_SECONDS: 3600 * 24 // 24 Hours

};
var AuthorityMetadataSource;

(function (AuthorityMetadataSource) {
  AuthorityMetadataSource["CONFIG"] = "config";
  AuthorityMetadataSource["CACHE"] = "cache";
  AuthorityMetadataSource["NETWORK"] = "network";
  AuthorityMetadataSource["HARDCODED_VALUES"] = "hardcoded_values";
})(AuthorityMetadataSource || (AuthorityMetadataSource = {}));

var SERVER_TELEM_CONSTANTS = {
  SCHEMA_VERSION: 5,
  MAX_CUR_HEADER_BYTES: 80,
  MAX_LAST_HEADER_BYTES: 330,
  MAX_CACHED_ERRORS: 50,
  CACHE_KEY: "server-telemetry",
  CATEGORY_SEPARATOR: "|",
  VALUE_SEPARATOR: ",",
  OVERFLOW_TRUE: "1",
  OVERFLOW_FALSE: "0",
  UNKNOWN_ERROR: "unknown_error"
};
/**
 * Type of the authentication request
 */

var AuthenticationScheme;

(function (AuthenticationScheme) {
  AuthenticationScheme["BEARER"] = "Bearer";
  AuthenticationScheme["POP"] = "pop";
  AuthenticationScheme["SSH"] = "ssh-cert";
})(AuthenticationScheme || (AuthenticationScheme = {}));
/**
 * Constants related to throttling
 */


var ThrottlingConstants = {
  // Default time to throttle RequestThumbprint in seconds
  DEFAULT_THROTTLE_TIME_SECONDS: 60,
  // Default maximum time to throttle in seconds, overrides what the server sends back
  DEFAULT_MAX_THROTTLE_TIME_SECONDS: 3600,
  // Prefix for storing throttling entries
  THROTTLING_PREFIX: "throttling",
  // Value assigned to the x-ms-lib-capability header to indicate to the server the library supports throttling
  X_MS_LIB_CAPABILITY_VALUE: "retry-after, h429"
};
var Errors = {
  INVALID_GRANT_ERROR: "invalid_grant",
  CLIENT_MISMATCH_ERROR: "client_mismatch"
};
/**
 * Password grant parameters
 */

var PasswordGrantConstants;

(function (PasswordGrantConstants) {
  PasswordGrantConstants["username"] = "username";
  PasswordGrantConstants["password"] = "password";
})(PasswordGrantConstants || (PasswordGrantConstants = {}));
/**
 * Response codes
 */


var ResponseCodes;

(function (ResponseCodes) {
  ResponseCodes[ResponseCodes["httpSuccess"] = 200] = "httpSuccess";
  ResponseCodes[ResponseCodes["httpBadRequest"] = 400] = "httpBadRequest";
})(ResponseCodes || (ResponseCodes = {}));
/**
 * Region Discovery Sources
 */


var RegionDiscoverySources;

(function (RegionDiscoverySources) {
  RegionDiscoverySources["FAILED_AUTO_DETECTION"] = "1";
  RegionDiscoverySources["INTERNAL_CACHE"] = "2";
  RegionDiscoverySources["ENVIRONMENT_VARIABLE"] = "3";
  RegionDiscoverySources["IMDS"] = "4";
})(RegionDiscoverySources || (RegionDiscoverySources = {}));
/**
 * Region Discovery Outcomes
 */


var RegionDiscoveryOutcomes;

(function (RegionDiscoveryOutcomes) {
  RegionDiscoveryOutcomes["CONFIGURED_MATCHES_DETECTED"] = "1";
  RegionDiscoveryOutcomes["CONFIGURED_NO_AUTO_DETECTION"] = "2";
  RegionDiscoveryOutcomes["CONFIGURED_NOT_DETECTED"] = "3";
  RegionDiscoveryOutcomes["AUTO_DETECTION_REQUESTED_SUCCESSFUL"] = "4";
  RegionDiscoveryOutcomes["AUTO_DETECTION_REQUESTED_FAILED"] = "5";
})(RegionDiscoveryOutcomes || (RegionDiscoveryOutcomes = {}));

var CacheOutcome;

(function (CacheOutcome) {
  CacheOutcome["NO_CACHE_HIT"] = "0";
  CacheOutcome["FORCE_REFRESH"] = "1";
  CacheOutcome["NO_CACHED_ACCESS_TOKEN"] = "2";
  CacheOutcome["CACHED_ACCESS_TOKEN_EXPIRED"] = "3";
  CacheOutcome["REFRESH_CACHED_ACCESS_TOKEN"] = "4";
})(CacheOutcome || (CacheOutcome = {}));

var JsonTypes;

(function (JsonTypes) {
  JsonTypes["Jwt"] = "JWT";
  JsonTypes["Jwk"] = "JWK";
})(JsonTypes || (JsonTypes = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * AuthErrorMessage class containing string constants used by error codes and messages.
 */

var AuthErrorMessage = {
  unexpectedError: {
    code: "unexpected_error",
    desc: "Unexpected error in authentication."
  },
  postRequestFailed: {
    code: "post_request_failed",
    desc: "Post request failed from the network, could be a 4xx/5xx or a network unavailability. Please check the exact error code for details."
  }
};
/**
 * General error class thrown by the MSAL.js library.
 */

var AuthError =
/** @class */
function (_super) {
  __extends(AuthError, _super);

  function AuthError(errorCode, errorMessage, suberror) {
    var _this = this;

    var errorString = errorMessage ? errorCode + ": " + errorMessage : errorCode;
    _this = _super.call(this, errorString) || this;
    Object.setPrototypeOf(_this, AuthError.prototype);
    _this.errorCode = errorCode || Constants.EMPTY_STRING;
    _this.errorMessage = errorMessage || Constants.EMPTY_STRING;
    _this.subError = suberror || Constants.EMPTY_STRING;
    _this.name = "AuthError";
    return _this;
  }

  AuthError.prototype.setCorrelationId = function (correlationId) {
    this.correlationId = correlationId;
  };
  /**
   * Creates an error that is thrown when something unexpected happens in the library.
   * @param errDesc
   */


  AuthError.createUnexpectedError = function (errDesc) {
    return new AuthError(AuthErrorMessage.unexpectedError.code, AuthErrorMessage.unexpectedError.desc + ": " + errDesc);
  };
  /**
   * Creates an error for post request failures.
   * @param errDesc
   * @returns
   */


  AuthError.createPostRequestFailed = function (errDesc) {
    return new AuthError(AuthErrorMessage.postRequestFailed.code, AuthErrorMessage.postRequestFailed.desc + ": " + errDesc);
  };

  return AuthError;
}(Error);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var DEFAULT_CRYPTO_IMPLEMENTATION = {
  createNewGuid: function () {
    var notImplErr = "Crypto interface - createNewGuid() has not been implemented";
    throw AuthError.createUnexpectedError(notImplErr);
  },
  base64Decode: function () {
    var notImplErr = "Crypto interface - base64Decode() has not been implemented";
    throw AuthError.createUnexpectedError(notImplErr);
  },
  base64Encode: function () {
    var notImplErr = "Crypto interface - base64Encode() has not been implemented";
    throw AuthError.createUnexpectedError(notImplErr);
  },
  generatePkceCodes: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - generatePkceCodes() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  getPublicKeyThumbprint: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - getPublicKeyThumbprint() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  removeTokenBindingKey: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - removeTokenBindingKey() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  clearKeystore: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - clearKeystore() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  signJwt: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - signJwt() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  hashString: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Crypto interface - hashString() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  }
};

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * ClientAuthErrorMessage class containing string constants used by error codes and messages.
 */

var ClientAuthErrorMessage = {
  clientInfoDecodingError: {
    code: "client_info_decoding_error",
    desc: "The client info could not be parsed/decoded correctly. Please review the trace to determine the root cause."
  },
  clientInfoEmptyError: {
    code: "client_info_empty_error",
    desc: "The client info was empty. Please review the trace to determine the root cause."
  },
  tokenParsingError: {
    code: "token_parsing_error",
    desc: "Token cannot be parsed. Please review stack trace to determine root cause."
  },
  nullOrEmptyToken: {
    code: "null_or_empty_token",
    desc: "The token is null or empty. Please review the trace to determine the root cause."
  },
  endpointResolutionError: {
    code: "endpoints_resolution_error",
    desc: "Error: could not resolve endpoints. Please check network and try again."
  },
  networkError: {
    code: "network_error",
    desc: "Network request failed. Please check network trace to determine root cause."
  },
  unableToGetOpenidConfigError: {
    code: "openid_config_error",
    desc: "Could not retrieve endpoints. Check your authority and verify the .well-known/openid-configuration endpoint returns the required endpoints."
  },
  hashNotDeserialized: {
    code: "hash_not_deserialized",
    desc: "The hash parameters could not be deserialized. Please review the trace to determine the root cause."
  },
  blankGuidGenerated: {
    code: "blank_guid_generated",
    desc: "The guid generated was blank. Please review the trace to determine the root cause."
  },
  invalidStateError: {
    code: "invalid_state",
    desc: "State was not the expected format. Please check the logs to determine whether the request was sent using ProtocolUtils.setRequestState()."
  },
  stateMismatchError: {
    code: "state_mismatch",
    desc: "State mismatch error. Please check your network. Continued requests may cause cache overflow."
  },
  stateNotFoundError: {
    code: "state_not_found",
    desc: "State not found"
  },
  nonceMismatchError: {
    code: "nonce_mismatch",
    desc: "Nonce mismatch error. This may be caused by a race condition in concurrent requests."
  },
  nonceNotFoundError: {
    code: "nonce_not_found",
    desc: "nonce not found"
  },
  authTimeNotFoundError: {
    code: "auth_time_not_found",
    desc: "Max Age was requested and the ID token is missing the auth_time variable." + " auth_time is an optional claim and is not enabled by default - it must be enabled." + " See https://aka.ms/msaljs/optional-claims for more information."
  },
  maxAgeTranspiredError: {
    code: "max_age_transpired",
    desc: "Max Age is set to 0, or too much time has elapsed since the last end-user authentication."
  },
  noTokensFoundError: {
    code: "no_tokens_found",
    desc: "No tokens were found for the given scopes, and no authorization code was passed to acquireToken. You must retrieve an authorization code before making a call to acquireToken()."
  },
  multipleMatchingTokens: {
    code: "multiple_matching_tokens",
    desc: "The cache contains multiple tokens satisfying the requirements. " + "Call AcquireToken again providing more requirements such as authority or account."
  },
  multipleMatchingAccounts: {
    code: "multiple_matching_accounts",
    desc: "The cache contains multiple accounts satisfying the given parameters. Please pass more info to obtain the correct account"
  },
  multipleMatchingAppMetadata: {
    code: "multiple_matching_appMetadata",
    desc: "The cache contains multiple appMetadata satisfying the given parameters. Please pass more info to obtain the correct appMetadata"
  },
  tokenRequestCannotBeMade: {
    code: "request_cannot_be_made",
    desc: "Token request cannot be made without authorization code or refresh token."
  },
  appendEmptyScopeError: {
    code: "cannot_append_empty_scope",
    desc: "Cannot append null or empty scope to ScopeSet. Please check the stack trace for more info."
  },
  removeEmptyScopeError: {
    code: "cannot_remove_empty_scope",
    desc: "Cannot remove null or empty scope from ScopeSet. Please check the stack trace for more info."
  },
  appendScopeSetError: {
    code: "cannot_append_scopeset",
    desc: "Cannot append ScopeSet due to error."
  },
  emptyInputScopeSetError: {
    code: "empty_input_scopeset",
    desc: "Empty input ScopeSet cannot be processed."
  },
  DeviceCodePollingCancelled: {
    code: "device_code_polling_cancelled",
    desc: "Caller has cancelled token endpoint polling during device code flow by setting DeviceCodeRequest.cancel = true."
  },
  DeviceCodeExpired: {
    code: "device_code_expired",
    desc: "Device code is expired."
  },
  DeviceCodeUnknownError: {
    code: "device_code_unknown_error",
    desc: "Device code stopped polling for unknown reasons."
  },
  NoAccountInSilentRequest: {
    code: "no_account_in_silent_request",
    desc: "Please pass an account object, silent flow is not supported without account information"
  },
  invalidCacheRecord: {
    code: "invalid_cache_record",
    desc: "Cache record object was null or undefined."
  },
  invalidCacheEnvironment: {
    code: "invalid_cache_environment",
    desc: "Invalid environment when attempting to create cache entry"
  },
  noAccountFound: {
    code: "no_account_found",
    desc: "No account found in cache for given key."
  },
  CachePluginError: {
    code: "no cache plugin set on CacheManager",
    desc: "ICachePlugin needs to be set before using readFromStorage or writeFromStorage"
  },
  noCryptoObj: {
    code: "no_crypto_object",
    desc: "No crypto object detected. This is required for the following operation: "
  },
  invalidCacheType: {
    code: "invalid_cache_type",
    desc: "Invalid cache type"
  },
  unexpectedAccountType: {
    code: "unexpected_account_type",
    desc: "Unexpected account type."
  },
  unexpectedCredentialType: {
    code: "unexpected_credential_type",
    desc: "Unexpected credential type."
  },
  invalidAssertion: {
    code: "invalid_assertion",
    desc: "Client assertion must meet requirements described in https://tools.ietf.org/html/rfc7515"
  },
  invalidClientCredential: {
    code: "invalid_client_credential",
    desc: "Client credential (secret, certificate, or assertion) must not be empty when creating a confidential client. An application should at most have one credential"
  },
  tokenRefreshRequired: {
    code: "token_refresh_required",
    desc: "Cannot return token from cache because it must be refreshed. This may be due to one of the following reasons: forceRefresh parameter is set to true, claims have been requested, there is no cached access token or it is expired."
  },
  userTimeoutReached: {
    code: "user_timeout_reached",
    desc: "User defined timeout for device code polling reached"
  },
  tokenClaimsRequired: {
    code: "token_claims_cnf_required_for_signedjwt",
    desc: "Cannot generate a POP jwt if the token_claims are not populated"
  },
  noAuthorizationCodeFromServer: {
    code: "authorization_code_missing_from_server_response",
    desc: "Server response does not contain an authorization code to proceed"
  },
  noAzureRegionDetected: {
    code: "no_azure_region_detected",
    desc: "No azure region was detected and no fallback was made available"
  },
  accessTokenEntityNullError: {
    code: "access_token_entity_null",
    desc: "Access token entity is null, please check logs and cache to ensure a valid access token is present."
  },
  bindingKeyNotRemovedError: {
    code: "binding_key_not_removed",
    desc: "Could not remove the credential's binding key from storage."
  },
  logoutNotSupported: {
    code: "end_session_endpoint_not_supported",
    desc: "Provided authority does not support logout."
  },
  keyIdMissing: {
    code: "key_id_missing",
    desc: "A keyId value is missing from the requested bound token's cache record and is required to match the token to it's stored binding key."
  }
};
/**
 * Error thrown when there is an error in the client code running on the browser.
 */

var ClientAuthError =
/** @class */
function (_super) {
  __extends(ClientAuthError, _super);

  function ClientAuthError(errorCode, errorMessage) {
    var _this = _super.call(this, errorCode, errorMessage) || this;

    _this.name = "ClientAuthError";
    Object.setPrototypeOf(_this, ClientAuthError.prototype);
    return _this;
  }
  /**
   * Creates an error thrown when client info object doesn't decode correctly.
   * @param caughtError
   */


  ClientAuthError.createClientInfoDecodingError = function (caughtError) {
    return new ClientAuthError(ClientAuthErrorMessage.clientInfoDecodingError.code, ClientAuthErrorMessage.clientInfoDecodingError.desc + " Failed with error: " + caughtError);
  };
  /**
   * Creates an error thrown if the client info is empty.
   * @param rawClientInfo
   */


  ClientAuthError.createClientInfoEmptyError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.clientInfoEmptyError.code, "" + ClientAuthErrorMessage.clientInfoEmptyError.desc);
  };
  /**
   * Creates an error thrown when the id token extraction errors out.
   * @param err
   */


  ClientAuthError.createTokenParsingError = function (caughtExtractionError) {
    return new ClientAuthError(ClientAuthErrorMessage.tokenParsingError.code, ClientAuthErrorMessage.tokenParsingError.desc + " Failed with error: " + caughtExtractionError);
  };
  /**
   * Creates an error thrown when the id token string is null or empty.
   * @param invalidRawTokenString
   */


  ClientAuthError.createTokenNullOrEmptyError = function (invalidRawTokenString) {
    return new ClientAuthError(ClientAuthErrorMessage.nullOrEmptyToken.code, ClientAuthErrorMessage.nullOrEmptyToken.desc + " Raw Token Value: " + invalidRawTokenString);
  };
  /**
   * Creates an error thrown when the endpoint discovery doesn't complete correctly.
   */


  ClientAuthError.createEndpointDiscoveryIncompleteError = function (errDetail) {
    return new ClientAuthError(ClientAuthErrorMessage.endpointResolutionError.code, ClientAuthErrorMessage.endpointResolutionError.desc + " Detail: " + errDetail);
  };
  /**
   * Creates an error thrown when the fetch client throws
   */


  ClientAuthError.createNetworkError = function (endpoint, errDetail) {
    return new ClientAuthError(ClientAuthErrorMessage.networkError.code, ClientAuthErrorMessage.networkError.desc + " | Fetch client threw: " + errDetail + " | Attempted to reach: " + endpoint.split("?")[0]);
  };
  /**
   * Creates an error thrown when the openid-configuration endpoint cannot be reached or does not contain the required data
   */


  ClientAuthError.createUnableToGetOpenidConfigError = function (errDetail) {
    return new ClientAuthError(ClientAuthErrorMessage.unableToGetOpenidConfigError.code, ClientAuthErrorMessage.unableToGetOpenidConfigError.desc + " Attempted to retrieve endpoints from: " + errDetail);
  };
  /**
   * Creates an error thrown when the hash cannot be deserialized.
   * @param hashParamObj
   */


  ClientAuthError.createHashNotDeserializedError = function (hashParamObj) {
    return new ClientAuthError(ClientAuthErrorMessage.hashNotDeserialized.code, ClientAuthErrorMessage.hashNotDeserialized.desc + " Given Object: " + hashParamObj);
  };
  /**
   * Creates an error thrown when the state cannot be parsed.
   * @param invalidState
   */


  ClientAuthError.createInvalidStateError = function (invalidState, errorString) {
    return new ClientAuthError(ClientAuthErrorMessage.invalidStateError.code, ClientAuthErrorMessage.invalidStateError.desc + " Invalid State: " + invalidState + ", Root Err: " + errorString);
  };
  /**
   * Creates an error thrown when two states do not match.
   */


  ClientAuthError.createStateMismatchError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.stateMismatchError.code, ClientAuthErrorMessage.stateMismatchError.desc);
  };
  /**
   * Creates an error thrown when the state is not present
   * @param missingState
   */


  ClientAuthError.createStateNotFoundError = function (missingState) {
    return new ClientAuthError(ClientAuthErrorMessage.stateNotFoundError.code, ClientAuthErrorMessage.stateNotFoundError.desc + ":  " + missingState);
  };
  /**
   * Creates an error thrown when the nonce does not match.
   */


  ClientAuthError.createNonceMismatchError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.nonceMismatchError.code, ClientAuthErrorMessage.nonceMismatchError.desc);
  };
  /**
   * Creates an error thrown when max_age was provided in the request, but auth_time is not in the token claims
   * @param missingNonce
   */


  ClientAuthError.createAuthTimeNotFoundError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.authTimeNotFoundError.code, ClientAuthErrorMessage.authTimeNotFoundError.desc);
  };
  /**
   * Creates an error thrown when too much time has elapsed since the last end-user authentication
   */


  ClientAuthError.createMaxAgeTranspiredError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.maxAgeTranspiredError.code, ClientAuthErrorMessage.maxAgeTranspiredError.desc);
  };
  /**
   * Creates an error thrown when the mnonce is not present
   * @param missingNonce
   */


  ClientAuthError.createNonceNotFoundError = function (missingNonce) {
    return new ClientAuthError(ClientAuthErrorMessage.nonceNotFoundError.code, ClientAuthErrorMessage.nonceNotFoundError.desc + ":  " + missingNonce);
  };
  /**
   * Throws error when multiple tokens are in cache.
   */


  ClientAuthError.createMultipleMatchingTokensInCacheError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.multipleMatchingTokens.code, ClientAuthErrorMessage.multipleMatchingTokens.desc + ".");
  };
  /**
   * Throws error when multiple accounts are in cache for the given params
   */


  ClientAuthError.createMultipleMatchingAccountsInCacheError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.multipleMatchingAccounts.code, ClientAuthErrorMessage.multipleMatchingAccounts.desc);
  };
  /**
   * Throws error when multiple appMetada are in cache for the given clientId.
   */


  ClientAuthError.createMultipleMatchingAppMetadataInCacheError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.multipleMatchingAppMetadata.code, ClientAuthErrorMessage.multipleMatchingAppMetadata.desc);
  };
  /**
   * Throws error when no auth code or refresh token is given to ServerTokenRequestParameters.
   */


  ClientAuthError.createTokenRequestCannotBeMadeError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.tokenRequestCannotBeMade.code, ClientAuthErrorMessage.tokenRequestCannotBeMade.desc);
  };
  /**
   * Throws error when attempting to append a null, undefined or empty scope to a set
   * @param givenScope
   */


  ClientAuthError.createAppendEmptyScopeToSetError = function (givenScope) {
    return new ClientAuthError(ClientAuthErrorMessage.appendEmptyScopeError.code, ClientAuthErrorMessage.appendEmptyScopeError.desc + " Given Scope: " + givenScope);
  };
  /**
   * Throws error when attempting to append a null, undefined or empty scope to a set
   * @param givenScope
   */


  ClientAuthError.createRemoveEmptyScopeFromSetError = function (givenScope) {
    return new ClientAuthError(ClientAuthErrorMessage.removeEmptyScopeError.code, ClientAuthErrorMessage.removeEmptyScopeError.desc + " Given Scope: " + givenScope);
  };
  /**
   * Throws error when attempting to append null or empty ScopeSet.
   * @param appendError
   */


  ClientAuthError.createAppendScopeSetError = function (appendError) {
    return new ClientAuthError(ClientAuthErrorMessage.appendScopeSetError.code, ClientAuthErrorMessage.appendScopeSetError.desc + " Detail Error: " + appendError);
  };
  /**
   * Throws error if ScopeSet is null or undefined.
   * @param givenScopeSet
   */


  ClientAuthError.createEmptyInputScopeSetError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.emptyInputScopeSetError.code, "" + ClientAuthErrorMessage.emptyInputScopeSetError.desc);
  };
  /**
   * Throws error if user sets CancellationToken.cancel = true during polling of token endpoint during device code flow
   */


  ClientAuthError.createDeviceCodeCancelledError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.DeviceCodePollingCancelled.code, "" + ClientAuthErrorMessage.DeviceCodePollingCancelled.desc);
  };
  /**
   * Throws error if device code is expired
   */


  ClientAuthError.createDeviceCodeExpiredError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.DeviceCodeExpired.code, "" + ClientAuthErrorMessage.DeviceCodeExpired.desc);
  };
  /**
   * Throws error if device code is expired
   */


  ClientAuthError.createDeviceCodeUnknownError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.DeviceCodeUnknownError.code, "" + ClientAuthErrorMessage.DeviceCodeUnknownError.desc);
  };
  /**
   * Throws error when silent requests are made without an account object
   */


  ClientAuthError.createNoAccountInSilentRequestError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.NoAccountInSilentRequest.code, "" + ClientAuthErrorMessage.NoAccountInSilentRequest.desc);
  };
  /**
   * Throws error when cache record is null or undefined.
   */


  ClientAuthError.createNullOrUndefinedCacheRecord = function () {
    return new ClientAuthError(ClientAuthErrorMessage.invalidCacheRecord.code, ClientAuthErrorMessage.invalidCacheRecord.desc);
  };
  /**
   * Throws error when provided environment is not part of the CloudDiscoveryMetadata object
   */


  ClientAuthError.createInvalidCacheEnvironmentError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.invalidCacheEnvironment.code, ClientAuthErrorMessage.invalidCacheEnvironment.desc);
  };
  /**
   * Throws error when account is not found in cache.
   */


  ClientAuthError.createNoAccountFoundError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.noAccountFound.code, ClientAuthErrorMessage.noAccountFound.desc);
  };
  /**
   * Throws error if ICachePlugin not set on CacheManager.
   */


  ClientAuthError.createCachePluginError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.CachePluginError.code, "" + ClientAuthErrorMessage.CachePluginError.desc);
  };
  /**
   * Throws error if crypto object not found.
   * @param operationName
   */


  ClientAuthError.createNoCryptoObjectError = function (operationName) {
    return new ClientAuthError(ClientAuthErrorMessage.noCryptoObj.code, "" + ClientAuthErrorMessage.noCryptoObj.desc + operationName);
  };
  /**
   * Throws error if cache type is invalid.
   */


  ClientAuthError.createInvalidCacheTypeError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.invalidCacheType.code, "" + ClientAuthErrorMessage.invalidCacheType.desc);
  };
  /**
   * Throws error if unexpected account type.
   */


  ClientAuthError.createUnexpectedAccountTypeError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.unexpectedAccountType.code, "" + ClientAuthErrorMessage.unexpectedAccountType.desc);
  };
  /**
   * Throws error if unexpected credential type.
   */


  ClientAuthError.createUnexpectedCredentialTypeError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.unexpectedCredentialType.code, "" + ClientAuthErrorMessage.unexpectedCredentialType.desc);
  };
  /**
   * Throws error if client assertion is not valid.
   */


  ClientAuthError.createInvalidAssertionError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.invalidAssertion.code, "" + ClientAuthErrorMessage.invalidAssertion.desc);
  };
  /**
   * Throws error if client assertion is not valid.
   */


  ClientAuthError.createInvalidCredentialError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.invalidClientCredential.code, "" + ClientAuthErrorMessage.invalidClientCredential.desc);
  };
  /**
   * Throws error if token cannot be retrieved from cache due to refresh being required.
   */


  ClientAuthError.createRefreshRequiredError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.tokenRefreshRequired.code, ClientAuthErrorMessage.tokenRefreshRequired.desc);
  };
  /**
   * Throws error if the user defined timeout is reached.
   */


  ClientAuthError.createUserTimeoutReachedError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.userTimeoutReached.code, ClientAuthErrorMessage.userTimeoutReached.desc);
  };
  /*
   * Throws error if token claims are not populated for a signed jwt generation
   */


  ClientAuthError.createTokenClaimsRequiredError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.tokenClaimsRequired.code, ClientAuthErrorMessage.tokenClaimsRequired.desc);
  };
  /**
   * Throws error when the authorization code is missing from the server response
   */


  ClientAuthError.createNoAuthCodeInServerResponseError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.noAuthorizationCodeFromServer.code, ClientAuthErrorMessage.noAuthorizationCodeFromServer.desc);
  };

  ClientAuthError.createBindingKeyNotRemovedError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.bindingKeyNotRemovedError.code, ClientAuthErrorMessage.bindingKeyNotRemovedError.desc);
  };
  /**
   * Thrown when logout is attempted for an authority that doesnt have an end_session_endpoint
   */


  ClientAuthError.createLogoutNotSupportedError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.logoutNotSupported.code, ClientAuthErrorMessage.logoutNotSupported.desc);
  };
  /**
   * Create an error when kid attribute is missing from a PoP token's cache record
   */


  ClientAuthError.createKeyIdMissingError = function () {
    return new ClientAuthError(ClientAuthErrorMessage.keyIdMissing.code, ClientAuthErrorMessage.keyIdMissing.desc);
  };

  return ClientAuthError;
}(AuthError);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * @hidden
 */

var StringUtils =
/** @class */
function () {
  function StringUtils() {}
  /**
   * decode a JWT
   *
   * @param authToken
   */


  StringUtils.decodeAuthToken = function (authToken) {
    if (StringUtils.isEmpty(authToken)) {
      throw ClientAuthError.createTokenNullOrEmptyError(authToken);
    }

    var tokenPartsRegex = /^([^\.\s]*)\.([^\.\s]+)\.([^\.\s]*)$/;
    var matches = tokenPartsRegex.exec(authToken);

    if (!matches || matches.length < 4) {
      throw ClientAuthError.createTokenParsingError("Given token is malformed: " + JSON.stringify(authToken));
    }

    var crackedToken = {
      header: matches[1],
      JWSPayload: matches[2],
      JWSSig: matches[3]
    };
    return crackedToken;
  };
  /**
   * Check if a string is empty.
   *
   * @param str
   */


  StringUtils.isEmpty = function (str) {
    return typeof str === "undefined" || !str || 0 === str.length;
  };
  /**
   * Check if stringified object is empty
   * @param strObj
   */


  StringUtils.isEmptyObj = function (strObj) {
    if (strObj && !StringUtils.isEmpty(strObj)) {
      try {
        var obj = JSON.parse(strObj);
        return Object.keys(obj).length === 0;
      } catch (e) {}
    }

    return true;
  };

  StringUtils.startsWith = function (str, search) {
    return str.indexOf(search) === 0;
  };

  StringUtils.endsWith = function (str, search) {
    return str.length >= search.length && str.lastIndexOf(search) === str.length - search.length;
  };
  /**
   * Parses string into an object.
   *
   * @param query
   */


  StringUtils.queryStringToObject = function (query) {
    var obj = {};
    var params = query.split("&");

    var decode = function (s) {
      return decodeURIComponent(s.replace(/\+/g, " "));
    };

    params.forEach(function (pair) {
      if (pair.trim()) {
        var _a = pair.split(/=(.+)/g, 2),
            key = _a[0],
            value = _a[1]; // Split on the first occurence of the '=' character


        if (key && value) {
          obj[decode(key)] = decode(value);
        }
      }
    });
    return obj;
  };
  /**
   * Trims entries in an array.
   *
   * @param arr
   */


  StringUtils.trimArrayEntries = function (arr) {
    return arr.map(function (entry) {
      return entry.trim();
    });
  };
  /**
   * Removes empty strings from array
   * @param arr
   */


  StringUtils.removeEmptyStringsFromArray = function (arr) {
    return arr.filter(function (entry) {
      return !StringUtils.isEmpty(entry);
    });
  };
  /**
   * Attempts to parse a string into JSON
   * @param str
   */


  StringUtils.jsonParseHelper = function (str) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  };
  /**
   * Tests if a given string matches a given pattern, with support for wildcards and queries.
   * @param pattern Wildcard pattern to string match. Supports "*" for wildcards and "?" for queries
   * @param input String to match against
   */


  StringUtils.matchPattern = function (pattern, input) {
    /**
     * Wildcard support: https://stackoverflow.com/a/3117248/4888559
     * Queries: replaces "?" in string with escaped "\?" for regex test
     */
    var regex = new RegExp(pattern.replace(/\\/g, "\\\\").replace(/\*/g, "[^ ]*").replace(/\?/g, "\\\?")); // eslint-disable-line security/detect-non-literal-regexp

    return regex.test(input);
  };

  return StringUtils;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Log message level.
 */

var LogLevel;

(function (LogLevel) {
  LogLevel[LogLevel["Error"] = 0] = "Error";
  LogLevel[LogLevel["Warning"] = 1] = "Warning";
  LogLevel[LogLevel["Info"] = 2] = "Info";
  LogLevel[LogLevel["Verbose"] = 3] = "Verbose";
  LogLevel[LogLevel["Trace"] = 4] = "Trace";
})(LogLevel || (LogLevel = {}));
/**
 * Class which facilitates logging of messages to a specific place.
 */


var Logger =
/** @class */
function () {
  function Logger(loggerOptions, packageName, packageVersion) {
    // Current log level, defaults to info.
    this.level = LogLevel.Info;

    var defaultLoggerCallback = function () {
      return;
    };

    this.localCallback = loggerOptions.loggerCallback || defaultLoggerCallback;
    this.piiLoggingEnabled = loggerOptions.piiLoggingEnabled || false;
    this.level = typeof loggerOptions.logLevel === "number" ? loggerOptions.logLevel : LogLevel.Info;
    this.correlationId = loggerOptions.correlationId || Constants.EMPTY_STRING;
    this.packageName = packageName || Constants.EMPTY_STRING;
    this.packageVersion = packageVersion || Constants.EMPTY_STRING;
  }
  /**
   * Create new Logger with existing configurations.
   */


  Logger.prototype.clone = function (packageName, packageVersion, correlationId) {
    return new Logger({
      loggerCallback: this.localCallback,
      piiLoggingEnabled: this.piiLoggingEnabled,
      logLevel: this.level,
      correlationId: correlationId || this.correlationId
    }, packageName, packageVersion);
  };
  /**
   * Log message with required options.
   */


  Logger.prototype.logMessage = function (logMessage, options) {
    if (options.logLevel > this.level || !this.piiLoggingEnabled && options.containsPii) {
      return;
    }

    var timestamp = new Date().toUTCString(); // Add correlationId to logs if set, correlationId provided on log messages take precedence

    var logHeader;

    if (!StringUtils.isEmpty(options.correlationId)) {
      logHeader = "[" + timestamp + "] : [" + options.correlationId + "]";
    } else if (!StringUtils.isEmpty(this.correlationId)) {
      logHeader = "[" + timestamp + "] : [" + this.correlationId + "]";
    } else {
      logHeader = "[" + timestamp + "]";
    }

    var log = logHeader + " : " + this.packageName + "@" + this.packageVersion + " : " + LogLevel[options.logLevel] + " - " + logMessage; // debug(`msal:${LogLevel[options.logLevel]}${options.containsPii ? "-Pii": Constants.EMPTY_STRING}${options.context ? `:${options.context}` : Constants.EMPTY_STRING}`)(logMessage);

    this.executeCallback(options.logLevel, log, options.containsPii || false);
  };
  /**
   * Execute callback with message.
   */


  Logger.prototype.executeCallback = function (level, message, containsPii) {
    if (this.localCallback) {
      this.localCallback(level, message, containsPii);
    }
  };
  /**
   * Logs error messages.
   */


  Logger.prototype.error = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Error,
      containsPii: false,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs error messages with PII.
   */


  Logger.prototype.errorPii = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Error,
      containsPii: true,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs warning messages.
   */


  Logger.prototype.warning = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Warning,
      containsPii: false,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs warning messages with PII.
   */


  Logger.prototype.warningPii = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Warning,
      containsPii: true,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs info messages.
   */


  Logger.prototype.info = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Info,
      containsPii: false,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs info messages with PII.
   */


  Logger.prototype.infoPii = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Info,
      containsPii: true,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs verbose messages.
   */


  Logger.prototype.verbose = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Verbose,
      containsPii: false,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs verbose messages with PII.
   */


  Logger.prototype.verbosePii = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Verbose,
      containsPii: true,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs trace messages.
   */


  Logger.prototype.trace = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Trace,
      containsPii: false,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Logs trace messages with PII.
   */


  Logger.prototype.tracePii = function (message, correlationId) {
    this.logMessage(message, {
      logLevel: LogLevel.Trace,
      containsPii: true,
      correlationId: correlationId || Constants.EMPTY_STRING
    });
  };
  /**
   * Returns whether PII Logging is enabled or not.
   */


  Logger.prototype.isPiiLoggingEnabled = function () {
    return this.piiLoggingEnabled || false;
  };

  return Logger;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/* eslint-disable header/header */

var name$1 = "@azure/msal-common";
var version$1 = "7.6.0";

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var AzureCloudInstance;

(function (AzureCloudInstance) {
  // AzureCloudInstance is not specified.
  AzureCloudInstance[AzureCloudInstance["None"] = 0] = "None"; // Microsoft Azure public cloud

  AzureCloudInstance["AzurePublic"] = "https://login.microsoftonline.com"; // Microsoft PPE

  AzureCloudInstance["AzurePpe"] = "https://login.windows-ppe.net"; // Microsoft Chinese national cloud

  AzureCloudInstance["AzureChina"] = "https://login.chinacloudapi.cn"; // Microsoft German national cloud ("Black Forest")

  AzureCloudInstance["AzureGermany"] = "https://login.microsoftonline.de"; // US Government cloud

  AzureCloudInstance["AzureUsGovernment"] = "https://login.microsoftonline.us";
})(AzureCloudInstance || (AzureCloudInstance = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Base type for credentials to be stored in the cache: eg: ACCESS_TOKEN, ID_TOKEN etc
 *
 * Key:Value Schema:
 *
 * Key: <home_account_id*>-<environment>-<credential_type>-<client_id>-<realm*>-<target*>-<requestedClaims*>-<scheme*>
 *
 * Value Schema:
 * {
 *      homeAccountId: home account identifier for the auth scheme,
 *      environment: entity that issued the token, represented as a full host
 *      credentialType: Type of credential as a string, can be one of the following: RefreshToken, AccessToken, IdToken, Password, Cookie, Certificate, Other
 *      clientId: client ID of the application
 *      secret: Actual credential as a string
 *      familyId: Family ID identifier, usually only used for refresh tokens
 *      realm: Full tenant or organizational identifier that the account belongs to
 *      target: Permissions that are included in the token, or for refresh tokens, the resource identifier.
 *      tokenType: Matches the authentication scheme for which the token was issued (i.e. Bearer or pop)
 *      requestedClaimsHash: Matches the SHA 256 hash of the claims object included in the token request
 *      userAssertionHash: Matches the SHA 256 hash of the obo_assertion for the OBO flow
 * }
 */

var CredentialEntity =
/** @class */
function () {
  function CredentialEntity() {}
  /**
   * Generate Account Id key component as per the schema: <home_account_id>-<environment>
   */


  CredentialEntity.prototype.generateAccountId = function () {
    return CredentialEntity.generateAccountIdForCacheKey(this.homeAccountId, this.environment);
  };
  /**
   * Generate Credential Id key component as per the schema: <credential_type>-<client_id>-<realm>
   */


  CredentialEntity.prototype.generateCredentialId = function () {
    return CredentialEntity.generateCredentialIdForCacheKey(this.credentialType, this.clientId, this.realm, this.familyId);
  };
  /**
   * Generate target key component as per schema: <target>
   */


  CredentialEntity.prototype.generateTarget = function () {
    return CredentialEntity.generateTargetForCacheKey(this.target);
  };
  /**
   * generates credential key
   */


  CredentialEntity.prototype.generateCredentialKey = function () {
    return CredentialEntity.generateCredentialCacheKey(this.homeAccountId, this.environment, this.credentialType, this.clientId, this.realm, this.target, this.familyId, this.tokenType, this.requestedClaimsHash);
  };
  /**
   * returns the type of the cache (in this case credential)
   */


  CredentialEntity.prototype.generateType = function () {
    switch (this.credentialType) {
      case CredentialType.ID_TOKEN:
        return CacheType.ID_TOKEN;

      case CredentialType.ACCESS_TOKEN:
      case CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME:
        return CacheType.ACCESS_TOKEN;

      case CredentialType.REFRESH_TOKEN:
        return CacheType.REFRESH_TOKEN;

      default:
        {
          throw ClientAuthError.createUnexpectedCredentialTypeError();
        }
    }
  };
  /**
   * helper function to return `CredentialType`
   * @param key
   */


  CredentialEntity.getCredentialType = function (key) {
    // First keyword search will match all "AccessToken" and "AccessToken_With_AuthScheme" credentials
    if (key.indexOf(CredentialType.ACCESS_TOKEN.toLowerCase()) !== -1) {
      // Perform second search to differentiate between "AccessToken" and "AccessToken_With_AuthScheme" credential types
      if (key.indexOf(CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME.toLowerCase()) !== -1) {
        return CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME;
      }

      return CredentialType.ACCESS_TOKEN;
    } else if (key.indexOf(CredentialType.ID_TOKEN.toLowerCase()) !== -1) {
      return CredentialType.ID_TOKEN;
    } else if (key.indexOf(CredentialType.REFRESH_TOKEN.toLowerCase()) !== -1) {
      return CredentialType.REFRESH_TOKEN;
    }

    return Constants.NOT_DEFINED;
  };
  /**
   * generates credential key
   * <home_account_id*>-\<environment>-<credential_type>-<client_id>-<realm\*>-<target\*>-<scheme\*>
   */


  CredentialEntity.generateCredentialCacheKey = function (homeAccountId, environment, credentialType, clientId, realm, target, familyId, tokenType, requestedClaimsHash) {
    var credentialKey = [this.generateAccountIdForCacheKey(homeAccountId, environment), this.generateCredentialIdForCacheKey(credentialType, clientId, realm, familyId), this.generateTargetForCacheKey(target), this.generateClaimsHashForCacheKey(requestedClaimsHash), this.generateSchemeForCacheKey(tokenType)];
    return credentialKey.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * generates Account Id for keys
   * @param homeAccountId
   * @param environment
   */


  CredentialEntity.generateAccountIdForCacheKey = function (homeAccountId, environment) {
    var accountId = [homeAccountId, environment];
    return accountId.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * Generates Credential Id for keys
   * @param credentialType
   * @param realm
   * @param clientId
   * @param familyId
   */


  CredentialEntity.generateCredentialIdForCacheKey = function (credentialType, clientId, realm, familyId) {
    var clientOrFamilyId = credentialType === CredentialType.REFRESH_TOKEN ? familyId || clientId : clientId;
    var credentialId = [credentialType, clientOrFamilyId, realm || Constants.EMPTY_STRING];
    return credentialId.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * Generate target key component as per schema: <target>
   */


  CredentialEntity.generateTargetForCacheKey = function (scopes) {
    return (scopes || Constants.EMPTY_STRING).toLowerCase();
  };
  /**
   * Generate requested claims key component as per schema: <requestedClaims>
   */


  CredentialEntity.generateClaimsHashForCacheKey = function (requestedClaimsHash) {
    return (requestedClaimsHash || Constants.EMPTY_STRING).toLowerCase();
  };
  /**
   * Generate scheme key componenet as per schema: <scheme>
   */


  CredentialEntity.generateSchemeForCacheKey = function (tokenType) {
    /*
     * PoP Tokens and SSH certs include scheme in cache key
     * Cast to lowercase to handle "bearer" from ADFS
     */
    return tokenType && tokenType.toLowerCase() !== AuthenticationScheme.BEARER.toLowerCase() ? tokenType.toLowerCase() : Constants.EMPTY_STRING;
  };

  return CredentialEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * ClientConfigurationErrorMessage class containing string constants used by error codes and messages.
 */

var ClientConfigurationErrorMessage = {
  redirectUriNotSet: {
    code: "redirect_uri_empty",
    desc: "A redirect URI is required for all calls, and none has been set."
  },
  postLogoutUriNotSet: {
    code: "post_logout_uri_empty",
    desc: "A post logout redirect has not been set."
  },
  claimsRequestParsingError: {
    code: "claims_request_parsing_error",
    desc: "Could not parse the given claims request object."
  },
  authorityUriInsecure: {
    code: "authority_uri_insecure",
    desc: "Authority URIs must use https.  Please see here for valid authority configuration options: https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications#configuration-options"
  },
  urlParseError: {
    code: "url_parse_error",
    desc: "URL could not be parsed into appropriate segments."
  },
  urlEmptyError: {
    code: "empty_url_error",
    desc: "URL was empty or null."
  },
  emptyScopesError: {
    code: "empty_input_scopes_error",
    desc: "Scopes cannot be passed as null, undefined or empty array because they are required to obtain an access token."
  },
  nonArrayScopesError: {
    code: "nonarray_input_scopes_error",
    desc: "Scopes cannot be passed as non-array."
  },
  clientIdSingleScopeError: {
    code: "clientid_input_scopes_error",
    desc: "Client ID can only be provided as a single scope."
  },
  invalidPrompt: {
    code: "invalid_prompt_value",
    desc: "Supported prompt values are 'login', 'select_account', 'consent', 'create', 'none' and 'no_session'.  Please see here for valid configuration options: https://azuread.github.io/microsoft-authentication-library-for-js/ref/modules/_azure_msal_common.html#commonauthorizationurlrequest"
  },
  invalidClaimsRequest: {
    code: "invalid_claims",
    desc: "Given claims parameter must be a stringified JSON object."
  },
  tokenRequestEmptyError: {
    code: "token_request_empty",
    desc: "Token request was empty and not found in cache."
  },
  logoutRequestEmptyError: {
    code: "logout_request_empty",
    desc: "The logout request was null or undefined."
  },
  invalidCodeChallengeMethod: {
    code: "invalid_code_challenge_method",
    desc: "code_challenge_method passed is invalid. Valid values are \"plain\" and \"S256\"."
  },
  invalidCodeChallengeParams: {
    code: "pkce_params_missing",
    desc: "Both params: code_challenge and code_challenge_method are to be passed if to be sent in the request"
  },
  invalidCloudDiscoveryMetadata: {
    code: "invalid_cloud_discovery_metadata",
    desc: "Invalid cloudDiscoveryMetadata provided. Must be a stringified JSON object containing tenant_discovery_endpoint and metadata fields"
  },
  invalidAuthorityMetadata: {
    code: "invalid_authority_metadata",
    desc: "Invalid authorityMetadata provided. Must by a stringified JSON object containing authorization_endpoint, token_endpoint, issuer fields."
  },
  untrustedAuthority: {
    code: "untrusted_authority",
    desc: "The provided authority is not a trusted authority. Please include this authority in the knownAuthorities config parameter."
  },
  invalidAzureCloudInstance: {
    code: "invalid_azure_cloud_instance",
    desc: "Invalid AzureCloudInstance provided. Please refer MSAL JS docs: aks.ms/msaljs/azure_cloud_instance for valid values"
  },
  missingSshJwk: {
    code: "missing_ssh_jwk",
    desc: "Missing sshJwk in SSH certificate request. A stringified JSON Web Key is required when using the SSH authentication scheme."
  },
  missingSshKid: {
    code: "missing_ssh_kid",
    desc: "Missing sshKid in SSH certificate request. A string that uniquely identifies the public SSH key is required when using the SSH authentication scheme."
  },
  missingNonceAuthenticationHeader: {
    code: "missing_nonce_authentication_header",
    desc: "Unable to find an authentication header containing server nonce. Either the Authentication-Info or WWW-Authenticate headers must be present in order to obtain a server nonce."
  },
  invalidAuthenticationHeader: {
    code: "invalid_authentication_header",
    desc: "Invalid authentication header provided"
  }
};
/**
 * Error thrown when there is an error in configuration of the MSAL.js library.
 */

var ClientConfigurationError =
/** @class */
function (_super) {
  __extends(ClientConfigurationError, _super);

  function ClientConfigurationError(errorCode, errorMessage) {
    var _this = _super.call(this, errorCode, errorMessage) || this;

    _this.name = "ClientConfigurationError";
    Object.setPrototypeOf(_this, ClientConfigurationError.prototype);
    return _this;
  }
  /**
   * Creates an error thrown when the redirect uri is empty (not set by caller)
   */


  ClientConfigurationError.createRedirectUriEmptyError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.redirectUriNotSet.code, ClientConfigurationErrorMessage.redirectUriNotSet.desc);
  };
  /**
   * Creates an error thrown when the post-logout redirect uri is empty (not set by caller)
   */


  ClientConfigurationError.createPostLogoutRedirectUriEmptyError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.postLogoutUriNotSet.code, ClientConfigurationErrorMessage.postLogoutUriNotSet.desc);
  };
  /**
   * Creates an error thrown when the claims request could not be successfully parsed
   */


  ClientConfigurationError.createClaimsRequestParsingError = function (claimsRequestParseError) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.claimsRequestParsingError.code, ClientConfigurationErrorMessage.claimsRequestParsingError.desc + " Given value: " + claimsRequestParseError);
  };
  /**
   * Creates an error thrown if authority uri is given an insecure protocol.
   * @param urlString
   */


  ClientConfigurationError.createInsecureAuthorityUriError = function (urlString) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.authorityUriInsecure.code, ClientConfigurationErrorMessage.authorityUriInsecure.desc + " Given URI: " + urlString);
  };
  /**
   * Creates an error thrown if URL string does not parse into separate segments.
   * @param urlString
   */


  ClientConfigurationError.createUrlParseError = function (urlParseError) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.urlParseError.code, ClientConfigurationErrorMessage.urlParseError.desc + " Given Error: " + urlParseError);
  };
  /**
   * Creates an error thrown if URL string is empty or null.
   * @param urlString
   */


  ClientConfigurationError.createUrlEmptyError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.urlEmptyError.code, ClientConfigurationErrorMessage.urlEmptyError.desc);
  };
  /**
   * Error thrown when scopes are empty.
   * @param scopesValue
   */


  ClientConfigurationError.createEmptyScopesArrayError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.emptyScopesError.code, "" + ClientConfigurationErrorMessage.emptyScopesError.desc);
  };
  /**
   * Error thrown when client id scope is not provided as single scope.
   * @param inputScopes
   */


  ClientConfigurationError.createClientIdSingleScopeError = function (inputScopes) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.clientIdSingleScopeError.code, ClientConfigurationErrorMessage.clientIdSingleScopeError.desc + " Given Scopes: " + inputScopes);
  };
  /**
   * Error thrown when prompt is not an allowed type.
   * @param promptValue
   */


  ClientConfigurationError.createInvalidPromptError = function (promptValue) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidPrompt.code, ClientConfigurationErrorMessage.invalidPrompt.desc + " Given value: " + promptValue);
  };
  /**
   * Creates error thrown when claims parameter is not a stringified JSON object
   */


  ClientConfigurationError.createInvalidClaimsRequestError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidClaimsRequest.code, ClientConfigurationErrorMessage.invalidClaimsRequest.desc);
  };
  /**
   * Throws error when token request is empty and nothing cached in storage.
   */


  ClientConfigurationError.createEmptyLogoutRequestError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.logoutRequestEmptyError.code, ClientConfigurationErrorMessage.logoutRequestEmptyError.desc);
  };
  /**
   * Throws error when token request is empty and nothing cached in storage.
   */


  ClientConfigurationError.createEmptyTokenRequestError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.tokenRequestEmptyError.code, ClientConfigurationErrorMessage.tokenRequestEmptyError.desc);
  };
  /**
   * Throws error when an invalid code_challenge_method is passed by the user
   */


  ClientConfigurationError.createInvalidCodeChallengeMethodError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidCodeChallengeMethod.code, ClientConfigurationErrorMessage.invalidCodeChallengeMethod.desc);
  };
  /**
   * Throws error when both params: code_challenge and code_challenge_method are not passed together
   */


  ClientConfigurationError.createInvalidCodeChallengeParamsError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidCodeChallengeParams.code, ClientConfigurationErrorMessage.invalidCodeChallengeParams.desc);
  };
  /**
   * Throws an error when the user passes invalid cloudDiscoveryMetadata
   */


  ClientConfigurationError.createInvalidCloudDiscoveryMetadataError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidCloudDiscoveryMetadata.code, ClientConfigurationErrorMessage.invalidCloudDiscoveryMetadata.desc);
  };
  /**
   * Throws an error when the user passes invalid cloudDiscoveryMetadata
   */


  ClientConfigurationError.createInvalidAuthorityMetadataError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidAuthorityMetadata.code, ClientConfigurationErrorMessage.invalidAuthorityMetadata.desc);
  };
  /**
   * Throws error when provided authority is not a member of the trusted host list
   */


  ClientConfigurationError.createUntrustedAuthorityError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.untrustedAuthority.code, ClientConfigurationErrorMessage.untrustedAuthority.desc);
  };
  /**
   * Throws error when the AzureCloudInstance is set to an invalid value
   */


  ClientConfigurationError.createInvalidAzureCloudInstanceError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidAzureCloudInstance.code, ClientConfigurationErrorMessage.invalidAzureCloudInstance.desc);
  };
  /**
   * Throws an error when the authentication scheme is set to SSH but the SSH public key is omitted from the request
   */


  ClientConfigurationError.createMissingSshJwkError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.missingSshJwk.code, ClientConfigurationErrorMessage.missingSshJwk.desc);
  };
  /**
   * Throws an error when the authentication scheme is set to SSH but the SSH public key ID is omitted from the request
   */


  ClientConfigurationError.createMissingSshKidError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.missingSshKid.code, ClientConfigurationErrorMessage.missingSshKid.desc);
  };
  /**
   * Throws error when provided headers don't contain a header that a server nonce can be extracted from
   */


  ClientConfigurationError.createMissingNonceAuthenticationHeadersError = function () {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.missingNonceAuthenticationHeader.code, ClientConfigurationErrorMessage.missingNonceAuthenticationHeader.desc);
  };
  /**
   * Throws error when a provided header is invalid in any way
   */


  ClientConfigurationError.createInvalidAuthenticationHeaderError = function (invalidHeaderName, details) {
    return new ClientConfigurationError(ClientConfigurationErrorMessage.invalidAuthenticationHeader.code, ClientConfigurationErrorMessage.invalidAuthenticationHeader.desc + ". Invalid header: " + invalidHeaderName + ". Details: " + details);
  };

  return ClientConfigurationError;
}(ClientAuthError);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * The ScopeSet class creates a set of scopes. Scopes are case-insensitive, unique values, so the Set object in JS makes
 * the most sense to implement for this class. All scopes are trimmed and converted to lower case strings in intersection and union functions
 * to ensure uniqueness of strings.
 */

var ScopeSet =
/** @class */
function () {
  function ScopeSet(inputScopes) {
    var _this = this; // Filter empty string and null/undefined array items


    var scopeArr = inputScopes ? StringUtils.trimArrayEntries(__spreadArrays(inputScopes)) : [];
    var filteredInput = scopeArr ? StringUtils.removeEmptyStringsFromArray(scopeArr) : []; // Validate and filter scopes (validate function throws if validation fails)

    this.validateInputScopes(filteredInput);
    this.scopes = new Set(); // Iterator in constructor not supported by IE11

    filteredInput.forEach(function (scope) {
      return _this.scopes.add(scope);
    });
  }
  /**
   * Factory method to create ScopeSet from space-delimited string
   * @param inputScopeString
   * @param appClientId
   * @param scopesRequired
   */


  ScopeSet.fromString = function (inputScopeString) {
    var scopeString = inputScopeString || Constants.EMPTY_STRING;
    var inputScopes = scopeString.split(" ");
    return new ScopeSet(inputScopes);
  };
  /**
   * Used to validate the scopes input parameter requested  by the developer.
   * @param {Array<string>} inputScopes - Developer requested permissions. Not all scopes are guaranteed to be included in the access token returned.
   * @param {boolean} scopesRequired - Boolean indicating whether the scopes array is required or not
   */


  ScopeSet.prototype.validateInputScopes = function (inputScopes) {
    // Check if scopes are required but not given or is an empty array
    if (!inputScopes || inputScopes.length < 1) {
      throw ClientConfigurationError.createEmptyScopesArrayError();
    }
  };
  /**
   * Check if a given scope is present in this set of scopes.
   * @param scope
   */


  ScopeSet.prototype.containsScope = function (scope) {
    var lowerCaseScopes = this.printScopesLowerCase().split(" ");
    var lowerCaseScopesSet = new ScopeSet(lowerCaseScopes); // compare lowercase scopes

    return !StringUtils.isEmpty(scope) ? lowerCaseScopesSet.scopes.has(scope.toLowerCase()) : false;
  };
  /**
   * Check if a set of scopes is present in this set of scopes.
   * @param scopeSet
   */


  ScopeSet.prototype.containsScopeSet = function (scopeSet) {
    var _this = this;

    if (!scopeSet || scopeSet.scopes.size <= 0) {
      return false;
    }

    return this.scopes.size >= scopeSet.scopes.size && scopeSet.asArray().every(function (scope) {
      return _this.containsScope(scope);
    });
  };
  /**
   * Check if set of scopes contains only the defaults
   */


  ScopeSet.prototype.containsOnlyOIDCScopes = function () {
    var _this = this;

    var defaultScopeCount = 0;
    OIDC_SCOPES.forEach(function (defaultScope) {
      if (_this.containsScope(defaultScope)) {
        defaultScopeCount += 1;
      }
    });
    return this.scopes.size === defaultScopeCount;
  };
  /**
   * Appends single scope if passed
   * @param newScope
   */


  ScopeSet.prototype.appendScope = function (newScope) {
    if (!StringUtils.isEmpty(newScope)) {
      this.scopes.add(newScope.trim());
    }
  };
  /**
   * Appends multiple scopes if passed
   * @param newScopes
   */


  ScopeSet.prototype.appendScopes = function (newScopes) {
    var _this = this;

    try {
      newScopes.forEach(function (newScope) {
        return _this.appendScope(newScope);
      });
    } catch (e) {
      throw ClientAuthError.createAppendScopeSetError(e);
    }
  };
  /**
   * Removes element from set of scopes.
   * @param scope
   */


  ScopeSet.prototype.removeScope = function (scope) {
    if (StringUtils.isEmpty(scope)) {
      throw ClientAuthError.createRemoveEmptyScopeFromSetError(scope);
    }

    this.scopes.delete(scope.trim());
  };
  /**
   * Removes default scopes from set of scopes
   * Primarily used to prevent cache misses if the default scopes are not returned from the server
   */


  ScopeSet.prototype.removeOIDCScopes = function () {
    var _this = this;

    OIDC_SCOPES.forEach(function (defaultScope) {
      _this.scopes.delete(defaultScope);
    });
  };
  /**
   * Combines an array of scopes with the current set of scopes.
   * @param otherScopes
   */


  ScopeSet.prototype.unionScopeSets = function (otherScopes) {
    if (!otherScopes) {
      throw ClientAuthError.createEmptyInputScopeSetError();
    }

    var unionScopes = new Set(); // Iterator in constructor not supported in IE11

    otherScopes.scopes.forEach(function (scope) {
      return unionScopes.add(scope.toLowerCase());
    });
    this.scopes.forEach(function (scope) {
      return unionScopes.add(scope.toLowerCase());
    });
    return unionScopes;
  };
  /**
   * Check if scopes intersect between this set and another.
   * @param otherScopes
   */


  ScopeSet.prototype.intersectingScopeSets = function (otherScopes) {
    if (!otherScopes) {
      throw ClientAuthError.createEmptyInputScopeSetError();
    } // Do not allow OIDC scopes to be the only intersecting scopes


    if (!otherScopes.containsOnlyOIDCScopes()) {
      otherScopes.removeOIDCScopes();
    }

    var unionScopes = this.unionScopeSets(otherScopes);
    var sizeOtherScopes = otherScopes.getScopeCount();
    var sizeThisScopes = this.getScopeCount();
    var sizeUnionScopes = unionScopes.size;
    return sizeUnionScopes < sizeThisScopes + sizeOtherScopes;
  };
  /**
   * Returns size of set of scopes.
   */


  ScopeSet.prototype.getScopeCount = function () {
    return this.scopes.size;
  };
  /**
   * Returns the scopes as an array of string values
   */


  ScopeSet.prototype.asArray = function () {
    var array = [];
    this.scopes.forEach(function (val) {
      return array.push(val);
    });
    return array;
  };
  /**
   * Prints scopes into a space-delimited string
   */


  ScopeSet.prototype.printScopes = function () {
    if (this.scopes) {
      var scopeArr = this.asArray();
      return scopeArr.join(" ");
    }

    return Constants.EMPTY_STRING;
  };
  /**
   * Prints scopes into a space-delimited lower-case string (used for caching)
   */


  ScopeSet.prototype.printScopesLowerCase = function () {
    return this.printScopes().toLowerCase();
  };

  return ScopeSet;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Function to build a client info object from server clientInfo string
 * @param rawClientInfo
 * @param crypto
 */

function buildClientInfo(rawClientInfo, crypto) {
  if (StringUtils.isEmpty(rawClientInfo)) {
    throw ClientAuthError.createClientInfoEmptyError();
  }

  try {
    var decodedClientInfo = crypto.base64Decode(rawClientInfo);
    return JSON.parse(decodedClientInfo);
  } catch (e) {
    throw ClientAuthError.createClientInfoDecodingError(e.message);
  }
}
/**
 * Function to build a client info object from cached homeAccountId string
 * @param homeAccountId
 */


function buildClientInfoFromHomeAccountId(homeAccountId) {
  if (StringUtils.isEmpty(homeAccountId)) {
    throw ClientAuthError.createClientInfoDecodingError("Home account ID was empty.");
  }

  var clientInfoParts = homeAccountId.split(Separators.CLIENT_INFO_SEPARATOR, 2);
  return {
    uid: clientInfoParts[0],
    utid: clientInfoParts.length < 2 ? Constants.EMPTY_STRING : clientInfoParts[1]
  };
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Authority types supported by MSAL.
 */

var AuthorityType;

(function (AuthorityType) {
  AuthorityType[AuthorityType["Default"] = 0] = "Default";
  AuthorityType[AuthorityType["Adfs"] = 1] = "Adfs";
  AuthorityType[AuthorityType["Dsts"] = 2] = "Dsts";
})(AuthorityType || (AuthorityType = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Type that defines required and optional parameters for an Account field (based on universal cache schema implemented by all MSALs).
 *
 * Key : Value Schema
 *
 * Key: <home_account_id>-<environment>-<realm*>
 *
 * Value Schema:
 * {
 *      homeAccountId: home account identifier for the auth scheme,
 *      environment: entity that issued the token, represented as a full host
 *      realm: Full tenant or organizational identifier that the account belongs to
 *      localAccountId: Original tenant-specific accountID, usually used for legacy cases
 *      username: primary username that represents the user, usually corresponds to preferred_username in the v2 endpt
 *      authorityType: Accounts authority type as a string
 *      name: Full name for the account, including given name and family name,
 *      clientInfo: Full base64 encoded client info received from ESTS
 *      lastModificationTime: last time this entity was modified in the cache
 *      lastModificationApp:
 *      idTokenClaims: Object containing claims parsed from ID token
 *      nativeAccountId: Account identifier on the native device
 * }
 */

var AccountEntity =
/** @class */
function () {
  function AccountEntity() {}
  /**
   * Generate Account Id key component as per the schema: <home_account_id>-<environment>
   */


  AccountEntity.prototype.generateAccountId = function () {
    var accountId = [this.homeAccountId, this.environment];
    return accountId.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * Generate Account Cache Key as per the schema: <home_account_id>-<environment>-<realm*>
   */


  AccountEntity.prototype.generateAccountKey = function () {
    return AccountEntity.generateAccountCacheKey({
      homeAccountId: this.homeAccountId,
      environment: this.environment,
      tenantId: this.realm,
      username: this.username,
      localAccountId: this.localAccountId
    });
  };
  /**
   * returns the type of the cache (in this case account)
   */


  AccountEntity.prototype.generateType = function () {
    switch (this.authorityType) {
      case CacheAccountType.ADFS_ACCOUNT_TYPE:
        return CacheType.ADFS;

      case CacheAccountType.MSAV1_ACCOUNT_TYPE:
        return CacheType.MSA;

      case CacheAccountType.MSSTS_ACCOUNT_TYPE:
        return CacheType.MSSTS;

      case CacheAccountType.GENERIC_ACCOUNT_TYPE:
        return CacheType.GENERIC;

      default:
        {
          throw ClientAuthError.createUnexpectedAccountTypeError();
        }
    }
  };
  /**
   * Returns the AccountInfo interface for this account.
   */


  AccountEntity.prototype.getAccountInfo = function () {
    return {
      homeAccountId: this.homeAccountId,
      environment: this.environment,
      tenantId: this.realm,
      username: this.username,
      localAccountId: this.localAccountId,
      name: this.name,
      idTokenClaims: this.idTokenClaims,
      nativeAccountId: this.nativeAccountId
    };
  };
  /**
   * Generates account key from interface
   * @param accountInterface
   */


  AccountEntity.generateAccountCacheKey = function (accountInterface) {
    var accountKey = [accountInterface.homeAccountId, accountInterface.environment || Constants.EMPTY_STRING, accountInterface.tenantId || Constants.EMPTY_STRING];
    return accountKey.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * Build Account cache from IdToken, clientInfo and authority/policy. Associated with AAD.
   * @param clientInfo
   * @param authority
   * @param idToken
   * @param policy
   */


  AccountEntity.createAccount = function (clientInfo, homeAccountId, idToken, authority, cloudGraphHostName, msGraphHost, environment, nativeAccountId) {
    var _a, _b, _c, _d, _e, _f;

    var account = new AccountEntity();
    account.authorityType = CacheAccountType.MSSTS_ACCOUNT_TYPE;
    account.clientInfo = clientInfo;
    account.homeAccountId = homeAccountId;
    account.nativeAccountId = nativeAccountId;
    var env = environment || authority && authority.getPreferredCache();

    if (!env) {
      throw ClientAuthError.createInvalidCacheEnvironmentError();
    }

    account.environment = env; // non AAD scenarios can have empty realm

    account.realm = ((_a = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _a === void 0 ? void 0 : _a.tid) || Constants.EMPTY_STRING;

    if (idToken) {
      account.idTokenClaims = idToken.claims; // How do you account for MSA CID here?

      account.localAccountId = ((_b = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _b === void 0 ? void 0 : _b.oid) || ((_c = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _c === void 0 ? void 0 : _c.sub) || Constants.EMPTY_STRING;
      /*
       * In B2C scenarios the emails claim is used instead of preferred_username and it is an array.
       * In most cases it will contain a single email. This field should not be relied upon if a custom
       * policy is configured to return more than 1 email.
       */

      var preferredUsername = (_d = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _d === void 0 ? void 0 : _d.preferred_username;
      var email = ((_e = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _e === void 0 ? void 0 : _e.emails) ? idToken.claims.emails[0] : null;
      account.username = preferredUsername || email || Constants.EMPTY_STRING;
      account.name = (_f = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _f === void 0 ? void 0 : _f.name;
    }

    account.cloudGraphHostName = cloudGraphHostName;
    account.msGraphHost = msGraphHost;
    return account;
  };
  /**
   * Builds non-AAD/ADFS account.
   * @param authority
   * @param idToken
   */


  AccountEntity.createGenericAccount = function (homeAccountId, idToken, authority, cloudGraphHostName, msGraphHost, environment) {
    var _a, _b, _c, _d;

    var account = new AccountEntity();
    account.authorityType = authority && authority.authorityType === AuthorityType.Adfs ? CacheAccountType.ADFS_ACCOUNT_TYPE : CacheAccountType.GENERIC_ACCOUNT_TYPE;
    account.homeAccountId = homeAccountId; // non AAD scenarios can have empty realm

    account.realm = Constants.EMPTY_STRING;
    var env = environment || authority && authority.getPreferredCache();

    if (!env) {
      throw ClientAuthError.createInvalidCacheEnvironmentError();
    }

    if (idToken) {
      // How do you account for MSA CID here?
      account.localAccountId = ((_a = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _a === void 0 ? void 0 : _a.oid) || ((_b = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _b === void 0 ? void 0 : _b.sub) || Constants.EMPTY_STRING; // upn claim for most ADFS scenarios

      account.username = ((_c = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _c === void 0 ? void 0 : _c.upn) || Constants.EMPTY_STRING;
      account.name = ((_d = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _d === void 0 ? void 0 : _d.name) || Constants.EMPTY_STRING;
      account.idTokenClaims = idToken === null || idToken === void 0 ? void 0 : idToken.claims;
    }

    account.environment = env;
    account.cloudGraphHostName = cloudGraphHostName;
    account.msGraphHost = msGraphHost;
    /*
     * add uniqueName to claims
     * account.name = idToken.claims.uniqueName;
     */

    return account;
  };
  /**
   * Generate HomeAccountId from server response
   * @param serverClientInfo
   * @param authType
   */


  AccountEntity.generateHomeAccountId = function (serverClientInfo, authType, logger, cryptoObj, idToken) {
    var _a;

    var accountId = ((_a = idToken === null || idToken === void 0 ? void 0 : idToken.claims) === null || _a === void 0 ? void 0 : _a.sub) ? idToken.claims.sub : Constants.EMPTY_STRING; // since ADFS does not have tid and does not set client_info

    if (authType === AuthorityType.Adfs || authType === AuthorityType.Dsts) {
      return accountId;
    } // for cases where there is clientInfo


    if (serverClientInfo) {
      try {
        var clientInfo = buildClientInfo(serverClientInfo, cryptoObj);

        if (!StringUtils.isEmpty(clientInfo.uid) && !StringUtils.isEmpty(clientInfo.utid)) {
          return "" + clientInfo.uid + Separators.CLIENT_INFO_SEPARATOR + clientInfo.utid;
        }
      } catch (e) {}
    } // default to "sub" claim


    logger.verbose("No client info in response");
    return accountId;
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  AccountEntity.isAccountEntity = function (entity) {
    if (!entity) {
      return false;
    }

    return entity.hasOwnProperty("homeAccountId") && entity.hasOwnProperty("environment") && entity.hasOwnProperty("realm") && entity.hasOwnProperty("localAccountId") && entity.hasOwnProperty("username") && entity.hasOwnProperty("authorityType");
  };
  /**
   * Helper function to determine whether 2 accountInfo objects represent the same account
   * @param accountA
   * @param accountB
   * @param compareClaims - If set to true idTokenClaims will also be compared to determine account equality
   */


  AccountEntity.accountInfoIsEqual = function (accountA, accountB, compareClaims) {
    if (!accountA || !accountB) {
      return false;
    }

    var claimsMatch = true; // default to true so as to not fail comparison below if compareClaims: false

    if (compareClaims) {
      var accountAClaims = accountA.idTokenClaims || {};
      var accountBClaims = accountB.idTokenClaims || {}; // issued at timestamp and nonce are expected to change each time a new id token is acquired

      claimsMatch = accountAClaims.iat === accountBClaims.iat && accountAClaims.nonce === accountBClaims.nonce;
    }

    return accountA.homeAccountId === accountB.homeAccountId && accountA.localAccountId === accountB.localAccountId && accountA.username === accountB.username && accountA.tenantId === accountB.tenantId && accountA.environment === accountB.environment && accountA.nativeAccountId === accountB.nativeAccountId && claimsMatch;
  };

  return AccountEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * JWT Token representation class. Parses token string and generates claims object.
 */

var AuthToken =
/** @class */
function () {
  function AuthToken(rawToken, crypto) {
    if (StringUtils.isEmpty(rawToken)) {
      throw ClientAuthError.createTokenNullOrEmptyError(rawToken);
    }

    this.rawToken = rawToken;
    this.claims = AuthToken.extractTokenClaims(rawToken, crypto);
  }
  /**
   * Extract token by decoding the rawToken
   *
   * @param encodedToken
   */


  AuthToken.extractTokenClaims = function (encodedToken, crypto) {
    var decodedToken = StringUtils.decodeAuthToken(encodedToken); // token will be decoded to get the username

    try {
      var base64TokenPayload = decodedToken.JWSPayload; // base64Decode() should throw an error if there is an issue

      var base64Decoded = crypto.base64Decode(base64TokenPayload);
      return JSON.parse(base64Decoded);
    } catch (err) {
      throw ClientAuthError.createTokenParsingError(err);
    }
  };
  /**
   * Determine if the token's max_age has transpired
   */


  AuthToken.checkMaxAge = function (authTime, maxAge) {
    /*
     * per https://openid.net/specs/openid-connect-core-1_0.html#AuthRequest
     * To force an immediate re-authentication: If an app requires that a user re-authenticate prior to access,
     * provide a value of 0 for the max_age parameter and the AS will force a fresh login.
     */
    var fiveMinuteSkew = 300000; // five minutes in milliseconds

    if (maxAge === 0 || Date.now() - fiveMinuteSkew > authTime + maxAge) {
      throw ClientAuthError.createMaxAgeTranspiredError();
    }
  };

  return AuthToken;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Interface class which implement cache storage functions used by MSAL to perform validity checks, and store tokens.
 */

var CacheManager =
/** @class */
function () {
  function CacheManager(clientId, cryptoImpl) {
    this.clientId = clientId;
    this.cryptoImpl = cryptoImpl;
  }
  /**
   * Returns all accounts in cache
   */


  CacheManager.prototype.getAllAccounts = function () {
    var _this = this;

    var currentAccounts = this.getAccountsFilteredBy();
    var accountValues = Object.keys(currentAccounts).map(function (accountKey) {
      return currentAccounts[accountKey];
    });
    var numAccounts = accountValues.length;

    if (numAccounts < 1) {
      return [];
    } else {
      var allAccounts = accountValues.map(function (value) {
        var accountEntity = CacheManager.toObject(new AccountEntity(), value);
        var accountInfo = accountEntity.getAccountInfo();

        var idToken = _this.readIdTokenFromCache(_this.clientId, accountInfo);

        if (idToken && !accountInfo.idTokenClaims) {
          accountInfo.idToken = idToken.secret;
          accountInfo.idTokenClaims = new AuthToken(idToken.secret, _this.cryptoImpl).claims;
        }

        return accountInfo;
      });
      return allAccounts;
    }
  };
  /**
   * saves a cache record
   * @param cacheRecord
   */


  CacheManager.prototype.saveCacheRecord = function (cacheRecord) {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!cacheRecord) {
              throw ClientAuthError.createNullOrUndefinedCacheRecord();
            }

            if (!!cacheRecord.account) {
              this.setAccount(cacheRecord.account);
            }

            if (!!cacheRecord.idToken) {
              this.setIdTokenCredential(cacheRecord.idToken);
            }

            if (!!!cacheRecord.accessToken) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , this.saveAccessToken(cacheRecord.accessToken)];

          case 1:
            _a.sent();

            _a.label = 2;

          case 2:
            if (!!cacheRecord.refreshToken) {
              this.setRefreshTokenCredential(cacheRecord.refreshToken);
            }

            if (!!cacheRecord.appMetadata) {
              this.setAppMetadata(cacheRecord.appMetadata);
            }

            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * saves access token credential
   * @param credential
   */


  CacheManager.prototype.saveAccessToken = function (credential) {
    return __awaiter(this, void 0, void 0, function () {
      var currentTokenCache, currentScopes, currentAccessTokens, removedAccessTokens_1;

      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            currentTokenCache = this.getCredentialsFilteredBy({
              clientId: credential.clientId,
              credentialType: credential.credentialType,
              environment: credential.environment,
              homeAccountId: credential.homeAccountId,
              realm: credential.realm,
              tokenType: credential.tokenType,
              requestedClaimsHash: credential.requestedClaimsHash
            });
            currentScopes = ScopeSet.fromString(credential.target);
            currentAccessTokens = Object.keys(currentTokenCache.accessTokens).map(function (key) {
              return currentTokenCache.accessTokens[key];
            });
            if (!currentAccessTokens) return [3
            /*break*/
            , 2];
            removedAccessTokens_1 = [];
            currentAccessTokens.forEach(function (tokenEntity) {
              var tokenScopeSet = ScopeSet.fromString(tokenEntity.target);

              if (tokenScopeSet.intersectingScopeSets(currentScopes)) {
                removedAccessTokens_1.push(_this.removeCredential(tokenEntity));
              }
            });
            return [4
            /*yield*/
            , Promise.all(removedAccessTokens_1)];

          case 1:
            _a.sent();

            _a.label = 2;

          case 2:
            this.setAccessTokenCredential(credential);
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * retrieve accounts matching all provided filters; if no filter is set, get all accounts
   * not checking for casing as keys are all generated in lower case, remember to convert to lower case if object properties are compared
   * @param homeAccountId
   * @param environment
   * @param realm
   */


  CacheManager.prototype.getAccountsFilteredBy = function (accountFilter) {
    return this.getAccountsFilteredByInternal(accountFilter ? accountFilter.homeAccountId : Constants.EMPTY_STRING, accountFilter ? accountFilter.environment : Constants.EMPTY_STRING, accountFilter ? accountFilter.realm : Constants.EMPTY_STRING, accountFilter ? accountFilter.nativeAccountId : Constants.EMPTY_STRING);
  };
  /**
   * retrieve accounts matching all provided filters; if no filter is set, get all accounts
   * not checking for casing as keys are all generated in lower case, remember to convert to lower case if object properties are compared
   * @param homeAccountId
   * @param environment
   * @param realm
   */


  CacheManager.prototype.getAccountsFilteredByInternal = function (homeAccountId, environment, realm, nativeAccountId) {
    var _this = this;

    var allCacheKeys = this.getKeys();
    var matchingAccounts = {};
    allCacheKeys.forEach(function (cacheKey) {
      var entity = _this.getAccount(cacheKey);

      if (!entity) {
        return;
      }

      if (!!homeAccountId && !_this.matchHomeAccountId(entity, homeAccountId)) {
        return;
      }

      if (!!environment && !_this.matchEnvironment(entity, environment)) {
        return;
      }

      if (!!realm && !_this.matchRealm(entity, realm)) {
        return;
      }

      if (!!nativeAccountId && !_this.matchNativeAccountId(entity, nativeAccountId)) {
        return;
      }

      matchingAccounts[cacheKey] = entity;
    });
    return matchingAccounts;
  };
  /**
   * retrieve credentails matching all provided filters; if no filter is set, get all credentials
   * @param homeAccountId
   * @param environment
   * @param credentialType
   * @param clientId
   * @param realm
   * @param target
   */


  CacheManager.prototype.getCredentialsFilteredBy = function (filter) {
    return this.getCredentialsFilteredByInternal(filter.homeAccountId, filter.environment, filter.credentialType, filter.clientId, filter.familyId, filter.realm, filter.target, filter.userAssertionHash, filter.tokenType, filter.keyId, filter.requestedClaimsHash);
  };
  /**
   * Support function to help match credentials
   * @param homeAccountId
   * @param environment
   * @param credentialType
   * @param clientId
   * @param realm
   * @param target
   * @param userAssertionHash
   * @param tokenType
   */


  CacheManager.prototype.getCredentialsFilteredByInternal = function (homeAccountId, environment, credentialType, clientId, familyId, realm, target, userAssertionHash, tokenType, keyId, requestedClaimsHash) {
    var _this = this;

    var allCacheKeys = this.getKeys();
    var matchingCredentials = {
      idTokens: {},
      accessTokens: {},
      refreshTokens: {}
    };
    allCacheKeys.forEach(function (cacheKey) {
      // don't parse any non-credential type cache entities
      var credType = CredentialEntity.getCredentialType(cacheKey);

      if (credType === Constants.NOT_DEFINED) {
        return;
      } // Attempt retrieval


      var entity = _this.getSpecificCredential(cacheKey, credType);

      if (!entity) {
        return;
      }

      if (!!userAssertionHash && !_this.matchUserAssertionHash(entity, userAssertionHash)) {
        return;
      }
      /*
       * homeAccountId can undefined, and we want to filter out cached items that have a homeAccountId of ""
       * because we don't want a client_credential request to return a cached token that has a homeAccountId
       */


      if (typeof homeAccountId === "string" && !_this.matchHomeAccountId(entity, homeAccountId)) {
        return;
      }

      if (!!environment && !_this.matchEnvironment(entity, environment)) {
        return;
      }

      if (!!realm && !_this.matchRealm(entity, realm)) {
        return;
      }

      if (!!credentialType && !_this.matchCredentialType(entity, credentialType)) {
        return;
      }

      if (!!clientId && !_this.matchClientId(entity, clientId)) {
        return;
      }

      if (!!familyId && !_this.matchFamilyId(entity, familyId)) {
        return;
      }
      /*
       * idTokens do not have "target", target specific refreshTokens do exist for some types of authentication
       * Resource specific refresh tokens case will be added when the support is deemed necessary
       */


      if (!!target && !_this.matchTarget(entity, target)) {
        return;
      } // If request OR cached entity has requested Claims Hash, check if they match


      if (requestedClaimsHash || entity.requestedClaimsHash) {
        // Don't match if either is undefined or they are different
        if (entity.requestedClaimsHash !== requestedClaimsHash) {
          return;
        }
      } // Access Token with Auth Scheme specific matching


      if (credentialType === CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME) {
        if (!!tokenType && !_this.matchTokenType(entity, tokenType)) {
          return;
        } // KeyId (sshKid) in request must match cached SSH certificate keyId because SSH cert is bound to a specific key


        if (tokenType === AuthenticationScheme.SSH) {
          if (keyId && !_this.matchKeyId(entity, keyId)) {
            return;
          }
        }
      } // At this point, the entity matches the request, update cache key if key schema has changed


      var updatedCacheKey = _this.updateCredentialCacheKey(cacheKey, entity);

      switch (credType) {
        case CredentialType.ID_TOKEN:
          matchingCredentials.idTokens[updatedCacheKey] = entity;
          break;

        case CredentialType.ACCESS_TOKEN:
        case CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME:
          matchingCredentials.accessTokens[updatedCacheKey] = entity;
          break;

        case CredentialType.REFRESH_TOKEN:
          matchingCredentials.refreshTokens[updatedCacheKey] = entity;
          break;
      }
    });
    return matchingCredentials;
  };
  /**
   * retrieve appMetadata matching all provided filters; if no filter is set, get all appMetadata
   * @param filter
   */


  CacheManager.prototype.getAppMetadataFilteredBy = function (filter) {
    return this.getAppMetadataFilteredByInternal(filter.environment, filter.clientId);
  };
  /**
   * Support function to help match appMetadata
   * @param environment
   * @param clientId
   */


  CacheManager.prototype.getAppMetadataFilteredByInternal = function (environment, clientId) {
    var _this = this;

    var allCacheKeys = this.getKeys();
    var matchingAppMetadata = {};
    allCacheKeys.forEach(function (cacheKey) {
      // don't parse any non-appMetadata type cache entities
      if (!_this.isAppMetadata(cacheKey)) {
        return;
      } // Attempt retrieval


      var entity = _this.getAppMetadata(cacheKey);

      if (!entity) {
        return;
      }

      if (!!environment && !_this.matchEnvironment(entity, environment)) {
        return;
      }

      if (!!clientId && !_this.matchClientId(entity, clientId)) {
        return;
      }

      matchingAppMetadata[cacheKey] = entity;
    });
    return matchingAppMetadata;
  };
  /**
   * retrieve authorityMetadata that contains a matching alias
   * @param filter
   */


  CacheManager.prototype.getAuthorityMetadataByAlias = function (host) {
    var _this = this;

    var allCacheKeys = this.getAuthorityMetadataKeys();
    var matchedEntity = null;
    allCacheKeys.forEach(function (cacheKey) {
      // don't parse any non-authorityMetadata type cache entities
      if (!_this.isAuthorityMetadata(cacheKey) || cacheKey.indexOf(_this.clientId) === -1) {
        return;
      } // Attempt retrieval


      var entity = _this.getAuthorityMetadata(cacheKey);

      if (!entity) {
        return;
      }

      if (entity.aliases.indexOf(host) === -1) {
        return;
      }

      matchedEntity = entity;
    });
    return matchedEntity;
  };
  /**
   * Removes all accounts and related tokens from cache.
   */


  CacheManager.prototype.removeAllAccounts = function () {
    return __awaiter(this, void 0, void 0, function () {
      var allCacheKeys, removedAccounts;

      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            allCacheKeys = this.getKeys();
            removedAccounts = [];
            allCacheKeys.forEach(function (cacheKey) {
              var entity = _this.getAccount(cacheKey);

              if (!entity) {
                return;
              }

              removedAccounts.push(_this.removeAccount(cacheKey));
            });
            return [4
            /*yield*/
            , Promise.all(removedAccounts)];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , true];
        }
      });
    });
  };
  /**
   * returns a boolean if the given account is removed
   * @param account
   */


  CacheManager.prototype.removeAccount = function (accountKey) {
    return __awaiter(this, void 0, void 0, function () {
      var account;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            account = this.getAccount(accountKey);

            if (!account) {
              throw ClientAuthError.createNoAccountFoundError();
            }

            return [4
            /*yield*/
            , this.removeAccountContext(account)];

          case 1:
            return [2
            /*return*/
            , _a.sent() && this.removeItem(accountKey, CacheSchemaType.ACCOUNT)];
        }
      });
    });
  };
  /**
   * Removes credentials associated with the provided account
   * @param account
   */


  CacheManager.prototype.removeAccountContext = function (account) {
    return __awaiter(this, void 0, void 0, function () {
      var allCacheKeys, accountId, removedCredentials;

      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            allCacheKeys = this.getKeys();
            accountId = account.generateAccountId();
            removedCredentials = [];
            allCacheKeys.forEach(function (cacheKey) {
              // don't parse any non-credential type cache entities
              var credType = CredentialEntity.getCredentialType(cacheKey);

              if (credType === Constants.NOT_DEFINED) {
                return;
              }

              var cacheEntity = _this.getSpecificCredential(cacheKey, credType);

              if (!!cacheEntity && accountId === cacheEntity.generateAccountId()) {
                removedCredentials.push(_this.removeCredential(cacheEntity));
              }
            });
            return [4
            /*yield*/
            , Promise.all(removedCredentials)];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , true];
        }
      });
    });
  };
  /**
   * returns a boolean if the given credential is removed
   * @param credential
   */


  CacheManager.prototype.removeCredential = function (credential) {
    return __awaiter(this, void 0, void 0, function () {
      var key, accessTokenWithAuthSchemeEntity, kid;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            key = credential.generateCredentialKey();
            if (!(credential.credentialType.toLowerCase() === CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME.toLowerCase())) return [3
            /*break*/
            , 4];
            if (!(credential.tokenType === AuthenticationScheme.POP)) return [3
            /*break*/
            , 4];
            accessTokenWithAuthSchemeEntity = credential;
            kid = accessTokenWithAuthSchemeEntity.keyId;
            if (!kid) return [3
            /*break*/
            , 4];
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.cryptoImpl.removeTokenBindingKey(kid)];

          case 2:
            _a.sent();

            return [3
            /*break*/
            , 4];

          case 3:
            _a.sent();

            throw ClientAuthError.createBindingKeyNotRemovedError();

          case 4:
            return [2
            /*return*/
            , this.removeItem(key, CacheSchemaType.CREDENTIAL)];
        }
      });
    });
  };
  /**
   * Removes all app metadata objects from cache.
   */


  CacheManager.prototype.removeAppMetadata = function () {
    var _this = this;

    var allCacheKeys = this.getKeys();
    allCacheKeys.forEach(function (cacheKey) {
      if (_this.isAppMetadata(cacheKey)) {
        _this.removeItem(cacheKey, CacheSchemaType.APP_METADATA);
      }
    });
    return true;
  };
  /**
   * Retrieve the cached credentials into a cacherecord
   * @param account
   * @param clientId
   * @param scopes
   * @param environment
   * @param authScheme
   */


  CacheManager.prototype.readCacheRecord = function (account, clientId, request, environment) {
    var cachedAccount = this.readAccountFromCache(account);
    var cachedIdToken = this.readIdTokenFromCache(clientId, account);
    var cachedAccessToken = this.readAccessTokenFromCache(clientId, account, request);
    var cachedRefreshToken = this.readRefreshTokenFromCache(clientId, account, false);
    var cachedAppMetadata = this.readAppMetadataFromCache(environment, clientId);

    if (cachedAccount && cachedIdToken) {
      cachedAccount.idTokenClaims = new AuthToken(cachedIdToken.secret, this.cryptoImpl).claims;
    }

    return {
      account: cachedAccount,
      idToken: cachedIdToken,
      accessToken: cachedAccessToken,
      refreshToken: cachedRefreshToken,
      appMetadata: cachedAppMetadata
    };
  };
  /**
   * Retrieve AccountEntity from cache
   * @param account
   */


  CacheManager.prototype.readAccountFromCache = function (account) {
    var accountKey = AccountEntity.generateAccountCacheKey(account);
    return this.getAccount(accountKey);
  };
  /**
   * Retrieve AccountEntity from cache
   * @param nativeAccountId
   * @returns AccountEntity or Null
   */


  CacheManager.prototype.readAccountFromCacheWithNativeAccountId = function (nativeAccountId) {
    // fetch account from memory
    var accountFilter = {
      nativeAccountId: nativeAccountId
    };
    var accountCache = this.getAccountsFilteredBy(accountFilter);
    var accounts = Object.keys(accountCache).map(function (key) {
      return accountCache[key];
    });

    if (accounts.length < 1) {
      return null;
    } else if (accounts.length > 1) {
      throw ClientAuthError.createMultipleMatchingAccountsInCacheError();
    }

    return accountCache[0];
  };
  /**
   * Retrieve IdTokenEntity from cache
   * @param clientId
   * @param account
   * @param inputRealm
   */


  CacheManager.prototype.readIdTokenFromCache = function (clientId, account) {
    var idTokenFilter = {
      homeAccountId: account.homeAccountId,
      environment: account.environment,
      credentialType: CredentialType.ID_TOKEN,
      clientId: clientId,
      realm: account.tenantId
    };
    var credentialCache = this.getCredentialsFilteredBy(idTokenFilter);
    var idTokens = Object.keys(credentialCache.idTokens).map(function (key) {
      return credentialCache.idTokens[key];
    });
    var numIdTokens = idTokens.length;

    if (numIdTokens < 1) {
      return null;
    } else if (numIdTokens > 1) {
      throw ClientAuthError.createMultipleMatchingTokensInCacheError();
    }

    return idTokens[0];
  };
  /**
   * Retrieve AccessTokenEntity from cache
   * @param clientId
   * @param account
   * @param scopes
   * @param authScheme
   */


  CacheManager.prototype.readAccessTokenFromCache = function (clientId, account, request) {
    var scopes = new ScopeSet(request.scopes || []);
    var authScheme = request.authenticationScheme || AuthenticationScheme.BEARER;
    /*
     * Distinguish between Bearer and PoP/SSH token cache types
     * Cast to lowercase to handle "bearer" from ADFS
     */

    var credentialType = authScheme && authScheme.toLowerCase() !== AuthenticationScheme.BEARER.toLowerCase() ? CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME : CredentialType.ACCESS_TOKEN;
    var accessTokenFilter = {
      homeAccountId: account.homeAccountId,
      environment: account.environment,
      credentialType: credentialType,
      clientId: clientId,
      realm: account.tenantId,
      target: scopes.printScopesLowerCase(),
      tokenType: authScheme,
      keyId: request.sshKid,
      requestedClaimsHash: request.requestedClaimsHash
    };
    var credentialCache = this.getCredentialsFilteredBy(accessTokenFilter);
    var accessTokens = Object.keys(credentialCache.accessTokens).map(function (key) {
      return credentialCache.accessTokens[key];
    });
    var numAccessTokens = accessTokens.length;

    if (numAccessTokens < 1) {
      return null;
    } else if (numAccessTokens > 1) {
      throw ClientAuthError.createMultipleMatchingTokensInCacheError();
    }

    return accessTokens[0];
  };
  /**
   * Helper to retrieve the appropriate refresh token from cache
   * @param clientId
   * @param account
   * @param familyRT
   */


  CacheManager.prototype.readRefreshTokenFromCache = function (clientId, account, familyRT) {
    var id = familyRT ? THE_FAMILY_ID : undefined;
    var refreshTokenFilter = {
      homeAccountId: account.homeAccountId,
      environment: account.environment,
      credentialType: CredentialType.REFRESH_TOKEN,
      clientId: clientId,
      familyId: id
    };
    var credentialCache = this.getCredentialsFilteredBy(refreshTokenFilter);
    var refreshTokens = Object.keys(credentialCache.refreshTokens).map(function (key) {
      return credentialCache.refreshTokens[key];
    });
    var numRefreshTokens = refreshTokens.length;

    if (numRefreshTokens < 1) {
      return null;
    } // address the else case after remove functions address environment aliases


    return refreshTokens[0];
  };
  /**
   * Retrieve AppMetadataEntity from cache
   */


  CacheManager.prototype.readAppMetadataFromCache = function (environment, clientId) {
    var appMetadataFilter = {
      environment: environment,
      clientId: clientId
    };
    var appMetadata = this.getAppMetadataFilteredBy(appMetadataFilter);
    var appMetadataEntries = Object.keys(appMetadata).map(function (key) {
      return appMetadata[key];
    });
    var numAppMetadata = appMetadataEntries.length;

    if (numAppMetadata < 1) {
      return null;
    } else if (numAppMetadata > 1) {
      throw ClientAuthError.createMultipleMatchingAppMetadataInCacheError();
    }

    return appMetadataEntries[0];
  };
  /**
   * Return the family_id value associated  with FOCI
   * @param environment
   * @param clientId
   */


  CacheManager.prototype.isAppMetadataFOCI = function (environment, clientId) {
    var appMetadata = this.readAppMetadataFromCache(environment, clientId);
    return !!(appMetadata && appMetadata.familyId === THE_FAMILY_ID);
  };
  /**
   * helper to match account ids
   * @param value
   * @param homeAccountId
   */


  CacheManager.prototype.matchHomeAccountId = function (entity, homeAccountId) {
    return !!(typeof entity.homeAccountId === "string" && homeAccountId === entity.homeAccountId);
  };
  /**
   * helper to match assertion
   * @param value
   * @param oboAssertion
   */


  CacheManager.prototype.matchUserAssertionHash = function (entity, userAssertionHash) {
    return !!(entity.userAssertionHash && userAssertionHash === entity.userAssertionHash);
  };
  /**
   * helper to match environment
   * @param value
   * @param environment
   */


  CacheManager.prototype.matchEnvironment = function (entity, environment) {
    var cloudMetadata = this.getAuthorityMetadataByAlias(environment);

    if (cloudMetadata && cloudMetadata.aliases.indexOf(entity.environment) > -1) {
      return true;
    }

    return false;
  };
  /**
   * helper to match credential type
   * @param entity
   * @param credentialType
   */


  CacheManager.prototype.matchCredentialType = function (entity, credentialType) {
    return entity.credentialType && credentialType.toLowerCase() === entity.credentialType.toLowerCase();
  };
  /**
   * helper to match client ids
   * @param entity
   * @param clientId
   */


  CacheManager.prototype.matchClientId = function (entity, clientId) {
    return !!(entity.clientId && clientId === entity.clientId);
  };
  /**
   * helper to match family ids
   * @param entity
   * @param familyId
   */


  CacheManager.prototype.matchFamilyId = function (entity, familyId) {
    return !!(entity.familyId && familyId === entity.familyId);
  };
  /**
   * helper to match realm
   * @param entity
   * @param realm
   */


  CacheManager.prototype.matchRealm = function (entity, realm) {
    return !!(entity.realm && realm === entity.realm);
  };
  /**
   * helper to match nativeAccountId
   * @param entity
   * @param nativeAccountId
   * @returns boolean indicating the match result
   */


  CacheManager.prototype.matchNativeAccountId = function (entity, nativeAccountId) {
    return !!(entity.nativeAccountId && nativeAccountId === entity.nativeAccountId);
  };
  /**
   * Returns true if the target scopes are a subset of the current entity's scopes, false otherwise.
   * @param entity
   * @param target
   */


  CacheManager.prototype.matchTarget = function (entity, target) {
    var isNotAccessTokenCredential = entity.credentialType !== CredentialType.ACCESS_TOKEN && entity.credentialType !== CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME;

    if (isNotAccessTokenCredential || !entity.target) {
      return false;
    }

    var entityScopeSet = ScopeSet.fromString(entity.target);
    var requestTargetScopeSet = ScopeSet.fromString(target);

    if (!requestTargetScopeSet.containsOnlyOIDCScopes()) {
      requestTargetScopeSet.removeOIDCScopes(); // ignore OIDC scopes
    } else {
      requestTargetScopeSet.removeScope(Constants.OFFLINE_ACCESS_SCOPE);
    }

    return entityScopeSet.containsScopeSet(requestTargetScopeSet);
  };
  /**
   * Returns true if the credential's tokenType or Authentication Scheme matches the one in the request, false otherwise
   * @param entity
   * @param tokenType
   */


  CacheManager.prototype.matchTokenType = function (entity, tokenType) {
    return !!(entity.tokenType && entity.tokenType === tokenType);
  };
  /**
   * Returns true if the credential's keyId matches the one in the request, false otherwise
   * @param entity
   * @param tokenType
   */


  CacheManager.prototype.matchKeyId = function (entity, keyId) {
    return !!(entity.keyId && entity.keyId === keyId);
  };
  /**
   * returns if a given cache entity is of the type appmetadata
   * @param key
   */


  CacheManager.prototype.isAppMetadata = function (key) {
    return key.indexOf(APP_METADATA) !== -1;
  };
  /**
   * returns if a given cache entity is of the type authoritymetadata
   * @param key
   */


  CacheManager.prototype.isAuthorityMetadata = function (key) {
    return key.indexOf(AUTHORITY_METADATA_CONSTANTS.CACHE_KEY) !== -1;
  };
  /**
   * returns cache key used for cloud instance metadata
   */


  CacheManager.prototype.generateAuthorityMetadataCacheKey = function (authority) {
    return AUTHORITY_METADATA_CONSTANTS.CACHE_KEY + "-" + this.clientId + "-" + authority;
  };
  /**
   * Returns the specific credential (IdToken/AccessToken/RefreshToken) from the cache
   * @param key
   * @param credType
   */


  CacheManager.prototype.getSpecificCredential = function (key, credType) {
    switch (credType) {
      case CredentialType.ID_TOKEN:
        {
          return this.getIdTokenCredential(key);
        }

      case CredentialType.ACCESS_TOKEN:
      case CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME:
        {
          return this.getAccessTokenCredential(key);
        }

      case CredentialType.REFRESH_TOKEN:
        {
          return this.getRefreshTokenCredential(key);
        }

      default:
        return null;
    }
  };
  /**
   * Helper to convert serialized data to object
   * @param obj
   * @param json
   */


  CacheManager.toObject = function (obj, json) {
    for (var propertyName in json) {
      obj[propertyName] = json[propertyName];
    }

    return obj;
  };

  return CacheManager;
}();

var DefaultStorageClass =
/** @class */
function (_super) {
  __extends(DefaultStorageClass, _super);

  function DefaultStorageClass() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  DefaultStorageClass.prototype.setAccount = function () {
    var notImplErr = "Storage interface - setAccount() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getAccount = function () {
    var notImplErr = "Storage interface - getAccount() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setIdTokenCredential = function () {
    var notImplErr = "Storage interface - setIdTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getIdTokenCredential = function () {
    var notImplErr = "Storage interface - getIdTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setAccessTokenCredential = function () {
    var notImplErr = "Storage interface - setAccessTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getAccessTokenCredential = function () {
    var notImplErr = "Storage interface - getAccessTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setRefreshTokenCredential = function () {
    var notImplErr = "Storage interface - setRefreshTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getRefreshTokenCredential = function () {
    var notImplErr = "Storage interface - getRefreshTokenCredential() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setAppMetadata = function () {
    var notImplErr = "Storage interface - setAppMetadata() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getAppMetadata = function () {
    var notImplErr = "Storage interface - getAppMetadata() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setServerTelemetry = function () {
    var notImplErr = "Storage interface - setServerTelemetry() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getServerTelemetry = function () {
    var notImplErr = "Storage interface - getServerTelemetry() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setAuthorityMetadata = function () {
    var notImplErr = "Storage interface - setAuthorityMetadata() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getAuthorityMetadata = function () {
    var notImplErr = "Storage interface - getAuthorityMetadata() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getAuthorityMetadataKeys = function () {
    var notImplErr = "Storage interface - getAuthorityMetadataKeys() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.setThrottlingCache = function () {
    var notImplErr = "Storage interface - setThrottlingCache() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getThrottlingCache = function () {
    var notImplErr = "Storage interface - getThrottlingCache() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.removeItem = function () {
    var notImplErr = "Storage interface - removeItem() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.containsKey = function () {
    var notImplErr = "Storage interface - containsKey() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.getKeys = function () {
    var notImplErr = "Storage interface - getKeys() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  DefaultStorageClass.prototype.clear = function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Storage interface - clear() has not been implemented for the cacheStorage interface.";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  };

  DefaultStorageClass.prototype.updateCredentialCacheKey = function () {
    var notImplErr = "Storage interface - updateCredentialCacheKey() has not been implemented for the cacheStorage interface.";
    throw AuthError.createUnexpectedError(notImplErr);
  };

  return DefaultStorageClass;
}(CacheManager);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
// Token renewal offset default in seconds

var DEFAULT_TOKEN_RENEWAL_OFFSET_SEC = 300;
var DEFAULT_SYSTEM_OPTIONS = {
  tokenRenewalOffsetSeconds: DEFAULT_TOKEN_RENEWAL_OFFSET_SEC,
  preventCorsPreflight: false,
  proxyUrl: Constants.EMPTY_STRING
};
var DEFAULT_LOGGER_IMPLEMENTATION = {
  loggerCallback: function () {// allow users to not set loggerCallback
  },
  piiLoggingEnabled: false,
  logLevel: LogLevel.Info,
  correlationId: Constants.EMPTY_STRING
};
var DEFAULT_NETWORK_IMPLEMENTATION = {
  sendGetRequestAsync: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Network interface - sendGetRequestAsync() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  },
  sendPostRequestAsync: function () {
    return __awaiter(this, void 0, void 0, function () {
      var notImplErr;
      return __generator(this, function (_a) {
        notImplErr = "Network interface - sendPostRequestAsync() has not been implemented";
        throw AuthError.createUnexpectedError(notImplErr);
      });
    });
  }
};
var DEFAULT_LIBRARY_INFO = {
  sku: Constants.SKU,
  version: version$1,
  cpu: Constants.EMPTY_STRING,
  os: Constants.EMPTY_STRING
};
var DEFAULT_CLIENT_CREDENTIALS = {
  clientSecret: Constants.EMPTY_STRING,
  clientAssertion: undefined
};
var DEFAULT_AZURE_CLOUD_OPTIONS = {
  azureCloudInstance: AzureCloudInstance.None,
  tenant: "" + Constants.DEFAULT_COMMON_TENANT
};
var DEFAULT_TELEMETRY_OPTIONS = {
  application: {
    appName: "",
    appVersion: ""
  }
};
/**
 * Function that sets the default options when not explicitly configured from app developer
 *
 * @param Configuration
 *
 * @returns Configuration
 */

function buildClientConfiguration(_a) {
  var userAuthOptions = _a.authOptions,
      userSystemOptions = _a.systemOptions,
      userLoggerOption = _a.loggerOptions,
      storageImplementation = _a.storageInterface,
      networkImplementation = _a.networkInterface,
      cryptoImplementation = _a.cryptoInterface,
      clientCredentials = _a.clientCredentials,
      libraryInfo = _a.libraryInfo,
      telemetry = _a.telemetry,
      serverTelemetryManager = _a.serverTelemetryManager,
      persistencePlugin = _a.persistencePlugin,
      serializableCache = _a.serializableCache;

  var loggerOptions = __assign(__assign({}, DEFAULT_LOGGER_IMPLEMENTATION), userLoggerOption);

  return {
    authOptions: buildAuthOptions(userAuthOptions),
    systemOptions: __assign(__assign({}, DEFAULT_SYSTEM_OPTIONS), userSystemOptions),
    loggerOptions: loggerOptions,
    storageInterface: storageImplementation || new DefaultStorageClass(userAuthOptions.clientId, DEFAULT_CRYPTO_IMPLEMENTATION),
    networkInterface: networkImplementation || DEFAULT_NETWORK_IMPLEMENTATION,
    cryptoInterface: cryptoImplementation || DEFAULT_CRYPTO_IMPLEMENTATION,
    clientCredentials: clientCredentials || DEFAULT_CLIENT_CREDENTIALS,
    libraryInfo: __assign(__assign({}, DEFAULT_LIBRARY_INFO), libraryInfo),
    telemetry: __assign(__assign({}, DEFAULT_TELEMETRY_OPTIONS), telemetry),
    serverTelemetryManager: serverTelemetryManager || null,
    persistencePlugin: persistencePlugin || null,
    serializableCache: serializableCache || null
  };
}
/**
 * Construct authoptions from the client and platform passed values
 * @param authOptions
 */


function buildAuthOptions(authOptions) {
  return __assign({
    clientCapabilities: [],
    azureCloudOptions: DEFAULT_AZURE_CLOUD_OPTIONS,
    skipAuthorityMetadataCache: false
  }, authOptions);
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Error thrown when there is an error with the server code, for example, unavailability.
 */

var ServerError =
/** @class */
function (_super) {
  __extends(ServerError, _super);

  function ServerError(errorCode, errorMessage, subError) {
    var _this = _super.call(this, errorCode, errorMessage, subError) || this;

    _this.name = "ServerError";
    Object.setPrototypeOf(_this, ServerError.prototype);
    return _this;
  }

  return ServerError;
}(AuthError);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var ThrottlingUtils =
/** @class */
function () {
  function ThrottlingUtils() {}
  /**
   * Prepares a RequestThumbprint to be stored as a key.
   * @param thumbprint
   */


  ThrottlingUtils.generateThrottlingStorageKey = function (thumbprint) {
    return ThrottlingConstants.THROTTLING_PREFIX + "." + JSON.stringify(thumbprint);
  };
  /**
   * Performs necessary throttling checks before a network request.
   * @param cacheManager
   * @param thumbprint
   */


  ThrottlingUtils.preProcess = function (cacheManager, thumbprint) {
    var _a;

    var key = ThrottlingUtils.generateThrottlingStorageKey(thumbprint);
    var value = cacheManager.getThrottlingCache(key);

    if (value) {
      if (value.throttleTime < Date.now()) {
        cacheManager.removeItem(key, CacheSchemaType.THROTTLING);
        return;
      }

      throw new ServerError(((_a = value.errorCodes) === null || _a === void 0 ? void 0 : _a.join(" ")) || Constants.EMPTY_STRING, value.errorMessage, value.subError);
    }
  };
  /**
   * Performs necessary throttling checks after a network request.
   * @param cacheManager
   * @param thumbprint
   * @param response
   */


  ThrottlingUtils.postProcess = function (cacheManager, thumbprint, response) {
    if (ThrottlingUtils.checkResponseStatus(response) || ThrottlingUtils.checkResponseForRetryAfter(response)) {
      var thumbprintValue = {
        throttleTime: ThrottlingUtils.calculateThrottleTime(parseInt(response.headers[HeaderNames.RETRY_AFTER])),
        error: response.body.error,
        errorCodes: response.body.error_codes,
        errorMessage: response.body.error_description,
        subError: response.body.suberror
      };
      cacheManager.setThrottlingCache(ThrottlingUtils.generateThrottlingStorageKey(thumbprint), thumbprintValue);
    }
  };
  /**
   * Checks a NetworkResponse object's status codes against 429 or 5xx
   * @param response
   */


  ThrottlingUtils.checkResponseStatus = function (response) {
    return response.status === 429 || response.status >= 500 && response.status < 600;
  };
  /**
   * Checks a NetworkResponse object's RetryAfter header
   * @param response
   */


  ThrottlingUtils.checkResponseForRetryAfter = function (response) {
    if (response.headers) {
      return response.headers.hasOwnProperty(HeaderNames.RETRY_AFTER) && (response.status < 200 || response.status >= 300);
    }

    return false;
  };
  /**
   * Calculates the Unix-time value for a throttle to expire given throttleTime in seconds.
   * @param throttleTime
   */


  ThrottlingUtils.calculateThrottleTime = function (throttleTime) {
    var time = throttleTime <= 0 ? 0 : throttleTime;
    var currentSeconds = Date.now() / 1000;
    return Math.floor(Math.min(currentSeconds + (time || ThrottlingConstants.DEFAULT_THROTTLE_TIME_SECONDS), currentSeconds + ThrottlingConstants.DEFAULT_MAX_THROTTLE_TIME_SECONDS) * 1000);
  };

  ThrottlingUtils.removeThrottle = function (cacheManager, clientId, request, homeAccountIdentifier) {
    var thumbprint = {
      clientId: clientId,
      authority: request.authority,
      scopes: request.scopes,
      homeAccountIdentifier: homeAccountIdentifier,
      claims: request.claims,
      authenticationScheme: request.authenticationScheme,
      resourceRequestMethod: request.resourceRequestMethod,
      resourceRequestUri: request.resourceRequestUri,
      shrClaims: request.shrClaims,
      sshKid: request.sshKid
    };
    var key = this.generateThrottlingStorageKey(thumbprint);
    return cacheManager.removeItem(key, CacheSchemaType.THROTTLING);
  };

  return ThrottlingUtils;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var NetworkManager =
/** @class */
function () {
  function NetworkManager(networkClient, cacheManager) {
    this.networkClient = networkClient;
    this.cacheManager = cacheManager;
  }
  /**
   * Wraps sendPostRequestAsync with necessary preflight and postflight logic
   * @param thumbprint
   * @param tokenEndpoint
   * @param options
   */


  NetworkManager.prototype.sendPostRequest = function (thumbprint, tokenEndpoint, options) {
    return __awaiter(this, void 0, void 0, function () {
      var response, e_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            ThrottlingUtils.preProcess(this.cacheManager, thumbprint);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.networkClient.sendPostRequestAsync(tokenEndpoint, options)];

          case 2:
            response = _a.sent();
            return [3
            /*break*/
            , 4];

          case 3:
            e_1 = _a.sent();

            if (e_1 instanceof AuthError) {
              throw e_1;
            } else {
              throw ClientAuthError.createNetworkError(tokenEndpoint, e_1);
            }

          case 4:
            ThrottlingUtils.postProcess(this.cacheManager, thumbprint, response);
            return [2
            /*return*/
            , response];
        }
      });
    });
  };

  return NetworkManager;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var CcsCredentialType;

(function (CcsCredentialType) {
  CcsCredentialType["HOME_ACCOUNT_ID"] = "home_account_id";
  CcsCredentialType["UPN"] = "UPN";
})(CcsCredentialType || (CcsCredentialType = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Base application class which will construct requests to send to and handle responses from the Microsoft STS using the authorization code flow.
 */

var BaseClient =
/** @class */
function () {
  function BaseClient(configuration, performanceClient) {
    // Set the configuration
    this.config = buildClientConfiguration(configuration); // Initialize the logger

    this.logger = new Logger(this.config.loggerOptions, name$1, version$1); // Initialize crypto

    this.cryptoUtils = this.config.cryptoInterface; // Initialize storage interface

    this.cacheManager = this.config.storageInterface; // Set the network interface

    this.networkClient = this.config.networkInterface; // Set the NetworkManager

    this.networkManager = new NetworkManager(this.networkClient, this.cacheManager); // Set TelemetryManager

    this.serverTelemetryManager = this.config.serverTelemetryManager; // set Authority

    this.authority = this.config.authOptions.authority; // set performance telemetry client

    this.performanceClient = performanceClient;
  }
  /**
   * Creates default headers for requests to token endpoint
   */


  BaseClient.prototype.createTokenRequestHeaders = function (ccsCred) {
    var headers = {};
    headers[HeaderNames.CONTENT_TYPE] = Constants.URL_FORM_CONTENT_TYPE;

    if (!this.config.systemOptions.preventCorsPreflight && ccsCred) {
      switch (ccsCred.type) {
        case CcsCredentialType.HOME_ACCOUNT_ID:
          try {
            var clientInfo = buildClientInfoFromHomeAccountId(ccsCred.credential);
            headers[HeaderNames.CCS_HEADER] = "Oid:" + clientInfo.uid + "@" + clientInfo.utid;
          } catch (e) {
            this.logger.verbose("Could not parse home account ID for CCS Header: " + e);
          }

          break;

        case CcsCredentialType.UPN:
          headers[HeaderNames.CCS_HEADER] = "UPN: " + ccsCred.credential;
          break;
      }
    }

    return headers;
  };
  /**
   * Http post to token endpoint
   * @param tokenEndpoint
   * @param queryString
   * @param headers
   * @param thumbprint
   */


  BaseClient.prototype.executePostToTokenEndpoint = function (tokenEndpoint, queryString, headers, thumbprint) {
    return __awaiter(this, void 0, void 0, function () {
      var response;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.networkManager.sendPostRequest(thumbprint, tokenEndpoint, {
              body: queryString,
              headers: headers,
              proxyUrl: this.config.systemOptions.proxyUrl
            })];

          case 1:
            response = _a.sent();

            if (this.config.serverTelemetryManager && response.status < 500 && response.status !== 429) {
              // Telemetry data successfully logged by server, clear Telemetry cache
              this.config.serverTelemetryManager.clearTelemetryCache();
            }

            return [2
            /*return*/
            , response];
        }
      });
    });
  };
  /**
   * Updates the authority object of the client. Endpoint discovery must be completed.
   * @param updatedAuthority
   */


  BaseClient.prototype.updateAuthority = function (updatedAuthority) {
    if (!updatedAuthority.discoveryComplete()) {
      throw ClientAuthError.createEndpointDiscoveryIncompleteError("Updated authority has not completed endpoint discovery.");
    }

    this.authority = updatedAuthority;
  };

  return BaseClient;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Validates server consumable params from the "request" objects
 */

var RequestValidator =
/** @class */
function () {
  function RequestValidator() {}
  /**
   * Utility to check if the `redirectUri` in the request is a non-null value
   * @param redirectUri
   */


  RequestValidator.validateRedirectUri = function (redirectUri) {
    if (StringUtils.isEmpty(redirectUri)) {
      throw ClientConfigurationError.createRedirectUriEmptyError();
    }
  };
  /**
   * Utility to validate prompt sent by the user in the request
   * @param prompt
   */


  RequestValidator.validatePrompt = function (prompt) {
    var promptValues = [];

    for (var value in PromptValue) {
      promptValues.push(PromptValue[value]);
    }

    if (promptValues.indexOf(prompt) < 0) {
      throw ClientConfigurationError.createInvalidPromptError(prompt);
    }
  };

  RequestValidator.validateClaims = function (claims) {
    try {
      JSON.parse(claims);
    } catch (e) {
      throw ClientConfigurationError.createInvalidClaimsRequestError();
    }
  };
  /**
   * Utility to validate code_challenge and code_challenge_method
   * @param codeChallenge
   * @param codeChallengeMethod
   */


  RequestValidator.validateCodeChallengeParams = function (codeChallenge, codeChallengeMethod) {
    if (StringUtils.isEmpty(codeChallenge) || StringUtils.isEmpty(codeChallengeMethod)) {
      throw ClientConfigurationError.createInvalidCodeChallengeParamsError();
    } else {
      this.validateCodeChallengeMethod(codeChallengeMethod);
    }
  };
  /**
   * Utility to validate code_challenge_method
   * @param codeChallengeMethod
   */


  RequestValidator.validateCodeChallengeMethod = function (codeChallengeMethod) {
    if ([CodeChallengeMethodValues.PLAIN, CodeChallengeMethodValues.S256].indexOf(codeChallengeMethod) < 0) {
      throw ClientConfigurationError.createInvalidCodeChallengeMethodError();
    }
  };
  /**
   * Removes unnecessary or duplicate query parameters from extraQueryParameters
   * @param request
   */


  RequestValidator.sanitizeEQParams = function (eQParams, queryParams) {
    if (!eQParams) {
      return {};
    } // Remove any query parameters already included in SSO params


    queryParams.forEach(function (value, key) {
      if (eQParams[key]) {
        delete eQParams[key];
      }
    });
    return eQParams;
  };

  return RequestValidator;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var RequestParameterBuilder =
/** @class */
function () {
  function RequestParameterBuilder() {
    this.parameters = new Map();
  }
  /**
   * add response_type = code
   */


  RequestParameterBuilder.prototype.addResponseTypeCode = function () {
    this.parameters.set(AADServerParamKeys.RESPONSE_TYPE, encodeURIComponent(Constants.CODE_RESPONSE_TYPE));
  };
  /**
   * add response_type = token id_token
   */


  RequestParameterBuilder.prototype.addResponseTypeForTokenAndIdToken = function () {
    this.parameters.set(AADServerParamKeys.RESPONSE_TYPE, encodeURIComponent(Constants.TOKEN_RESPONSE_TYPE + " " + Constants.ID_TOKEN_RESPONSE_TYPE));
  };
  /**
   * add response_mode. defaults to query.
   * @param responseMode
   */


  RequestParameterBuilder.prototype.addResponseMode = function (responseMode) {
    this.parameters.set(AADServerParamKeys.RESPONSE_MODE, encodeURIComponent(responseMode ? responseMode : ResponseMode.QUERY));
  };
  /**
   * Add flag to indicate STS should attempt to use WAM if available
   */


  RequestParameterBuilder.prototype.addNativeBroker = function () {
    this.parameters.set(AADServerParamKeys.NATIVE_BROKER, encodeURIComponent("1"));
  };
  /**
   * add scopes. set addOidcScopes to false to prevent default scopes in non-user scenarios
   * @param scopeSet
   * @param addOidcScopes
   */


  RequestParameterBuilder.prototype.addScopes = function (scopes, addOidcScopes) {
    if (addOidcScopes === void 0) {
      addOidcScopes = true;
    }

    var requestScopes = addOidcScopes ? __spreadArrays(scopes || [], OIDC_DEFAULT_SCOPES) : scopes || [];
    var scopeSet = new ScopeSet(requestScopes);
    this.parameters.set(AADServerParamKeys.SCOPE, encodeURIComponent(scopeSet.printScopes()));
  };
  /**
   * add clientId
   * @param clientId
   */


  RequestParameterBuilder.prototype.addClientId = function (clientId) {
    this.parameters.set(AADServerParamKeys.CLIENT_ID, encodeURIComponent(clientId));
  };
  /**
   * add redirect_uri
   * @param redirectUri
   */


  RequestParameterBuilder.prototype.addRedirectUri = function (redirectUri) {
    RequestValidator.validateRedirectUri(redirectUri);
    this.parameters.set(AADServerParamKeys.REDIRECT_URI, encodeURIComponent(redirectUri));
  };
  /**
   * add post logout redirectUri
   * @param redirectUri
   */


  RequestParameterBuilder.prototype.addPostLogoutRedirectUri = function (redirectUri) {
    RequestValidator.validateRedirectUri(redirectUri);
    this.parameters.set(AADServerParamKeys.POST_LOGOUT_URI, encodeURIComponent(redirectUri));
  };
  /**
   * add id_token_hint to logout request
   * @param idTokenHint
   */


  RequestParameterBuilder.prototype.addIdTokenHint = function (idTokenHint) {
    this.parameters.set(AADServerParamKeys.ID_TOKEN_HINT, encodeURIComponent(idTokenHint));
  };
  /**
   * add domain_hint
   * @param domainHint
   */


  RequestParameterBuilder.prototype.addDomainHint = function (domainHint) {
    this.parameters.set(SSOTypes.DOMAIN_HINT, encodeURIComponent(domainHint));
  };
  /**
   * add login_hint
   * @param loginHint
   */


  RequestParameterBuilder.prototype.addLoginHint = function (loginHint) {
    this.parameters.set(SSOTypes.LOGIN_HINT, encodeURIComponent(loginHint));
  };
  /**
   * Adds the CCS (Cache Credential Service) query parameter for login_hint
   * @param loginHint
   */


  RequestParameterBuilder.prototype.addCcsUpn = function (loginHint) {
    this.parameters.set(HeaderNames.CCS_HEADER, encodeURIComponent("UPN:" + loginHint));
  };
  /**
   * Adds the CCS (Cache Credential Service) query parameter for account object
   * @param loginHint
   */


  RequestParameterBuilder.prototype.addCcsOid = function (clientInfo) {
    this.parameters.set(HeaderNames.CCS_HEADER, encodeURIComponent("Oid:" + clientInfo.uid + "@" + clientInfo.utid));
  };
  /**
   * add sid
   * @param sid
   */


  RequestParameterBuilder.prototype.addSid = function (sid) {
    this.parameters.set(SSOTypes.SID, encodeURIComponent(sid));
  };
  /**
   * add claims
   * @param claims
   */


  RequestParameterBuilder.prototype.addClaims = function (claims, clientCapabilities) {
    var mergedClaims = this.addClientCapabilitiesToClaims(claims, clientCapabilities);
    RequestValidator.validateClaims(mergedClaims);
    this.parameters.set(AADServerParamKeys.CLAIMS, encodeURIComponent(mergedClaims));
  };
  /**
   * add correlationId
   * @param correlationId
   */


  RequestParameterBuilder.prototype.addCorrelationId = function (correlationId) {
    this.parameters.set(AADServerParamKeys.CLIENT_REQUEST_ID, encodeURIComponent(correlationId));
  };
  /**
   * add library info query params
   * @param libraryInfo
   */


  RequestParameterBuilder.prototype.addLibraryInfo = function (libraryInfo) {
    // Telemetry Info
    this.parameters.set(AADServerParamKeys.X_CLIENT_SKU, libraryInfo.sku);
    this.parameters.set(AADServerParamKeys.X_CLIENT_VER, libraryInfo.version);

    if (libraryInfo.os) {
      this.parameters.set(AADServerParamKeys.X_CLIENT_OS, libraryInfo.os);
    }

    if (libraryInfo.cpu) {
      this.parameters.set(AADServerParamKeys.X_CLIENT_CPU, libraryInfo.cpu);
    }
  };
  /**
   * Add client telemetry parameters
   * @param appTelemetry
   */


  RequestParameterBuilder.prototype.addApplicationTelemetry = function (appTelemetry) {
    if (appTelemetry === null || appTelemetry === void 0 ? void 0 : appTelemetry.appName) {
      this.parameters.set(AADServerParamKeys.X_APP_NAME, appTelemetry.appName);
    }

    if (appTelemetry === null || appTelemetry === void 0 ? void 0 : appTelemetry.appVersion) {
      this.parameters.set(AADServerParamKeys.X_APP_VER, appTelemetry.appVersion);
    }
  };
  /**
   * add prompt
   * @param prompt
   */


  RequestParameterBuilder.prototype.addPrompt = function (prompt) {
    RequestValidator.validatePrompt(prompt);
    this.parameters.set("" + AADServerParamKeys.PROMPT, encodeURIComponent(prompt));
  };
  /**
   * add state
   * @param state
   */


  RequestParameterBuilder.prototype.addState = function (state) {
    if (!StringUtils.isEmpty(state)) {
      this.parameters.set(AADServerParamKeys.STATE, encodeURIComponent(state));
    }
  };
  /**
   * add nonce
   * @param nonce
   */


  RequestParameterBuilder.prototype.addNonce = function (nonce) {
    this.parameters.set(AADServerParamKeys.NONCE, encodeURIComponent(nonce));
  };
  /**
   * add code_challenge and code_challenge_method
   * - throw if either of them are not passed
   * @param codeChallenge
   * @param codeChallengeMethod
   */


  RequestParameterBuilder.prototype.addCodeChallengeParams = function (codeChallenge, codeChallengeMethod) {
    RequestValidator.validateCodeChallengeParams(codeChallenge, codeChallengeMethod);

    if (codeChallenge && codeChallengeMethod) {
      this.parameters.set(AADServerParamKeys.CODE_CHALLENGE, encodeURIComponent(codeChallenge));
      this.parameters.set(AADServerParamKeys.CODE_CHALLENGE_METHOD, encodeURIComponent(codeChallengeMethod));
    } else {
      throw ClientConfigurationError.createInvalidCodeChallengeParamsError();
    }
  };
  /**
   * add the `authorization_code` passed by the user to exchange for a token
   * @param code
   */


  RequestParameterBuilder.prototype.addAuthorizationCode = function (code) {
    this.parameters.set(AADServerParamKeys.CODE, encodeURIComponent(code));
  };
  /**
   * add the `authorization_code` passed by the user to exchange for a token
   * @param code
   */


  RequestParameterBuilder.prototype.addDeviceCode = function (code) {
    this.parameters.set(AADServerParamKeys.DEVICE_CODE, encodeURIComponent(code));
  };
  /**
   * add the `refreshToken` passed by the user
   * @param refreshToken
   */


  RequestParameterBuilder.prototype.addRefreshToken = function (refreshToken) {
    this.parameters.set(AADServerParamKeys.REFRESH_TOKEN, encodeURIComponent(refreshToken));
  };
  /**
   * add the `code_verifier` passed by the user to exchange for a token
   * @param codeVerifier
   */


  RequestParameterBuilder.prototype.addCodeVerifier = function (codeVerifier) {
    this.parameters.set(AADServerParamKeys.CODE_VERIFIER, encodeURIComponent(codeVerifier));
  };
  /**
   * add client_secret
   * @param clientSecret
   */


  RequestParameterBuilder.prototype.addClientSecret = function (clientSecret) {
    this.parameters.set(AADServerParamKeys.CLIENT_SECRET, encodeURIComponent(clientSecret));
  };
  /**
   * add clientAssertion for confidential client flows
   * @param clientAssertion
   */


  RequestParameterBuilder.prototype.addClientAssertion = function (clientAssertion) {
    if (!StringUtils.isEmpty(clientAssertion)) {
      this.parameters.set(AADServerParamKeys.CLIENT_ASSERTION, encodeURIComponent(clientAssertion));
    }
  };
  /**
   * add clientAssertionType for confidential client flows
   * @param clientAssertionType
   */


  RequestParameterBuilder.prototype.addClientAssertionType = function (clientAssertionType) {
    if (!StringUtils.isEmpty(clientAssertionType)) {
      this.parameters.set(AADServerParamKeys.CLIENT_ASSERTION_TYPE, encodeURIComponent(clientAssertionType));
    }
  };
  /**
   * add OBO assertion for confidential client flows
   * @param clientAssertion
   */


  RequestParameterBuilder.prototype.addOboAssertion = function (oboAssertion) {
    this.parameters.set(AADServerParamKeys.OBO_ASSERTION, encodeURIComponent(oboAssertion));
  };
  /**
   * add grant type
   * @param grantType
   */


  RequestParameterBuilder.prototype.addRequestTokenUse = function (tokenUse) {
    this.parameters.set(AADServerParamKeys.REQUESTED_TOKEN_USE, encodeURIComponent(tokenUse));
  };
  /**
   * add grant type
   * @param grantType
   */


  RequestParameterBuilder.prototype.addGrantType = function (grantType) {
    this.parameters.set(AADServerParamKeys.GRANT_TYPE, encodeURIComponent(grantType));
  };
  /**
   * add client info
   *
   */


  RequestParameterBuilder.prototype.addClientInfo = function () {
    this.parameters.set(CLIENT_INFO, "1");
  };
  /**
   * add extraQueryParams
   * @param eQparams
   */


  RequestParameterBuilder.prototype.addExtraQueryParameters = function (eQparams) {
    var _this = this;

    RequestValidator.sanitizeEQParams(eQparams, this.parameters);
    Object.keys(eQparams).forEach(function (key) {
      _this.parameters.set(key, eQparams[key]);
    });
  };

  RequestParameterBuilder.prototype.addClientCapabilitiesToClaims = function (claims, clientCapabilities) {
    var mergedClaims; // Parse provided claims into JSON object or initialize empty object

    if (!claims) {
      mergedClaims = {};
    } else {
      try {
        mergedClaims = JSON.parse(claims);
      } catch (e) {
        throw ClientConfigurationError.createInvalidClaimsRequestError();
      }
    }

    if (clientCapabilities && clientCapabilities.length > 0) {
      if (!mergedClaims.hasOwnProperty(ClaimsRequestKeys.ACCESS_TOKEN)) {
        // Add access_token key to claims object
        mergedClaims[ClaimsRequestKeys.ACCESS_TOKEN] = {};
      } // Add xms_cc claim with provided clientCapabilities to access_token key


      mergedClaims[ClaimsRequestKeys.ACCESS_TOKEN][ClaimsRequestKeys.XMS_CC] = {
        values: clientCapabilities
      };
    }

    return JSON.stringify(mergedClaims);
  };
  /**
   * adds `username` for Password Grant flow
   * @param username
   */


  RequestParameterBuilder.prototype.addUsername = function (username) {
    this.parameters.set(PasswordGrantConstants.username, encodeURIComponent(username));
  };
  /**
   * adds `password` for Password Grant flow
   * @param password
   */


  RequestParameterBuilder.prototype.addPassword = function (password) {
    this.parameters.set(PasswordGrantConstants.password, encodeURIComponent(password));
  };
  /**
   * add pop_jwk to query params
   * @param cnfString
   */


  RequestParameterBuilder.prototype.addPopToken = function (cnfString) {
    if (!StringUtils.isEmpty(cnfString)) {
      this.parameters.set(AADServerParamKeys.TOKEN_TYPE, AuthenticationScheme.POP);
      this.parameters.set(AADServerParamKeys.REQ_CNF, encodeURIComponent(cnfString));
    }
  };
  /**
   * add SSH JWK and key ID to query params
   */


  RequestParameterBuilder.prototype.addSshJwk = function (sshJwkString) {
    if (!StringUtils.isEmpty(sshJwkString)) {
      this.parameters.set(AADServerParamKeys.TOKEN_TYPE, AuthenticationScheme.SSH);
      this.parameters.set(AADServerParamKeys.REQ_CNF, encodeURIComponent(sshJwkString));
    }
  };
  /**
   * add server telemetry fields
   * @param serverTelemetryManager
   */


  RequestParameterBuilder.prototype.addServerTelemetry = function (serverTelemetryManager) {
    this.parameters.set(AADServerParamKeys.X_CLIENT_CURR_TELEM, serverTelemetryManager.generateCurrentRequestHeaderValue());
    this.parameters.set(AADServerParamKeys.X_CLIENT_LAST_TELEM, serverTelemetryManager.generateLastRequestHeaderValue());
  };
  /**
   * Adds parameter that indicates to the server that throttling is supported
   */


  RequestParameterBuilder.prototype.addThrottling = function () {
    this.parameters.set(AADServerParamKeys.X_MS_LIB_CAPABILITY, ThrottlingConstants.X_MS_LIB_CAPABILITY_VALUE);
  };
  /**
   * Adds logout_hint parameter for "silent" logout which prevent server account picker
   */


  RequestParameterBuilder.prototype.addLogoutHint = function (logoutHint) {
    this.parameters.set(AADServerParamKeys.LOGOUT_HINT, encodeURIComponent(logoutHint));
  };
  /**
   * Utility to create a URL from the params map
   */


  RequestParameterBuilder.prototype.createQueryString = function () {
    var queryParameterArray = new Array();
    this.parameters.forEach(function (value, key) {
      queryParameterArray.push(key + "=" + value);
    });
    return queryParameterArray.join("&");
  };

  return RequestParameterBuilder;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * ID_TOKEN Cache
 *
 * Key:Value Schema:
 *
 * Key Example: uid.utid-login.microsoftonline.com-idtoken-clientId-contoso.com-
 *
 * Value Schema:
 * {
 *      homeAccountId: home account identifier for the auth scheme,
 *      environment: entity that issued the token, represented as a full host
 *      credentialType: Type of credential as a string, can be one of the following: RefreshToken, AccessToken, IdToken, Password, Cookie, Certificate, Other
 *      clientId: client ID of the application
 *      secret: Actual credential as a string
 *      realm: Full tenant or organizational identifier that the account belongs to
 * }
 */

var IdTokenEntity =
/** @class */
function (_super) {
  __extends(IdTokenEntity, _super);

  function IdTokenEntity() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Create IdTokenEntity
   * @param homeAccountId
   * @param authenticationResult
   * @param clientId
   * @param authority
   */


  IdTokenEntity.createIdTokenEntity = function (homeAccountId, environment, idToken, clientId, tenantId) {
    var idTokenEntity = new IdTokenEntity();
    idTokenEntity.credentialType = CredentialType.ID_TOKEN;
    idTokenEntity.homeAccountId = homeAccountId;
    idTokenEntity.environment = environment;
    idTokenEntity.clientId = clientId;
    idTokenEntity.secret = idToken;
    idTokenEntity.realm = tenantId;
    return idTokenEntity;
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  IdTokenEntity.isIdTokenEntity = function (entity) {
    if (!entity) {
      return false;
    }

    return entity.hasOwnProperty("homeAccountId") && entity.hasOwnProperty("environment") && entity.hasOwnProperty("credentialType") && entity.hasOwnProperty("realm") && entity.hasOwnProperty("clientId") && entity.hasOwnProperty("secret") && entity["credentialType"] === CredentialType.ID_TOKEN;
  };

  return IdTokenEntity;
}(CredentialEntity);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Utility class which exposes functions for managing date and time operations.
 */

var TimeUtils =
/** @class */
function () {
  function TimeUtils() {}
  /**
   * return the current time in Unix time (seconds).
   */


  TimeUtils.nowSeconds = function () {
    // Date.getTime() returns in milliseconds.
    return Math.round(new Date().getTime() / 1000.0);
  };
  /**
   * check if a token is expired based on given UTC time in seconds.
   * @param expiresOn
   */


  TimeUtils.isTokenExpired = function (expiresOn, offset) {
    // check for access token expiry
    var expirationSec = Number(expiresOn) || 0;
    var offsetCurrentTimeSec = TimeUtils.nowSeconds() + offset; // If current time + offset is greater than token expiration time, then token is expired.

    return offsetCurrentTimeSec > expirationSec;
  };
  /**
   * If the current time is earlier than the time that a token was cached at, we must discard the token
   * i.e. The system clock was turned back after acquiring the cached token
   * @param cachedAt
   * @param offset
   */


  TimeUtils.wasClockTurnedBack = function (cachedAt) {
    var cachedAtSec = Number(cachedAt);
    return cachedAtSec > TimeUtils.nowSeconds();
  };
  /**
   * Waits for t number of milliseconds
   * @param t number
   * @param value T
   */


  TimeUtils.delay = function (t, value) {
    return new Promise(function (resolve) {
      return setTimeout(function () {
        return resolve(value);
      }, t);
    });
  };

  return TimeUtils;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * ACCESS_TOKEN Credential Type
 *
 * Key:Value Schema:
 *
 * Key Example: uid.utid-login.microsoftonline.com-accesstoken-clientId-contoso.com-user.read
 *
 * Value Schema:
 * {
 *      homeAccountId: home account identifier for the auth scheme,
 *      environment: entity that issued the token, represented as a full host
 *      credentialType: Type of credential as a string, can be one of the following: RefreshToken, AccessToken, IdToken, Password, Cookie, Certificate, Other
 *      clientId: client ID of the application
 *      secret: Actual credential as a string
 *      familyId: Family ID identifier, usually only used for refresh tokens
 *      realm: Full tenant or organizational identifier that the account belongs to
 *      target: Permissions that are included in the token, or for refresh tokens, the resource identifier.
 *      cachedAt: Absolute device time when entry was created in the cache.
 *      expiresOn: Token expiry time, calculated based on current UTC time in seconds. Represented as a string.
 *      extendedExpiresOn: Additional extended expiry time until when token is valid in case of server-side outage. Represented as string in UTC seconds.
 *      keyId: used for POP and SSH tokenTypes
 *      tokenType: Type of the token issued. Usually "Bearer"
 * }
 */

var AccessTokenEntity =
/** @class */
function (_super) {
  __extends(AccessTokenEntity, _super);

  function AccessTokenEntity() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Create AccessTokenEntity
   * @param homeAccountId
   * @param environment
   * @param accessToken
   * @param clientId
   * @param tenantId
   * @param scopes
   * @param expiresOn
   * @param extExpiresOn
   */


  AccessTokenEntity.createAccessTokenEntity = function (homeAccountId, environment, accessToken, clientId, tenantId, scopes, expiresOn, extExpiresOn, cryptoUtils, refreshOn, tokenType, userAssertionHash, keyId, requestedClaims, requestedClaimsHash) {
    var _a, _b;

    var atEntity = new AccessTokenEntity();
    atEntity.homeAccountId = homeAccountId;
    atEntity.credentialType = CredentialType.ACCESS_TOKEN;
    atEntity.secret = accessToken;
    var currentTime = TimeUtils.nowSeconds();
    atEntity.cachedAt = currentTime.toString();
    /*
     * Token expiry time.
     * This value should be  calculated based on the current UTC time measured locally and the value  expires_in Represented as a string in JSON.
     */

    atEntity.expiresOn = expiresOn.toString();
    atEntity.extendedExpiresOn = extExpiresOn.toString();

    if (refreshOn) {
      atEntity.refreshOn = refreshOn.toString();
    }

    atEntity.environment = environment;
    atEntity.clientId = clientId;
    atEntity.realm = tenantId;
    atEntity.target = scopes;
    atEntity.userAssertionHash = userAssertionHash;
    atEntity.tokenType = StringUtils.isEmpty(tokenType) ? AuthenticationScheme.BEARER : tokenType;

    if (requestedClaims) {
      atEntity.requestedClaims = requestedClaims;
      atEntity.requestedClaimsHash = requestedClaimsHash;
    }
    /*
     * Create Access Token With Auth Scheme instead of regular access token
     * Cast to lower to handle "bearer" from ADFS
     */


    if (((_a = atEntity.tokenType) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== AuthenticationScheme.BEARER.toLowerCase()) {
      atEntity.credentialType = CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME;

      switch (atEntity.tokenType) {
        case AuthenticationScheme.POP:
          // Make sure keyId is present and add it to credential
          var tokenClaims = AuthToken.extractTokenClaims(accessToken, cryptoUtils);

          if (!((_b = tokenClaims === null || tokenClaims === void 0 ? void 0 : tokenClaims.cnf) === null || _b === void 0 ? void 0 : _b.kid)) {
            throw ClientAuthError.createTokenClaimsRequiredError();
          }

          atEntity.keyId = tokenClaims.cnf.kid;
          break;

        case AuthenticationScheme.SSH:
          atEntity.keyId = keyId;
      }
    }

    return atEntity;
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  AccessTokenEntity.isAccessTokenEntity = function (entity) {
    if (!entity) {
      return false;
    }

    return entity.hasOwnProperty("homeAccountId") && entity.hasOwnProperty("environment") && entity.hasOwnProperty("credentialType") && entity.hasOwnProperty("realm") && entity.hasOwnProperty("clientId") && entity.hasOwnProperty("secret") && entity.hasOwnProperty("target") && (entity["credentialType"] === CredentialType.ACCESS_TOKEN || entity["credentialType"] === CredentialType.ACCESS_TOKEN_WITH_AUTH_SCHEME);
  };

  return AccessTokenEntity;
}(CredentialEntity);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * REFRESH_TOKEN Cache
 *
 * Key:Value Schema:
 *
 * Key Example: uid.utid-login.microsoftonline.com-refreshtoken-clientId--
 *
 * Value:
 * {
 *      homeAccountId: home account identifier for the auth scheme,
 *      environment: entity that issued the token, represented as a full host
 *      credentialType: Type of credential as a string, can be one of the following: RefreshToken, AccessToken, IdToken, Password, Cookie, Certificate, Other
 *      clientId: client ID of the application
 *      secret: Actual credential as a string
 *      familyId: Family ID identifier, '1' represents Microsoft Family
 *      realm: Full tenant or organizational identifier that the account belongs to
 *      target: Permissions that are included in the token, or for refresh tokens, the resource identifier.
 * }
 */

var RefreshTokenEntity =
/** @class */
function (_super) {
  __extends(RefreshTokenEntity, _super);

  function RefreshTokenEntity() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Create RefreshTokenEntity
   * @param homeAccountId
   * @param authenticationResult
   * @param clientId
   * @param authority
   */


  RefreshTokenEntity.createRefreshTokenEntity = function (homeAccountId, environment, refreshToken, clientId, familyId, userAssertionHash) {
    var rtEntity = new RefreshTokenEntity();
    rtEntity.clientId = clientId;
    rtEntity.credentialType = CredentialType.REFRESH_TOKEN;
    rtEntity.environment = environment;
    rtEntity.homeAccountId = homeAccountId;
    rtEntity.secret = refreshToken;
    rtEntity.userAssertionHash = userAssertionHash;
    if (familyId) rtEntity.familyId = familyId;
    return rtEntity;
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  RefreshTokenEntity.isRefreshTokenEntity = function (entity) {
    if (!entity) {
      return false;
    }

    return entity.hasOwnProperty("homeAccountId") && entity.hasOwnProperty("environment") && entity.hasOwnProperty("credentialType") && entity.hasOwnProperty("clientId") && entity.hasOwnProperty("secret") && entity["credentialType"] === CredentialType.REFRESH_TOKEN;
  };

  return RefreshTokenEntity;
}(CredentialEntity);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * InteractionRequiredServerErrorMessage contains string constants used by error codes and messages returned by the server indicating interaction is required
 */

var InteractionRequiredServerErrorMessage = ["interaction_required", "consent_required", "login_required"];
var InteractionRequiredAuthSubErrorMessage = ["message_only", "additional_action", "basic_action", "user_password_expired", "consent_required"];
/**
 * Interaction required errors defined by the SDK
 */

var InteractionRequiredAuthErrorMessage = {
  noTokensFoundError: {
    code: "no_tokens_found",
    desc: "No refresh token found in the cache. Please sign-in."
  },
  native_account_unavailable: {
    code: "native_account_unavailable",
    desc: "The requested account is not available in the native broker. It may have been deleted or logged out. Please sign-in again using an interactive API."
  }
};
/**
 * Error thrown when user interaction is required.
 */

var InteractionRequiredAuthError =
/** @class */
function (_super) {
  __extends(InteractionRequiredAuthError, _super);

  function InteractionRequiredAuthError(errorCode, errorMessage, subError) {
    var _this = _super.call(this, errorCode, errorMessage, subError) || this;

    _this.name = "InteractionRequiredAuthError";
    Object.setPrototypeOf(_this, InteractionRequiredAuthError.prototype);
    return _this;
  }
  /**
   * Helper function used to determine if an error thrown by the server requires interaction to resolve
   * @param errorCode
   * @param errorString
   * @param subError
   */


  InteractionRequiredAuthError.isInteractionRequiredError = function (errorCode, errorString, subError) {
    var isInteractionRequiredErrorCode = !!errorCode && InteractionRequiredServerErrorMessage.indexOf(errorCode) > -1;
    var isInteractionRequiredSubError = !!subError && InteractionRequiredAuthSubErrorMessage.indexOf(subError) > -1;
    var isInteractionRequiredErrorDesc = !!errorString && InteractionRequiredServerErrorMessage.some(function (irErrorCode) {
      return errorString.indexOf(irErrorCode) > -1;
    });
    return isInteractionRequiredErrorCode || isInteractionRequiredErrorDesc || isInteractionRequiredSubError;
  };
  /**
   * Creates an error thrown when the authorization code required for a token request is null or empty.
   */


  InteractionRequiredAuthError.createNoTokensFoundError = function () {
    return new InteractionRequiredAuthError(InteractionRequiredAuthErrorMessage.noTokensFoundError.code, InteractionRequiredAuthErrorMessage.noTokensFoundError.desc);
  };
  /**
   * Creates an error thrown when the native broker returns ACCOUNT_UNAVAILABLE status, indicating that the account was removed and interactive sign-in is required
   * @returns
   */


  InteractionRequiredAuthError.createNativeAccountUnavailableError = function () {
    return new InteractionRequiredAuthError(InteractionRequiredAuthErrorMessage.native_account_unavailable.code, InteractionRequiredAuthErrorMessage.native_account_unavailable.desc);
  };

  return InteractionRequiredAuthError;
}(AuthError);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var CacheRecord =
/** @class */
function () {
  function CacheRecord(accountEntity, idTokenEntity, accessTokenEntity, refreshTokenEntity, appMetadataEntity) {
    this.account = accountEntity || null;
    this.idToken = idTokenEntity || null;
    this.accessToken = accessTokenEntity || null;
    this.refreshToken = refreshTokenEntity || null;
    this.appMetadata = appMetadataEntity || null;
  }

  return CacheRecord;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Class which provides helpers for OAuth 2.0 protocol specific values
 */

var ProtocolUtils =
/** @class */
function () {
  function ProtocolUtils() {}
  /**
   * Appends user state with random guid, or returns random guid.
   * @param userState
   * @param randomGuid
   */


  ProtocolUtils.setRequestState = function (cryptoObj, userState, meta) {
    var libraryState = ProtocolUtils.generateLibraryState(cryptoObj, meta);
    return !StringUtils.isEmpty(userState) ? "" + libraryState + Constants.RESOURCE_DELIM + userState : libraryState;
  };
  /**
   * Generates the state value used by the common library.
   * @param randomGuid
   * @param cryptoObj
   */


  ProtocolUtils.generateLibraryState = function (cryptoObj, meta) {
    if (!cryptoObj) {
      throw ClientAuthError.createNoCryptoObjectError("generateLibraryState");
    } // Create a state object containing a unique id and the timestamp of the request creation


    var stateObj = {
      id: cryptoObj.createNewGuid()
    };

    if (meta) {
      stateObj.meta = meta;
    }

    var stateString = JSON.stringify(stateObj);
    return cryptoObj.base64Encode(stateString);
  };
  /**
   * Parses the state into the RequestStateObject, which contains the LibraryState info and the state passed by the user.
   * @param state
   * @param cryptoObj
   */


  ProtocolUtils.parseRequestState = function (cryptoObj, state) {
    if (!cryptoObj) {
      throw ClientAuthError.createNoCryptoObjectError("parseRequestState");
    }

    if (StringUtils.isEmpty(state)) {
      throw ClientAuthError.createInvalidStateError(state, "Null, undefined or empty state");
    }

    try {
      // Split the state between library state and user passed state and decode them separately
      var splitState = state.split(Constants.RESOURCE_DELIM);
      var libraryState = splitState[0];
      var userState = splitState.length > 1 ? splitState.slice(1).join(Constants.RESOURCE_DELIM) : Constants.EMPTY_STRING;
      var libraryStateString = cryptoObj.base64Decode(libraryState);
      var libraryStateObj = JSON.parse(libraryStateString);
      return {
        userRequestState: !StringUtils.isEmpty(userState) ? userState : Constants.EMPTY_STRING,
        libraryState: libraryStateObj
      };
    } catch (e) {
      throw ClientAuthError.createInvalidStateError(state, e);
    }
  };

  return ProtocolUtils;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Url object class which can perform various transformations on url strings.
 */

var UrlString =
/** @class */
function () {
  function UrlString(url) {
    this._urlString = url;

    if (StringUtils.isEmpty(this._urlString)) {
      // Throws error if url is empty
      throw ClientConfigurationError.createUrlEmptyError();
    }

    if (StringUtils.isEmpty(this.getHash())) {
      this._urlString = UrlString.canonicalizeUri(url);
    }
  }

  Object.defineProperty(UrlString.prototype, "urlString", {
    get: function () {
      return this._urlString;
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Ensure urls are lower case and end with a / character.
   * @param url
   */

  UrlString.canonicalizeUri = function (url) {
    if (url) {
      var lowerCaseUrl = url.toLowerCase();

      if (StringUtils.endsWith(lowerCaseUrl, "?")) {
        lowerCaseUrl = lowerCaseUrl.slice(0, -1);
      } else if (StringUtils.endsWith(lowerCaseUrl, "?/")) {
        lowerCaseUrl = lowerCaseUrl.slice(0, -2);
      }

      if (!StringUtils.endsWith(lowerCaseUrl, "/")) {
        lowerCaseUrl += "/";
      }

      return lowerCaseUrl;
    }

    return url;
  };
  /**
   * Throws if urlString passed is not a valid authority URI string.
   */


  UrlString.prototype.validateAsUri = function () {
    // Attempts to parse url for uri components
    var components;

    try {
      components = this.getUrlComponents();
    } catch (e) {
      throw ClientConfigurationError.createUrlParseError(e);
    } // Throw error if URI or path segments are not parseable.


    if (!components.HostNameAndPort || !components.PathSegments) {
      throw ClientConfigurationError.createUrlParseError("Given url string: " + this.urlString);
    } // Throw error if uri is insecure.


    if (!components.Protocol || components.Protocol.toLowerCase() !== "https:") {
      throw ClientConfigurationError.createInsecureAuthorityUriError(this.urlString);
    }
  };
  /**
   * Given a url and a query string return the url with provided query string appended
   * @param url
   * @param queryString
   */


  UrlString.appendQueryString = function (url, queryString) {
    if (StringUtils.isEmpty(queryString)) {
      return url;
    }

    return url.indexOf("?") < 0 ? url + "?" + queryString : url + "&" + queryString;
  };
  /**
   * Returns a url with the hash removed
   * @param url
   */


  UrlString.removeHashFromUrl = function (url) {
    return UrlString.canonicalizeUri(url.split("#")[0]);
  };
  /**
   * Given a url like https://a:b/common/d?e=f#g, and a tenantId, returns https://a:b/tenantId/d
   * @param href The url
   * @param tenantId The tenant id to replace
   */


  UrlString.prototype.replaceTenantPath = function (tenantId) {
    var urlObject = this.getUrlComponents();
    var pathArray = urlObject.PathSegments;

    if (tenantId && pathArray.length !== 0 && (pathArray[0] === AADAuthorityConstants.COMMON || pathArray[0] === AADAuthorityConstants.ORGANIZATIONS)) {
      pathArray[0] = tenantId;
    }

    return UrlString.constructAuthorityUriFromObject(urlObject);
  };
  /**
   * Returns the anchor part(#) of the URL
   */


  UrlString.prototype.getHash = function () {
    return UrlString.parseHash(this.urlString);
  };
  /**
   * Parses out the components from a url string.
   * @returns An object with the various components. Please cache this value insted of calling this multiple times on the same url.
   */


  UrlString.prototype.getUrlComponents = function () {
    // https://gist.github.com/curtisz/11139b2cfcaef4a261e0
    var regEx = RegExp("^(([^:/?#]+):)?(//([^/?#]*))?([^?#]*)(\\?([^#]*))?(#(.*))?"); // If url string does not match regEx, we throw an error

    var match = this.urlString.match(regEx);

    if (!match) {
      throw ClientConfigurationError.createUrlParseError("Given url string: " + this.urlString);
    } // Url component object


    var urlComponents = {
      Protocol: match[1],
      HostNameAndPort: match[4],
      AbsolutePath: match[5],
      QueryString: match[7]
    };
    var pathSegments = urlComponents.AbsolutePath.split("/");
    pathSegments = pathSegments.filter(function (val) {
      return val && val.length > 0;
    }); // remove empty elements

    urlComponents.PathSegments = pathSegments;

    if (!StringUtils.isEmpty(urlComponents.QueryString) && urlComponents.QueryString.endsWith("/")) {
      urlComponents.QueryString = urlComponents.QueryString.substring(0, urlComponents.QueryString.length - 1);
    }

    return urlComponents;
  };

  UrlString.getDomainFromUrl = function (url) {
    var regEx = RegExp("^([^:/?#]+://)?([^/?#]*)");
    var match = url.match(regEx);

    if (!match) {
      throw ClientConfigurationError.createUrlParseError("Given url string: " + url);
    }

    return match[2];
  };

  UrlString.getAbsoluteUrl = function (relativeUrl, baseUrl) {
    if (relativeUrl[0] === Constants.FORWARD_SLASH) {
      var url = new UrlString(baseUrl);
      var baseComponents = url.getUrlComponents();
      return baseComponents.Protocol + "//" + baseComponents.HostNameAndPort + relativeUrl;
    }

    return relativeUrl;
  };
  /**
   * Parses hash string from given string. Returns empty string if no hash symbol is found.
   * @param hashString
   */


  UrlString.parseHash = function (hashString) {
    var hashIndex1 = hashString.indexOf("#");
    var hashIndex2 = hashString.indexOf("#/");

    if (hashIndex2 > -1) {
      return hashString.substring(hashIndex2 + 2);
    } else if (hashIndex1 > -1) {
      return hashString.substring(hashIndex1 + 1);
    }

    return Constants.EMPTY_STRING;
  };
  /**
   * Parses query string from given string. Returns empty string if no query symbol is found.
   * @param queryString
   */


  UrlString.parseQueryString = function (queryString) {
    var queryIndex1 = queryString.indexOf("?");
    var queryIndex2 = queryString.indexOf("/?");

    if (queryIndex2 > -1) {
      return queryString.substring(queryIndex2 + 2);
    } else if (queryIndex1 > -1) {
      return queryString.substring(queryIndex1 + 1);
    }

    return Constants.EMPTY_STRING;
  };

  UrlString.constructAuthorityUriFromObject = function (urlObject) {
    return new UrlString(urlObject.Protocol + "//" + urlObject.HostNameAndPort + "/" + urlObject.PathSegments.join("/"));
  };
  /**
   * Returns URL hash as server auth code response object.
   */


  UrlString.getDeserializedHash = function (hash) {
    // Check if given hash is empty
    if (StringUtils.isEmpty(hash)) {
      return {};
    } // Strip the # symbol if present


    var parsedHash = UrlString.parseHash(hash); // If # symbol was not present, above will return empty string, so give original hash value

    var deserializedHash = StringUtils.queryStringToObject(StringUtils.isEmpty(parsedHash) ? hash : parsedHash); // Check if deserialization didn't work

    if (!deserializedHash) {
      throw ClientAuthError.createHashNotDeserializedError(JSON.stringify(deserializedHash));
    }

    return deserializedHash;
  };
  /**
   * Returns URL query string as server auth code response object.
   */


  UrlString.getDeserializedQueryString = function (query) {
    // Check if given query is empty
    if (StringUtils.isEmpty(query)) {
      return {};
    } // Strip the ? symbol if present


    var parsedQueryString = UrlString.parseQueryString(query); // If ? symbol was not present, above will return empty string, so give original query value

    var deserializedQueryString = StringUtils.queryStringToObject(StringUtils.isEmpty(parsedQueryString) ? query : parsedQueryString); // Check if deserialization didn't work

    if (!deserializedQueryString) {
      throw ClientAuthError.createHashNotDeserializedError(JSON.stringify(deserializedQueryString));
    }

    return deserializedQueryString;
  };
  /**
   * Check if the hash of the URL string contains known properties
   */


  UrlString.hashContainsKnownProperties = function (hash) {
    if (StringUtils.isEmpty(hash) || hash.indexOf("=") < 0) {
      // Hash doesn't contain key/value pairs
      return false;
    }

    var parameters = UrlString.getDeserializedHash(hash);
    return !!(parameters.code || parameters.error_description || parameters.error || parameters.state);
  };

  return UrlString;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var KeyLocation;

(function (KeyLocation) {
  KeyLocation["SW"] = "sw";
  KeyLocation["UHW"] = "uhw";
})(KeyLocation || (KeyLocation = {}));

var PopTokenGenerator =
/** @class */
function () {
  function PopTokenGenerator(cryptoUtils) {
    this.cryptoUtils = cryptoUtils;
  }
  /**
   * Generates the req_cnf validated at the RP in the POP protocol for SHR parameters
   * and returns an object containing the keyid, the full req_cnf string and the req_cnf string hash
   * @param request
   * @returns
   */


  PopTokenGenerator.prototype.generateCnf = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var reqCnf, reqCnfString, _a;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            return [4
            /*yield*/
            , this.generateKid(request)];

          case 1:
            reqCnf = _b.sent();
            reqCnfString = this.cryptoUtils.base64Encode(JSON.stringify(reqCnf));
            _a = {
              kid: reqCnf.kid,
              reqCnfString: reqCnfString
            };
            return [4
            /*yield*/
            , this.cryptoUtils.hashString(reqCnfString)];

          case 2:
            return [2
            /*return*/
            , (_a.reqCnfHash = _b.sent(), _a)];
        }
      });
    });
  };
  /**
   * Generates key_id for a SHR token request
   * @param request
   * @returns
   */


  PopTokenGenerator.prototype.generateKid = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var kidThumbprint;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.cryptoUtils.getPublicKeyThumbprint(request)];

          case 1:
            kidThumbprint = _a.sent();
            return [2
            /*return*/
            , {
              kid: kidThumbprint,
              xms_ksl: KeyLocation.SW
            }];
        }
      });
    });
  };
  /**
   * Signs the POP access_token with the local generated key-pair
   * @param accessToken
   * @param request
   * @returns
   */


  PopTokenGenerator.prototype.signPopToken = function (accessToken, keyId, request) {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        return [2
        /*return*/
        , this.signPayload(accessToken, keyId, request)];
      });
    });
  };
  /**
   * Utility function to generate the signed JWT for an access_token
   * @param payload
   * @param kid
   * @param request
   * @param claims
   * @returns
   */


  PopTokenGenerator.prototype.signPayload = function (payload, keyId, request, claims) {
    return __awaiter(this, void 0, void 0, function () {
      var resourceRequestMethod, resourceRequestUri, shrClaims, shrNonce, resourceUrlString, resourceUrlComponents;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            resourceRequestMethod = request.resourceRequestMethod, resourceRequestUri = request.resourceRequestUri, shrClaims = request.shrClaims, shrNonce = request.shrNonce;
            resourceUrlString = resourceRequestUri ? new UrlString(resourceRequestUri) : undefined;
            resourceUrlComponents = resourceUrlString === null || resourceUrlString === void 0 ? void 0 : resourceUrlString.getUrlComponents();
            return [4
            /*yield*/
            , this.cryptoUtils.signJwt(__assign({
              at: payload,
              ts: TimeUtils.nowSeconds(),
              m: resourceRequestMethod === null || resourceRequestMethod === void 0 ? void 0 : resourceRequestMethod.toUpperCase(),
              u: resourceUrlComponents === null || resourceUrlComponents === void 0 ? void 0 : resourceUrlComponents.HostNameAndPort,
              nonce: shrNonce || this.cryptoUtils.createNewGuid(),
              p: resourceUrlComponents === null || resourceUrlComponents === void 0 ? void 0 : resourceUrlComponents.AbsolutePath,
              q: (resourceUrlComponents === null || resourceUrlComponents === void 0 ? void 0 : resourceUrlComponents.QueryString) ? [[], resourceUrlComponents.QueryString] : undefined,
              client_claims: shrClaims || undefined
            }, claims), keyId, request.correlationId)];

          case 1:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };

  return PopTokenGenerator;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * APP_METADATA Cache
 *
 * Key:Value Schema:
 *
 * Key: appmetadata-<environment>-<client_id>
 *
 * Value:
 * {
 *      clientId: client ID of the application
 *      environment: entity that issued the token, represented as a full host
 *      familyId: Family ID identifier, '1' represents Microsoft Family
 * }
 */

var AppMetadataEntity =
/** @class */
function () {
  function AppMetadataEntity() {}
  /**
   * Generate AppMetadata Cache Key as per the schema: appmetadata-<environment>-<client_id>
   */


  AppMetadataEntity.prototype.generateAppMetadataKey = function () {
    return AppMetadataEntity.generateAppMetadataCacheKey(this.environment, this.clientId);
  };
  /**
   * Generate AppMetadata Cache Key
   */


  AppMetadataEntity.generateAppMetadataCacheKey = function (environment, clientId) {
    var appMetaDataKeyArray = [APP_METADATA, environment, clientId];
    return appMetaDataKeyArray.join(Separators.CACHE_KEY_SEPARATOR).toLowerCase();
  };
  /**
   * Creates AppMetadataEntity
   * @param clientId
   * @param environment
   * @param familyId
   */


  AppMetadataEntity.createAppMetadataEntity = function (clientId, environment, familyId) {
    var appMetadata = new AppMetadataEntity();
    appMetadata.clientId = clientId;
    appMetadata.environment = environment;

    if (familyId) {
      appMetadata.familyId = familyId;
    }

    return appMetadata;
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  AppMetadataEntity.isAppMetadataEntity = function (key, entity) {
    if (!entity) {
      return false;
    }

    return key.indexOf(APP_METADATA) === 0 && entity.hasOwnProperty("clientId") && entity.hasOwnProperty("environment");
  };

  return AppMetadataEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * This class instance helps track the memory changes facilitating
 * decisions to read from and write to the persistent cache
 */

var TokenCacheContext =
/** @class */
function () {
  function TokenCacheContext(tokenCache, hasChanged) {
    this.cache = tokenCache;
    this.hasChanged = hasChanged;
  }

  Object.defineProperty(TokenCacheContext.prototype, "cacheHasChanged", {
    /**
     * boolean which indicates the changes in cache
     */
    get: function () {
      return this.hasChanged;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(TokenCacheContext.prototype, "tokenCache", {
    /**
     * function to retrieve the token cache
     */
    get: function () {
      return this.cache;
    },
    enumerable: false,
    configurable: true
  });
  return TokenCacheContext;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Class that handles response parsing.
 */

var ResponseHandler =
/** @class */
function () {
  function ResponseHandler(clientId, cacheStorage, cryptoObj, logger, serializableCache, persistencePlugin) {
    this.clientId = clientId;
    this.cacheStorage = cacheStorage;
    this.cryptoObj = cryptoObj;
    this.logger = logger;
    this.serializableCache = serializableCache;
    this.persistencePlugin = persistencePlugin;
  }
  /**
   * Function which validates server authorization code response.
   * @param serverResponseHash
   * @param cachedState
   * @param cryptoObj
   */


  ResponseHandler.prototype.validateServerAuthorizationCodeResponse = function (serverResponseHash, cachedState, cryptoObj) {
    if (!serverResponseHash.state || !cachedState) {
      throw !serverResponseHash.state ? ClientAuthError.createStateNotFoundError("Server State") : ClientAuthError.createStateNotFoundError("Cached State");
    }

    if (decodeURIComponent(serverResponseHash.state) !== decodeURIComponent(cachedState)) {
      throw ClientAuthError.createStateMismatchError();
    } // Check for error


    if (serverResponseHash.error || serverResponseHash.error_description || serverResponseHash.suberror) {
      if (InteractionRequiredAuthError.isInteractionRequiredError(serverResponseHash.error, serverResponseHash.error_description, serverResponseHash.suberror)) {
        throw new InteractionRequiredAuthError(serverResponseHash.error || Constants.EMPTY_STRING, serverResponseHash.error_description, serverResponseHash.suberror);
      }

      throw new ServerError(serverResponseHash.error || Constants.EMPTY_STRING, serverResponseHash.error_description, serverResponseHash.suberror);
    }

    if (serverResponseHash.client_info) {
      buildClientInfo(serverResponseHash.client_info, cryptoObj);
    }
  };
  /**
   * Function which validates server authorization token response.
   * @param serverResponse
   */


  ResponseHandler.prototype.validateTokenResponse = function (serverResponse) {
    // Check for error
    if (serverResponse.error || serverResponse.error_description || serverResponse.suberror) {
      if (InteractionRequiredAuthError.isInteractionRequiredError(serverResponse.error, serverResponse.error_description, serverResponse.suberror)) {
        throw new InteractionRequiredAuthError(serverResponse.error, serverResponse.error_description, serverResponse.suberror);
      }

      var errString = serverResponse.error_codes + " - [" + serverResponse.timestamp + "]: " + serverResponse.error_description + " - Correlation ID: " + serverResponse.correlation_id + " - Trace ID: " + serverResponse.trace_id;
      throw new ServerError(serverResponse.error, errString, serverResponse.suberror);
    }
  };
  /**
   * Returns a constructed token response based on given string. Also manages the cache updates and cleanups.
   * @param serverTokenResponse
   * @param authority
   */


  ResponseHandler.prototype.handleServerTokenResponse = function (serverTokenResponse, authority, reqTimestamp, request, authCodePayload, userAssertionHash, handlingRefreshTokenResponse, forceCacheRefreshTokenResponse, serverRequestId) {
    return __awaiter(this, void 0, void 0, function () {
      var idTokenObj, authTime, requestStateObj, cacheRecord, cacheContext, key, account;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (serverTokenResponse.id_token) {
              idTokenObj = new AuthToken(serverTokenResponse.id_token || Constants.EMPTY_STRING, this.cryptoObj); // token nonce check (TODO: Add a warning if no nonce is given?)

              if (authCodePayload && !StringUtils.isEmpty(authCodePayload.nonce)) {
                if (idTokenObj.claims.nonce !== authCodePayload.nonce) {
                  throw ClientAuthError.createNonceMismatchError();
                }
              } // token max_age check


              if (request.maxAge || request.maxAge === 0) {
                authTime = idTokenObj.claims.auth_time;

                if (!authTime) {
                  throw ClientAuthError.createAuthTimeNotFoundError();
                }

                AuthToken.checkMaxAge(authTime, request.maxAge);
              }
            } // generate homeAccountId


            this.homeAccountIdentifier = AccountEntity.generateHomeAccountId(serverTokenResponse.client_info || Constants.EMPTY_STRING, authority.authorityType, this.logger, this.cryptoObj, idTokenObj);

            if (!!authCodePayload && !!authCodePayload.state) {
              requestStateObj = ProtocolUtils.parseRequestState(this.cryptoObj, authCodePayload.state);
            } // Add keyId from request to serverTokenResponse if defined


            serverTokenResponse.key_id = serverTokenResponse.key_id || request.sshKid || undefined;
            cacheRecord = this.generateCacheRecord(serverTokenResponse, authority, reqTimestamp, request, idTokenObj, userAssertionHash, authCodePayload);
            _a.label = 1;

          case 1:
            _a.trys.push([1,, 5, 8]);

            if (!(this.persistencePlugin && this.serializableCache)) return [3
            /*break*/
            , 3];
            this.logger.verbose("Persistence enabled, calling beforeCacheAccess");
            cacheContext = new TokenCacheContext(this.serializableCache, true);
            return [4
            /*yield*/
            , this.persistencePlugin.beforeCacheAccess(cacheContext)];

          case 2:
            _a.sent();

            _a.label = 3;

          case 3:
            /*
             * When saving a refreshed tokens to the cache, it is expected that the account that was used is present in the cache.
             * If not present, we should return null, as it's the case that another application called removeAccount in between
             * the calls to getAllAccounts and acquireTokenSilent. We should not overwrite that removal, unless explicitly flagged by
             * the developer, as in the case of refresh token flow used in ADAL Node to MSAL Node migration.
             */
            if (handlingRefreshTokenResponse && !forceCacheRefreshTokenResponse && cacheRecord.account) {
              key = cacheRecord.account.generateAccountKey();
              account = this.cacheStorage.getAccount(key);

              if (!account) {
                this.logger.warning("Account used to refresh tokens not in persistence, refreshed tokens will not be stored in the cache");
                return [2
                /*return*/
                , ResponseHandler.generateAuthenticationResult(this.cryptoObj, authority, cacheRecord, false, request, idTokenObj, requestStateObj, undefined, serverRequestId)];
              }
            }

            return [4
            /*yield*/
            , this.cacheStorage.saveCacheRecord(cacheRecord)];

          case 4:
            _a.sent();

            return [3
            /*break*/
            , 8];

          case 5:
            if (!(this.persistencePlugin && this.serializableCache && cacheContext)) return [3
            /*break*/
            , 7];
            this.logger.verbose("Persistence enabled, calling afterCacheAccess");
            return [4
            /*yield*/
            , this.persistencePlugin.afterCacheAccess(cacheContext)];

          case 6:
            _a.sent();

            _a.label = 7;

          case 7:
            return [7
            /*endfinally*/
            ];

          case 8:
            return [2
            /*return*/
            , ResponseHandler.generateAuthenticationResult(this.cryptoObj, authority, cacheRecord, false, request, idTokenObj, requestStateObj, serverTokenResponse.spa_code, serverRequestId)];
        }
      });
    });
  };
  /**
   * Generates CacheRecord
   * @param serverTokenResponse
   * @param idTokenObj
   * @param authority
   */


  ResponseHandler.prototype.generateCacheRecord = function (serverTokenResponse, authority, reqTimestamp, request, idTokenObj, userAssertionHash, authCodePayload) {
    var env = authority.getPreferredCache();

    if (StringUtils.isEmpty(env)) {
      throw ClientAuthError.createInvalidCacheEnvironmentError();
    } // IdToken: non AAD scenarios can have empty realm


    var cachedIdToken;
    var cachedAccount;

    if (!StringUtils.isEmpty(serverTokenResponse.id_token) && !!idTokenObj) {
      cachedIdToken = IdTokenEntity.createIdTokenEntity(this.homeAccountIdentifier, env, serverTokenResponse.id_token || Constants.EMPTY_STRING, this.clientId, idTokenObj.claims.tid || Constants.EMPTY_STRING);
      cachedAccount = this.generateAccountEntity(serverTokenResponse, idTokenObj, authority, authCodePayload);
    } // AccessToken


    var cachedAccessToken = null;

    if (!StringUtils.isEmpty(serverTokenResponse.access_token)) {
      // If scopes not returned in server response, use request scopes
      var responseScopes = serverTokenResponse.scope ? ScopeSet.fromString(serverTokenResponse.scope) : new ScopeSet(request.scopes || []);
      /*
       * Use timestamp calculated before request
       * Server may return timestamps as strings, parse to numbers if so.
       */

      var expiresIn = (typeof serverTokenResponse.expires_in === "string" ? parseInt(serverTokenResponse.expires_in, 10) : serverTokenResponse.expires_in) || 0;
      var extExpiresIn = (typeof serverTokenResponse.ext_expires_in === "string" ? parseInt(serverTokenResponse.ext_expires_in, 10) : serverTokenResponse.ext_expires_in) || 0;
      var refreshIn = (typeof serverTokenResponse.refresh_in === "string" ? parseInt(serverTokenResponse.refresh_in, 10) : serverTokenResponse.refresh_in) || undefined;
      var tokenExpirationSeconds = reqTimestamp + expiresIn;
      var extendedTokenExpirationSeconds = tokenExpirationSeconds + extExpiresIn;
      var refreshOnSeconds = refreshIn && refreshIn > 0 ? reqTimestamp + refreshIn : undefined; // non AAD scenarios can have empty realm

      cachedAccessToken = AccessTokenEntity.createAccessTokenEntity(this.homeAccountIdentifier, env, serverTokenResponse.access_token || Constants.EMPTY_STRING, this.clientId, idTokenObj ? idTokenObj.claims.tid || Constants.EMPTY_STRING : authority.tenant, responseScopes.printScopes(), tokenExpirationSeconds, extendedTokenExpirationSeconds, this.cryptoObj, refreshOnSeconds, serverTokenResponse.token_type, userAssertionHash, serverTokenResponse.key_id, request.claims, request.requestedClaimsHash);
    } // refreshToken


    var cachedRefreshToken = null;

    if (!StringUtils.isEmpty(serverTokenResponse.refresh_token)) {
      cachedRefreshToken = RefreshTokenEntity.createRefreshTokenEntity(this.homeAccountIdentifier, env, serverTokenResponse.refresh_token || Constants.EMPTY_STRING, this.clientId, serverTokenResponse.foci, userAssertionHash);
    } // appMetadata


    var cachedAppMetadata = null;

    if (!StringUtils.isEmpty(serverTokenResponse.foci)) {
      cachedAppMetadata = AppMetadataEntity.createAppMetadataEntity(this.clientId, env, serverTokenResponse.foci);
    }

    return new CacheRecord(cachedAccount, cachedIdToken, cachedAccessToken, cachedRefreshToken, cachedAppMetadata);
  };
  /**
   * Generate Account
   * @param serverTokenResponse
   * @param idToken
   * @param authority
   */


  ResponseHandler.prototype.generateAccountEntity = function (serverTokenResponse, idToken, authority, authCodePayload) {
    var authorityType = authority.authorityType;
    var cloudGraphHostName = authCodePayload ? authCodePayload.cloud_graph_host_name : Constants.EMPTY_STRING;
    var msGraphhost = authCodePayload ? authCodePayload.msgraph_host : Constants.EMPTY_STRING; // ADFS does not require client_info in the response

    if (authorityType === AuthorityType.Adfs) {
      this.logger.verbose("Authority type is ADFS, creating ADFS account");
      return AccountEntity.createGenericAccount(this.homeAccountIdentifier, idToken, authority, cloudGraphHostName, msGraphhost);
    } // This fallback applies to B2C as well as they fall under an AAD account type.


    if (StringUtils.isEmpty(serverTokenResponse.client_info) && authority.protocolMode === "AAD") {
      throw ClientAuthError.createClientInfoEmptyError();
    }

    return serverTokenResponse.client_info ? AccountEntity.createAccount(serverTokenResponse.client_info, this.homeAccountIdentifier, idToken, authority, cloudGraphHostName, msGraphhost) : AccountEntity.createGenericAccount(this.homeAccountIdentifier, idToken, authority, cloudGraphHostName, msGraphhost);
  };
  /**
   * Creates an @AuthenticationResult from @CacheRecord , @IdToken , and a boolean that states whether or not the result is from cache.
   *
   * Optionally takes a state string that is set as-is in the response.
   *
   * @param cacheRecord
   * @param idTokenObj
   * @param fromTokenCache
   * @param stateString
   */


  ResponseHandler.generateAuthenticationResult = function (cryptoObj, authority, cacheRecord, fromTokenCache, request, idTokenObj, requestState, code, requestId) {
    var _a, _b, _c;

    return __awaiter(this, void 0, void 0, function () {
      var accessToken, responseScopes, expiresOn, extExpiresOn, familyId, popTokenGenerator, _d, secret, keyId, uid, tid;

      return __generator(this, function (_e) {
        switch (_e.label) {
          case 0:
            accessToken = Constants.EMPTY_STRING;
            responseScopes = [];
            expiresOn = null;
            familyId = Constants.EMPTY_STRING;
            if (!cacheRecord.accessToken) return [3
            /*break*/
            , 4];
            if (!(cacheRecord.accessToken.tokenType === AuthenticationScheme.POP)) return [3
            /*break*/
            , 2];
            popTokenGenerator = new PopTokenGenerator(cryptoObj);
            _d = cacheRecord.accessToken, secret = _d.secret, keyId = _d.keyId;

            if (!keyId) {
              throw ClientAuthError.createKeyIdMissingError();
            }

            return [4
            /*yield*/
            , popTokenGenerator.signPopToken(secret, keyId, request)];

          case 1:
            accessToken = _e.sent();
            return [3
            /*break*/
            , 3];

          case 2:
            accessToken = cacheRecord.accessToken.secret;
            _e.label = 3;

          case 3:
            responseScopes = ScopeSet.fromString(cacheRecord.accessToken.target).asArray();
            expiresOn = new Date(Number(cacheRecord.accessToken.expiresOn) * 1000);
            extExpiresOn = new Date(Number(cacheRecord.accessToken.extendedExpiresOn) * 1000);
            _e.label = 4;

          case 4:
            if (cacheRecord.appMetadata) {
              familyId = cacheRecord.appMetadata.familyId === THE_FAMILY_ID ? THE_FAMILY_ID : Constants.EMPTY_STRING;
            }

            uid = (idTokenObj === null || idTokenObj === void 0 ? void 0 : idTokenObj.claims.oid) || (idTokenObj === null || idTokenObj === void 0 ? void 0 : idTokenObj.claims.sub) || Constants.EMPTY_STRING;
            tid = (idTokenObj === null || idTokenObj === void 0 ? void 0 : idTokenObj.claims.tid) || Constants.EMPTY_STRING;
            return [2
            /*return*/
            , {
              authority: authority.canonicalAuthority,
              uniqueId: uid,
              tenantId: tid,
              scopes: responseScopes,
              account: cacheRecord.account ? cacheRecord.account.getAccountInfo() : null,
              idToken: idTokenObj ? idTokenObj.rawToken : Constants.EMPTY_STRING,
              idTokenClaims: idTokenObj ? idTokenObj.claims : {},
              accessToken: accessToken,
              fromCache: fromTokenCache,
              expiresOn: expiresOn,
              correlationId: request.correlationId,
              requestId: requestId || Constants.EMPTY_STRING,
              extExpiresOn: extExpiresOn,
              familyId: familyId,
              tokenType: ((_a = cacheRecord.accessToken) === null || _a === void 0 ? void 0 : _a.tokenType) || Constants.EMPTY_STRING,
              state: requestState ? requestState.userRequestState : Constants.EMPTY_STRING,
              cloudGraphHostName: ((_b = cacheRecord.account) === null || _b === void 0 ? void 0 : _b.cloudGraphHostName) || Constants.EMPTY_STRING,
              msGraphHost: ((_c = cacheRecord.account) === null || _c === void 0 ? void 0 : _c.msGraphHost) || Constants.EMPTY_STRING,
              code: code,
              fromNativeBroker: false
            }];
        }
      });
    });
  };

  return ResponseHandler;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Oauth2.0 Authorization Code client
 */

var AuthorizationCodeClient =
/** @class */
function (_super) {
  __extends(AuthorizationCodeClient, _super);

  function AuthorizationCodeClient(configuration) {
    var _this = _super.call(this, configuration) || this; // Flag to indicate if client is for hybrid spa auth code redemption


    _this.includeRedirectUri = true;
    return _this;
  }
  /**
   * Creates the URL of the authorization request letting the user input credentials and consent to the
   * application. The URL target the /authorize endpoint of the authority configured in the
   * application object.
   *
   * Once the user inputs their credentials and consents, the authority will send a response to the redirect URI
   * sent in the request and should contain an authorization code, which can then be used to acquire tokens via
   * acquireToken(AuthorizationCodeRequest)
   * @param request
   */


  AuthorizationCodeClient.prototype.getAuthCodeUrl = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var queryString;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.createAuthCodeUrlQueryString(request)];

          case 1:
            queryString = _a.sent();
            return [2
            /*return*/
            , UrlString.appendQueryString(this.authority.authorizationEndpoint, queryString)];
        }
      });
    });
  };
  /**
   * API to acquire a token in exchange of 'authorization_code` acquired by the user in the first leg of the
   * authorization_code_grant
   * @param request
   */


  AuthorizationCodeClient.prototype.acquireToken = function (request, authCodePayload) {
    var _a;

    return __awaiter(this, void 0, void 0, function () {
      var reqTimestamp, response, requestId, responseHandler;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            this.logger.info("in acquireToken call");

            if (!request || StringUtils.isEmpty(request.code)) {
              throw ClientAuthError.createTokenRequestCannotBeMadeError();
            }

            reqTimestamp = TimeUtils.nowSeconds();
            return [4
            /*yield*/
            , this.executeTokenRequest(this.authority, request)];

          case 1:
            response = _b.sent();
            requestId = (_a = response.headers) === null || _a === void 0 ? void 0 : _a[HeaderNames.X_MS_REQUEST_ID];
            responseHandler = new ResponseHandler(this.config.authOptions.clientId, this.cacheManager, this.cryptoUtils, this.logger, this.config.serializableCache, this.config.persistencePlugin); // Validate response. This function throws a server error if an error is returned by the server.

            responseHandler.validateTokenResponse(response.body);
            return [4
            /*yield*/
            , responseHandler.handleServerTokenResponse(response.body, this.authority, reqTimestamp, request, authCodePayload, undefined, undefined, undefined, requestId)];

          case 2:
            return [2
            /*return*/
            , _b.sent()];
        }
      });
    });
  };
  /**
   * Handles the hash fragment response from public client code request. Returns a code response used by
   * the client to exchange for a token in acquireToken.
   * @param hashFragment
   */


  AuthorizationCodeClient.prototype.handleFragmentResponse = function (hashFragment, cachedState) {
    // Handle responses.
    var responseHandler = new ResponseHandler(this.config.authOptions.clientId, this.cacheManager, this.cryptoUtils, this.logger, null, null); // Deserialize hash fragment response parameters.

    var hashUrlString = new UrlString(hashFragment); // Deserialize hash fragment response parameters.

    var serverParams = UrlString.getDeserializedHash(hashUrlString.getHash()); // Get code response

    responseHandler.validateServerAuthorizationCodeResponse(serverParams, cachedState, this.cryptoUtils); // throw when there is no auth code in the response

    if (!serverParams.code) {
      throw ClientAuthError.createNoAuthCodeInServerResponseError();
    }

    return __assign(__assign({}, serverParams), {
      // Code param is optional in ServerAuthorizationCodeResponse but required in AuthorizationCodePaylod
      code: serverParams.code
    });
  };
  /**
   * Used to log out the current user, and redirect the user to the postLogoutRedirectUri.
   * Default behaviour is to redirect the user to `window.location.href`.
   * @param authorityUri
   */


  AuthorizationCodeClient.prototype.getLogoutUri = function (logoutRequest) {
    // Throw error if logoutRequest is null/undefined
    if (!logoutRequest) {
      throw ClientConfigurationError.createEmptyLogoutRequestError();
    }

    var queryString = this.createLogoutUrlQueryString(logoutRequest); // Construct logout URI

    return UrlString.appendQueryString(this.authority.endSessionEndpoint, queryString);
  };
  /**
   * Executes POST request to token endpoint
   * @param authority
   * @param request
   */


  AuthorizationCodeClient.prototype.executeTokenRequest = function (authority, request) {
    return __awaiter(this, void 0, void 0, function () {
      var thumbprint, requestBody, queryParameters, ccsCredential, clientInfo, headers, endpoint;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            thumbprint = {
              clientId: this.config.authOptions.clientId,
              authority: authority.canonicalAuthority,
              scopes: request.scopes,
              claims: request.claims,
              authenticationScheme: request.authenticationScheme,
              resourceRequestMethod: request.resourceRequestMethod,
              resourceRequestUri: request.resourceRequestUri,
              shrClaims: request.shrClaims,
              sshKid: request.sshKid
            };
            return [4
            /*yield*/
            , this.createTokenRequestBody(request)];

          case 1:
            requestBody = _a.sent();
            queryParameters = this.createTokenQueryParameters(request);
            ccsCredential = undefined;

            if (request.clientInfo) {
              try {
                clientInfo = buildClientInfo(request.clientInfo, this.cryptoUtils);
                ccsCredential = {
                  credential: "" + clientInfo.uid + Separators.CLIENT_INFO_SEPARATOR + clientInfo.utid,
                  type: CcsCredentialType.HOME_ACCOUNT_ID
                };
              } catch (e) {
                this.logger.verbose("Could not parse client info for CCS Header: " + e);
              }
            }

            headers = this.createTokenRequestHeaders(ccsCredential || request.ccsCredential);
            endpoint = StringUtils.isEmpty(queryParameters) ? authority.tokenEndpoint : authority.tokenEndpoint + "?" + queryParameters;
            return [2
            /*return*/
            , this.executePostToTokenEndpoint(endpoint, requestBody, headers, thumbprint)];
        }
      });
    });
  };
  /**
   * Creates query string for the /token request
   * @param request
   */


  AuthorizationCodeClient.prototype.createTokenQueryParameters = function (request) {
    var parameterBuilder = new RequestParameterBuilder();

    if (request.tokenQueryParameters) {
      parameterBuilder.addExtraQueryParameters(request.tokenQueryParameters);
    }

    return parameterBuilder.createQueryString();
  };
  /**
   * Generates a map for all the params to be sent to the service
   * @param request
   */


  AuthorizationCodeClient.prototype.createTokenRequestBody = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var parameterBuilder, clientAssertion, popTokenGenerator, reqCnfData, correlationId, ccsCred, clientInfo, clientInfo;

      var _a;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            parameterBuilder = new RequestParameterBuilder();
            parameterBuilder.addClientId(this.config.authOptions.clientId);
            /*
             * For hybrid spa flow, there will be a code but no verifier
             * In this scenario, don't include redirect uri as auth code will not be bound to redirect URI
             */

            if (!this.includeRedirectUri) {
              // Just validate
              RequestValidator.validateRedirectUri(request.redirectUri);
            } else {
              // Validate and include redirect uri
              parameterBuilder.addRedirectUri(request.redirectUri);
            } // Add scope array, parameter builder will add default scopes and dedupe


            parameterBuilder.addScopes(request.scopes); // add code: user set, not validated

            parameterBuilder.addAuthorizationCode(request.code); // Add library metadata

            parameterBuilder.addLibraryInfo(this.config.libraryInfo);
            parameterBuilder.addApplicationTelemetry(this.config.telemetry.application);
            parameterBuilder.addThrottling();

            if (this.serverTelemetryManager) {
              parameterBuilder.addServerTelemetry(this.serverTelemetryManager);
            } // add code_verifier if passed


            if (request.codeVerifier) {
              parameterBuilder.addCodeVerifier(request.codeVerifier);
            }

            if (this.config.clientCredentials.clientSecret) {
              parameterBuilder.addClientSecret(this.config.clientCredentials.clientSecret);
            }

            if (this.config.clientCredentials.clientAssertion) {
              clientAssertion = this.config.clientCredentials.clientAssertion;
              parameterBuilder.addClientAssertion(clientAssertion.assertion);
              parameterBuilder.addClientAssertionType(clientAssertion.assertionType);
            }

            parameterBuilder.addGrantType(GrantType.AUTHORIZATION_CODE_GRANT);
            parameterBuilder.addClientInfo();
            if (!(request.authenticationScheme === AuthenticationScheme.POP)) return [3
            /*break*/
            , 2];
            popTokenGenerator = new PopTokenGenerator(this.cryptoUtils);
            return [4
            /*yield*/
            , popTokenGenerator.generateCnf(request)];

          case 1:
            reqCnfData = _b.sent(); // SPA PoP requires full Base64Url encoded req_cnf string (unhashed)

            parameterBuilder.addPopToken(reqCnfData.reqCnfString);
            return [3
            /*break*/
            , 3];

          case 2:
            if (request.authenticationScheme === AuthenticationScheme.SSH) {
              if (request.sshJwk) {
                parameterBuilder.addSshJwk(request.sshJwk);
              } else {
                throw ClientConfigurationError.createMissingSshJwkError();
              }
            }

            _b.label = 3;

          case 3:
            correlationId = request.correlationId || this.config.cryptoInterface.createNewGuid();
            parameterBuilder.addCorrelationId(correlationId);

            if (!StringUtils.isEmptyObj(request.claims) || this.config.authOptions.clientCapabilities && this.config.authOptions.clientCapabilities.length > 0) {
              parameterBuilder.addClaims(request.claims, this.config.authOptions.clientCapabilities);
            }

            ccsCred = undefined;

            if (request.clientInfo) {
              try {
                clientInfo = buildClientInfo(request.clientInfo, this.cryptoUtils);
                ccsCred = {
                  credential: "" + clientInfo.uid + Separators.CLIENT_INFO_SEPARATOR + clientInfo.utid,
                  type: CcsCredentialType.HOME_ACCOUNT_ID
                };
              } catch (e) {
                this.logger.verbose("Could not parse client info for CCS Header: " + e);
              }
            } else {
              ccsCred = request.ccsCredential;
            } // Adds these as parameters in the request instead of headers to prevent CORS preflight request


            if (this.config.systemOptions.preventCorsPreflight && ccsCred) {
              switch (ccsCred.type) {
                case CcsCredentialType.HOME_ACCOUNT_ID:
                  try {
                    clientInfo = buildClientInfoFromHomeAccountId(ccsCred.credential);
                    parameterBuilder.addCcsOid(clientInfo);
                  } catch (e) {
                    this.logger.verbose("Could not parse home account ID for CCS Header: " + e);
                  }

                  break;

                case CcsCredentialType.UPN:
                  parameterBuilder.addCcsUpn(ccsCred.credential);
                  break;
              }
            }

            if (request.tokenBodyParameters) {
              parameterBuilder.addExtraQueryParameters(request.tokenBodyParameters);
            } // Add hybrid spa parameters if not already provided


            if (request.enableSpaAuthorizationCode && (!request.tokenBodyParameters || !request.tokenBodyParameters[AADServerParamKeys.RETURN_SPA_CODE])) {
              parameterBuilder.addExtraQueryParameters((_a = {}, _a[AADServerParamKeys.RETURN_SPA_CODE] = "1", _a));
            }

            return [2
            /*return*/
            , parameterBuilder.createQueryString()];
        }
      });
    });
  };
  /**
   * This API validates the `AuthorizationCodeUrlRequest` and creates a URL
   * @param request
   */


  AuthorizationCodeClient.prototype.createAuthCodeUrlQueryString = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var parameterBuilder, requestScopes, correlationId, accountSid, accountLoginHintClaim, clientInfo, clientInfo, clientInfo, popTokenGenerator, reqCnfData;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            parameterBuilder = new RequestParameterBuilder();
            parameterBuilder.addClientId(this.config.authOptions.clientId);
            requestScopes = __spreadArrays(request.scopes || [], request.extraScopesToConsent || []);
            parameterBuilder.addScopes(requestScopes); // validate the redirectUri (to be a non null value)

            parameterBuilder.addRedirectUri(request.redirectUri);
            correlationId = request.correlationId || this.config.cryptoInterface.createNewGuid();
            parameterBuilder.addCorrelationId(correlationId); // add response_mode. If not passed in it defaults to query.

            parameterBuilder.addResponseMode(request.responseMode); // add response_type = code

            parameterBuilder.addResponseTypeCode(); // add library info parameters

            parameterBuilder.addLibraryInfo(this.config.libraryInfo);
            parameterBuilder.addApplicationTelemetry(this.config.telemetry.application); // add client_info=1

            parameterBuilder.addClientInfo();

            if (request.codeChallenge && request.codeChallengeMethod) {
              parameterBuilder.addCodeChallengeParams(request.codeChallenge, request.codeChallengeMethod);
            }

            if (request.prompt) {
              parameterBuilder.addPrompt(request.prompt);
            }

            if (request.domainHint) {
              parameterBuilder.addDomainHint(request.domainHint);
            } // Add sid or loginHint with preference for login_hint claim (in request) -> sid -> loginHint (upn/email) -> username of AccountInfo object


            if (request.prompt !== PromptValue.SELECT_ACCOUNT) {
              // AAD will throw if prompt=select_account is passed with an account hint
              if (request.sid && request.prompt === PromptValue.NONE) {
                // SessionID is only used in silent calls
                this.logger.verbose("createAuthCodeUrlQueryString: Prompt is none, adding sid from request");
                parameterBuilder.addSid(request.sid);
              } else if (request.account) {
                accountSid = this.extractAccountSid(request.account);
                accountLoginHintClaim = this.extractLoginHint(request.account); // If login_hint claim is present, use it over sid/username

                if (accountLoginHintClaim) {
                  this.logger.verbose("createAuthCodeUrlQueryString: login_hint claim present on account");
                  parameterBuilder.addLoginHint(accountLoginHintClaim);

                  try {
                    clientInfo = buildClientInfoFromHomeAccountId(request.account.homeAccountId);
                    parameterBuilder.addCcsOid(clientInfo);
                  } catch (e) {
                    this.logger.verbose("createAuthCodeUrlQueryString: Could not parse home account ID for CCS Header");
                  }
                } else if (accountSid && request.prompt === PromptValue.NONE) {
                  /*
                   * If account and loginHint are provided, we will check account first for sid before adding loginHint
                   * SessionId is only used in silent calls
                   */
                  this.logger.verbose("createAuthCodeUrlQueryString: Prompt is none, adding sid from account");
                  parameterBuilder.addSid(accountSid);

                  try {
                    clientInfo = buildClientInfoFromHomeAccountId(request.account.homeAccountId);
                    parameterBuilder.addCcsOid(clientInfo);
                  } catch (e) {
                    this.logger.verbose("createAuthCodeUrlQueryString: Could not parse home account ID for CCS Header");
                  }
                } else if (request.loginHint) {
                  this.logger.verbose("createAuthCodeUrlQueryString: Adding login_hint from request");
                  parameterBuilder.addLoginHint(request.loginHint);
                  parameterBuilder.addCcsUpn(request.loginHint);
                } else if (request.account.username) {
                  // Fallback to account username if provided
                  this.logger.verbose("createAuthCodeUrlQueryString: Adding login_hint from account");
                  parameterBuilder.addLoginHint(request.account.username);

                  try {
                    clientInfo = buildClientInfoFromHomeAccountId(request.account.homeAccountId);
                    parameterBuilder.addCcsOid(clientInfo);
                  } catch (e) {
                    this.logger.verbose("createAuthCodeUrlQueryString: Could not parse home account ID for CCS Header");
                  }
                }
              } else if (request.loginHint) {
                this.logger.verbose("createAuthCodeUrlQueryString: No account, adding login_hint from request");
                parameterBuilder.addLoginHint(request.loginHint);
                parameterBuilder.addCcsUpn(request.loginHint);
              }
            } else {
              this.logger.verbose("createAuthCodeUrlQueryString: Prompt is select_account, ignoring account hints");
            }

            if (request.nonce) {
              parameterBuilder.addNonce(request.nonce);
            }

            if (request.state) {
              parameterBuilder.addState(request.state);
            }

            if (!StringUtils.isEmpty(request.claims) || this.config.authOptions.clientCapabilities && this.config.authOptions.clientCapabilities.length > 0) {
              parameterBuilder.addClaims(request.claims, this.config.authOptions.clientCapabilities);
            }

            if (request.extraQueryParameters) {
              parameterBuilder.addExtraQueryParameters(request.extraQueryParameters);
            }

            if (!request.nativeBroker) return [3
            /*break*/
            , 2]; // signal ests that this is a WAM call

            parameterBuilder.addNativeBroker();
            if (!(request.authenticationScheme === AuthenticationScheme.POP)) return [3
            /*break*/
            , 2];
            popTokenGenerator = new PopTokenGenerator(this.cryptoUtils);
            return [4
            /*yield*/
            , popTokenGenerator.generateCnf(request)];

          case 1:
            reqCnfData = _a.sent();
            parameterBuilder.addPopToken(reqCnfData.reqCnfHash);
            _a.label = 2;

          case 2:
            return [2
            /*return*/
            , parameterBuilder.createQueryString()];
        }
      });
    });
  };
  /**
   * This API validates the `EndSessionRequest` and creates a URL
   * @param request
   */


  AuthorizationCodeClient.prototype.createLogoutUrlQueryString = function (request) {
    var parameterBuilder = new RequestParameterBuilder();

    if (request.postLogoutRedirectUri) {
      parameterBuilder.addPostLogoutRedirectUri(request.postLogoutRedirectUri);
    }

    if (request.correlationId) {
      parameterBuilder.addCorrelationId(request.correlationId);
    }

    if (request.idTokenHint) {
      parameterBuilder.addIdTokenHint(request.idTokenHint);
    }

    if (request.state) {
      parameterBuilder.addState(request.state);
    }

    if (request.logoutHint) {
      parameterBuilder.addLogoutHint(request.logoutHint);
    }

    if (request.extraQueryParameters) {
      parameterBuilder.addExtraQueryParameters(request.extraQueryParameters);
    }

    return parameterBuilder.createQueryString();
  };
  /**
   * Helper to get sid from account. Returns null if idTokenClaims are not present or sid is not present.
   * @param account
   */


  AuthorizationCodeClient.prototype.extractAccountSid = function (account) {
    var _a;

    return ((_a = account.idTokenClaims) === null || _a === void 0 ? void 0 : _a.sid) || null;
  };

  AuthorizationCodeClient.prototype.extractLoginHint = function (account) {
    var _a;

    return ((_a = account.idTokenClaims) === null || _a === void 0 ? void 0 : _a.login_hint) || null;
  };

  return AuthorizationCodeClient;
}(BaseClient);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Enumeration of operations that are instrumented by have their performance measured by the PerformanceClient.
 *
 * @export
 * @enum {number}
 */

var PerformanceEvents;

(function (PerformanceEvents) {
  /**
   * acquireTokenByCode API (msal-browser and msal-node).
   * Used to acquire tokens by trading an authorization code against the token endpoint.
   */
  PerformanceEvents["AcquireTokenByCode"] = "acquireTokenByCode";
  /**
   * acquireTokenByRefreshToken API (msal-browser and msal-node).
   * Used to renew an access token using a refresh token against the token endpoint.
   */

  PerformanceEvents["AcquireTokenByRefreshToken"] = "acquireTokenByRefreshToken";
  /**
   * acquireTokenSilent API (msal-browser and msal-node).
   * Used to silently acquire a new access token (from the cache or the network).
   */

  PerformanceEvents["AcquireTokenSilent"] = "acquireTokenSilent";
  /**
   * acquireTokenSilentAsync (msal-browser).
   * Internal API for acquireTokenSilent.
   */

  PerformanceEvents["AcquireTokenSilentAsync"] = "acquireTokenSilentAsync";
  /**
   * acquireTokenPopup (msal-browser).
   * Used to acquire a new access token interactively through pop ups
   */

  PerformanceEvents["AcquireTokenPopup"] = "acquireTokenPopup";
  /**
   * getPublicKeyThumbprint API in CryptoOpts class (msal-browser).
   * Used to generate a public/private keypair and generate a public key thumbprint for pop requests.
   */

  PerformanceEvents["CryptoOptsGetPublicKeyThumbprint"] = "cryptoOptsGetPublicKeyThumbprint";
  /**
   * signJwt API in CryptoOpts class (msal-browser).
   * Used to signed a pop token.
   */

  PerformanceEvents["CryptoOptsSignJwt"] = "cryptoOptsSignJwt";
  /**
   * acquireToken API in the SilentCacheClient class (msal-browser).
   * Used to read access tokens from the cache.
   */

  PerformanceEvents["SilentCacheClientAcquireToken"] = "silentCacheClientAcquireToken";
  /**
   * acquireToken API in the SilentIframeClient class (msal-browser).
   * Used to acquire a new set of tokens from the authorize endpoint in a hidden iframe.
   */

  PerformanceEvents["SilentIframeClientAcquireToken"] = "silentIframeClientAcquireToken";
  /**
   * acquireToken API in SilentRereshClient (msal-browser).
   * Used to acquire a new set of tokens from the token endpoint using a refresh token.
   */

  PerformanceEvents["SilentRefreshClientAcquireToken"] = "silentRefreshClientAcquireToken";
  /**
   * ssoSilent API (msal-browser).
   * Used to silently acquire an authorization code and set of tokens using a hidden iframe.
   */

  PerformanceEvents["SsoSilent"] = "ssoSilent";
  /**
   * getDiscoveredAuthority API in StandardInteractionClient class (msal-browser).
   * Used to load authority metadata for a request.
   */

  PerformanceEvents["StandardInteractionClientGetDiscoveredAuthority"] = "standardInteractionClientGetDiscoveredAuthority";
  /**
   * acquireToken APIs in msal-browser.
   * Used to make an /authorize endpoint call with native brokering enabled.
   */

  PerformanceEvents["FetchAccountIdWithNativeBroker"] = "fetchAccountIdWithNativeBroker";
  /**
   * acquireToken API in NativeInteractionClient class (msal-browser).
   * Used to acquire a token from Native component when native brokering is enabled.
   */

  PerformanceEvents["NativeInteractionClientAcquireToken"] = "nativeInteractionClientAcquireToken";
  /**
   * Time spent creating default headers for requests to token endpoint
   */

  PerformanceEvents["BaseClientCreateTokenRequestHeaders"] = "baseClientCreateTokenRequestHeaders";
  /**
   * Used to measure the time taken for completing embedded-broker handshake (PW-Broker).
   */

  PerformanceEvents["BrokerHandhshake"] = "brokerHandshake";
  /**
   * acquireTokenByRefreshToken API in BrokerClientApplication (PW-Broker) .
   */

  PerformanceEvents["AcquireTokenByRefreshTokenInBroker"] = "acquireTokenByRefreshTokenInBroker";
  /**
   * Time taken for token acquisition by broker
   */

  PerformanceEvents["AcquireTokenByBroker"] = "acquireTokenByBroker";
  /**
   * Time spent on the network for refresh token acquisition
   */

  PerformanceEvents["RefreshTokenClientExecuteTokenRequest"] = "refreshTokenClientExecuteTokenRequest";
  /**
   * Time taken for acquiring refresh token , records RT size
   */

  PerformanceEvents["RefreshTokenClientAcquireToken"] = "refreshTokenClientAcquireToken";
  /**
   * Time taken for acquiring cached refresh token
   */

  PerformanceEvents["RefreshTokenClientAcquireTokenWithCachedRefreshToken"] = "refreshTokenClientAcquireTokenWithCachedRefreshToken";
})(PerformanceEvents || (PerformanceEvents = {}));
/**
 * State of the performance event.
 *
 * @export
 * @enum {number}
 */


var PerformanceEventStatus;

(function (PerformanceEventStatus) {
  PerformanceEventStatus[PerformanceEventStatus["NotStarted"] = 0] = "NotStarted";
  PerformanceEventStatus[PerformanceEventStatus["InProgress"] = 1] = "InProgress";
  PerformanceEventStatus[PerformanceEventStatus["Completed"] = 2] = "Completed";
})(PerformanceEventStatus || (PerformanceEventStatus = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * OAuth2.0 refresh token client
 */

var RefreshTokenClient =
/** @class */
function (_super) {
  __extends(RefreshTokenClient, _super);

  function RefreshTokenClient(configuration, performanceClient) {
    return _super.call(this, configuration, performanceClient) || this;
  }

  RefreshTokenClient.prototype.acquireToken = function (request) {
    var _a, _b;

    return __awaiter(this, void 0, void 0, function () {
      var atsMeasurement, reqTimestamp, response, requestId, responseHandler;

      var _this = this;

      return __generator(this, function (_c) {
        switch (_c.label) {
          case 0:
            atsMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.RefreshTokenClientAcquireToken, request.correlationId);
            this.logger.verbose("RefreshTokenClientAcquireToken called", request.correlationId);
            reqTimestamp = TimeUtils.nowSeconds();
            return [4
            /*yield*/
            , this.executeTokenRequest(request, this.authority)];

          case 1:
            response = _c.sent();
            requestId = (_b = response.headers) === null || _b === void 0 ? void 0 : _b[HeaderNames.X_MS_REQUEST_ID];
            responseHandler = new ResponseHandler(this.config.authOptions.clientId, this.cacheManager, this.cryptoUtils, this.logger, this.config.serializableCache, this.config.persistencePlugin);
            responseHandler.validateTokenResponse(response.body);
            return [2
            /*return*/
            , responseHandler.handleServerTokenResponse(response.body, this.authority, reqTimestamp, request, undefined, undefined, true, request.forceCache, requestId).then(function (result) {
              var _a;

              atsMeasurement === null || atsMeasurement === void 0 ? void 0 : atsMeasurement.endMeasurement({
                success: true,
                refreshTokenSize: ((_a = response.body.refresh_token) === null || _a === void 0 ? void 0 : _a.length) || 0
              });
              return result;
            }).catch(function (error) {
              _this.logger.verbose("Error in fetching refresh token", request.correlationId);

              atsMeasurement === null || atsMeasurement === void 0 ? void 0 : atsMeasurement.endMeasurement({
                errorCode: error.errorCode,
                subErrorCode: error.subError,
                success: false,
                refreshTokenSize: undefined
              });
              throw error;
            })];
        }
      });
    });
  };
  /**
   * Gets cached refresh token and attaches to request, then calls acquireToken API
   * @param request
   */


  RefreshTokenClient.prototype.acquireTokenByRefreshToken = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var isFOCI, noFamilyRTInCache, clientMismatchErrorWithFamilyRT;
      return __generator(this, function (_a) {
        // Cannot renew token if no request object is given.
        if (!request) {
          throw ClientConfigurationError.createEmptyTokenRequestError();
        } // We currently do not support silent flow for account === null use cases; This will be revisited for confidential flow usecases


        if (!request.account) {
          throw ClientAuthError.createNoAccountInSilentRequestError();
        }

        isFOCI = this.cacheManager.isAppMetadataFOCI(request.account.environment, this.config.authOptions.clientId); // if the app is part of the family, retrive a Family refresh token if present and make a refreshTokenRequest

        if (isFOCI) {
          try {
            return [2
            /*return*/
            , this.acquireTokenWithCachedRefreshToken(request, true)];
          } catch (e) {
            noFamilyRTInCache = e instanceof InteractionRequiredAuthError && e.errorCode === InteractionRequiredAuthErrorMessage.noTokensFoundError.code;
            clientMismatchErrorWithFamilyRT = e instanceof ServerError && e.errorCode === Errors.INVALID_GRANT_ERROR && e.subError === Errors.CLIENT_MISMATCH_ERROR; // if family Refresh Token (FRT) cache acquisition fails or if client_mismatch error is seen with FRT, reattempt with application Refresh Token (ART)

            if (noFamilyRTInCache || clientMismatchErrorWithFamilyRT) {
              return [2
              /*return*/
              , this.acquireTokenWithCachedRefreshToken(request, false)]; // throw in all other cases
            } else {
              throw e;
            }
          }
        } // fall back to application refresh token acquisition


        return [2
        /*return*/
        , this.acquireTokenWithCachedRefreshToken(request, false)];
      });
    });
  };
  /**
   * makes a network call to acquire tokens by exchanging RefreshToken available in userCache; throws if refresh token is not cached
   * @param request
   */


  RefreshTokenClient.prototype.acquireTokenWithCachedRefreshToken = function (request, foci) {
    var _a;

    return __awaiter(this, void 0, void 0, function () {
      var atsMeasurement, refreshToken, refreshTokenRequest;
      return __generator(this, function (_b) {
        atsMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.RefreshTokenClientAcquireTokenWithCachedRefreshToken, request.correlationId);
        this.logger.verbose("RefreshTokenClientAcquireTokenWithCachedRefreshToken called", request.correlationId);
        refreshToken = this.cacheManager.readRefreshTokenFromCache(this.config.authOptions.clientId, request.account, foci);

        if (!refreshToken) {
          atsMeasurement === null || atsMeasurement === void 0 ? void 0 : atsMeasurement.discardMeasurement();
          throw InteractionRequiredAuthError.createNoTokensFoundError();
        } // attach cached RT size to the current measurement


        atsMeasurement === null || atsMeasurement === void 0 ? void 0 : atsMeasurement.endMeasurement({
          success: true
        });
        refreshTokenRequest = __assign(__assign({}, request), {
          refreshToken: refreshToken.secret,
          authenticationScheme: request.authenticationScheme || AuthenticationScheme.BEARER,
          ccsCredential: {
            credential: request.account.homeAccountId,
            type: CcsCredentialType.HOME_ACCOUNT_ID
          }
        });
        return [2
        /*return*/
        , this.acquireToken(refreshTokenRequest)];
      });
    });
  };
  /**
   * Constructs the network message and makes a NW call to the underlying secure token service
   * @param request
   * @param authority
   */


  RefreshTokenClient.prototype.executeTokenRequest = function (request, authority) {
    var _a;

    return __awaiter(this, void 0, void 0, function () {
      var acquireTokenMeasurement, requestBody, queryParameters, headers, thumbprint, endpoint;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            acquireTokenMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.RefreshTokenClientExecuteTokenRequest, request.correlationId);
            return [4
            /*yield*/
            , this.createTokenRequestBody(request)];

          case 1:
            requestBody = _b.sent();
            queryParameters = this.createTokenQueryParameters(request);
            headers = this.createTokenRequestHeaders(request.ccsCredential);
            thumbprint = {
              clientId: this.config.authOptions.clientId,
              authority: authority.canonicalAuthority,
              scopes: request.scopes,
              claims: request.claims,
              authenticationScheme: request.authenticationScheme,
              resourceRequestMethod: request.resourceRequestMethod,
              resourceRequestUri: request.resourceRequestUri,
              shrClaims: request.shrClaims,
              sshKid: request.sshKid
            };
            endpoint = UrlString.appendQueryString(authority.tokenEndpoint, queryParameters);
            return [2
            /*return*/
            , this.executePostToTokenEndpoint(endpoint, requestBody, headers, thumbprint).then(function (result) {
              acquireTokenMeasurement === null || acquireTokenMeasurement === void 0 ? void 0 : acquireTokenMeasurement.endMeasurement({
                success: true
              });
              return result;
            }).catch(function (error) {
              acquireTokenMeasurement === null || acquireTokenMeasurement === void 0 ? void 0 : acquireTokenMeasurement.endMeasurement({
                success: false
              });
              throw error;
            })];
        }
      });
    });
  };
  /**
   * Creates query string for the /token request
   * @param request
   */


  RefreshTokenClient.prototype.createTokenQueryParameters = function (request) {
    var parameterBuilder = new RequestParameterBuilder();

    if (request.tokenQueryParameters) {
      parameterBuilder.addExtraQueryParameters(request.tokenQueryParameters);
    }

    return parameterBuilder.createQueryString();
  };
  /**
   * Helper function to create the token request body
   * @param request
   */


  RefreshTokenClient.prototype.createTokenRequestBody = function (request) {
    var _a;

    return __awaiter(this, void 0, void 0, function () {
      var correlationId, acquireTokenMeasurement, parameterBuilder, clientAssertion, popTokenGenerator, reqCnfData, clientInfo;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            correlationId = request.correlationId;
            acquireTokenMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.BaseClientCreateTokenRequestHeaders, correlationId);
            parameterBuilder = new RequestParameterBuilder();
            parameterBuilder.addClientId(this.config.authOptions.clientId);
            parameterBuilder.addScopes(request.scopes);
            parameterBuilder.addGrantType(GrantType.REFRESH_TOKEN_GRANT);
            parameterBuilder.addClientInfo();
            parameterBuilder.addLibraryInfo(this.config.libraryInfo);
            parameterBuilder.addApplicationTelemetry(this.config.telemetry.application);
            parameterBuilder.addThrottling();

            if (this.serverTelemetryManager) {
              parameterBuilder.addServerTelemetry(this.serverTelemetryManager);
            }

            parameterBuilder.addCorrelationId(correlationId);
            parameterBuilder.addRefreshToken(request.refreshToken);

            if (this.config.clientCredentials.clientSecret) {
              parameterBuilder.addClientSecret(this.config.clientCredentials.clientSecret);
            }

            if (this.config.clientCredentials.clientAssertion) {
              clientAssertion = this.config.clientCredentials.clientAssertion;
              parameterBuilder.addClientAssertion(clientAssertion.assertion);
              parameterBuilder.addClientAssertionType(clientAssertion.assertionType);
            }

            if (!(request.authenticationScheme === AuthenticationScheme.POP)) return [3
            /*break*/
            , 2];
            popTokenGenerator = new PopTokenGenerator(this.cryptoUtils);
            return [4
            /*yield*/
            , popTokenGenerator.generateCnf(request)];

          case 1:
            reqCnfData = _b.sent(); // SPA PoP requires full Base64Url encoded req_cnf string (unhashed)

            parameterBuilder.addPopToken(reqCnfData.reqCnfString);
            return [3
            /*break*/
            , 3];

          case 2:
            if (request.authenticationScheme === AuthenticationScheme.SSH) {
              if (request.sshJwk) {
                parameterBuilder.addSshJwk(request.sshJwk);
              } else {
                acquireTokenMeasurement === null || acquireTokenMeasurement === void 0 ? void 0 : acquireTokenMeasurement.endMeasurement({
                  success: false
                });
                throw ClientConfigurationError.createMissingSshJwkError();
              }
            }

            _b.label = 3;

          case 3:
            if (!StringUtils.isEmptyObj(request.claims) || this.config.authOptions.clientCapabilities && this.config.authOptions.clientCapabilities.length > 0) {
              parameterBuilder.addClaims(request.claims, this.config.authOptions.clientCapabilities);
            }

            if (this.config.systemOptions.preventCorsPreflight && request.ccsCredential) {
              switch (request.ccsCredential.type) {
                case CcsCredentialType.HOME_ACCOUNT_ID:
                  try {
                    clientInfo = buildClientInfoFromHomeAccountId(request.ccsCredential.credential);
                    parameterBuilder.addCcsOid(clientInfo);
                  } catch (e) {
                    this.logger.verbose("Could not parse home account ID for CCS Header: " + e);
                  }

                  break;

                case CcsCredentialType.UPN:
                  parameterBuilder.addCcsUpn(request.ccsCredential.credential);
                  break;
              }
            }

            acquireTokenMeasurement === null || acquireTokenMeasurement === void 0 ? void 0 : acquireTokenMeasurement.endMeasurement({
              success: true
            });
            return [2
            /*return*/
            , parameterBuilder.createQueryString()];
        }
      });
    });
  };

  return RefreshTokenClient;
}(BaseClient);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentFlowClient =
/** @class */
function (_super) {
  __extends(SilentFlowClient, _super);

  function SilentFlowClient(configuration, performanceClient) {
    return _super.call(this, configuration, performanceClient) || this;
  }
  /**
   * Retrieves a token from cache if it is still valid, or uses the cached refresh token to renew
   * the given token and returns the renewed token
   * @param request
   */


  SilentFlowClient.prototype.acquireToken = function (request) {
    return __awaiter(this, void 0, void 0, function () {
      var e_1, refreshTokenClient;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            _a.trys.push([0, 2,, 3]);

            return [4
            /*yield*/
            , this.acquireCachedToken(request)];

          case 1:
            return [2
            /*return*/
            , _a.sent()];

          case 2:
            e_1 = _a.sent();

            if (e_1 instanceof ClientAuthError && e_1.errorCode === ClientAuthErrorMessage.tokenRefreshRequired.code) {
              refreshTokenClient = new RefreshTokenClient(this.config, this.performanceClient);
              return [2
              /*return*/
              , refreshTokenClient.acquireTokenByRefreshToken(request)];
            } else {
              throw e_1;
            }

          case 3:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Retrieves token from cache or throws an error if it must be refreshed.
   * @param request
   */


  SilentFlowClient.prototype.acquireCachedToken = function (request) {
    var _a, _b, _c, _d;

    return __awaiter(this, void 0, void 0, function () {
      var environment, cacheRecord;
      return __generator(this, function (_e) {
        switch (_e.label) {
          case 0:
            // Cannot renew token if no request object is given.
            if (!request) {
              throw ClientConfigurationError.createEmptyTokenRequestError();
            }

            if (request.forceRefresh) {
              // Must refresh due to present force_refresh flag.
              (_a = this.serverTelemetryManager) === null || _a === void 0 ? void 0 : _a.setCacheOutcome(CacheOutcome.FORCE_REFRESH);
              this.logger.info("SilentFlowClient:acquireCachedToken - Skipping cache because forceRefresh is true.");
              throw ClientAuthError.createRefreshRequiredError();
            } // We currently do not support silent flow for account === null use cases; This will be revisited for confidential flow usecases


            if (!request.account) {
              throw ClientAuthError.createNoAccountInSilentRequestError();
            }

            environment = request.authority || this.authority.getPreferredCache();
            cacheRecord = this.cacheManager.readCacheRecord(request.account, this.config.authOptions.clientId, request, environment);

            if (!cacheRecord.accessToken) {
              // Must refresh due to non-existent access_token.
              (_b = this.serverTelemetryManager) === null || _b === void 0 ? void 0 : _b.setCacheOutcome(CacheOutcome.NO_CACHED_ACCESS_TOKEN);
              this.logger.info("SilentFlowClient:acquireCachedToken - No access token found in cache for the given properties.");
              throw ClientAuthError.createRefreshRequiredError();
            } else if (TimeUtils.wasClockTurnedBack(cacheRecord.accessToken.cachedAt) || TimeUtils.isTokenExpired(cacheRecord.accessToken.expiresOn, this.config.systemOptions.tokenRenewalOffsetSeconds)) {
              // Must refresh due to expired access_token.
              (_c = this.serverTelemetryManager) === null || _c === void 0 ? void 0 : _c.setCacheOutcome(CacheOutcome.CACHED_ACCESS_TOKEN_EXPIRED);
              this.logger.info("SilentFlowClient:acquireCachedToken - Cached access token is expired or will expire within " + this.config.systemOptions.tokenRenewalOffsetSeconds + " seconds.");
              throw ClientAuthError.createRefreshRequiredError();
            } else if (cacheRecord.accessToken.refreshOn && TimeUtils.isTokenExpired(cacheRecord.accessToken.refreshOn, 0)) {
              // Must refresh due to the refresh_in value.
              (_d = this.serverTelemetryManager) === null || _d === void 0 ? void 0 : _d.setCacheOutcome(CacheOutcome.REFRESH_CACHED_ACCESS_TOKEN);
              this.logger.info("SilentFlowClient:acquireCachedToken - Cached access token's refreshOn property has been exceeded'.");
              throw ClientAuthError.createRefreshRequiredError();
            }

            if (this.config.serverTelemetryManager) {
              this.config.serverTelemetryManager.incrementCacheHits();
            }

            return [4
            /*yield*/
            , this.generateResultFromCacheRecord(cacheRecord, request)];

          case 1:
            return [2
            /*return*/
            , _e.sent()];
        }
      });
    });
  };
  /**
   * Helper function to build response object from the CacheRecord
   * @param cacheRecord
   */


  SilentFlowClient.prototype.generateResultFromCacheRecord = function (cacheRecord, request) {
    return __awaiter(this, void 0, void 0, function () {
      var idTokenObj, authTime;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (cacheRecord.idToken) {
              idTokenObj = new AuthToken(cacheRecord.idToken.secret, this.config.cryptoInterface);
            } // token max_age check


            if (request.maxAge || request.maxAge === 0) {
              authTime = idTokenObj === null || idTokenObj === void 0 ? void 0 : idTokenObj.claims.auth_time;

              if (!authTime) {
                throw ClientAuthError.createAuthTimeNotFoundError();
              }

              AuthToken.checkMaxAge(authTime, request.maxAge);
            }

            return [4
            /*yield*/
            , ResponseHandler.generateAuthenticationResult(this.cryptoUtils, this.authority, cacheRecord, true, request, idTokenObj)];

          case 1:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };

  return SilentFlowClient;
}(BaseClient);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

function isOpenIdConfigResponse(response) {
  return response.hasOwnProperty("authorization_endpoint") && response.hasOwnProperty("token_endpoint") && response.hasOwnProperty("issuer") && response.hasOwnProperty("jwks_uri");
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var rawMetdataJSON = {
  "endpointMetadata": {
    "https://login.microsoftonline.com/common/": {
      "token_endpoint": "https://login.microsoftonline.com/common/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.com/common/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.com/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.com/common/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.com/common/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.com",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pas.windows.net"
    },
    "https://login.chinacloudapi.cn/common/": {
      "token_endpoint": "https://login.chinacloudapi.cn/common/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.chinacloudapi.cn/common/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.partner.microsoftonline.cn/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://microsoftgraph.chinacloudapi.cn/oidc/userinfo",
      "authorization_endpoint": "https://login.chinacloudapi.cn/common/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.chinacloudapi.cn/common/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.chinacloudapi.cn/common/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.chinacloudapi.cn/common/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "partner.microsoftonline.cn",
      "cloud_graph_host_name": "graph.chinacloudapi.cn",
      "msgraph_host": "microsoftgraph.chinacloudapi.cn",
      "rbac_url": "https://pas.chinacloudapi.cn"
    },
    "https://login.microsoftonline.us/common/": {
      "token_endpoint": "https://login.microsoftonline.us/common/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.us/common/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.us/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.us/common/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.us/common/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.us/common/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.us/common/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.us",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pasff.usgovcloudapi.net"
    },
    "https://login.microsoftonline.com/consumers/": {
      "token_endpoint": "https://login.microsoftonline.com/consumers/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.com/consumers/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.com/9188040d-6c67-4c5b-b112-36a304b66dad/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.com/consumers/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.com/consumers/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.com/consumers/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.com",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pas.windows.net"
    },
    "https://login.chinacloudapi.cn/consumers/": {
      "token_endpoint": "https://login.chinacloudapi.cn/consumers/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.chinacloudapi.cn/consumers/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.partner.microsoftonline.cn/9188040d-6c67-4c5b-b112-36a304b66dad/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://microsoftgraph.chinacloudapi.cn/oidc/userinfo",
      "authorization_endpoint": "https://login.chinacloudapi.cn/consumers/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.chinacloudapi.cn/consumers/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.chinacloudapi.cn/consumers/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.chinacloudapi.cn/consumers/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "partner.microsoftonline.cn",
      "cloud_graph_host_name": "graph.chinacloudapi.cn",
      "msgraph_host": "microsoftgraph.chinacloudapi.cn",
      "rbac_url": "https://pas.chinacloudapi.cn"
    },
    "https://login.microsoftonline.us/consumers/": {
      "token_endpoint": "https://login.microsoftonline.us/consumers/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.us/consumers/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.us/9188040d-6c67-4c5b-b112-36a304b66dad/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.us/consumers/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.us/consumers/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.us/consumers/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.us/consumers/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.us",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pasff.usgovcloudapi.net"
    },
    "https://login.microsoftonline.com/organizations/": {
      "token_endpoint": "https://login.microsoftonline.com/organizations/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.com/organizations/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.com/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.com/organizations/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.com/organizations/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.com/organizations/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.com",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pas.windows.net"
    },
    "https://login.chinacloudapi.cn/organizations/": {
      "token_endpoint": "https://login.chinacloudapi.cn/organizations/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.chinacloudapi.cn/organizations/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.partner.microsoftonline.cn/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://microsoftgraph.chinacloudapi.cn/oidc/userinfo",
      "authorization_endpoint": "https://login.chinacloudapi.cn/organizations/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.chinacloudapi.cn/organizations/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.chinacloudapi.cn/organizations/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.chinacloudapi.cn/organizations/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "partner.microsoftonline.cn",
      "cloud_graph_host_name": "graph.chinacloudapi.cn",
      "msgraph_host": "microsoftgraph.chinacloudapi.cn",
      "rbac_url": "https://pas.chinacloudapi.cn"
    },
    "https://login.microsoftonline.us/organizations/": {
      "token_endpoint": "https://login.microsoftonline.us/organizations/oauth2/v2.0/token",
      "token_endpoint_auth_methods_supported": ["client_secret_post", "private_key_jwt", "client_secret_basic"],
      "jwks_uri": "https://login.microsoftonline.us/organizations/discovery/v2.0/keys",
      "response_modes_supported": ["query", "fragment", "form_post"],
      "subject_types_supported": ["pairwise"],
      "id_token_signing_alg_values_supported": ["RS256"],
      "response_types_supported": ["code", "id_token", "code id_token", "id_token token"],
      "scopes_supported": ["openid", "profile", "email", "offline_access"],
      "issuer": "https://login.microsoftonline.us/{tenantid}/v2.0",
      "request_uri_parameter_supported": false,
      "userinfo_endpoint": "https://graph.microsoft.com/oidc/userinfo",
      "authorization_endpoint": "https://login.microsoftonline.us/organizations/oauth2/v2.0/authorize",
      "device_authorization_endpoint": "https://login.microsoftonline.us/organizations/oauth2/v2.0/devicecode",
      "http_logout_supported": true,
      "frontchannel_logout_supported": true,
      "end_session_endpoint": "https://login.microsoftonline.us/organizations/oauth2/v2.0/logout",
      "claims_supported": ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
      "kerberos_endpoint": "https://login.microsoftonline.us/organizations/kerberos",
      "tenant_region_scope": null,
      "cloud_instance_name": "microsoftonline.us",
      "cloud_graph_host_name": "graph.windows.net",
      "msgraph_host": "graph.microsoft.com",
      "rbac_url": "https://pasff.usgovcloudapi.net"
    }
  },
  "instanceDiscoveryMetadata": {
    "https://login.microsoftonline.com/common/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.chinacloudapi.cn/common/": {
      "tenant_discovery_endpoint": "https://login.chinacloudapi.cn/common/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.microsoftonline.us/common/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.us/common/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.microsoftonline.com/consumers/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.com/consumers/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.chinacloudapi.cn/consumers/": {
      "tenant_discovery_endpoint": "https://login.chinacloudapi.cn/consumers/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.microsoftonline.us/consumers/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.us/consumers/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.microsoftonline.com/organizations/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.com/organizations/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.chinacloudapi.cn/organizations/": {
      "tenant_discovery_endpoint": "https://login.chinacloudapi.cn/organizations/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    },
    "https://login.microsoftonline.us/organizations/": {
      "tenant_discovery_endpoint": "https://login.microsoftonline.us/organizations/v2.0/.well-known/openid-configuration",
      "api-version": "1.1",
      "metadata": [{
        "preferred_network": "login.microsoftonline.com",
        "preferred_cache": "login.windows.net",
        "aliases": ["login.microsoftonline.com", "login.windows.net", "login.microsoft.com", "sts.windows.net"]
      }, {
        "preferred_network": "login.partner.microsoftonline.cn",
        "preferred_cache": "login.partner.microsoftonline.cn",
        "aliases": ["login.partner.microsoftonline.cn", "login.chinacloudapi.cn"]
      }, {
        "preferred_network": "login.microsoftonline.de",
        "preferred_cache": "login.microsoftonline.de",
        "aliases": ["login.microsoftonline.de"]
      }, {
        "preferred_network": "login.microsoftonline.us",
        "preferred_cache": "login.microsoftonline.us",
        "aliases": ["login.microsoftonline.us", "login.usgovcloudapi.net"]
      }, {
        "preferred_network": "login-us.microsoftonline.com",
        "preferred_cache": "login-us.microsoftonline.com",
        "aliases": ["login-us.microsoftonline.com"]
      }]
    }
  }
};
var EndpointMetadata = rawMetdataJSON.endpointMetadata;
var InstanceDiscoveryMetadata = rawMetdataJSON.instanceDiscoveryMetadata;

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Protocol modes supported by MSAL.
 */

var ProtocolMode;

(function (ProtocolMode) {
  ProtocolMode["AAD"] = "AAD";
  ProtocolMode["OIDC"] = "OIDC";
})(ProtocolMode || (ProtocolMode = {}));

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var AuthorityMetadataEntity =
/** @class */
function () {
  function AuthorityMetadataEntity() {
    this.expiresAt = TimeUtils.nowSeconds() + AUTHORITY_METADATA_CONSTANTS.REFRESH_TIME_SECONDS;
  }
  /**
   * Update the entity with new aliases, preferred_cache and preferred_network values
   * @param metadata
   * @param fromNetwork
   */


  AuthorityMetadataEntity.prototype.updateCloudDiscoveryMetadata = function (metadata, fromNetwork) {
    this.aliases = metadata.aliases;
    this.preferred_cache = metadata.preferred_cache;
    this.preferred_network = metadata.preferred_network;
    this.aliasesFromNetwork = fromNetwork;
  };
  /**
   * Update the entity with new endpoints
   * @param metadata
   * @param fromNetwork
   */


  AuthorityMetadataEntity.prototype.updateEndpointMetadata = function (metadata, fromNetwork) {
    this.authorization_endpoint = metadata.authorization_endpoint;
    this.token_endpoint = metadata.token_endpoint;
    this.end_session_endpoint = metadata.end_session_endpoint;
    this.issuer = metadata.issuer;
    this.endpointsFromNetwork = fromNetwork;
    this.jwks_uri = metadata.jwks_uri;
  };
  /**
   * Save the authority that was used to create this cache entry
   * @param authority
   */


  AuthorityMetadataEntity.prototype.updateCanonicalAuthority = function (authority) {
    this.canonical_authority = authority;
  };
  /**
   * Reset the exiresAt value
   */


  AuthorityMetadataEntity.prototype.resetExpiresAt = function () {
    this.expiresAt = TimeUtils.nowSeconds() + AUTHORITY_METADATA_CONSTANTS.REFRESH_TIME_SECONDS;
  };
  /**
   * Returns whether or not the data needs to be refreshed
   */


  AuthorityMetadataEntity.prototype.isExpired = function () {
    return this.expiresAt <= TimeUtils.nowSeconds();
  };
  /**
   * Validates an entity: checks for all expected params
   * @param entity
   */


  AuthorityMetadataEntity.isAuthorityMetadataEntity = function (key, entity) {
    if (!entity) {
      return false;
    }

    return key.indexOf(AUTHORITY_METADATA_CONSTANTS.CACHE_KEY) === 0 && entity.hasOwnProperty("aliases") && entity.hasOwnProperty("preferred_cache") && entity.hasOwnProperty("preferred_network") && entity.hasOwnProperty("canonical_authority") && entity.hasOwnProperty("authorization_endpoint") && entity.hasOwnProperty("token_endpoint") && entity.hasOwnProperty("issuer") && entity.hasOwnProperty("aliasesFromNetwork") && entity.hasOwnProperty("endpointsFromNetwork") && entity.hasOwnProperty("expiresAt") && entity.hasOwnProperty("jwks_uri");
  };

  return AuthorityMetadataEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

function isCloudInstanceDiscoveryResponse(response) {
  return response.hasOwnProperty("tenant_discovery_endpoint") && response.hasOwnProperty("metadata");
}

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var RegionDiscovery =
/** @class */
function () {
  function RegionDiscovery(networkInterface) {
    this.networkInterface = networkInterface;
  }
  /**
   * Detect the region from the application's environment.
   *
   * @returns Promise<string | null>
   */


  RegionDiscovery.prototype.detectRegion = function (environmentRegion, regionDiscoveryMetadata, proxyUrl) {
    return __awaiter(this, void 0, void 0, function () {
      var autodetectedRegionName, options, localIMDSVersionResponse, currentIMDSVersion, currentIMDSVersionResponse;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            autodetectedRegionName = environmentRegion;
            if (!!autodetectedRegionName) return [3
            /*break*/
            , 8];
            options = RegionDiscovery.IMDS_OPTIONS;

            if (proxyUrl) {
              options.proxyUrl = proxyUrl;
            }

            _a.label = 1;

          case 1:
            _a.trys.push([1, 6,, 7]);

            return [4
            /*yield*/
            , this.getRegionFromIMDS(Constants.IMDS_VERSION, options)];

          case 2:
            localIMDSVersionResponse = _a.sent();

            if (localIMDSVersionResponse.status === ResponseCodes.httpSuccess) {
              autodetectedRegionName = localIMDSVersionResponse.body;
              regionDiscoveryMetadata.region_source = RegionDiscoverySources.IMDS;
            }

            if (!(localIMDSVersionResponse.status === ResponseCodes.httpBadRequest)) return [3
            /*break*/
            , 5];
            return [4
            /*yield*/
            , this.getCurrentVersion(options)];

          case 3:
            currentIMDSVersion = _a.sent();

            if (!currentIMDSVersion) {
              regionDiscoveryMetadata.region_source = RegionDiscoverySources.FAILED_AUTO_DETECTION;
              return [2
              /*return*/
              , null];
            }

            return [4
            /*yield*/
            , this.getRegionFromIMDS(currentIMDSVersion, options)];

          case 4:
            currentIMDSVersionResponse = _a.sent();

            if (currentIMDSVersionResponse.status === ResponseCodes.httpSuccess) {
              autodetectedRegionName = currentIMDSVersionResponse.body;
              regionDiscoveryMetadata.region_source = RegionDiscoverySources.IMDS;
            }

            _a.label = 5;

          case 5:
            return [3
            /*break*/
            , 7];

          case 6:
            _a.sent();

            regionDiscoveryMetadata.region_source = RegionDiscoverySources.FAILED_AUTO_DETECTION;
            return [2
            /*return*/
            , null];

          case 7:
            return [3
            /*break*/
            , 9];

          case 8:
            regionDiscoveryMetadata.region_source = RegionDiscoverySources.ENVIRONMENT_VARIABLE;
            _a.label = 9;

          case 9:
            // If no region was auto detected from the environment or from the IMDS endpoint, mark the attempt as a FAILED_AUTO_DETECTION
            if (!autodetectedRegionName) {
              regionDiscoveryMetadata.region_source = RegionDiscoverySources.FAILED_AUTO_DETECTION;
            }

            return [2
            /*return*/
            , autodetectedRegionName || null];
        }
      });
    });
  };
  /**
   * Make the call to the IMDS endpoint
   *
   * @param imdsEndpointUrl
   * @returns Promise<NetworkResponse<string>>
   */


  RegionDiscovery.prototype.getRegionFromIMDS = function (version, options) {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        return [2
        /*return*/
        , this.networkInterface.sendGetRequestAsync(Constants.IMDS_ENDPOINT + "?api-version=" + version + "&format=text", options, Constants.IMDS_TIMEOUT)];
      });
    });
  };
  /**
   * Get the most recent version of the IMDS endpoint available
   *
   * @returns Promise<string | null>
   */


  RegionDiscovery.prototype.getCurrentVersion = function (options) {
    return __awaiter(this, void 0, void 0, function () {
      var response;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            _a.trys.push([0, 2,, 3]);

            return [4
            /*yield*/
            , this.networkInterface.sendGetRequestAsync(Constants.IMDS_ENDPOINT + "?format=json", options)];

          case 1:
            response = _a.sent(); // When IMDS endpoint is called without the api version query param, bad request response comes back with latest version.

            if (response.status === ResponseCodes.httpBadRequest && response.body && response.body["newest-versions"] && response.body["newest-versions"].length > 0) {
              return [2
              /*return*/
              , response.body["newest-versions"][0]];
            }

            return [2
            /*return*/
            , null];

          case 2:
            _a.sent();

            return [2
            /*return*/
            , null];

          case 3:
            return [2
            /*return*/
            ];
        }
      });
    });
  }; // Options for the IMDS endpoint request


  RegionDiscovery.IMDS_OPTIONS = {
    headers: {
      Metadata: "true"
    }
  };
  return RegionDiscovery;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * The authority class validates the authority URIs used by the user, and retrieves the OpenID Configuration Data from the
 * endpoint. It will store the pertinent config data in this object for use during token calls.
 */

var Authority =
/** @class */
function () {
  function Authority(authority, networkInterface, cacheManager, authorityOptions, proxyUrl) {
    this.canonicalAuthority = authority;

    this._canonicalAuthority.validateAsUri();

    this.networkInterface = networkInterface;
    this.cacheManager = cacheManager;
    this.authorityOptions = authorityOptions;
    this.regionDiscovery = new RegionDiscovery(networkInterface);
    this.regionDiscoveryMetadata = {
      region_used: undefined,
      region_source: undefined,
      region_outcome: undefined
    };
    this.proxyUrl = proxyUrl || Constants.EMPTY_STRING;
  }

  Object.defineProperty(Authority.prototype, "authorityType", {
    // See above for AuthorityType
    get: function () {
      var pathSegments = this.canonicalAuthorityUrlComponents.PathSegments;

      if (pathSegments.length) {
        switch (pathSegments[0].toLowerCase()) {
          case Constants.ADFS:
            return AuthorityType.Adfs;

          case Constants.DSTS:
            return AuthorityType.Dsts;
        }
      }

      return AuthorityType.Default;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "protocolMode", {
    /**
     * ProtocolMode enum representing the way endpoints are constructed.
     */
    get: function () {
      return this.authorityOptions.protocolMode;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "options", {
    /**
     * Returns authorityOptions which can be used to reinstantiate a new authority instance
     */
    get: function () {
      return this.authorityOptions;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "canonicalAuthority", {
    /**
     * A URL that is the authority set by the developer
     */
    get: function () {
      return this._canonicalAuthority.urlString;
    },

    /**
     * Sets canonical authority.
     */
    set: function (url) {
      this._canonicalAuthority = new UrlString(url);

      this._canonicalAuthority.validateAsUri();

      this._canonicalAuthorityUrlComponents = null;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "canonicalAuthorityUrlComponents", {
    /**
     * Get authority components.
     */
    get: function () {
      if (!this._canonicalAuthorityUrlComponents) {
        this._canonicalAuthorityUrlComponents = this._canonicalAuthority.getUrlComponents();
      }

      return this._canonicalAuthorityUrlComponents;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "hostnameAndPort", {
    /**
     * Get hostname and port i.e. login.microsoftonline.com
     */
    get: function () {
      return this.canonicalAuthorityUrlComponents.HostNameAndPort.toLowerCase();
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "tenant", {
    /**
     * Get tenant for authority.
     */
    get: function () {
      return this.canonicalAuthorityUrlComponents.PathSegments[0];
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "authorizationEndpoint", {
    /**
     * OAuth /authorize endpoint for requests
     */
    get: function () {
      if (this.discoveryComplete()) {
        var endpoint = this.replacePath(this.metadata.authorization_endpoint);
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "tokenEndpoint", {
    /**
     * OAuth /token endpoint for requests
     */
    get: function () {
      if (this.discoveryComplete()) {
        var endpoint = this.replacePath(this.metadata.token_endpoint);
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "deviceCodeEndpoint", {
    get: function () {
      if (this.discoveryComplete()) {
        var endpoint = this.replacePath(this.metadata.token_endpoint.replace("/token", "/devicecode"));
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "endSessionEndpoint", {
    /**
     * OAuth logout endpoint for requests
     */
    get: function () {
      if (this.discoveryComplete()) {
        // ROPC policies may not have end_session_endpoint set
        if (!this.metadata.end_session_endpoint) {
          throw ClientAuthError.createLogoutNotSupportedError();
        }

        var endpoint = this.replacePath(this.metadata.end_session_endpoint);
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "selfSignedJwtAudience", {
    /**
     * OAuth issuer for requests
     */
    get: function () {
      if (this.discoveryComplete()) {
        var endpoint = this.replacePath(this.metadata.issuer);
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(Authority.prototype, "jwksUri", {
    /**
     * Jwks_uri for token signing keys
     */
    get: function () {
      if (this.discoveryComplete()) {
        var endpoint = this.replacePath(this.metadata.jwks_uri);
        return this.replaceTenant(endpoint);
      } else {
        throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
      }
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Replaces tenant in url path with current tenant. Defaults to common.
   * @param urlString
   */

  Authority.prototype.replaceTenant = function (urlString) {
    return urlString.replace(/{tenant}|{tenantid}/g, this.tenant);
  };
  /**
   * Replaces path such as tenant or policy with the current tenant or policy.
   * @param urlString
   */


  Authority.prototype.replacePath = function (urlString) {
    var endpoint = urlString;
    var cachedAuthorityUrl = new UrlString(this.metadata.canonical_authority);
    var cachedAuthorityParts = cachedAuthorityUrl.getUrlComponents().PathSegments;
    var currentAuthorityParts = this.canonicalAuthorityUrlComponents.PathSegments;
    currentAuthorityParts.forEach(function (currentPart, index) {
      var cachedPart = cachedAuthorityParts[index];

      if (currentPart !== cachedPart) {
        endpoint = endpoint.replace("/" + cachedPart + "/", "/" + currentPart + "/");
      }
    });
    return endpoint;
  };

  Object.defineProperty(Authority.prototype, "defaultOpenIdConfigurationEndpoint", {
    /**
     * The default open id configuration endpoint for any canonical authority.
     */
    get: function () {
      if (this.authorityType === AuthorityType.Adfs || this.authorityType === AuthorityType.Dsts || this.protocolMode === ProtocolMode.OIDC) {
        return this.canonicalAuthority + ".well-known/openid-configuration";
      }

      return this.canonicalAuthority + "v2.0/.well-known/openid-configuration";
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Boolean that returns whethr or not tenant discovery has been completed.
   */

  Authority.prototype.discoveryComplete = function () {
    return !!this.metadata;
  };
  /**
   * Perform endpoint discovery to discover aliases, preferred_cache, preferred_network
   * and the /authorize, /token and logout endpoints.
   */


  Authority.prototype.resolveEndpointsAsync = function () {
    return __awaiter(this, void 0, void 0, function () {
      var metadataEntity, cloudDiscoverySource, endpointSource, cacheKey;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            metadataEntity = this.cacheManager.getAuthorityMetadataByAlias(this.hostnameAndPort);

            if (!metadataEntity) {
              metadataEntity = new AuthorityMetadataEntity();
              metadataEntity.updateCanonicalAuthority(this.canonicalAuthority);
            }

            return [4
            /*yield*/
            , this.updateCloudDiscoveryMetadata(metadataEntity)];

          case 1:
            cloudDiscoverySource = _a.sent();
            this.canonicalAuthority = this.canonicalAuthority.replace(this.hostnameAndPort, metadataEntity.preferred_network);
            return [4
            /*yield*/
            , this.updateEndpointMetadata(metadataEntity)];

          case 2:
            endpointSource = _a.sent();

            if (cloudDiscoverySource !== AuthorityMetadataSource.CACHE && endpointSource !== AuthorityMetadataSource.CACHE) {
              // Reset the expiration time unless both values came from a successful cache lookup
              metadataEntity.resetExpiresAt();
              metadataEntity.updateCanonicalAuthority(this.canonicalAuthority);
            }

            cacheKey = this.cacheManager.generateAuthorityMetadataCacheKey(metadataEntity.preferred_cache);
            this.cacheManager.setAuthorityMetadata(cacheKey, metadataEntity);
            this.metadata = metadataEntity;
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Update AuthorityMetadataEntity with new endpoints and return where the information came from
   * @param metadataEntity
   */


  Authority.prototype.updateEndpointMetadata = function (metadataEntity) {
    var _a, _b;

    return __awaiter(this, void 0, void 0, function () {
      var metadata, harcodedMetadata;
      return __generator(this, function (_c) {
        switch (_c.label) {
          case 0:
            metadata = this.getEndpointMetadataFromConfig();

            if (metadata) {
              metadataEntity.updateEndpointMetadata(metadata, false);
              return [2
              /*return*/
              , AuthorityMetadataSource.CONFIG];
            }

            if (this.isAuthoritySameType(metadataEntity) && metadataEntity.endpointsFromNetwork && !metadataEntity.isExpired()) {
              // No need to update
              return [2
              /*return*/
              , AuthorityMetadataSource.CACHE];
            }

            harcodedMetadata = this.getEndpointMetadataFromHardcodedValues();
            return [4
            /*yield*/
            , this.getEndpointMetadataFromNetwork()];

          case 1:
            metadata = _c.sent();
            if (!metadata) return [3
            /*break*/
            , 4];
            if (!((_a = this.authorityOptions.azureRegionConfiguration) === null || _a === void 0 ? void 0 : _a.azureRegion)) return [3
            /*break*/
            , 3];
            return [4
            /*yield*/
            , this.updateMetadataWithRegionalInformation(metadata)];

          case 2:
            metadata = _c.sent();
            _c.label = 3;

          case 3:
            metadataEntity.updateEndpointMetadata(metadata, true);
            return [2
            /*return*/
            , AuthorityMetadataSource.NETWORK];

          case 4:
            if (!(harcodedMetadata && !this.authorityOptions.skipAuthorityMetadataCache)) return [3
            /*break*/
            , 7];
            if (!((_b = this.authorityOptions.azureRegionConfiguration) === null || _b === void 0 ? void 0 : _b.azureRegion)) return [3
            /*break*/
            , 6];
            return [4
            /*yield*/
            , this.updateMetadataWithRegionalInformation(harcodedMetadata)];

          case 5:
            harcodedMetadata = _c.sent();
            _c.label = 6;

          case 6:
            metadataEntity.updateEndpointMetadata(harcodedMetadata, false);
            return [2
            /*return*/
            , AuthorityMetadataSource.HARDCODED_VALUES];

          case 7:
            throw ClientAuthError.createUnableToGetOpenidConfigError(this.defaultOpenIdConfigurationEndpoint);
        }
      });
    });
  };
  /**
   * Compares the number of url components after the domain to determine if the cached
   * authority metadata can be used for the requested authority. Protects against same domain different
   * authority such as login.microsoftonline.com/tenant and login.microsoftonline.com/tfp/tenant/policy
   * @param metadataEntity
   */


  Authority.prototype.isAuthoritySameType = function (metadataEntity) {
    var cachedAuthorityUrl = new UrlString(metadataEntity.canonical_authority);
    var cachedParts = cachedAuthorityUrl.getUrlComponents().PathSegments;
    return cachedParts.length === this.canonicalAuthorityUrlComponents.PathSegments.length;
  };
  /**
   * Parse authorityMetadata config option
   */


  Authority.prototype.getEndpointMetadataFromConfig = function () {
    if (this.authorityOptions.authorityMetadata) {
      try {
        return JSON.parse(this.authorityOptions.authorityMetadata);
      } catch (e) {
        throw ClientConfigurationError.createInvalidAuthorityMetadataError();
      }
    }

    return null;
  };
  /**
   * Gets OAuth endpoints from the given OpenID configuration endpoint.
   *
   * @param hasHardcodedMetadata boolean
   */


  Authority.prototype.getEndpointMetadataFromNetwork = function () {
    return __awaiter(this, void 0, void 0, function () {
      var options, response;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            options = {};

            if (this.proxyUrl) {
              options.proxyUrl = this.proxyUrl;
            }

            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.networkInterface.sendGetRequestAsync(this.defaultOpenIdConfigurationEndpoint, options)];

          case 2:
            response = _a.sent();
            return [2
            /*return*/
            , isOpenIdConfigResponse(response.body) ? response.body : null];

          case 3:
            _a.sent();

            return [2
            /*return*/
            , null];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Get OAuth endpoints for common authorities.
   */


  Authority.prototype.getEndpointMetadataFromHardcodedValues = function () {
    if (this.canonicalAuthority in EndpointMetadata) {
      return EndpointMetadata[this.canonicalAuthority];
    }

    return null;
  };
  /**
   * Update the retrieved metadata with regional information.
   */


  Authority.prototype.updateMetadataWithRegionalInformation = function (metadata) {
    var _a, _b, _c, _d, _e;

    return __awaiter(this, void 0, void 0, function () {
      var autodetectedRegionName, azureRegion;
      return __generator(this, function (_f) {
        switch (_f.label) {
          case 0:
            return [4
            /*yield*/
            , this.regionDiscovery.detectRegion((_a = this.authorityOptions.azureRegionConfiguration) === null || _a === void 0 ? void 0 : _a.environmentRegion, this.regionDiscoveryMetadata, this.proxyUrl)];

          case 1:
            autodetectedRegionName = _f.sent();
            azureRegion = ((_b = this.authorityOptions.azureRegionConfiguration) === null || _b === void 0 ? void 0 : _b.azureRegion) === Constants.AZURE_REGION_AUTO_DISCOVER_FLAG ? autodetectedRegionName : (_c = this.authorityOptions.azureRegionConfiguration) === null || _c === void 0 ? void 0 : _c.azureRegion;

            if (((_d = this.authorityOptions.azureRegionConfiguration) === null || _d === void 0 ? void 0 : _d.azureRegion) === Constants.AZURE_REGION_AUTO_DISCOVER_FLAG) {
              this.regionDiscoveryMetadata.region_outcome = autodetectedRegionName ? RegionDiscoveryOutcomes.AUTO_DETECTION_REQUESTED_SUCCESSFUL : RegionDiscoveryOutcomes.AUTO_DETECTION_REQUESTED_FAILED;
            } else {
              if (autodetectedRegionName) {
                this.regionDiscoveryMetadata.region_outcome = ((_e = this.authorityOptions.azureRegionConfiguration) === null || _e === void 0 ? void 0 : _e.azureRegion) === autodetectedRegionName ? RegionDiscoveryOutcomes.CONFIGURED_MATCHES_DETECTED : RegionDiscoveryOutcomes.CONFIGURED_NOT_DETECTED;
              } else {
                this.regionDiscoveryMetadata.region_outcome = RegionDiscoveryOutcomes.CONFIGURED_NO_AUTO_DETECTION;
              }
            }

            if (azureRegion) {
              this.regionDiscoveryMetadata.region_used = azureRegion;
              return [2
              /*return*/
              , Authority.replaceWithRegionalInformation(metadata, azureRegion)];
            }

            return [2
            /*return*/
            , metadata];
        }
      });
    });
  };
  /**
   * Updates the AuthorityMetadataEntity with new aliases, preferred_network and preferred_cache
   * and returns where the information was retrieved from
   * @param cachedMetadata
   * @param newMetadata
   */


  Authority.prototype.updateCloudDiscoveryMetadata = function (metadataEntity) {
    return __awaiter(this, void 0, void 0, function () {
      var metadata, harcodedMetadata;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            metadata = this.getCloudDiscoveryMetadataFromConfig();

            if (metadata) {
              metadataEntity.updateCloudDiscoveryMetadata(metadata, false);
              return [2
              /*return*/
              , AuthorityMetadataSource.CONFIG];
            } // If The cached metadata came from config but that config was not passed to this instance, we must go to the network


            if (this.isAuthoritySameType(metadataEntity) && metadataEntity.aliasesFromNetwork && !metadataEntity.isExpired()) {
              // No need to update
              return [2
              /*return*/
              , AuthorityMetadataSource.CACHE];
            }

            harcodedMetadata = this.getCloudDiscoveryMetadataFromHarcodedValues();
            return [4
            /*yield*/
            , this.getCloudDiscoveryMetadataFromNetwork()];

          case 1:
            metadata = _a.sent();

            if (metadata) {
              metadataEntity.updateCloudDiscoveryMetadata(metadata, true);
              return [2
              /*return*/
              , AuthorityMetadataSource.NETWORK];
            }

            if (harcodedMetadata && !this.options.skipAuthorityMetadataCache) {
              metadataEntity.updateCloudDiscoveryMetadata(harcodedMetadata, false);
              return [2
              /*return*/
              , AuthorityMetadataSource.HARDCODED_VALUES];
            } else {
              // Metadata could not be obtained from config, cache or network
              throw ClientConfigurationError.createUntrustedAuthorityError();
            }

        }
      });
    });
  };
  /**
   * Parse cloudDiscoveryMetadata config or check knownAuthorities
   */


  Authority.prototype.getCloudDiscoveryMetadataFromConfig = function () {
    // Check if network response was provided in config
    if (this.authorityOptions.cloudDiscoveryMetadata) {
      try {
        var parsedResponse = JSON.parse(this.authorityOptions.cloudDiscoveryMetadata);
        var metadata = Authority.getCloudDiscoveryMetadataFromNetworkResponse(parsedResponse.metadata, this.hostnameAndPort);

        if (metadata) {
          return metadata;
        }
      } catch (e) {
        throw ClientConfigurationError.createInvalidCloudDiscoveryMetadataError();
      }
    } // If cloudDiscoveryMetadata is empty or does not contain the host, check knownAuthorities


    if (this.isInKnownAuthorities()) {
      return Authority.createCloudDiscoveryMetadataFromHost(this.hostnameAndPort);
    }

    return null;
  };
  /**
   * Called to get metadata from network if CloudDiscoveryMetadata was not populated by config
   *
   * @param hasHardcodedMetadata boolean
   */


  Authority.prototype.getCloudDiscoveryMetadataFromNetwork = function () {
    return __awaiter(this, void 0, void 0, function () {
      var instanceDiscoveryEndpoint, options, match, response, metadata;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            instanceDiscoveryEndpoint = "" + Constants.AAD_INSTANCE_DISCOVERY_ENDPT + this.canonicalAuthority + "oauth2/v2.0/authorize";
            options = {};

            if (this.proxyUrl) {
              options.proxyUrl = this.proxyUrl;
            }

            match = null;
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.networkInterface.sendGetRequestAsync(instanceDiscoveryEndpoint, options)];

          case 2:
            response = _a.sent();
            metadata = isCloudInstanceDiscoveryResponse(response.body) ? response.body.metadata : [];

            if (metadata.length === 0) {
              // If no metadata is returned, authority is untrusted
              return [2
              /*return*/
              , null];
            }

            match = Authority.getCloudDiscoveryMetadataFromNetworkResponse(metadata, this.hostnameAndPort);
            return [3
            /*break*/
            , 4];

          case 3:
            _a.sent();

            return [2
            /*return*/
            , null];

          case 4:
            if (!match) {
              // Custom Domain scenario, host is trusted because Instance Discovery call succeeded
              match = Authority.createCloudDiscoveryMetadataFromHost(this.hostnameAndPort);
            }

            return [2
            /*return*/
            , match];
        }
      });
    });
  };
  /**
   * Get cloud discovery metadata for common authorities
   */


  Authority.prototype.getCloudDiscoveryMetadataFromHarcodedValues = function () {
    if (this.canonicalAuthority in InstanceDiscoveryMetadata) {
      return InstanceDiscoveryMetadata[this.canonicalAuthority];
    }

    return null;
  };
  /**
   * Helper function to determine if this host is included in the knownAuthorities config option
   */


  Authority.prototype.isInKnownAuthorities = function () {
    var _this = this;

    var matches = this.authorityOptions.knownAuthorities.filter(function (authority) {
      return UrlString.getDomainFromUrl(authority).toLowerCase() === _this.hostnameAndPort;
    });
    return matches.length > 0;
  };
  /**
   * helper function to populate the authority based on azureCloudOptions
   * @param authorityString
   * @param azureCloudOptions
   */


  Authority.generateAuthority = function (authorityString, azureCloudOptions) {
    var authorityAzureCloudInstance;

    if (azureCloudOptions && azureCloudOptions.azureCloudInstance !== AzureCloudInstance.None) {
      var tenant = azureCloudOptions.tenant ? azureCloudOptions.tenant : Constants.DEFAULT_COMMON_TENANT;
      authorityAzureCloudInstance = azureCloudOptions.azureCloudInstance + "/" + tenant + "/";
    }

    return authorityAzureCloudInstance ? authorityAzureCloudInstance : authorityString;
  };
  /**
   * Creates cloud discovery metadata object from a given host
   * @param host
   */


  Authority.createCloudDiscoveryMetadataFromHost = function (host) {
    return {
      preferred_network: host,
      preferred_cache: host,
      aliases: [host]
    };
  };
  /**
   * Searches instance discovery network response for the entry that contains the host in the aliases list
   * @param response
   * @param authority
   */


  Authority.getCloudDiscoveryMetadataFromNetworkResponse = function (response, authority) {
    for (var i = 0; i < response.length; i++) {
      var metadata = response[i];

      if (metadata.aliases.indexOf(authority) > -1) {
        return metadata;
      }
    }

    return null;
  };
  /**
   * helper function to generate environment from authority object
   */


  Authority.prototype.getPreferredCache = function () {
    if (this.discoveryComplete()) {
      return this.metadata.preferred_cache;
    } else {
      throw ClientAuthError.createEndpointDiscoveryIncompleteError("Discovery incomplete.");
    }
  };
  /**
   * Returns whether or not the provided host is an alias of this authority instance
   * @param host
   */


  Authority.prototype.isAlias = function (host) {
    return this.metadata.aliases.indexOf(host) > -1;
  };
  /**
   * Checks whether the provided host is that of a public cloud authority
   *
   * @param authority string
   * @returns bool
   */


  Authority.isPublicCloudAuthority = function (host) {
    return Constants.KNOWN_PUBLIC_CLOUDS.indexOf(host) >= 0;
  };
  /**
   * Rebuild the authority string with the region
   *
   * @param host string
   * @param region string
   */


  Authority.buildRegionalAuthorityString = function (host, region, queryString) {
    // Create and validate a Url string object with the initial authority string
    var authorityUrlInstance = new UrlString(host);
    authorityUrlInstance.validateAsUri();
    var authorityUrlParts = authorityUrlInstance.getUrlComponents();
    var hostNameAndPort = region + "." + authorityUrlParts.HostNameAndPort;

    if (this.isPublicCloudAuthority(authorityUrlParts.HostNameAndPort)) {
      hostNameAndPort = region + "." + Constants.REGIONAL_AUTH_PUBLIC_CLOUD_SUFFIX;
    } // Include the query string portion of the url


    var url = UrlString.constructAuthorityUriFromObject(__assign(__assign({}, authorityUrlInstance.getUrlComponents()), {
      HostNameAndPort: hostNameAndPort
    })).urlString; // Add the query string if a query string was provided

    if (queryString) return url + "?" + queryString;
    return url;
  };
  /**
   * Replace the endpoints in the metadata object with their regional equivalents.
   *
   * @param metadata OpenIdConfigResponse
   * @param azureRegion string
   */


  Authority.replaceWithRegionalInformation = function (metadata, azureRegion) {
    metadata.authorization_endpoint = Authority.buildRegionalAuthorityString(metadata.authorization_endpoint, azureRegion); // TODO: Enquire on whether we should leave the query string or remove it before releasing the feature

    metadata.token_endpoint = Authority.buildRegionalAuthorityString(metadata.token_endpoint, azureRegion, Constants.REGIONAL_AUTH_NON_MSI_QUERY_STRING);

    if (metadata.end_session_endpoint) {
      metadata.end_session_endpoint = Authority.buildRegionalAuthorityString(metadata.end_session_endpoint, azureRegion);
    }

    return metadata;
  };

  return Authority;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var AuthorityFactory =
/** @class */
function () {
  function AuthorityFactory() {}
  /**
   * Create an authority object of the correct type based on the url
   * Performs basic authority validation - checks to see if the authority is of a valid type (i.e. aad, b2c, adfs)
   *
   * Also performs endpoint discovery.
   *
   * @param authorityUri
   * @param networkClient
   * @param protocolMode
   */


  AuthorityFactory.createDiscoveredInstance = function (authorityUri, networkClient, cacheManager, authorityOptions, proxyUrl) {
    return __awaiter(this, void 0, void 0, function () {
      var acquireTokenAuthority, e_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            acquireTokenAuthority = AuthorityFactory.createInstance(authorityUri, networkClient, cacheManager, authorityOptions, proxyUrl);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , acquireTokenAuthority.resolveEndpointsAsync()];

          case 2:
            _a.sent();

            return [2
            /*return*/
            , acquireTokenAuthority];

          case 3:
            e_1 = _a.sent();
            throw ClientAuthError.createEndpointDiscoveryIncompleteError(e_1);

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Create an authority object of the correct type based on the url
   * Performs basic authority validation - checks to see if the authority is of a valid type (i.e. aad, b2c, adfs)
   *
   * Does not perform endpoint discovery.
   *
   * @param authorityUrl
   * @param networkInterface
   * @param protocolMode
   */


  AuthorityFactory.createInstance = function (authorityUrl, networkInterface, cacheManager, authorityOptions, proxyUrl) {
    // Throw error if authority url is empty
    if (StringUtils.isEmpty(authorityUrl)) {
      throw ClientConfigurationError.createUrlEmptyError();
    }

    return new Authority(authorityUrl, networkInterface, cacheManager, authorityOptions, proxyUrl);
  };

  return AuthorityFactory;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var ServerTelemetryEntity =
/** @class */
function () {
  function ServerTelemetryEntity() {
    this.failedRequests = [];
    this.errors = [];
    this.cacheHits = 0;
  }
  /**
   * validates if a given cache entry is "Telemetry", parses <key,value>
   * @param key
   * @param entity
   */


  ServerTelemetryEntity.isServerTelemetryEntity = function (key, entity) {
    var validateKey = key.indexOf(SERVER_TELEM_CONSTANTS.CACHE_KEY) === 0;
    var validateEntity = true;

    if (entity) {
      validateEntity = entity.hasOwnProperty("failedRequests") && entity.hasOwnProperty("errors") && entity.hasOwnProperty("cacheHits");
    }

    return validateKey && validateEntity;
  };

  return ServerTelemetryEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var ThrottlingEntity =
/** @class */
function () {
  function ThrottlingEntity() {}
  /**
   * validates if a given cache entry is "Throttling", parses <key,value>
   * @param key
   * @param entity
   */


  ThrottlingEntity.isThrottlingEntity = function (key, entity) {
    var validateKey = false;

    if (key) {
      validateKey = key.indexOf(ThrottlingConstants.THROTTLING_PREFIX) === 0;
    }

    var validateEntity = true;

    if (entity) {
      validateEntity = entity.hasOwnProperty("throttleTime");
    }

    return validateKey && validateEntity;
  };

  return ThrottlingEntity;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var StubbedNetworkModule = {
  sendGetRequestAsync: function () {
    var notImplErr = "Network interface - sendGetRequestAsync() has not been implemented for the Network interface.";
    return Promise.reject(AuthError.createUnexpectedError(notImplErr));
  },
  sendPostRequestAsync: function () {
    var notImplErr = "Network interface - sendPostRequestAsync() has not been implemented for the Network interface.";
    return Promise.reject(AuthError.createUnexpectedError(notImplErr));
  }
};

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * ClientAuthErrorMessage class containing string constants used by error codes and messages.
 */

var JoseHeaderErrorMessage = {
  missingKidError: {
    code: "missing_kid_error",
    desc: "The JOSE Header for the requested JWT, JWS or JWK object requires a keyId to be configured as the 'kid' header claim. No 'kid' value was provided."
  },
  missingAlgError: {
    code: "missing_alg_error",
    desc: "The JOSE Header for the requested JWT, JWS or JWK object requires an algorithm to be specified as the 'alg' header claim. No 'alg' value was provided."
  }
};
/**
 * Error thrown when there is an error in the client code running on the browser.
 */

var JoseHeaderError =
/** @class */
function (_super) {
  __extends(JoseHeaderError, _super);

  function JoseHeaderError(errorCode, errorMessage) {
    var _this = _super.call(this, errorCode, errorMessage) || this;

    _this.name = "JoseHeaderError";
    Object.setPrototypeOf(_this, JoseHeaderError.prototype);
    return _this;
  }
  /**
   * Creates an error thrown when keyId isn't set on JOSE header.
   */


  JoseHeaderError.createMissingKidError = function () {
    return new JoseHeaderError(JoseHeaderErrorMessage.missingKidError.code, JoseHeaderErrorMessage.missingKidError.desc);
  };
  /**
   * Creates an error thrown when algorithm isn't set on JOSE header.
   */


  JoseHeaderError.createMissingAlgError = function () {
    return new JoseHeaderError(JoseHeaderErrorMessage.missingAlgError.code, JoseHeaderErrorMessage.missingAlgError.desc);
  };

  return JoseHeaderError;
}(AuthError);

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var JoseHeader =
/** @class */
function () {
  function JoseHeader(options) {
    this.typ = options.typ;
    this.alg = options.alg;
    this.kid = options.kid;
  }
  /**
   * Builds SignedHttpRequest formatted JOSE Header from the
   * JOSE Header options provided or previously set on the object and returns
   * the stringified header object.
   * Throws if keyId or algorithm aren't provided since they are required for Access Token Binding.
   * @param shrHeaderOptions
   * @returns
   */


  JoseHeader.getShrHeaderString = function (shrHeaderOptions) {
    // KeyID is required on the SHR header
    if (!shrHeaderOptions.kid) {
      throw JoseHeaderError.createMissingKidError();
    } // Alg is required on the SHR header


    if (!shrHeaderOptions.alg) {
      throw JoseHeaderError.createMissingAlgError();
    }

    var shrHeader = new JoseHeader({
      // Access Token PoP headers must have type JWT, but the type header can be overriden for special cases
      typ: shrHeaderOptions.typ || JsonTypes.Jwt,
      kid: shrHeaderOptions.kid,
      alg: shrHeaderOptions.alg
    });
    return JSON.stringify(shrHeader);
  };

  return JoseHeader;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var ServerTelemetryManager =
/** @class */
function () {
  function ServerTelemetryManager(telemetryRequest, cacheManager) {
    this.cacheOutcome = CacheOutcome.NO_CACHE_HIT;
    this.cacheManager = cacheManager;
    this.apiId = telemetryRequest.apiId;
    this.correlationId = telemetryRequest.correlationId;
    this.wrapperSKU = telemetryRequest.wrapperSKU || Constants.EMPTY_STRING;
    this.wrapperVer = telemetryRequest.wrapperVer || Constants.EMPTY_STRING;
    this.telemetryCacheKey = SERVER_TELEM_CONSTANTS.CACHE_KEY + Separators.CACHE_KEY_SEPARATOR + telemetryRequest.clientId;
  }
  /**
   * API to add MSER Telemetry to request
   */


  ServerTelemetryManager.prototype.generateCurrentRequestHeaderValue = function () {
    var request = "" + this.apiId + SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR + this.cacheOutcome;
    var platformFields = [this.wrapperSKU, this.wrapperVer].join(SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR);
    var regionDiscoveryFields = this.getRegionDiscoveryFields();
    var requestWithRegionDiscoveryFields = [request, regionDiscoveryFields].join(SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR);
    return [SERVER_TELEM_CONSTANTS.SCHEMA_VERSION, requestWithRegionDiscoveryFields, platformFields].join(SERVER_TELEM_CONSTANTS.CATEGORY_SEPARATOR);
  };
  /**
   * API to add MSER Telemetry for the last failed request
   */


  ServerTelemetryManager.prototype.generateLastRequestHeaderValue = function () {
    var lastRequests = this.getLastRequests();
    var maxErrors = ServerTelemetryManager.maxErrorsToSend(lastRequests);
    var failedRequests = lastRequests.failedRequests.slice(0, 2 * maxErrors).join(SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR);
    var errors = lastRequests.errors.slice(0, maxErrors).join(SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR);
    var errorCount = lastRequests.errors.length; // Indicate whether this header contains all data or partial data

    var overflow = maxErrors < errorCount ? SERVER_TELEM_CONSTANTS.OVERFLOW_TRUE : SERVER_TELEM_CONSTANTS.OVERFLOW_FALSE;
    var platformFields = [errorCount, overflow].join(SERVER_TELEM_CONSTANTS.VALUE_SEPARATOR);
    return [SERVER_TELEM_CONSTANTS.SCHEMA_VERSION, lastRequests.cacheHits, failedRequests, errors, platformFields].join(SERVER_TELEM_CONSTANTS.CATEGORY_SEPARATOR);
  };
  /**
   * API to cache token failures for MSER data capture
   * @param error
   */


  ServerTelemetryManager.prototype.cacheFailedRequest = function (error) {
    var lastRequests = this.getLastRequests();

    if (lastRequests.errors.length >= SERVER_TELEM_CONSTANTS.MAX_CACHED_ERRORS) {
      // Remove a cached error to make room, first in first out
      lastRequests.failedRequests.shift(); // apiId

      lastRequests.failedRequests.shift(); // correlationId

      lastRequests.errors.shift();
    }

    lastRequests.failedRequests.push(this.apiId, this.correlationId);

    if (!StringUtils.isEmpty(error.subError)) {
      lastRequests.errors.push(error.subError);
    } else if (!StringUtils.isEmpty(error.errorCode)) {
      lastRequests.errors.push(error.errorCode);
    } else if (!!error && error.toString()) {
      lastRequests.errors.push(error.toString());
    } else {
      lastRequests.errors.push(SERVER_TELEM_CONSTANTS.UNKNOWN_ERROR);
    }

    this.cacheManager.setServerTelemetry(this.telemetryCacheKey, lastRequests);
    return;
  };
  /**
   * Update server telemetry cache entry by incrementing cache hit counter
   */


  ServerTelemetryManager.prototype.incrementCacheHits = function () {
    var lastRequests = this.getLastRequests();
    lastRequests.cacheHits += 1;
    this.cacheManager.setServerTelemetry(this.telemetryCacheKey, lastRequests);
    return lastRequests.cacheHits;
  };
  /**
   * Get the server telemetry entity from cache or initialize a new one
   */


  ServerTelemetryManager.prototype.getLastRequests = function () {
    var initialValue = new ServerTelemetryEntity();
    var lastRequests = this.cacheManager.getServerTelemetry(this.telemetryCacheKey);
    return lastRequests || initialValue;
  };
  /**
   * Remove server telemetry cache entry
   */


  ServerTelemetryManager.prototype.clearTelemetryCache = function () {
    var lastRequests = this.getLastRequests();
    var numErrorsFlushed = ServerTelemetryManager.maxErrorsToSend(lastRequests);
    var errorCount = lastRequests.errors.length;

    if (numErrorsFlushed === errorCount) {
      // All errors were sent on last request, clear Telemetry cache
      this.cacheManager.removeItem(this.telemetryCacheKey);
    } else {
      // Partial data was flushed to server, construct a new telemetry cache item with errors that were not flushed
      var serverTelemEntity = new ServerTelemetryEntity();
      serverTelemEntity.failedRequests = lastRequests.failedRequests.slice(numErrorsFlushed * 2); // failedRequests contains 2 items for each error

      serverTelemEntity.errors = lastRequests.errors.slice(numErrorsFlushed);
      this.cacheManager.setServerTelemetry(this.telemetryCacheKey, serverTelemEntity);
    }
  };
  /**
   * Returns the maximum number of errors that can be flushed to the server in the next network request
   * @param serverTelemetryEntity
   */


  ServerTelemetryManager.maxErrorsToSend = function (serverTelemetryEntity) {
    var i;
    var maxErrors = 0;
    var dataSize = 0;
    var errorCount = serverTelemetryEntity.errors.length;

    for (i = 0; i < errorCount; i++) {
      // failedRequests parameter contains pairs of apiId and correlationId, multiply index by 2 to preserve pairs
      var apiId = serverTelemetryEntity.failedRequests[2 * i] || Constants.EMPTY_STRING;
      var correlationId = serverTelemetryEntity.failedRequests[2 * i + 1] || Constants.EMPTY_STRING;
      var errorCode = serverTelemetryEntity.errors[i] || Constants.EMPTY_STRING; // Count number of characters that would be added to header, each character is 1 byte. Add 3 at the end to account for separators

      dataSize += apiId.toString().length + correlationId.toString().length + errorCode.length + 3;

      if (dataSize < SERVER_TELEM_CONSTANTS.MAX_LAST_HEADER_BYTES) {
        // Adding this entry to the header would still keep header size below the limit
        maxErrors += 1;
      } else {
        break;
      }
    }

    return maxErrors;
  };
  /**
   * Get the region discovery fields
   *
   * @returns string
   */


  ServerTelemetryManager.prototype.getRegionDiscoveryFields = function () {
    var regionDiscoveryFields = [];
    regionDiscoveryFields.push(this.regionUsed || Constants.EMPTY_STRING);
    regionDiscoveryFields.push(this.regionSource || Constants.EMPTY_STRING);
    regionDiscoveryFields.push(this.regionOutcome || Constants.EMPTY_STRING);
    return regionDiscoveryFields.join(",");
  };
  /**
   * Update the region discovery metadata
   *
   * @param regionDiscoveryMetadata
   * @returns void
   */


  ServerTelemetryManager.prototype.updateRegionDiscoveryMetadata = function (regionDiscoveryMetadata) {
    this.regionUsed = regionDiscoveryMetadata.region_used;
    this.regionSource = regionDiscoveryMetadata.region_source;
    this.regionOutcome = regionDiscoveryMetadata.region_outcome;
  };
  /**
   * Set cache outcome
   */


  ServerTelemetryManager.prototype.setCacheOutcome = function (cacheOutcome) {
    this.cacheOutcome = cacheOutcome;
  };

  return ServerTelemetryManager;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var PerformanceClient =
/** @class */
function () {
  /**
   * Creates an instance of PerformanceClient,
   * an abstract class containing core performance telemetry logic.
   *
   * @constructor
   * @param {string} clientId Client ID of the application
   * @param {string} authority Authority used by the application
   * @param {Logger} logger Logger used by the application
   * @param {string} libraryName Name of the library
   * @param {string} libraryVersion Version of the library
   */
  function PerformanceClient(clientId, authority, logger, libraryName, libraryVersion, applicationTelemetry) {
    this.authority = authority;
    this.libraryName = libraryName;
    this.libraryVersion = libraryVersion;
    this.applicationTelemetry = applicationTelemetry;
    this.clientId = clientId;
    this.logger = logger;
    this.callbacks = new Map();
    this.eventsByCorrelationId = new Map();
    this.measurementsById = new Map();
  }
  /**
   * Starts measuring performance for a given operation. Returns a function that should be used to end the measurement.
   *
   * @param {PerformanceEvents} measureName
   * @param {?string} [correlationId]
   * @returns {InProgressPerformanceEvent}
   */


  PerformanceClient.prototype.startMeasurement = function (measureName, correlationId) {
    var _this = this;

    var _a, _b; // Generate a placeholder correlation if the request does not provide one


    var eventCorrelationId = correlationId || this.generateId();

    if (!correlationId) {
      this.logger.info("PerformanceClient: No correlation id provided for " + measureName + ", generating", eventCorrelationId);
    }

    this.logger.trace("PerformanceClient: Performance measurement started for " + measureName, eventCorrelationId);
    var performanceMeasurement = this.startPerformanceMeasuremeant(measureName, eventCorrelationId);
    performanceMeasurement.startMeasurement();
    var inProgressEvent = {
      eventId: this.generateId(),
      status: PerformanceEventStatus.InProgress,
      authority: this.authority,
      libraryName: this.libraryName,
      libraryVersion: this.libraryVersion,
      appName: (_a = this.applicationTelemetry) === null || _a === void 0 ? void 0 : _a.appName,
      appVersion: (_b = this.applicationTelemetry) === null || _b === void 0 ? void 0 : _b.appVersion,
      clientId: this.clientId,
      name: measureName,
      startTimeMs: Date.now(),
      correlationId: eventCorrelationId
    }; // Store in progress events so they can be discarded if not ended properly

    this.cacheEventByCorrelationId(inProgressEvent);
    this.cacheMeasurement(inProgressEvent, performanceMeasurement); // Return the event and functions the caller can use to properly end/flush the measurement

    return {
      endMeasurement: function (event) {
        var completedEvent = _this.endMeasurement(__assign(__assign({}, inProgressEvent), event));

        if (completedEvent) {
          // Cache event so that submeasurements can be added downstream
          _this.cacheEventByCorrelationId(completedEvent);
        }

        return completedEvent;
      },
      flushMeasurement: function () {
        return _this.flushMeasurements(inProgressEvent.name, inProgressEvent.correlationId);
      },
      discardMeasurement: function () {
        return _this.discardMeasurements(inProgressEvent.correlationId);
      },
      measurement: performanceMeasurement,
      event: inProgressEvent
    };
  };
  /**
   * Stops measuring the performance for an operation. Should only be called directly by PerformanceClient classes,
   * as consumers should instead use the function returned by startMeasurement.
   *
   * @param {PerformanceEvent} event
   * @returns {(PerformanceEvent | null)}
   */


  PerformanceClient.prototype.endMeasurement = function (event) {
    var performanceMeasurement = this.measurementsById.get(event.eventId);

    if (performanceMeasurement) {
      // Immediately delete so that the same event isnt ended twice
      this.measurementsById.delete(event.eventId);
      performanceMeasurement.endMeasurement();
      var durationMs = performanceMeasurement.flushMeasurement(); // null indicates no measurement was taken (e.g. needed performance APIs not present)

      if (durationMs !== null) {
        this.logger.trace("PerformanceClient: Performance measurement ended for " + event.name + ": " + durationMs + " ms", event.correlationId);

        var completedEvent = __assign(__assign({
          // Allow duration to be overwritten when event ends (e.g. testing), but not status
          durationMs: Math.round(durationMs)
        }, event), {
          status: PerformanceEventStatus.Completed
        });

        return completedEvent;
      } else {
        this.logger.trace("PerformanceClient: Performance measurement not taken", event.correlationId);
      }
    } else {
      this.logger.trace("PerformanceClient: Measurement not found for " + event.eventId, event.correlationId);
    }

    return null;
  };
  /**
   * Upserts event into event cache.
   * First key is the correlation id, second key is the event id.
   * Allows for events to be grouped by correlation id,
   * and to easily allow for properties on them to be updated.
   *
   * @private
   * @param {PerformanceEvent} event
   */


  PerformanceClient.prototype.cacheEventByCorrelationId = function (event) {
    var existingEvents = this.eventsByCorrelationId.get(event.correlationId);

    if (existingEvents) {
      this.logger.trace("PerformanceClient: Performance measurement for " + event.name + " added/updated", event.correlationId);
      existingEvents.set(event.eventId, event);
    } else {
      this.logger.trace("PerformanceClient: Performance measurement for " + event.name + " started", event.correlationId);
      this.eventsByCorrelationId.set(event.correlationId, new Map().set(event.eventId, event));
    }
  };
  /**
   * Cache measurements by their id.
   *
   * @private
   * @param {PerformanceEvent} event
   * @param {IPerformanceMeasurement} measurement
   */


  PerformanceClient.prototype.cacheMeasurement = function (event, measurement) {
    this.measurementsById.set(event.eventId, measurement);
  };
  /**
   * Gathers and emits performance events for measurements taked for the given top-level API and correlation ID.
   *
   * @param {PerformanceEvents} measureName
   * @param {string} correlationId
   */


  PerformanceClient.prototype.flushMeasurements = function (measureName, correlationId) {
    var _this = this;

    this.logger.trace("PerformanceClient: Performance measurements flushed for " + measureName, correlationId);
    var eventsForCorrelationId = this.eventsByCorrelationId.get(correlationId);

    if (eventsForCorrelationId) {
      this.discardMeasurements(correlationId);
      /*
       * Manually end incomplete submeasurements to ensure there arent orphaned/never ending events.
       * Incomplete submeasurements are likely an instrumentation bug that should be fixed.
       * IE only supports Map.forEach.
       */

      var completedEvents_1 = [];
      eventsForCorrelationId.forEach(function (event) {
        if (event.name !== measureName && event.status !== PerformanceEventStatus.Completed) {
          _this.logger.trace("PerformanceClient: Incomplete submeasurement " + event.name + " found for " + measureName, correlationId);

          var completedEvent = _this.endMeasurement(event);

          if (completedEvent) {
            completedEvents_1.push(completedEvent);
          }
        }

        completedEvents_1.push(event);
      }); // Sort events by start time (earliest first)

      var sortedCompletedEvents = completedEvents_1.sort(function (eventA, eventB) {
        return eventA.startTimeMs - eventB.startTimeMs;
      }); // Take completed top level event and add completed submeasurements durations as properties

      var topLevelEvents = sortedCompletedEvents.filter(function (event) {
        return event.name === measureName && event.status === PerformanceEventStatus.Completed;
      });

      if (topLevelEvents.length > 0) {
        /*
         * Only take the first top-level event if there are multiple events with the same correlation id.
         * This greatly simplifies logic for submeasurements.
         */
        if (topLevelEvents.length > 1) {
          this.logger.verbose("PerformanceClient: Multiple distinct top-level performance events found, using the first", correlationId);
        }

        var topLevelEvent = topLevelEvents[0];
        this.logger.verbose("PerformanceClient: Measurement found for " + measureName, correlationId); // Build event object with top level and sub measurements

        var eventToEmit = sortedCompletedEvents.reduce(function (previous, current) {
          if (current.name !== measureName) {
            _this.logger.trace("PerformanceClient: Complete submeasurement found for " + current.name, correlationId); // TODO: Emit additional properties for each subMeasurement


            var subMeasurementName = current.name + "DurationMs";
            /*
             * Some code paths, such as resolving an authority, can occur multiple times.
             * Only take the first measurement, since the second could be read from the cache,
             * or due to the same correlation id being used for two distinct requests.
             */

            if (!previous[subMeasurementName]) {
              previous[subMeasurementName] = current.durationMs;
            } else {
              _this.logger.verbose("PerformanceClient: Submeasurement for " + measureName + " already exists for " + current.name + ", ignoring", correlationId);
            }

            if (current.accessTokenSize) {
              previous.accessTokenSize = current.accessTokenSize;
            }

            if (current.idTokenSize) {
              previous.idTokenSize = current.idTokenSize;
            }

            if (current.refreshTokenSize) {
              previous.refreshTokenSize = current.refreshTokenSize;
            }
          }

          return previous;
        }, topLevelEvent);
        this.emitEvents([eventToEmit], eventToEmit.correlationId);
      } else {
        this.logger.verbose("PerformanceClient: No completed top-level measurements found for " + measureName, correlationId);
      }
    } else {
      this.logger.verbose("PerformanceClient: No measurements found", correlationId);
    }
  };
  /**
   * Removes measurements for a given correlation id.
   *
   * @param {string} correlationId
   */


  PerformanceClient.prototype.discardMeasurements = function (correlationId) {
    this.logger.trace("PerformanceClient: Performance measurements discarded", correlationId);
    this.eventsByCorrelationId.delete(correlationId);
  };
  /**
   * Registers a callback function to receive performance events.
   *
   * @param {PerformanceCallbackFunction} callback
   * @returns {string}
   */


  PerformanceClient.prototype.addPerformanceCallback = function (callback) {
    var callbackId = this.generateId();
    this.callbacks.set(callbackId, callback);
    this.logger.verbose("PerformanceClient: Performance callback registered with id: " + callbackId);
    return callbackId;
  };
  /**
   * Removes a callback registered with addPerformanceCallback.
   *
   * @param {string} callbackId
   * @returns {boolean}
   */


  PerformanceClient.prototype.removePerformanceCallback = function (callbackId) {
    var result = this.callbacks.delete(callbackId);

    if (result) {
      this.logger.verbose("PerformanceClient: Performance callback " + callbackId + " removed.");
    } else {
      this.logger.verbose("PerformanceClient: Performance callback " + callbackId + " not removed.");
    }

    return result;
  };
  /**
   * Emits events to all registered callbacks.
   *
   * @param {PerformanceEvent[]} events
   * @param {?string} [correlationId]
   */


  PerformanceClient.prototype.emitEvents = function (events, correlationId) {
    var _this = this;

    this.logger.verbose("PerformanceClient: Emitting performance events", correlationId);
    this.callbacks.forEach(function (callback, callbackId) {
      _this.logger.trace("PerformanceClient: Emitting event to callback " + callbackId, correlationId);

      callback.apply(null, [events]);
    });
  };

  return PerformanceClient;
}();

/*! @azure/msal-common v7.6.0 2022-10-10 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var StubPerformanceMeasurement =
/** @class */
function () {
  function StubPerformanceMeasurement() {}
  /* eslint-disable-next-line @typescript-eslint/no-empty-function */


  StubPerformanceMeasurement.prototype.startMeasurement = function () {};
  /* eslint-disable-next-line @typescript-eslint/no-empty-function */


  StubPerformanceMeasurement.prototype.endMeasurement = function () {};

  StubPerformanceMeasurement.prototype.flushMeasurement = function () {
    return null;
  };

  return StubPerformanceMeasurement;
}();

var StubPerformanceClient =
/** @class */
function (_super) {
  __extends(StubPerformanceClient, _super);

  function StubPerformanceClient() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  StubPerformanceClient.prototype.generateId = function () {
    return "callback-id";
  };

  StubPerformanceClient.prototype.startPerformanceMeasuremeant = function () {
    return new StubPerformanceMeasurement();
  };

  return StubPerformanceClient;
}(PerformanceClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * BrowserAuthErrorMessage class containing string constants used by error codes and messages.
 */

var BrowserAuthErrorMessage = {
  pkceNotGenerated: {
    code: "pkce_not_created",
    desc: "The PKCE code challenge and verifier could not be generated."
  },
  cryptoDoesNotExist: {
    code: "crypto_nonexistent",
    desc: "The crypto object or function is not available."
  },
  httpMethodNotImplementedError: {
    code: "http_method_not_implemented",
    desc: "The HTTP method given has not been implemented in this library."
  },
  emptyNavigateUriError: {
    code: "empty_navigate_uri",
    desc: "Navigation URI is empty. Please check stack trace for more info."
  },
  hashEmptyError: {
    code: "hash_empty_error",
    desc: "Hash value cannot be processed because it is empty. Please verify that your redirectUri is not clearing the hash. For more visit: aka.ms/msaljs/browser-errors."
  },
  hashDoesNotContainStateError: {
    code: "no_state_in_hash",
    desc: "Hash does not contain state. Please verify that the request originated from msal."
  },
  hashDoesNotContainKnownPropertiesError: {
    code: "hash_does_not_contain_known_properties",
    desc: "Hash does not contain known properites. Please verify that your redirectUri is not changing the hash. For more visit: aka.ms/msaljs/browser-errors."
  },
  unableToParseStateError: {
    code: "unable_to_parse_state",
    desc: "Unable to parse state. Please verify that the request originated from msal."
  },
  stateInteractionTypeMismatchError: {
    code: "state_interaction_type_mismatch",
    desc: "Hash contains state but the interaction type does not match the caller."
  },
  interactionInProgress: {
    code: "interaction_in_progress",
    desc: "Interaction is currently in progress. Please ensure that this interaction has been completed before calling an interactive API.  For more visit: aka.ms/msaljs/browser-errors."
  },
  popupWindowError: {
    code: "popup_window_error",
    desc: "Error opening popup window. This can happen if you are using IE or if popups are blocked in the browser."
  },
  emptyWindowError: {
    code: "empty_window_error",
    desc: "window.open returned null or undefined window object."
  },
  userCancelledError: {
    code: "user_cancelled",
    desc: "User cancelled the flow."
  },
  monitorPopupTimeoutError: {
    code: "monitor_window_timeout",
    desc: "Token acquisition in popup failed due to timeout. For more visit: aka.ms/msaljs/browser-errors."
  },
  monitorIframeTimeoutError: {
    code: "monitor_window_timeout",
    desc: "Token acquisition in iframe failed due to timeout. For more visit: aka.ms/msaljs/browser-errors."
  },
  redirectInIframeError: {
    code: "redirect_in_iframe",
    desc: "Redirects are not supported for iframed or brokered applications. Please ensure you are using MSAL.js in a top frame of the window if using the redirect APIs, or use the popup APIs."
  },
  blockTokenRequestsInHiddenIframeError: {
    code: "block_iframe_reload",
    desc: "Request was blocked inside an iframe because MSAL detected an authentication response. For more visit: aka.ms/msaljs/browser-errors"
  },
  blockAcquireTokenInPopupsError: {
    code: "block_nested_popups",
    desc: "Request was blocked inside a popup because MSAL detected it was running in a popup."
  },
  iframeClosedPrematurelyError: {
    code: "iframe_closed_prematurely",
    desc: "The iframe being monitored was closed prematurely."
  },
  silentLogoutUnsupportedError: {
    code: "silent_logout_unsupported",
    desc: "Silent logout not supported. Please call logoutRedirect or logoutPopup instead."
  },
  noAccountError: {
    code: "no_account_error",
    desc: "No account object provided to acquireTokenSilent and no active account has been set. Please call setActiveAccount or provide an account on the request."
  },
  silentPromptValueError: {
    code: "silent_prompt_value_error",
    desc: "The value given for the prompt value is not valid for silent requests - must be set to 'none'."
  },
  noTokenRequestCacheError: {
    code: "no_token_request_cache_error",
    desc: "No token request found in cache."
  },
  unableToParseTokenRequestCacheError: {
    code: "unable_to_parse_token_request_cache_error",
    desc: "The cached token request could not be parsed."
  },
  noCachedAuthorityError: {
    code: "no_cached_authority_error",
    desc: "No cached authority found."
  },
  authRequestNotSet: {
    code: "auth_request_not_set_error",
    desc: "Auth Request not set. Please ensure initiateAuthRequest was called from the InteractionHandler"
  },
  invalidCacheType: {
    code: "invalid_cache_type",
    desc: "Invalid cache type"
  },
  notInBrowserEnvironment: {
    code: "non_browser_environment",
    desc: "Login and token requests are not supported in non-browser environments."
  },
  databaseNotOpen: {
    code: "database_not_open",
    desc: "Database is not open!"
  },
  noNetworkConnectivity: {
    code: "no_network_connectivity",
    desc: "No network connectivity. Check your internet connection."
  },
  postRequestFailed: {
    code: "post_request_failed",
    desc: "Network request failed: If the browser threw a CORS error, check that the redirectUri is registered in the Azure App Portal as type 'SPA'"
  },
  getRequestFailed: {
    code: "get_request_failed",
    desc: "Network request failed. Please check the network trace to determine root cause."
  },
  failedToParseNetworkResponse: {
    code: "failed_to_parse_response",
    desc: "Failed to parse network response. Check network trace."
  },
  unableToLoadTokenError: {
    code: "unable_to_load_token",
    desc: "Error loading token to cache."
  },
  signingKeyNotFoundInStorage: {
    code: "crypto_key_not_found",
    desc: "Cryptographic Key or Keypair not found in browser storage."
  },
  authCodeRequired: {
    code: "auth_code_required",
    desc: "An authorization code must be provided (as the `code` property on the request) to this flow."
  },
  authCodeOrNativeAccountRequired: {
    code: "auth_code_or_nativeAccountId_required",
    desc: "An authorization code or nativeAccountId must be provided to this flow."
  },
  databaseUnavailable: {
    code: "database_unavailable",
    desc: "IndexedDB, which is required for persistent cryptographic key storage, is unavailable. This may be caused by browser privacy features which block persistent storage in third-party contexts."
  },
  unableToAcquireTokenFromNativePlatform: {
    code: "unable_to_acquire_token_from_native_platform",
    desc: "Unable to acquire token from native platform. For a list of possible reasons visit aka.ms/msaljs/browser-errors."
  },
  nativeHandshakeTimeout: {
    code: "native_handshake_timeout",
    desc: "Timed out while attempting to establish connection to browser extension"
  },
  nativeExtensionNotInstalled: {
    code: "native_extension_not_installed",
    desc: "Native extension is not installed. If you think this is a mistake call the initialize function."
  },
  nativeConnectionNotEstablished: {
    code: "native_connection_not_established",
    desc: "Connection to native platform has not been established. Please install a compatible browser extension and run initialize(). For more please visit aka.ms/msaljs/browser-errors."
  },
  nativeBrokerCalledBeforeInitialize: {
    code: "native_broker_called_before_initialize",
    desc: "You must call and await the initialize function before attempting to call any other MSAL API when native brokering is enabled. For more please visit aka.ms/msaljs/browser-errors."
  },
  nativePromptNotSupported: {
    code: "native_prompt_not_supported",
    desc: "The provided prompt is not supported by the native platform. This request should be routed to the web based flow."
  }
};
/**
 * Browser library error class thrown by the MSAL.js library for SPAs
 */

var BrowserAuthError =
/** @class */
function (_super) {
  __extends$1(BrowserAuthError, _super);

  function BrowserAuthError(errorCode, errorMessage) {
    var _this = _super.call(this, errorCode, errorMessage) || this;

    Object.setPrototypeOf(_this, BrowserAuthError.prototype);
    _this.name = "BrowserAuthError";
    return _this;
  }
  /**
   * Creates an error thrown when PKCE is not implemented.
   * @param errDetail
   */


  BrowserAuthError.createPkceNotGeneratedError = function (errDetail) {
    return new BrowserAuthError(BrowserAuthErrorMessage.pkceNotGenerated.code, BrowserAuthErrorMessage.pkceNotGenerated.desc + " Detail:" + errDetail);
  };
  /**
   * Creates an error thrown when the crypto object is unavailable.
   * @param errDetail
   */


  BrowserAuthError.createCryptoNotAvailableError = function (errDetail) {
    return new BrowserAuthError(BrowserAuthErrorMessage.cryptoDoesNotExist.code, BrowserAuthErrorMessage.cryptoDoesNotExist.desc + " Detail:" + errDetail);
  };
  /**
   * Creates an error thrown when an HTTP method hasn't been implemented by the browser class.
   * @param method
   */


  BrowserAuthError.createHttpMethodNotImplementedError = function (method) {
    return new BrowserAuthError(BrowserAuthErrorMessage.httpMethodNotImplementedError.code, BrowserAuthErrorMessage.httpMethodNotImplementedError.desc + " Given Method: " + method);
  };
  /**
   * Creates an error thrown when the navigation URI is empty.
   */


  BrowserAuthError.createEmptyNavigationUriError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.emptyNavigateUriError.code, BrowserAuthErrorMessage.emptyNavigateUriError.desc);
  };
  /**
   * Creates an error thrown when the hash string value is unexpectedly empty.
   * @param hashValue
   */


  BrowserAuthError.createEmptyHashError = function (hashValue) {
    return new BrowserAuthError(BrowserAuthErrorMessage.hashEmptyError.code, BrowserAuthErrorMessage.hashEmptyError.desc + " Given Url: " + hashValue);
  };
  /**
   * Creates an error thrown when the hash string value is unexpectedly empty.
   */


  BrowserAuthError.createHashDoesNotContainStateError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.hashDoesNotContainStateError.code, BrowserAuthErrorMessage.hashDoesNotContainStateError.desc);
  };
  /**
   * Creates an error thrown when the hash string value does not contain known properties
   */


  BrowserAuthError.createHashDoesNotContainKnownPropertiesError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.hashDoesNotContainKnownPropertiesError.code, BrowserAuthErrorMessage.hashDoesNotContainKnownPropertiesError.desc);
  };
  /**
   * Creates an error thrown when the hash string value is unexpectedly empty.
   */


  BrowserAuthError.createUnableToParseStateError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.unableToParseStateError.code, BrowserAuthErrorMessage.unableToParseStateError.desc);
  };
  /**
   * Creates an error thrown when the state value in the hash does not match the interaction type of the API attempting to consume it.
   */


  BrowserAuthError.createStateInteractionTypeMismatchError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.stateInteractionTypeMismatchError.code, BrowserAuthErrorMessage.stateInteractionTypeMismatchError.desc);
  };
  /**
   * Creates an error thrown when a browser interaction (redirect or popup) is in progress.
   */


  BrowserAuthError.createInteractionInProgressError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.interactionInProgress.code, BrowserAuthErrorMessage.interactionInProgress.desc);
  };
  /**
   * Creates an error thrown when the popup window could not be opened.
   * @param errDetail
   */


  BrowserAuthError.createPopupWindowError = function (errDetail) {
    var errorMessage = BrowserAuthErrorMessage.popupWindowError.desc;
    errorMessage = !StringUtils.isEmpty(errDetail) ? errorMessage + " Details: " + errDetail : errorMessage;
    return new BrowserAuthError(BrowserAuthErrorMessage.popupWindowError.code, errorMessage);
  };
  /**
   * Creates an error thrown when window.open returns an empty window object.
   * @param errDetail
   */


  BrowserAuthError.createEmptyWindowCreatedError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.emptyWindowError.code, BrowserAuthErrorMessage.emptyWindowError.desc);
  };
  /**
   * Creates an error thrown when the user closes a popup.
   */


  BrowserAuthError.createUserCancelledError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.userCancelledError.code, BrowserAuthErrorMessage.userCancelledError.desc);
  };
  /**
   * Creates an error thrown when monitorPopupFromHash times out for a given popup.
   */


  BrowserAuthError.createMonitorPopupTimeoutError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.monitorPopupTimeoutError.code, BrowserAuthErrorMessage.monitorPopupTimeoutError.desc);
  };
  /**
   * Creates an error thrown when monitorIframeFromHash times out for a given iframe.
   */


  BrowserAuthError.createMonitorIframeTimeoutError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.monitorIframeTimeoutError.code, BrowserAuthErrorMessage.monitorIframeTimeoutError.desc);
  };
  /**
   * Creates an error thrown when navigateWindow is called inside an iframe or brokered applications.
   * @param windowParentCheck
   */


  BrowserAuthError.createRedirectInIframeError = function (windowParentCheck) {
    return new BrowserAuthError(BrowserAuthErrorMessage.redirectInIframeError.code, BrowserAuthErrorMessage.redirectInIframeError.desc + " (window.parent !== window) => " + windowParentCheck);
  };
  /**
   * Creates an error thrown when an auth reload is done inside an iframe.
   */


  BrowserAuthError.createBlockReloadInHiddenIframeError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.blockTokenRequestsInHiddenIframeError.code, BrowserAuthErrorMessage.blockTokenRequestsInHiddenIframeError.desc);
  };
  /**
   * Creates an error thrown when a popup attempts to call an acquireToken API
   * @returns
   */


  BrowserAuthError.createBlockAcquireTokenInPopupsError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.blockAcquireTokenInPopupsError.code, BrowserAuthErrorMessage.blockAcquireTokenInPopupsError.desc);
  };
  /**
   * Creates an error thrown when an iframe is found to be closed before the timeout is reached.
   */


  BrowserAuthError.createIframeClosedPrematurelyError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.iframeClosedPrematurelyError.code, BrowserAuthErrorMessage.iframeClosedPrematurelyError.desc);
  };
  /**
   * Creates an error thrown when the logout API is called on any of the silent interaction clients
   */


  BrowserAuthError.createSilentLogoutUnsupportedError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.silentLogoutUnsupportedError.code, BrowserAuthErrorMessage.silentLogoutUnsupportedError.desc);
  };
  /**
   * Creates an error thrown when the account object is not provided in the acquireTokenSilent API.
   */


  BrowserAuthError.createNoAccountError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.noAccountError.code, BrowserAuthErrorMessage.noAccountError.desc);
  };
  /**
   * Creates an error thrown when a given prompt value is invalid for silent requests.
   */


  BrowserAuthError.createSilentPromptValueError = function (givenPrompt) {
    return new BrowserAuthError(BrowserAuthErrorMessage.silentPromptValueError.code, BrowserAuthErrorMessage.silentPromptValueError.desc + " Given value: " + givenPrompt);
  };
  /**
   * Creates an error thrown when the cached token request could not be retrieved from the cache
   */


  BrowserAuthError.createUnableToParseTokenRequestCacheError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.unableToParseTokenRequestCacheError.code, BrowserAuthErrorMessage.unableToParseTokenRequestCacheError.desc);
  };
  /**
   * Creates an error thrown when the token request could not be retrieved from the cache
   */


  BrowserAuthError.createNoTokenRequestCacheError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.noTokenRequestCacheError.code, BrowserAuthErrorMessage.noTokenRequestCacheError.desc);
  };
  /**
   * Creates an error thrown when handleCodeResponse is called before initiateAuthRequest (InteractionHandler)
   */


  BrowserAuthError.createAuthRequestNotSetError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.authRequestNotSet.code, BrowserAuthErrorMessage.authRequestNotSet.desc);
  };
  /**
   * Creates an error thrown when the authority could not be retrieved from the cache
   */


  BrowserAuthError.createNoCachedAuthorityError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.noCachedAuthorityError.code, BrowserAuthErrorMessage.noCachedAuthorityError.desc);
  };
  /**
   * Creates an error thrown if cache type is invalid.
   */


  BrowserAuthError.createInvalidCacheTypeError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.invalidCacheType.code, "" + BrowserAuthErrorMessage.invalidCacheType.desc);
  };
  /**
   * Create an error thrown when login and token requests are made from a non-browser environment
   */


  BrowserAuthError.createNonBrowserEnvironmentError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.notInBrowserEnvironment.code, BrowserAuthErrorMessage.notInBrowserEnvironment.desc);
  };
  /**
   * Create an error thrown when indexDB database is not open
   */


  BrowserAuthError.createDatabaseNotOpenError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.databaseNotOpen.code, BrowserAuthErrorMessage.databaseNotOpen.desc);
  };
  /**
   * Create an error thrown when token fetch fails due to no internet
   */


  BrowserAuthError.createNoNetworkConnectivityError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.noNetworkConnectivity.code, BrowserAuthErrorMessage.noNetworkConnectivity.desc);
  };
  /**
   * Create an error thrown when token fetch fails due to reasons other than internet connectivity
   */


  BrowserAuthError.createPostRequestFailedError = function (errorDesc, endpoint) {
    return new BrowserAuthError(BrowserAuthErrorMessage.postRequestFailed.code, BrowserAuthErrorMessage.postRequestFailed.desc + " | Network client threw: " + errorDesc + " | Attempted to reach: " + endpoint.split("?")[0]);
  };
  /**
   * Create an error thrown when get request fails due to reasons other than internet connectivity
   */


  BrowserAuthError.createGetRequestFailedError = function (errorDesc, endpoint) {
    return new BrowserAuthError(BrowserAuthErrorMessage.getRequestFailed.code, BrowserAuthErrorMessage.getRequestFailed.desc + " | Network client threw: " + errorDesc + " | Attempted to reach: " + endpoint.split("?")[0]);
  };
  /**
   * Create an error thrown when network client fails to parse network response
   */


  BrowserAuthError.createFailedToParseNetworkResponseError = function (endpoint) {
    return new BrowserAuthError(BrowserAuthErrorMessage.failedToParseNetworkResponse.code, BrowserAuthErrorMessage.failedToParseNetworkResponse.desc + " | Attempted to reach: " + endpoint.split("?")[0]);
  };
  /**
   * Create an error thrown when the necessary information is not available to sideload tokens
   */


  BrowserAuthError.createUnableToLoadTokenError = function (errorDetail) {
    return new BrowserAuthError(BrowserAuthErrorMessage.unableToLoadTokenError.code, BrowserAuthErrorMessage.unableToLoadTokenError.desc + " | " + errorDetail);
  };
  /**
   * Create an error thrown when the queried cryptographic key is not found in IndexedDB
   */


  BrowserAuthError.createSigningKeyNotFoundInStorageError = function (keyId) {
    return new BrowserAuthError(BrowserAuthErrorMessage.signingKeyNotFoundInStorage.code, BrowserAuthErrorMessage.signingKeyNotFoundInStorage.desc + " | No match found for KeyId: " + keyId);
  };
  /**
   * Create an error when an authorization code is required but not provided
   */


  BrowserAuthError.createAuthCodeRequiredError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.authCodeRequired.code, BrowserAuthErrorMessage.authCodeRequired.desc);
  };
  /**
   * Create an error when an authorization code or native account ID is required but not provided
   */


  BrowserAuthError.createAuthCodeOrNativeAccountIdRequiredError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.authCodeOrNativeAccountRequired.code, BrowserAuthErrorMessage.authCodeOrNativeAccountRequired.desc);
  };
  /**
   * Create an error when IndexedDB is unavailable
   */


  BrowserAuthError.createDatabaseUnavailableError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.databaseUnavailable.code, BrowserAuthErrorMessage.databaseUnavailable.desc);
  };
  /**
   * Create an error when native token acquisition is not possible
   */


  BrowserAuthError.createUnableToAcquireTokenFromNativePlatformError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.unableToAcquireTokenFromNativePlatform.code, BrowserAuthErrorMessage.unableToAcquireTokenFromNativePlatform.desc);
  };
  /**
   * Create an error thrown when Handshake with browser extension times out
   */


  BrowserAuthError.createNativeHandshakeTimeoutError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.nativeHandshakeTimeout.code, BrowserAuthErrorMessage.nativeHandshakeTimeout.desc);
  };
  /**
   * Create an error thrown when browser extension is not installed
   */


  BrowserAuthError.createNativeExtensionNotInstalledError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.nativeExtensionNotInstalled.code, BrowserAuthErrorMessage.nativeExtensionNotInstalled.desc);
  };
  /**
   * Create an error when native connection has not been established
   * @returns
   */


  BrowserAuthError.createNativeConnectionNotEstablishedError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.nativeConnectionNotEstablished.code, BrowserAuthErrorMessage.nativeConnectionNotEstablished.desc);
  };
  /**
   * Create an error thrown when the initialize function hasn't been called
   */


  BrowserAuthError.createNativeBrokerCalledBeforeInitialize = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.nativeBrokerCalledBeforeInitialize.code, BrowserAuthErrorMessage.nativeBrokerCalledBeforeInitialize.desc);
  };
  /**
   * Create an error thrown when requesting a token directly from the native platform with an unsupported prompt parameter e.g. select_account, login or create
   * These requests must go through eSTS to ensure eSTS is aware of the new account
   */


  BrowserAuthError.createNativePromptParameterNotSupportedError = function () {
    return new BrowserAuthError(BrowserAuthErrorMessage.nativePromptNotSupported.code, BrowserAuthErrorMessage.nativePromptNotSupported.desc);
  };

  return BrowserAuthError;
}(AuthError);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Constants
 */

var BrowserConstants = {
  /**
   * Interaction in progress cache value
   */
  INTERACTION_IN_PROGRESS_VALUE: "interaction_in_progress",

  /**
   * Invalid grant error code
   */
  INVALID_GRANT_ERROR: "invalid_grant",

  /**
   * Default popup window width
   */
  POPUP_WIDTH: 483,

  /**
   * Default popup window height
   */
  POPUP_HEIGHT: 600,

  /**
   * Name of the popup window starts with
   */
  POPUP_NAME_PREFIX: "msal",

  /**
   * Default popup monitor poll interval in milliseconds
   */
  POLL_INTERVAL_MS: 50,

  /**
   * Msal-browser SKU
   */
  MSAL_SKU: "msal.js.browser"
};
var NativeConstants = {
  CHANNEL_ID: "53ee284d-920a-4b59-9d30-a60315b26836",
  PREFERRED_EXTENSION_ID: "ppnbnpeolgkicgegkbkbjmhlideopiji",
  MATS_TELEMETRY: "MATS"
};
var NativeExtensionMethod;

(function (NativeExtensionMethod) {
  NativeExtensionMethod["HandshakeRequest"] = "Handshake";
  NativeExtensionMethod["HandshakeResponse"] = "HandshakeResponse";
  NativeExtensionMethod["GetToken"] = "GetToken";
  NativeExtensionMethod["Response"] = "Response";
})(NativeExtensionMethod || (NativeExtensionMethod = {}));

var BrowserCacheLocation;

(function (BrowserCacheLocation) {
  BrowserCacheLocation["LocalStorage"] = "localStorage";
  BrowserCacheLocation["SessionStorage"] = "sessionStorage";
  BrowserCacheLocation["MemoryStorage"] = "memoryStorage";
})(BrowserCacheLocation || (BrowserCacheLocation = {}));
/**
 * HTTP Request types supported by MSAL.
 */


var HTTP_REQUEST_TYPE;

(function (HTTP_REQUEST_TYPE) {
  HTTP_REQUEST_TYPE["GET"] = "GET";
  HTTP_REQUEST_TYPE["POST"] = "POST";
})(HTTP_REQUEST_TYPE || (HTTP_REQUEST_TYPE = {}));
/**
 * Temporary cache keys for MSAL, deleted after any request.
 */


var TemporaryCacheKeys;

(function (TemporaryCacheKeys) {
  TemporaryCacheKeys["AUTHORITY"] = "authority";
  TemporaryCacheKeys["ACQUIRE_TOKEN_ACCOUNT"] = "acquireToken.account";
  TemporaryCacheKeys["SESSION_STATE"] = "session.state";
  TemporaryCacheKeys["REQUEST_STATE"] = "request.state";
  TemporaryCacheKeys["NONCE_IDTOKEN"] = "nonce.id_token";
  TemporaryCacheKeys["ORIGIN_URI"] = "request.origin";
  TemporaryCacheKeys["RENEW_STATUS"] = "token.renew.status";
  TemporaryCacheKeys["URL_HASH"] = "urlHash";
  TemporaryCacheKeys["REQUEST_PARAMS"] = "request.params";
  TemporaryCacheKeys["SCOPES"] = "scopes";
  TemporaryCacheKeys["INTERACTION_STATUS_KEY"] = "interaction.status";
  TemporaryCacheKeys["CCS_CREDENTIAL"] = "ccs.credential";
  TemporaryCacheKeys["CORRELATION_ID"] = "request.correlationId";
  TemporaryCacheKeys["NATIVE_REQUEST"] = "request.native";
})(TemporaryCacheKeys || (TemporaryCacheKeys = {}));
/**
 * Cache keys stored in-memory
 */


var InMemoryCacheKeys;

(function (InMemoryCacheKeys) {
  InMemoryCacheKeys["WRAPPER_SKU"] = "wrapper.sku";
  InMemoryCacheKeys["WRAPPER_VER"] = "wrapper.version";
})(InMemoryCacheKeys || (InMemoryCacheKeys = {}));
/**
 * API Codes for Telemetry purposes.
 * Before adding a new code you must claim it in the MSAL Telemetry tracker as these number spaces are shared across all MSALs
 * 0-99 Silent Flow
 * 800-899 Auth Code Flow
 */


var ApiId;

(function (ApiId) {
  ApiId[ApiId["acquireTokenRedirect"] = 861] = "acquireTokenRedirect";
  ApiId[ApiId["acquireTokenPopup"] = 862] = "acquireTokenPopup";
  ApiId[ApiId["ssoSilent"] = 863] = "ssoSilent";
  ApiId[ApiId["acquireTokenSilent_authCode"] = 864] = "acquireTokenSilent_authCode";
  ApiId[ApiId["handleRedirectPromise"] = 865] = "handleRedirectPromise";
  ApiId[ApiId["acquireTokenByCode"] = 866] = "acquireTokenByCode";
  ApiId[ApiId["acquireTokenSilent_silentFlow"] = 61] = "acquireTokenSilent_silentFlow";
  ApiId[ApiId["logout"] = 961] = "logout";
  ApiId[ApiId["logoutPopup"] = 962] = "logoutPopup";
})(ApiId || (ApiId = {}));
/*
 * Interaction type of the API - used for state and telemetry
 */


var InteractionType;

(function (InteractionType) {
  InteractionType["Redirect"] = "redirect";
  InteractionType["Popup"] = "popup";
  InteractionType["Silent"] = "silent";
  InteractionType["None"] = "none";
})(InteractionType || (InteractionType = {}));
/**
 * Types of interaction currently in progress.
 * Used in events in wrapper libraries to invoke functions when certain interaction is in progress or all interactions are complete.
 */


var InteractionStatus;

(function (InteractionStatus) {
  /**
   * Initial status before interaction occurs
   */
  InteractionStatus["Startup"] = "startup";
  /**
   * Status set when all login calls occuring
   */

  InteractionStatus["Login"] = "login";
  /**
   * Status set when logout call occuring
   */

  InteractionStatus["Logout"] = "logout";
  /**
   * Status set for acquireToken calls
   */

  InteractionStatus["AcquireToken"] = "acquireToken";
  /**
   * Status set for ssoSilent calls
   */

  InteractionStatus["SsoSilent"] = "ssoSilent";
  /**
   * Status set when handleRedirect in progress
   */

  InteractionStatus["HandleRedirect"] = "handleRedirect";
  /**
   * Status set when interaction is complete
   */

  InteractionStatus["None"] = "none";
})(InteractionStatus || (InteractionStatus = {}));

var DEFAULT_REQUEST = {
  scopes: OIDC_DEFAULT_SCOPES
};
/**
 * JWK Key Format string (Type MUST be defined for window crypto APIs)
 */

var KEY_FORMAT_JWK = "jwk"; // Supported wrapper SKUs

var WrapperSKU;

(function (WrapperSKU) {
  WrapperSKU["React"] = "@azure/msal-react";
  WrapperSKU["Angular"] = "@azure/msal-angular";
})(WrapperSKU || (WrapperSKU = {})); // DatabaseStorage Constants


var DB_NAME = "msal.db";
var DB_VERSION = 1;
var DB_TABLE_NAME = DB_NAME + ".keys";

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * BrowserAuthErrorMessage class containing string constants used by error codes and messages.
 */

var BrowserConfigurationAuthErrorMessage = {
  redirectUriNotSet: {
    code: "redirect_uri_empty",
    desc: "A redirect URI is required for all calls, and none has been set."
  },
  postLogoutUriNotSet: {
    code: "post_logout_uri_empty",
    desc: "A post logout redirect has not been set."
  },
  storageNotSupportedError: {
    code: "storage_not_supported",
    desc: "Given storage configuration option was not supported."
  },
  noRedirectCallbacksSet: {
    code: "no_redirect_callbacks",
    desc: "No redirect callbacks have been set. Please call setRedirectCallbacks() with the appropriate function arguments before continuing. " + "More information is available here: https://github.com/AzureAD/microsoft-authentication-library-for-js/wiki/MSAL-basics."
  },
  invalidCallbackObject: {
    code: "invalid_callback_object",
    desc: "The object passed for the callback was invalid. " + "More information is available here: https://github.com/AzureAD/microsoft-authentication-library-for-js/wiki/MSAL-basics."
  },
  stubPcaInstanceCalled: {
    code: "stubbed_public_client_application_called",
    desc: "Stub instance of Public Client Application was called. If using msal-react, please ensure context is not used without a provider. For more visit: aka.ms/msaljs/browser-errors"
  },
  inMemRedirectUnavailable: {
    code: "in_mem_redirect_unavailable",
    desc: "Redirect cannot be supported. In-memory storage was selected and storeAuthStateInCookie=false, which would cause the library to be unable to handle the incoming hash. If you would like to use the redirect API, please use session/localStorage or set storeAuthStateInCookie=true."
  }
};
/**
 * Browser library error class thrown by the MSAL.js library for SPAs
 */

var BrowserConfigurationAuthError =
/** @class */
function (_super) {
  __extends$1(BrowserConfigurationAuthError, _super);

  function BrowserConfigurationAuthError(errorCode, errorMessage) {
    var _this = _super.call(this, errorCode, errorMessage) || this;

    _this.name = "BrowserConfigurationAuthError";
    Object.setPrototypeOf(_this, BrowserConfigurationAuthError.prototype);
    return _this;
  }
  /**
   * Creates an error thrown when the redirect uri is empty (not set by caller)
   */


  BrowserConfigurationAuthError.createRedirectUriEmptyError = function () {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.redirectUriNotSet.code, BrowserConfigurationAuthErrorMessage.redirectUriNotSet.desc);
  };
  /**
   * Creates an error thrown when the post-logout redirect uri is empty (not set by caller)
   */


  BrowserConfigurationAuthError.createPostLogoutRedirectUriEmptyError = function () {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.postLogoutUriNotSet.code, BrowserConfigurationAuthErrorMessage.postLogoutUriNotSet.desc);
  };
  /**
   * Creates error thrown when given storage location is not supported.
   * @param givenStorageLocation
   */


  BrowserConfigurationAuthError.createStorageNotSupportedError = function (givenStorageLocation) {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.storageNotSupportedError.code, BrowserConfigurationAuthErrorMessage.storageNotSupportedError.desc + " Given Location: " + givenStorageLocation);
  };
  /**
   * Creates error thrown when redirect callbacks are not set before calling loginRedirect() or acquireTokenRedirect().
   */


  BrowserConfigurationAuthError.createRedirectCallbacksNotSetError = function () {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.noRedirectCallbacksSet.code, BrowserConfigurationAuthErrorMessage.noRedirectCallbacksSet.desc);
  };
  /**
   * Creates error thrown when the stub instance of PublicClientApplication is called.
   */


  BrowserConfigurationAuthError.createStubPcaInstanceCalledError = function () {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.stubPcaInstanceCalled.code, BrowserConfigurationAuthErrorMessage.stubPcaInstanceCalled.desc);
  };
  /*
   * Create an error thrown when in-memory storage is used and storeAuthStateInCookie=false.
   */


  BrowserConfigurationAuthError.createInMemoryRedirectUnavailableError = function () {
    return new BrowserConfigurationAuthError(BrowserConfigurationAuthErrorMessage.inMemRedirectUnavailable.code, BrowserConfigurationAuthErrorMessage.inMemRedirectUnavailable.desc);
  };

  return BrowserConfigurationAuthError;
}(AuthError);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var BrowserStorage =
/** @class */
function () {
  function BrowserStorage(cacheLocation) {
    this.validateWindowStorage(cacheLocation);
    this.windowStorage = window[cacheLocation];
  }

  BrowserStorage.prototype.validateWindowStorage = function (cacheLocation) {
    if (cacheLocation !== BrowserCacheLocation.LocalStorage && cacheLocation !== BrowserCacheLocation.SessionStorage) {
      throw BrowserConfigurationAuthError.createStorageNotSupportedError(cacheLocation);
    }

    var storageSupported = !!window[cacheLocation];

    if (!storageSupported) {
      throw BrowserConfigurationAuthError.createStorageNotSupportedError(cacheLocation);
    }
  };

  BrowserStorage.prototype.getItem = function (key) {
    return this.windowStorage.getItem(key);
  };

  BrowserStorage.prototype.setItem = function (key, value) {
    this.windowStorage.setItem(key, value);
  };

  BrowserStorage.prototype.removeItem = function (key) {
    this.windowStorage.removeItem(key);
  };

  BrowserStorage.prototype.getKeys = function () {
    return Object.keys(this.windowStorage);
  };

  BrowserStorage.prototype.containsKey = function (key) {
    return this.windowStorage.hasOwnProperty(key);
  };

  return BrowserStorage;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var MemoryStorage =
/** @class */
function () {
  function MemoryStorage() {
    this.cache = new Map();
  }

  MemoryStorage.prototype.getItem = function (key) {
    return this.cache.get(key) || null;
  };

  MemoryStorage.prototype.setItem = function (key, value) {
    this.cache.set(key, value);
  };

  MemoryStorage.prototype.removeItem = function (key) {
    this.cache.delete(key);
  };

  MemoryStorage.prototype.getKeys = function () {
    var cacheKeys = [];
    this.cache.forEach(function (value, key) {
      cacheKeys.push(key);
    });
    return cacheKeys;
  };

  MemoryStorage.prototype.containsKey = function (key) {
    return this.cache.has(key);
  };

  MemoryStorage.prototype.clear = function () {
    this.cache.clear();
  };

  return MemoryStorage;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var BrowserProtocolUtils =
/** @class */
function () {
  function BrowserProtocolUtils() {}
  /**
   * Extracts the BrowserStateObject from the state string.
   * @param browserCrypto
   * @param state
   */


  BrowserProtocolUtils.extractBrowserRequestState = function (browserCrypto, state) {
    if (StringUtils.isEmpty(state)) {
      return null;
    }

    try {
      var requestStateObj = ProtocolUtils.parseRequestState(browserCrypto, state);
      return requestStateObj.libraryState.meta;
    } catch (e) {
      throw ClientAuthError.createInvalidStateError(state, e);
    }
  };
  /**
   * Parses properties of server response from url hash
   * @param locationHash Hash from url
   */


  BrowserProtocolUtils.parseServerResponseFromHash = function (locationHash) {
    if (!locationHash) {
      return {};
    }

    var hashUrlString = new UrlString(locationHash);
    return UrlString.getDeserializedHash(hashUrlString.getHash());
  };

  return BrowserProtocolUtils;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * This class implements the cache storage interface for MSAL through browser local or session storage.
 * Cookies are only used if storeAuthStateInCookie is true, and are only used for
 * parameters such as state and nonce, generally.
 */

var BrowserCacheManager =
/** @class */
function (_super) {
  __extends$1(BrowserCacheManager, _super);

  function BrowserCacheManager(clientId, cacheConfig, cryptoImpl, logger) {
    var _this = _super.call(this, clientId, cryptoImpl) || this; // Cookie life calculation (hours * minutes * seconds * ms)


    _this.COOKIE_LIFE_MULTIPLIER = 24 * 60 * 60 * 1000;
    _this.cacheConfig = cacheConfig;
    _this.logger = logger;
    _this.internalStorage = new MemoryStorage();
    _this.browserStorage = _this.setupBrowserStorage(_this.cacheConfig.cacheLocation);
    _this.temporaryCacheStorage = _this.setupTemporaryCacheStorage(_this.cacheConfig.cacheLocation); // Migrate any cache entries from older versions of MSAL.

    _this.migrateCacheEntries();

    return _this;
  }
  /**
   * Returns a window storage class implementing the IWindowStorage interface that corresponds to the configured cacheLocation.
   * @param cacheLocation
   */


  BrowserCacheManager.prototype.setupBrowserStorage = function (cacheLocation) {
    switch (cacheLocation) {
      case BrowserCacheLocation.LocalStorage:
      case BrowserCacheLocation.SessionStorage:
        try {
          // Temporary cache items will always be stored in session storage to mitigate problems caused by multiple tabs
          return new BrowserStorage(cacheLocation);
        } catch (e) {
          this.logger.verbose(e);
          break;
        }

    }

    this.cacheConfig.cacheLocation = BrowserCacheLocation.MemoryStorage;
    return new MemoryStorage();
  };
  /**
   *
   * @param cacheLocation
   */


  BrowserCacheManager.prototype.setupTemporaryCacheStorage = function (cacheLocation) {
    switch (cacheLocation) {
      case BrowserCacheLocation.LocalStorage:
      case BrowserCacheLocation.SessionStorage:
        try {
          // Temporary cache items will always be stored in session storage to mitigate problems caused by multiple tabs
          return new BrowserStorage(BrowserCacheLocation.SessionStorage);
        } catch (e) {
          this.logger.verbose(e);
          return this.internalStorage;
        }

      case BrowserCacheLocation.MemoryStorage:
      default:
        return this.internalStorage;
    }
  };
  /**
   * Migrate all old cache entries to new schema. No rollback supported.
   * @param storeAuthStateInCookie
   */


  BrowserCacheManager.prototype.migrateCacheEntries = function () {
    var _this = this;

    var idTokenKey = Constants.CACHE_PREFIX + "." + PersistentCacheKeys.ID_TOKEN;
    var clientInfoKey = Constants.CACHE_PREFIX + "." + PersistentCacheKeys.CLIENT_INFO;
    var errorKey = Constants.CACHE_PREFIX + "." + PersistentCacheKeys.ERROR;
    var errorDescKey = Constants.CACHE_PREFIX + "." + PersistentCacheKeys.ERROR_DESC;
    var idTokenValue = this.browserStorage.getItem(idTokenKey);
    var clientInfoValue = this.browserStorage.getItem(clientInfoKey);
    var errorValue = this.browserStorage.getItem(errorKey);
    var errorDescValue = this.browserStorage.getItem(errorDescKey);
    var values = [idTokenValue, clientInfoValue, errorValue, errorDescValue];
    var keysToMigrate = [PersistentCacheKeys.ID_TOKEN, PersistentCacheKeys.CLIENT_INFO, PersistentCacheKeys.ERROR, PersistentCacheKeys.ERROR_DESC];
    keysToMigrate.forEach(function (cacheKey, index) {
      return _this.migrateCacheEntry(cacheKey, values[index]);
    });
  };
  /**
   * Utility function to help with migration.
   * @param newKey
   * @param value
   * @param storeAuthStateInCookie
   */


  BrowserCacheManager.prototype.migrateCacheEntry = function (newKey, value) {
    if (value) {
      this.setTemporaryCache(newKey, value, true);
    }
  };
  /**
   * Parses passed value as JSON object, JSON.parse() will throw an error.
   * @param input
   */


  BrowserCacheManager.prototype.validateAndParseJson = function (jsonValue) {
    try {
      var parsedJson = JSON.parse(jsonValue);
      /**
       * There are edge cases in which JSON.parse will successfully parse a non-valid JSON object
       * (e.g. JSON.parse will parse an escaped string into an unescaped string), so adding a type check
       * of the parsed value is necessary in order to be certain that the string represents a valid JSON object.
       *
       */

      return parsedJson && typeof parsedJson === "object" ? parsedJson : null;
    } catch (error) {
      return null;
    }
  };
  /**
   * fetches the entry from the browser storage based off the key
   * @param key
   */


  BrowserCacheManager.prototype.getItem = function (key) {
    return this.browserStorage.getItem(key);
  };
  /**
   * sets the entry in the browser storage
   * @param key
   * @param value
   */


  BrowserCacheManager.prototype.setItem = function (key, value) {
    this.browserStorage.setItem(key, value);
  };
  /**
   * fetch the account entity from the platform cache
   * @param accountKey
   */


  BrowserCacheManager.prototype.getAccount = function (accountKey) {
    var account = this.getItem(accountKey);

    if (!account) {
      return null;
    }

    var parsedAccount = this.validateAndParseJson(account);

    if (!parsedAccount || !AccountEntity.isAccountEntity(parsedAccount)) {
      return null;
    }

    return CacheManager.toObject(new AccountEntity(), parsedAccount);
  };
  /**
   * set account entity in the platform cache
   * @param key
   * @param value
   */


  BrowserCacheManager.prototype.setAccount = function (account) {
    this.logger.trace("BrowserCacheManager.setAccount called");
    var key = account.generateAccountKey();
    this.setItem(key, JSON.stringify(account));
  };
  /**
   * generates idToken entity from a string
   * @param idTokenKey
   */


  BrowserCacheManager.prototype.getIdTokenCredential = function (idTokenKey) {
    var value = this.getItem(idTokenKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getIdTokenCredential: called, no cache hit");
      return null;
    }

    var parsedIdToken = this.validateAndParseJson(value);

    if (!parsedIdToken || !IdTokenEntity.isIdTokenEntity(parsedIdToken)) {
      this.logger.trace("BrowserCacheManager.getIdTokenCredential: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getIdTokenCredential: cache hit");
    return CacheManager.toObject(new IdTokenEntity(), parsedIdToken);
  };
  /**
   * set IdToken credential to the platform cache
   * @param idToken
   */


  BrowserCacheManager.prototype.setIdTokenCredential = function (idToken) {
    this.logger.trace("BrowserCacheManager.setIdTokenCredential called");
    var idTokenKey = idToken.generateCredentialKey();
    this.setItem(idTokenKey, JSON.stringify(idToken));
  };
  /**
   * generates accessToken entity from a string
   * @param key
   */


  BrowserCacheManager.prototype.getAccessTokenCredential = function (accessTokenKey) {
    var value = this.getItem(accessTokenKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getAccessTokenCredential: called, no cache hit");
      return null;
    }

    var parsedAccessToken = this.validateAndParseJson(value);

    if (!parsedAccessToken || !AccessTokenEntity.isAccessTokenEntity(parsedAccessToken)) {
      this.logger.trace("BrowserCacheManager.getAccessTokenCredential: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getAccessTokenCredential: cache hit");
    return CacheManager.toObject(new AccessTokenEntity(), parsedAccessToken);
  };
  /**
   * set accessToken credential to the platform cache
   * @param accessToken
   */


  BrowserCacheManager.prototype.setAccessTokenCredential = function (accessToken) {
    this.logger.trace("BrowserCacheManager.setAccessTokenCredential called");
    var accessTokenKey = accessToken.generateCredentialKey();
    this.setItem(accessTokenKey, JSON.stringify(accessToken));
  };
  /**
   * generates refreshToken entity from a string
   * @param refreshTokenKey
   */


  BrowserCacheManager.prototype.getRefreshTokenCredential = function (refreshTokenKey) {
    var value = this.getItem(refreshTokenKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getRefreshTokenCredential: called, no cache hit");
      return null;
    }

    var parsedRefreshToken = this.validateAndParseJson(value);

    if (!parsedRefreshToken || !RefreshTokenEntity.isRefreshTokenEntity(parsedRefreshToken)) {
      this.logger.trace("BrowserCacheManager.getRefreshTokenCredential: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getRefreshTokenCredential: cache hit");
    return CacheManager.toObject(new RefreshTokenEntity(), parsedRefreshToken);
  };
  /**
   * set refreshToken credential to the platform cache
   * @param refreshToken
   */


  BrowserCacheManager.prototype.setRefreshTokenCredential = function (refreshToken) {
    this.logger.trace("BrowserCacheManager.setRefreshTokenCredential called");
    var refreshTokenKey = refreshToken.generateCredentialKey();
    this.setItem(refreshTokenKey, JSON.stringify(refreshToken));
  };
  /**
   * fetch appMetadata entity from the platform cache
   * @param appMetadataKey
   */


  BrowserCacheManager.prototype.getAppMetadata = function (appMetadataKey) {
    var value = this.getItem(appMetadataKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getAppMetadata: called, no cache hit");
      return null;
    }

    var parsedMetadata = this.validateAndParseJson(value);

    if (!parsedMetadata || !AppMetadataEntity.isAppMetadataEntity(appMetadataKey, parsedMetadata)) {
      this.logger.trace("BrowserCacheManager.getAppMetadata: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getAppMetadata: cache hit");
    return CacheManager.toObject(new AppMetadataEntity(), parsedMetadata);
  };
  /**
   * set appMetadata entity to the platform cache
   * @param appMetadata
   */


  BrowserCacheManager.prototype.setAppMetadata = function (appMetadata) {
    this.logger.trace("BrowserCacheManager.setAppMetadata called");
    var appMetadataKey = appMetadata.generateAppMetadataKey();
    this.setItem(appMetadataKey, JSON.stringify(appMetadata));
  };
  /**
   * fetch server telemetry entity from the platform cache
   * @param serverTelemetryKey
   */


  BrowserCacheManager.prototype.getServerTelemetry = function (serverTelemetryKey) {
    var value = this.getItem(serverTelemetryKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getServerTelemetry: called, no cache hit");
      return null;
    }

    var parsedMetadata = this.validateAndParseJson(value);

    if (!parsedMetadata || !ServerTelemetryEntity.isServerTelemetryEntity(serverTelemetryKey, parsedMetadata)) {
      this.logger.trace("BrowserCacheManager.getServerTelemetry: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getServerTelemetry: cache hit");
    return CacheManager.toObject(new ServerTelemetryEntity(), parsedMetadata);
  };
  /**
   * set server telemetry entity to the platform cache
   * @param serverTelemetryKey
   * @param serverTelemetry
   */


  BrowserCacheManager.prototype.setServerTelemetry = function (serverTelemetryKey, serverTelemetry) {
    this.logger.trace("BrowserCacheManager.setServerTelemetry called");
    this.setItem(serverTelemetryKey, JSON.stringify(serverTelemetry));
  };
  /**
   *
   */


  BrowserCacheManager.prototype.getAuthorityMetadata = function (key) {
    var value = this.internalStorage.getItem(key);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getAuthorityMetadata: called, no cache hit");
      return null;
    }

    var parsedMetadata = this.validateAndParseJson(value);

    if (parsedMetadata && AuthorityMetadataEntity.isAuthorityMetadataEntity(key, parsedMetadata)) {
      this.logger.trace("BrowserCacheManager.getAuthorityMetadata: cache hit");
      return CacheManager.toObject(new AuthorityMetadataEntity(), parsedMetadata);
    }

    return null;
  };
  /**
   *
   */


  BrowserCacheManager.prototype.getAuthorityMetadataKeys = function () {
    var _this = this;

    var allKeys = this.internalStorage.getKeys();
    return allKeys.filter(function (key) {
      return _this.isAuthorityMetadata(key);
    });
  };
  /**
   * Sets wrapper metadata in memory
   * @param wrapperSKU
   * @param wrapperVersion
   */


  BrowserCacheManager.prototype.setWrapperMetadata = function (wrapperSKU, wrapperVersion) {
    this.internalStorage.setItem(InMemoryCacheKeys.WRAPPER_SKU, wrapperSKU);
    this.internalStorage.setItem(InMemoryCacheKeys.WRAPPER_VER, wrapperVersion);
  };
  /**
   * Returns wrapper metadata from in-memory storage
   */


  BrowserCacheManager.prototype.getWrapperMetadata = function () {
    var sku = this.internalStorage.getItem(InMemoryCacheKeys.WRAPPER_SKU) || Constants.EMPTY_STRING;
    var version = this.internalStorage.getItem(InMemoryCacheKeys.WRAPPER_VER) || Constants.EMPTY_STRING;
    return [sku, version];
  };
  /**
   *
   * @param entity
   */


  BrowserCacheManager.prototype.setAuthorityMetadata = function (key, entity) {
    this.logger.trace("BrowserCacheManager.setAuthorityMetadata called");
    this.internalStorage.setItem(key, JSON.stringify(entity));
  };
  /**
   * Gets the active account
   */


  BrowserCacheManager.prototype.getActiveAccount = function () {
    var activeAccountIdKey = this.generateCacheKey(PersistentCacheKeys.ACTIVE_ACCOUNT);
    var activeAccountId = this.browserStorage.getItem(activeAccountIdKey);

    if (!activeAccountId) {
      return null;
    }

    return this.getAccountInfoByFilter({
      localAccountId: activeAccountId
    })[0] || null;
  };
  /**
   * Sets the active account's localAccountId in cache
   * @param account
   */


  BrowserCacheManager.prototype.setActiveAccount = function (account) {
    var activeAccountIdKey = this.generateCacheKey(PersistentCacheKeys.ACTIVE_ACCOUNT);

    if (account) {
      this.logger.verbose("setActiveAccount: Active account set");
      this.browserStorage.setItem(activeAccountIdKey, account.localAccountId);
    } else {
      this.logger.verbose("setActiveAccount: No account passed, active account not set");
      this.browserStorage.removeItem(activeAccountIdKey);
    }
  };
  /**
   * Gets a list of accounts that match all of the filters provided
   * @param account
   */


  BrowserCacheManager.prototype.getAccountInfoByFilter = function (accountFilter) {
    var allAccounts = this.getAllAccounts();
    return allAccounts.filter(function (accountObj) {
      if (accountFilter.username && accountFilter.username.toLowerCase() !== accountObj.username.toLowerCase()) {
        return false;
      }

      if (accountFilter.homeAccountId && accountFilter.homeAccountId !== accountObj.homeAccountId) {
        return false;
      }

      if (accountFilter.localAccountId && accountFilter.localAccountId !== accountObj.localAccountId) {
        return false;
      }

      if (accountFilter.tenantId && accountFilter.tenantId !== accountObj.tenantId) {
        return false;
      }

      if (accountFilter.environment && accountFilter.environment !== accountObj.environment) {
        return false;
      }

      return true;
    });
  };
  /**
   * Checks the cache for accounts matching loginHint or SID
   * @param loginHint
   * @param sid
   */


  BrowserCacheManager.prototype.getAccountInfoByHints = function (loginHint, sid) {
    var matchingAccounts = this.getAllAccounts().filter(function (accountInfo) {
      if (sid) {
        var accountSid = accountInfo.idTokenClaims && accountInfo.idTokenClaims["sid"];
        return sid === accountSid;
      }

      if (loginHint) {
        return loginHint === accountInfo.username;
      }

      return false;
    });

    if (matchingAccounts.length === 1) {
      return matchingAccounts[0];
    } else if (matchingAccounts.length > 1) {
      throw ClientAuthError.createMultipleMatchingAccountsInCacheError();
    }

    return null;
  };
  /**
   * fetch throttling entity from the platform cache
   * @param throttlingCacheKey
   */


  BrowserCacheManager.prototype.getThrottlingCache = function (throttlingCacheKey) {
    var value = this.getItem(throttlingCacheKey);

    if (!value) {
      this.logger.trace("BrowserCacheManager.getThrottlingCache: called, no cache hit");
      return null;
    }

    var parsedThrottlingCache = this.validateAndParseJson(value);

    if (!parsedThrottlingCache || !ThrottlingEntity.isThrottlingEntity(throttlingCacheKey, parsedThrottlingCache)) {
      this.logger.trace("BrowserCacheManager.getThrottlingCache: called, no cache hit");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getThrottlingCache: cache hit");
    return CacheManager.toObject(new ThrottlingEntity(), parsedThrottlingCache);
  };
  /**
   * set throttling entity to the platform cache
   * @param throttlingCacheKey
   * @param throttlingCache
   */


  BrowserCacheManager.prototype.setThrottlingCache = function (throttlingCacheKey, throttlingCache) {
    this.logger.trace("BrowserCacheManager.setThrottlingCache called");
    this.setItem(throttlingCacheKey, JSON.stringify(throttlingCache));
  };
  /**
   * Gets cache item with given key.
   * Will retrieve from cookies if storeAuthStateInCookie is set to true.
   * @param key
   */


  BrowserCacheManager.prototype.getTemporaryCache = function (cacheKey, generateKey) {
    var key = generateKey ? this.generateCacheKey(cacheKey) : cacheKey;

    if (this.cacheConfig.storeAuthStateInCookie) {
      var itemCookie = this.getItemCookie(key);

      if (itemCookie) {
        this.logger.trace("BrowserCacheManager.getTemporaryCache: storeAuthStateInCookies set to true, retrieving from cookies");
        return itemCookie;
      }
    }

    var value = this.temporaryCacheStorage.getItem(key);

    if (!value) {
      // If temp cache item not found in session/memory, check local storage for items set by old versions
      if (this.cacheConfig.cacheLocation === BrowserCacheLocation.LocalStorage) {
        var item = this.browserStorage.getItem(key);

        if (item) {
          this.logger.trace("BrowserCacheManager.getTemporaryCache: Temporary cache item found in local storage");
          return item;
        }
      }

      this.logger.trace("BrowserCacheManager.getTemporaryCache: No cache item found in local storage");
      return null;
    }

    this.logger.trace("BrowserCacheManager.getTemporaryCache: Temporary cache item returned");
    return value;
  };
  /**
   * Sets the cache item with the key and value given.
   * Stores in cookie if storeAuthStateInCookie is set to true.
   * This can cause cookie overflow if used incorrectly.
   * @param key
   * @param value
   */


  BrowserCacheManager.prototype.setTemporaryCache = function (cacheKey, value, generateKey) {
    var key = generateKey ? this.generateCacheKey(cacheKey) : cacheKey;
    this.temporaryCacheStorage.setItem(key, value);

    if (this.cacheConfig.storeAuthStateInCookie) {
      this.logger.trace("BrowserCacheManager.setTemporaryCache: storeAuthStateInCookie set to true, setting item cookie");
      this.setItemCookie(key, value);
    }
  };
  /**
   * Removes the cache item with the given key.
   * Will also clear the cookie item if storeAuthStateInCookie is set to true.
   * @param key
   */


  BrowserCacheManager.prototype.removeItem = function (key) {
    this.browserStorage.removeItem(key);
    this.temporaryCacheStorage.removeItem(key);

    if (this.cacheConfig.storeAuthStateInCookie) {
      this.logger.trace("BrowserCacheManager.removeItem: storeAuthStateInCookie is true, clearing item cookie");
      this.clearItemCookie(key);
    }

    return true;
  };
  /**
   * Checks whether key is in cache.
   * @param key
   */


  BrowserCacheManager.prototype.containsKey = function (key) {
    return this.browserStorage.containsKey(key) || this.temporaryCacheStorage.containsKey(key);
  };
  /**
   * Gets all keys in window.
   */


  BrowserCacheManager.prototype.getKeys = function () {
    return __spread(this.browserStorage.getKeys(), this.temporaryCacheStorage.getKeys());
  };
  /**
   * Clears all cache entries created by MSAL.
   */


  BrowserCacheManager.prototype.clear = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            // Removes all accounts and their credentials
            return [4
            /*yield*/
            , this.removeAllAccounts()];

          case 1:
            // Removes all accounts and their credentials
            _a.sent();

            this.removeAppMetadata(); // Removes all remaining MSAL cache items

            this.getKeys().forEach(function (cacheKey) {
              // Check if key contains msal prefix; For now, we are clearing all the cache items created by MSAL.js
              if ((_this.browserStorage.containsKey(cacheKey) || _this.temporaryCacheStorage.containsKey(cacheKey)) && (cacheKey.indexOf(Constants.CACHE_PREFIX) !== -1 || cacheKey.indexOf(_this.clientId) !== -1)) {
                _this.removeItem(cacheKey);
              }
            });
            this.internalStorage.clear();
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Add value to cookies
   * @param cookieName
   * @param cookieValue
   * @param expires
   */


  BrowserCacheManager.prototype.setItemCookie = function (cookieName, cookieValue, expires) {
    var cookieStr = encodeURIComponent(cookieName) + "=" + encodeURIComponent(cookieValue) + ";path=/;";

    if (expires) {
      var expireTime = this.getCookieExpirationTime(expires);
      cookieStr += "expires=" + expireTime + ";";
    }

    if (this.cacheConfig.secureCookies) {
      cookieStr += "Secure;";
    }

    document.cookie = cookieStr;
  };
  /**
   * Get one item by key from cookies
   * @param cookieName
   */


  BrowserCacheManager.prototype.getItemCookie = function (cookieName) {
    var name = encodeURIComponent(cookieName) + "=";
    var cookieList = document.cookie.split(";");

    for (var i = 0; i < cookieList.length; i++) {
      var cookie = cookieList[i];

      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }

      if (cookie.indexOf(name) === 0) {
        return decodeURIComponent(cookie.substring(name.length, cookie.length));
      }
    }

    return Constants.EMPTY_STRING;
  };
  /**
   * Clear all msal-related cookies currently set in the browser. Should only be used to clear temporary cache items.
   */


  BrowserCacheManager.prototype.clearMsalCookies = function () {
    var _this = this;

    var cookiePrefix = Constants.CACHE_PREFIX + "." + this.clientId;
    var cookieList = document.cookie.split(";");
    cookieList.forEach(function (cookie) {
      while (cookie.charAt(0) === " ") {
        // eslint-disable-next-line no-param-reassign
        cookie = cookie.substring(1);
      }

      if (cookie.indexOf(cookiePrefix) === 0) {
        var cookieKey = cookie.split("=")[0];

        _this.clearItemCookie(cookieKey);
      }
    });
  };
  /**
   * Clear an item in the cookies by key
   * @param cookieName
   */


  BrowserCacheManager.prototype.clearItemCookie = function (cookieName) {
    this.setItemCookie(cookieName, Constants.EMPTY_STRING, -1);
  };
  /**
   * Get cookie expiration time
   * @param cookieLifeDays
   */


  BrowserCacheManager.prototype.getCookieExpirationTime = function (cookieLifeDays) {
    var today = new Date();
    var expr = new Date(today.getTime() + cookieLifeDays * this.COOKIE_LIFE_MULTIPLIER);
    return expr.toUTCString();
  };
  /**
   * Gets the cache object referenced by the browser
   */


  BrowserCacheManager.prototype.getCache = function () {
    return this.browserStorage;
  };
  /**
   * interface compat, we cannot overwrite browser cache; Functionality is supported by individual entities in browser
   */


  BrowserCacheManager.prototype.setCache = function () {// sets nothing
  };
  /**
   * Prepend msal.<client-id> to each key; Skip for any JSON object as Key (defined schemas do not need the key appended: AccessToken Keys or the upcoming schema)
   * @param key
   * @param addInstanceId
   */


  BrowserCacheManager.prototype.generateCacheKey = function (key) {
    var generatedKey = this.validateAndParseJson(key);

    if (!generatedKey) {
      if (StringUtils.startsWith(key, Constants.CACHE_PREFIX) || StringUtils.startsWith(key, PersistentCacheKeys.ADAL_ID_TOKEN)) {
        return key;
      }

      return Constants.CACHE_PREFIX + "." + this.clientId + "." + key;
    }

    return JSON.stringify(key);
  };
  /**
   * Create authorityKey to cache authority
   * @param state
   */


  BrowserCacheManager.prototype.generateAuthorityKey = function (stateString) {
    var stateId = ProtocolUtils.parseRequestState(this.cryptoImpl, stateString).libraryState.id;
    return this.generateCacheKey(TemporaryCacheKeys.AUTHORITY + "." + stateId);
  };
  /**
   * Create Nonce key to cache nonce
   * @param state
   */


  BrowserCacheManager.prototype.generateNonceKey = function (stateString) {
    var stateId = ProtocolUtils.parseRequestState(this.cryptoImpl, stateString).libraryState.id;
    return this.generateCacheKey(TemporaryCacheKeys.NONCE_IDTOKEN + "." + stateId);
  };
  /**
   * Creates full cache key for the request state
   * @param stateString State string for the request
   */


  BrowserCacheManager.prototype.generateStateKey = function (stateString) {
    // Use the library state id to key temp storage for uniqueness for multiple concurrent requests
    var stateId = ProtocolUtils.parseRequestState(this.cryptoImpl, stateString).libraryState.id;
    return this.generateCacheKey(TemporaryCacheKeys.REQUEST_STATE + "." + stateId);
  };
  /**
   * Gets the cached authority based on the cached state. Returns empty if no cached state found.
   */


  BrowserCacheManager.prototype.getCachedAuthority = function (cachedState) {
    var stateCacheKey = this.generateStateKey(cachedState);
    var state = this.getTemporaryCache(stateCacheKey);

    if (!state) {
      return null;
    }

    var authorityCacheKey = this.generateAuthorityKey(state);
    return this.getTemporaryCache(authorityCacheKey);
  };
  /**
   * Updates account, authority, and state in cache
   * @param serverAuthenticationRequest
   * @param account
   */


  BrowserCacheManager.prototype.updateCacheEntries = function (state, nonce, authorityInstance, loginHint, account) {
    this.logger.trace("BrowserCacheManager.updateCacheEntries called"); // Cache the request state

    var stateCacheKey = this.generateStateKey(state);
    this.setTemporaryCache(stateCacheKey, state, false); // Cache the nonce

    var nonceCacheKey = this.generateNonceKey(state);
    this.setTemporaryCache(nonceCacheKey, nonce, false); // Cache authorityKey

    var authorityCacheKey = this.generateAuthorityKey(state);
    this.setTemporaryCache(authorityCacheKey, authorityInstance, false);

    if (account) {
      var ccsCredential = {
        credential: account.homeAccountId,
        type: CcsCredentialType.HOME_ACCOUNT_ID
      };
      this.setTemporaryCache(TemporaryCacheKeys.CCS_CREDENTIAL, JSON.stringify(ccsCredential), true);
    } else if (!StringUtils.isEmpty(loginHint)) {
      var ccsCredential = {
        credential: loginHint,
        type: CcsCredentialType.UPN
      };
      this.setTemporaryCache(TemporaryCacheKeys.CCS_CREDENTIAL, JSON.stringify(ccsCredential), true);
    }
  };
  /**
   * Reset all temporary cache items
   * @param state
   */


  BrowserCacheManager.prototype.resetRequestCache = function (state) {
    var _this = this;

    this.logger.trace("BrowserCacheManager.resetRequestCache called"); // check state and remove associated cache items

    if (!StringUtils.isEmpty(state)) {
      this.getKeys().forEach(function (key) {
        if (key.indexOf(state) !== -1) {
          _this.removeItem(key);
        }
      });
    } // delete generic interactive request parameters


    if (state) {
      this.removeItem(this.generateStateKey(state));
      this.removeItem(this.generateNonceKey(state));
      this.removeItem(this.generateAuthorityKey(state));
    }

    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.REQUEST_PARAMS));
    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.ORIGIN_URI));
    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.URL_HASH));
    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.CORRELATION_ID));
    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.CCS_CREDENTIAL));
    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.NATIVE_REQUEST));
    this.setInteractionInProgress(false);
  };
  /**
   * Removes temporary cache for the provided state
   * @param stateString
   */


  BrowserCacheManager.prototype.cleanRequestByState = function (stateString) {
    this.logger.trace("BrowserCacheManager.cleanRequestByState called"); // Interaction is completed - remove interaction status.

    if (stateString) {
      var stateKey = this.generateStateKey(stateString);
      var cachedState = this.temporaryCacheStorage.getItem(stateKey);
      this.logger.infoPii("BrowserCacheManager.cleanRequestByState: Removing temporary cache items for state: " + cachedState);
      this.resetRequestCache(cachedState || Constants.EMPTY_STRING);
    }

    this.clearMsalCookies();
  };
  /**
   * Looks in temporary cache for any state values with the provided interactionType and removes all temporary cache items for that state
   * Used in scenarios where temp cache needs to be cleaned but state is not known, such as clicking browser back button.
   * @param interactionType
   */


  BrowserCacheManager.prototype.cleanRequestByInteractionType = function (interactionType) {
    var _this = this;

    this.logger.trace("BrowserCacheManager.cleanRequestByInteractionType called"); // Loop through all keys to find state key

    this.getKeys().forEach(function (key) {
      // If this key is not the state key, move on
      if (key.indexOf(TemporaryCacheKeys.REQUEST_STATE) === -1) {
        return;
      } // Retrieve state value, return if not a valid value


      var stateValue = _this.temporaryCacheStorage.getItem(key);

      if (!stateValue) {
        return;
      } // Extract state and ensure it matches given InteractionType, then clean request cache


      var parsedState = BrowserProtocolUtils.extractBrowserRequestState(_this.cryptoImpl, stateValue);

      if (parsedState && parsedState.interactionType === interactionType) {
        _this.logger.infoPii("BrowserCacheManager.cleanRequestByInteractionType: Removing temporary cache items for state: " + stateValue);

        _this.resetRequestCache(stateValue);
      }
    });
    this.clearMsalCookies();
    this.setInteractionInProgress(false);
  };

  BrowserCacheManager.prototype.cacheCodeRequest = function (authCodeRequest, browserCrypto) {
    this.logger.trace("BrowserCacheManager.cacheCodeRequest called");
    var encodedValue = browserCrypto.base64Encode(JSON.stringify(authCodeRequest));
    this.setTemporaryCache(TemporaryCacheKeys.REQUEST_PARAMS, encodedValue, true);
  };
  /**
   * Gets the token exchange parameters from the cache. Throws an error if nothing is found.
   */


  BrowserCacheManager.prototype.getCachedRequest = function (state, browserCrypto) {
    this.logger.trace("BrowserCacheManager.getCachedRequest called"); // Get token request from cache and parse as TokenExchangeParameters.

    var encodedTokenRequest = this.getTemporaryCache(TemporaryCacheKeys.REQUEST_PARAMS, true);

    if (!encodedTokenRequest) {
      throw BrowserAuthError.createNoTokenRequestCacheError();
    }

    var parsedRequest = this.validateAndParseJson(browserCrypto.base64Decode(encodedTokenRequest));

    if (!parsedRequest) {
      throw BrowserAuthError.createUnableToParseTokenRequestCacheError();
    }

    this.removeItem(this.generateCacheKey(TemporaryCacheKeys.REQUEST_PARAMS)); // Get cached authority and use if no authority is cached with request.

    if (StringUtils.isEmpty(parsedRequest.authority)) {
      var authorityCacheKey = this.generateAuthorityKey(state);
      var cachedAuthority = this.getTemporaryCache(authorityCacheKey);

      if (!cachedAuthority) {
        throw BrowserAuthError.createNoCachedAuthorityError();
      }

      parsedRequest.authority = cachedAuthority;
    }

    return parsedRequest;
  };
  /**
   * Gets cached native request for redirect flows
   */


  BrowserCacheManager.prototype.getCachedNativeRequest = function () {
    this.logger.trace("BrowserCacheManager.getCachedNativeRequest called");
    var cachedRequest = this.getTemporaryCache(TemporaryCacheKeys.NATIVE_REQUEST, true);

    if (!cachedRequest) {
      this.logger.trace("BrowserCacheManager.getCachedNativeRequest: No cached native request found");
      return null;
    }

    var parsedRequest = this.validateAndParseJson(cachedRequest);

    if (!parsedRequest) {
      this.logger.error("BrowserCacheManager.getCachedNativeRequest: Unable to parse native request");
      return null;
    }

    return parsedRequest;
  };

  BrowserCacheManager.prototype.isInteractionInProgress = function (matchClientId) {
    var clientId = this.getInteractionInProgress();

    if (matchClientId) {
      return clientId === this.clientId;
    } else {
      return !!clientId;
    }
  };

  BrowserCacheManager.prototype.getInteractionInProgress = function () {
    var key = Constants.CACHE_PREFIX + "." + TemporaryCacheKeys.INTERACTION_STATUS_KEY;
    return this.getTemporaryCache(key, false);
  };

  BrowserCacheManager.prototype.setInteractionInProgress = function (inProgress) {
    // Ensure we don't overwrite interaction in progress for a different clientId
    var key = Constants.CACHE_PREFIX + "." + TemporaryCacheKeys.INTERACTION_STATUS_KEY;

    if (inProgress) {
      if (this.getInteractionInProgress()) {
        throw BrowserAuthError.createInteractionInProgressError();
      } else {
        // No interaction is in progress
        this.setTemporaryCache(key, this.clientId, false);
      }
    } else if (!inProgress && this.getInteractionInProgress() === this.clientId) {
      this.removeItem(key);
    }
  };
  /**
   * Returns username retrieved from ADAL or MSAL v1 idToken
   */


  BrowserCacheManager.prototype.getLegacyLoginHint = function () {
    // Only check for adal/msal token if no SSO params are being used
    var adalIdTokenString = this.getTemporaryCache(PersistentCacheKeys.ADAL_ID_TOKEN);

    if (adalIdTokenString) {
      this.browserStorage.removeItem(PersistentCacheKeys.ADAL_ID_TOKEN);
      this.logger.verbose("Cached ADAL id token retrieved.");
    } // Check for cached MSAL v1 id token


    var msalIdTokenString = this.getTemporaryCache(PersistentCacheKeys.ID_TOKEN, true);

    if (msalIdTokenString) {
      this.removeItem(this.generateCacheKey(PersistentCacheKeys.ID_TOKEN));
      this.logger.verbose("Cached MSAL.js v1 id token retrieved");
    }

    var cachedIdTokenString = msalIdTokenString || adalIdTokenString;

    if (cachedIdTokenString) {
      var cachedIdToken = new AuthToken(cachedIdTokenString, this.cryptoImpl);

      if (cachedIdToken.claims && cachedIdToken.claims.preferred_username) {
        this.logger.verbose("No SSO params used and ADAL/MSAL v1 token retrieved, setting ADAL/MSAL v1 preferred_username as loginHint");
        return cachedIdToken.claims.preferred_username;
      } else if (cachedIdToken.claims && cachedIdToken.claims.upn) {
        this.logger.verbose("No SSO params used and ADAL/MSAL v1 token retrieved, setting ADAL/MSAL v1 upn as loginHint");
        return cachedIdToken.claims.upn;
      } else {
        this.logger.verbose("No SSO params used and ADAL/MSAL v1 token retrieved, however, no account hint claim found. Enable preferred_username or upn id token claim to get SSO.");
      }
    }

    return null;
  };
  /**
   * Updates a credential's cache key if the current cache key is outdated
   */


  BrowserCacheManager.prototype.updateCredentialCacheKey = function (currentCacheKey, credential) {
    var updatedCacheKey = credential.generateCredentialKey();

    if (currentCacheKey !== updatedCacheKey) {
      var cacheItem = this.getItem(currentCacheKey);

      if (cacheItem) {
        this.removeItem(currentCacheKey);
        this.setItem(updatedCacheKey, cacheItem);
        this.logger.verbose("Updated an outdated " + credential.credentialType + " cache key");
        return updatedCacheKey;
      } else {
        this.logger.error("Attempted to update an outdated " + credential.credentialType + " cache key but no item matching the outdated key was found in storage");
      }
    }

    return currentCacheKey;
  };

  return BrowserCacheManager;
}(CacheManager);

var DEFAULT_BROWSER_CACHE_MANAGER = function (clientId, logger) {
  var cacheOptions = {
    cacheLocation: BrowserCacheLocation.MemoryStorage,
    storeAuthStateInCookie: false,
    secureCookies: false
  };
  return new BrowserCacheManager(clientId, cacheOptions, DEFAULT_CRYPTO_IMPLEMENTATION, logger);
};

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/* eslint-disable header/header */

var name = "@azure/msal-browser";
var version = "2.26.0";

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * This class implements the Fetch API for GET and POST requests. See more here: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 */

var FetchClient =
/** @class */
function () {
  function FetchClient() {}
  /**
   * Fetch Client for REST endpoints - Get request
   * @param url
   * @param headers
   * @param body
   */


  FetchClient.prototype.sendGetRequestAsync = function (url, options) {
    return __awaiter$1(this, void 0, void 0, function () {
      var response, e_1, _a;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            _b.trys.push([0, 2,, 3]);

            return [4
            /*yield*/
            , fetch(url, {
              method: HTTP_REQUEST_TYPE.GET,
              headers: this.getFetchHeaders(options)
            })];

          case 1:
            response = _b.sent();
            return [3
            /*break*/
            , 3];

          case 2:
            e_1 = _b.sent();

            if (window.navigator.onLine) {
              throw BrowserAuthError.createGetRequestFailedError(e_1, url);
            } else {
              throw BrowserAuthError.createNoNetworkConnectivityError();
            }

          case 3:
            _b.trys.push([3, 5,, 6]);

            _a = {
              headers: this.getHeaderDict(response.headers)
            };
            return [4
            /*yield*/
            , response.json()];

          case 4:
            return [2
            /*return*/
            , (_a.body = _b.sent(), _a.status = response.status, _a)];

          case 5:
            _b.sent();

            throw BrowserAuthError.createFailedToParseNetworkResponseError(url);

          case 6:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Fetch Client for REST endpoints - Post request
   * @param url
   * @param headers
   * @param body
   */


  FetchClient.prototype.sendPostRequestAsync = function (url, options) {
    return __awaiter$1(this, void 0, void 0, function () {
      var reqBody, response, e_3, _a;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            reqBody = options && options.body || Constants.EMPTY_STRING;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , fetch(url, {
              method: HTTP_REQUEST_TYPE.POST,
              headers: this.getFetchHeaders(options),
              body: reqBody
            })];

          case 2:
            response = _b.sent();
            return [3
            /*break*/
            , 4];

          case 3:
            e_3 = _b.sent();

            if (window.navigator.onLine) {
              throw BrowserAuthError.createPostRequestFailedError(e_3, url);
            } else {
              throw BrowserAuthError.createNoNetworkConnectivityError();
            }

          case 4:
            _b.trys.push([4, 6,, 7]);

            _a = {
              headers: this.getHeaderDict(response.headers)
            };
            return [4
            /*yield*/
            , response.json()];

          case 5:
            return [2
            /*return*/
            , (_a.body = _b.sent(), _a.status = response.status, _a)];

          case 6:
            _b.sent();

            throw BrowserAuthError.createFailedToParseNetworkResponseError(url);

          case 7:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Get Fetch API Headers object from string map
   * @param inputHeaders
   */


  FetchClient.prototype.getFetchHeaders = function (options) {
    var headers = new Headers();

    if (!(options && options.headers)) {
      return headers;
    }

    var optionsHeaders = options.headers;
    Object.keys(optionsHeaders).forEach(function (key) {
      headers.append(key, optionsHeaders[key]);
    });
    return headers;
  };

  FetchClient.prototype.getHeaderDict = function (headers) {
    var headerDict = {};
    headers.forEach(function (value, key) {
      headerDict[key] = value;
    });
    return headerDict;
  };

  return FetchClient;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * This client implements the XMLHttpRequest class to send GET and POST requests.
 */

var XhrClient =
/** @class */
function () {
  function XhrClient() {}
  /**
   * XhrClient for REST endpoints - Get request
   * @param url
   * @param headers
   * @param body
   */


  XhrClient.prototype.sendGetRequestAsync = function (url, options) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.sendRequestAsync(url, HTTP_REQUEST_TYPE.GET, options)];
      });
    });
  };
  /**
   * XhrClient for REST endpoints - Post request
   * @param url
   * @param headers
   * @param body
   */


  XhrClient.prototype.sendPostRequestAsync = function (url, options) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.sendRequestAsync(url, HTTP_REQUEST_TYPE.POST, options)];
      });
    });
  };
  /**
   * Helper for XhrClient requests.
   * @param url
   * @param method
   * @param options
   */


  XhrClient.prototype.sendRequestAsync = function (url, method, options) {
    var _this = this;

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(method, url,
      /* async: */
      true);

      _this.setXhrHeaders(xhr, options);

      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          if (method === HTTP_REQUEST_TYPE.POST) {
            reject(BrowserAuthError.createPostRequestFailedError("Failed with status " + xhr.status, url));
          } else {
            reject(BrowserAuthError.createGetRequestFailedError("Failed with status " + xhr.status, url));
          }
        }

        try {
          var jsonResponse = JSON.parse(xhr.responseText);
          var networkResponse = {
            headers: _this.getHeaderDict(xhr),
            body: jsonResponse,
            status: xhr.status
          };
          resolve(networkResponse);
        } catch (e) {
          reject(BrowserAuthError.createFailedToParseNetworkResponseError(url));
        }
      };

      xhr.onerror = function () {
        if (window.navigator.onLine) {
          if (method === HTTP_REQUEST_TYPE.POST) {
            reject(BrowserAuthError.createPostRequestFailedError("Failed with status " + xhr.status, url));
          } else {
            reject(BrowserAuthError.createGetRequestFailedError("Failed with status " + xhr.status, url));
          }
        } else {
          reject(BrowserAuthError.createNoNetworkConnectivityError());
        }
      };

      if (method === HTTP_REQUEST_TYPE.POST && options && options.body) {
        xhr.send(options.body);
      } else if (method === HTTP_REQUEST_TYPE.GET) {
        xhr.send();
      } else {
        throw BrowserAuthError.createHttpMethodNotImplementedError(method);
      }
    });
  };
  /**
   * Helper to set XHR headers for request.
   * @param xhr
   * @param options
   */


  XhrClient.prototype.setXhrHeaders = function (xhr, options) {
    if (options && options.headers) {
      var headers_1 = options.headers;
      Object.keys(headers_1).forEach(function (key) {
        xhr.setRequestHeader(key, headers_1[key]);
      });
    }
  };
  /**
   * Gets a string map of the headers received in the response.
   *
   * Algorithm comes from https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getAllResponseHeaders
   * @param xhr
   */


  XhrClient.prototype.getHeaderDict = function (xhr) {
    var headerString = xhr.getAllResponseHeaders();
    var headerArr = headerString.trim().split(/[\r\n]+/);
    var headerDict = {};
    headerArr.forEach(function (value) {
      var parts = value.split(": ");
      var headerName = parts.shift();
      var headerVal = parts.join(": ");

      if (headerName && headerVal) {
        headerDict[headerName] = headerVal;
      }
    });
    return headerDict;
  };

  return XhrClient;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Utility class for browser specific functions
 */

var BrowserUtils =
/** @class */
function () {
  function BrowserUtils() {} // #region Window Navigation and URL management

  /**
   * Clears hash from window url.
   */


  BrowserUtils.clearHash = function (contentWindow) {
    // Office.js sets history.replaceState to null
    contentWindow.location.hash = Constants.EMPTY_STRING;

    if (typeof contentWindow.history.replaceState === "function") {
      // Full removes "#" from url
      contentWindow.history.replaceState(null, Constants.EMPTY_STRING, "" + contentWindow.location.origin + contentWindow.location.pathname + contentWindow.location.search);
    }
  };
  /**
   * Replaces current hash with hash from provided url
   */


  BrowserUtils.replaceHash = function (url) {
    var urlParts = url.split("#");
    urlParts.shift(); // Remove part before the hash

    window.location.hash = urlParts.length > 0 ? urlParts.join("#") : Constants.EMPTY_STRING;
  };
  /**
   * Returns boolean of whether the current window is in an iframe or not.
   */


  BrowserUtils.isInIframe = function () {
    return window.parent !== window;
  };
  /**
   * Returns boolean of whether or not the current window is a popup opened by msal
   */


  BrowserUtils.isInPopup = function () {
    return typeof window !== "undefined" && !!window.opener && window.opener !== window && typeof window.name === "string" && window.name.indexOf(BrowserConstants.POPUP_NAME_PREFIX + ".") === 0;
  }; // #endregion

  /**
   * Returns current window URL as redirect uri
   */


  BrowserUtils.getCurrentUri = function () {
    return window.location.href.split("?")[0].split("#")[0];
  };
  /**
   * Gets the homepage url for the current window location.
   */


  BrowserUtils.getHomepage = function () {
    var currentUrl = new UrlString(window.location.href);
    var urlComponents = currentUrl.getUrlComponents();
    return urlComponents.Protocol + "//" + urlComponents.HostNameAndPort + "/";
  };
  /**
   * Returns best compatible network client object.
   */


  BrowserUtils.getBrowserNetworkClient = function () {
    if (window.fetch && window.Headers) {
      return new FetchClient();
    } else {
      return new XhrClient();
    }
  };
  /**
   * Throws error if we have completed an auth and are
   * attempting another auth request inside an iframe.
   */


  BrowserUtils.blockReloadInHiddenIframes = function () {
    var isResponseHash = UrlString.hashContainsKnownProperties(window.location.hash); // return an error if called from the hidden iframe created by the msal js silent calls

    if (isResponseHash && BrowserUtils.isInIframe()) {
      throw BrowserAuthError.createBlockReloadInHiddenIframeError();
    }
  };
  /**
   * Block redirect operations in iframes unless explicitly allowed
   * @param interactionType Interaction type for the request
   * @param allowRedirectInIframe Config value to allow redirects when app is inside an iframe
   */


  BrowserUtils.blockRedirectInIframe = function (interactionType, allowRedirectInIframe) {
    var isIframedApp = BrowserUtils.isInIframe();

    if (interactionType === InteractionType.Redirect && isIframedApp && !allowRedirectInIframe) {
      // If we are not in top frame, we shouldn't redirect. This is also handled by the service.
      throw BrowserAuthError.createRedirectInIframeError(isIframedApp);
    }
  };
  /**
   * Block redirectUri loaded in popup from calling AcquireToken APIs
   */


  BrowserUtils.blockAcquireTokenInPopups = function () {
    // Popups opened by msal popup APIs are given a name that starts with "msal."
    if (BrowserUtils.isInPopup()) {
      throw BrowserAuthError.createBlockAcquireTokenInPopupsError();
    }
  };
  /**
   * Throws error if token requests are made in non-browser environment
   * @param isBrowserEnvironment Flag indicating if environment is a browser.
   */


  BrowserUtils.blockNonBrowserEnvironment = function (isBrowserEnvironment) {
    if (!isBrowserEnvironment) {
      throw BrowserAuthError.createNonBrowserEnvironmentError();
    }
  };
  /**
   * Throws error if native brokering is enabled but initialize hasn't been called
   * @param allowNativeBroker
   * @param initialized
   */


  BrowserUtils.blockNativeBrokerCalledBeforeInitialized = function (allowNativeBroker, initialized) {
    if (allowNativeBroker && !initialized) {
      throw BrowserAuthError.createNativeBrokerCalledBeforeInitialize();
    }
  };
  /**
   * Returns boolean of whether current browser is an Internet Explorer or Edge browser.
   */


  BrowserUtils.detectIEOrEdge = function () {
    var ua = window.navigator.userAgent;
    var msie = ua.indexOf("MSIE ");
    var msie11 = ua.indexOf("Trident/");
    var msedge = ua.indexOf("Edge/");
    var isIE = msie > 0 || msie11 > 0;
    var isEdge = msedge > 0;
    return isIE || isEdge;
  };

  return BrowserUtils;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var BaseInteractionClient =
/** @class */
function () {
  function BaseInteractionClient(config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, performanceClient, nativeMessageHandler, correlationId) {
    this.config = config;
    this.browserStorage = storageImpl;
    this.browserCrypto = browserCrypto;
    this.networkClient = this.config.system.networkClient;
    this.eventHandler = eventHandler;
    this.navigationClient = navigationClient;
    this.nativeMessageHandler = nativeMessageHandler;
    this.correlationId = correlationId || this.browserCrypto.createNewGuid();
    this.logger = logger.clone(BrowserConstants.MSAL_SKU, version, this.correlationId);
    this.performanceClient = performanceClient;
  }

  BaseInteractionClient.prototype.clearCacheOnLogout = function (account) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!account) return [3
            /*break*/
            , 5];

            if (AccountEntity.accountInfoIsEqual(account, this.browserStorage.getActiveAccount(), false)) {
              this.logger.verbose("Setting active account to null");
              this.browserStorage.setActiveAccount(null);
            }

            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.browserStorage.removeAccount(AccountEntity.generateAccountCacheKey(account))];

          case 2:
            _a.sent();

            this.logger.verbose("Cleared cache items belonging to the account provided in the logout request.");
            return [3
            /*break*/
            , 4];

          case 3:
            _a.sent();

            this.logger.error("Account provided in logout request was not found. Local cache unchanged.");
            return [3
            /*break*/
            , 4];

          case 4:
            return [3
            /*break*/
            , 9];

          case 5:
            _a.trys.push([5, 8,, 9]);

            this.logger.verbose("No account provided in logout request, clearing all cache items.", this.correlationId); // Clear all accounts and tokens

            return [4
            /*yield*/
            , this.browserStorage.clear()];

          case 6:
            // Clear all accounts and tokens
            _a.sent(); // Clear any stray keys from IndexedDB


            return [4
            /*yield*/
            , this.browserCrypto.clearKeystore()];

          case 7:
            // Clear any stray keys from IndexedDB
            _a.sent();

            return [3
            /*break*/
            , 9];

          case 8:
            _a.sent();

            this.logger.error("Attempted to clear all MSAL cache items and failed. Local cache unchanged.");
            return [3
            /*break*/
            , 9];

          case 9:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Initializer function for all request APIs
   * @param request
   */


  BaseInteractionClient.prototype.initializeBaseRequest = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var authority, scopes, validatedRequest, _a;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            this.logger.verbose("Initializing BaseAuthRequest");
            authority = request.authority || this.config.auth.authority;
            scopes = __spread(request && request.scopes || []);
            validatedRequest = __assign$1(__assign$1({}, request), {
              correlationId: this.correlationId,
              authority: authority,
              scopes: scopes
            }); // Set authenticationScheme to BEARER if not explicitly set in the request

            if (!validatedRequest.authenticationScheme) {
              validatedRequest.authenticationScheme = AuthenticationScheme.BEARER;
              this.logger.verbose("Authentication Scheme wasn't explicitly set in request, defaulting to \"Bearer\" request");
            } else {
              if (validatedRequest.authenticationScheme === AuthenticationScheme.SSH) {
                if (!request.sshJwk) {
                  throw ClientConfigurationError.createMissingSshJwkError();
                }

                if (!request.sshKid) {
                  throw ClientConfigurationError.createMissingSshKidError();
                }
              }

              this.logger.verbose("Authentication Scheme set to \"" + validatedRequest.authenticationScheme + "\" as configured in Auth request");
            }

            if (!(request.claims && !StringUtils.isEmpty(request.claims))) return [3
            /*break*/
            , 2];
            _a = validatedRequest;
            return [4
            /*yield*/
            , this.browserCrypto.hashString(request.claims)];

          case 1:
            _a.requestedClaimsHash = _b.sent();
            _b.label = 2;

          case 2:
            return [2
            /*return*/
            , validatedRequest];
        }
      });
    });
  };
  /**
   *
   * Use to get the redirect uri configured in MSAL or null.
   * @param requestRedirectUri
   * @returns Redirect URL
   *
   */


  BaseInteractionClient.prototype.getRedirectUri = function (requestRedirectUri) {
    this.logger.verbose("getRedirectUri called");
    var redirectUri = requestRedirectUri || this.config.auth.redirectUri || BrowserUtils.getCurrentUri();
    return UrlString.getAbsoluteUrl(redirectUri, BrowserUtils.getCurrentUri());
  };
  /**
   *
   * @param apiId
   * @param correlationId
   * @param forceRefresh
   */


  BaseInteractionClient.prototype.initializeServerTelemetryManager = function (apiId, forceRefresh) {
    this.logger.verbose("initializeServerTelemetryManager called");
    var telemetryPayload = {
      clientId: this.config.auth.clientId,
      correlationId: this.correlationId,
      apiId: apiId,
      forceRefresh: forceRefresh || false,
      wrapperSKU: this.browserStorage.getWrapperMetadata()[0],
      wrapperVer: this.browserStorage.getWrapperMetadata()[1]
    };
    return new ServerTelemetryManager(telemetryPayload, this.browserStorage);
  };
  /**
   * Used to get a discovered version of the default authority.
   * @param requestAuthority
   * @param requestCorrelationId
   */


  BaseInteractionClient.prototype.getDiscoveredAuthority = function (requestAuthority) {
    return __awaiter$1(this, void 0, void 0, function () {
      var authorityOptions;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("getDiscoveredAuthority called");
            authorityOptions = {
              protocolMode: this.config.auth.protocolMode,
              knownAuthorities: this.config.auth.knownAuthorities,
              cloudDiscoveryMetadata: this.config.auth.cloudDiscoveryMetadata,
              authorityMetadata: this.config.auth.authorityMetadata
            };
            if (!requestAuthority) return [3
            /*break*/
            , 2];
            this.logger.verbose("Creating discovered authority with request authority");
            return [4
            /*yield*/
            , AuthorityFactory.createDiscoveredInstance(requestAuthority, this.config.system.networkClient, this.browserStorage, authorityOptions)];

          case 1:
            return [2
            /*return*/
            , _a.sent()];

          case 2:
            this.logger.verbose("Creating discovered authority with configured authority");
            return [4
            /*yield*/
            , AuthorityFactory.createDiscoveredInstance(this.config.auth.authority, this.config.system.networkClient, this.browserStorage, authorityOptions)];

          case 3:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };

  return BaseInteractionClient;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Defines the class structure and helper functions used by the "standard", non-brokered auth flows (popup, redirect, silent (RT), silent (iframe))
 */

var StandardInteractionClient =
/** @class */
function (_super) {
  __extends$1(StandardInteractionClient, _super);

  function StandardInteractionClient() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Generates an auth code request tied to the url request.
   * @param request
   */


  StandardInteractionClient.prototype.initializeAuthorizationCodeRequest = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var generatedPkceParams, authCodeRequest;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("initializeAuthorizationRequest called", request.correlationId);
            return [4
            /*yield*/
            , this.browserCrypto.generatePkceCodes()];

          case 1:
            generatedPkceParams = _a.sent();
            authCodeRequest = __assign$1(__assign$1({}, request), {
              redirectUri: request.redirectUri,
              code: Constants.EMPTY_STRING,
              codeVerifier: generatedPkceParams.verifier
            });
            request.codeChallenge = generatedPkceParams.challenge;
            request.codeChallengeMethod = Constants.S256_CODE_CHALLENGE_METHOD;
            return [2
            /*return*/
            , authCodeRequest];
        }
      });
    });
  };
  /**
   * Initializer for the logout request.
   * @param logoutRequest
   */


  StandardInteractionClient.prototype.initializeLogoutRequest = function (logoutRequest) {
    this.logger.verbose("initializeLogoutRequest called", logoutRequest === null || logoutRequest === void 0 ? void 0 : logoutRequest.correlationId);

    var validLogoutRequest = __assign$1({
      correlationId: this.correlationId || this.browserCrypto.createNewGuid()
    }, logoutRequest);
    /**
     * Set logout_hint to be login_hint from ID Token Claims if present
     * and logoutHint attribute wasn't manually set in logout request
     */


    if (logoutRequest) {
      // If logoutHint isn't set and an account was passed in, try to extract logoutHint from ID Token Claims
      if (!logoutRequest.logoutHint) {
        if (logoutRequest.account) {
          var logoutHint = this.getLogoutHintFromIdTokenClaims(logoutRequest.account);

          if (logoutHint) {
            this.logger.verbose("Setting logoutHint to login_hint ID Token Claim value for the account provided");
            validLogoutRequest.logoutHint = logoutHint;
          }
        } else {
          this.logger.verbose("logoutHint was not set and account was not passed into logout request, logoutHint will not be set");
        }
      } else {
        this.logger.verbose("logoutHint has already been set in logoutRequest");
      }
    } else {
      this.logger.verbose("logoutHint will not be set since no logout request was configured");
    }
    /*
     * Only set redirect uri if logout request isn't provided or the set uri isn't null.
     * Otherwise, use passed uri, config, or current page.
     */


    if (!logoutRequest || logoutRequest.postLogoutRedirectUri !== null) {
      if (logoutRequest && logoutRequest.postLogoutRedirectUri) {
        this.logger.verbose("Setting postLogoutRedirectUri to uri set on logout request", validLogoutRequest.correlationId);
        validLogoutRequest.postLogoutRedirectUri = UrlString.getAbsoluteUrl(logoutRequest.postLogoutRedirectUri, BrowserUtils.getCurrentUri());
      } else if (this.config.auth.postLogoutRedirectUri === null) {
        this.logger.verbose("postLogoutRedirectUri configured as null and no uri set on request, not passing post logout redirect", validLogoutRequest.correlationId);
      } else if (this.config.auth.postLogoutRedirectUri) {
        this.logger.verbose("Setting postLogoutRedirectUri to configured uri", validLogoutRequest.correlationId);
        validLogoutRequest.postLogoutRedirectUri = UrlString.getAbsoluteUrl(this.config.auth.postLogoutRedirectUri, BrowserUtils.getCurrentUri());
      } else {
        this.logger.verbose("Setting postLogoutRedirectUri to current page", validLogoutRequest.correlationId);
        validLogoutRequest.postLogoutRedirectUri = UrlString.getAbsoluteUrl(BrowserUtils.getCurrentUri(), BrowserUtils.getCurrentUri());
      }
    } else {
      this.logger.verbose("postLogoutRedirectUri passed as null, not setting post logout redirect uri", validLogoutRequest.correlationId);
    }

    return validLogoutRequest;
  };
  /**
   * Parses login_hint ID Token Claim out of AccountInfo object to be used as
   * logout_hint in end session request.
   * @param account
   */


  StandardInteractionClient.prototype.getLogoutHintFromIdTokenClaims = function (account) {
    var idTokenClaims = account.idTokenClaims;

    if (idTokenClaims) {
      if (idTokenClaims.login_hint) {
        return idTokenClaims.login_hint;
      } else {
        this.logger.verbose("The ID Token Claims tied to the provided account do not contain a login_hint claim, logoutHint will not be added to logout request");
      }
    } else {
      this.logger.verbose("The provided account does not contain ID Token Claims, logoutHint will not be added to logout request");
    }

    return null;
  };
  /**
   * Creates an Authorization Code Client with the given authority, or the default authority.
   * @param serverTelemetryManager
   * @param authorityUrl
   */


  StandardInteractionClient.prototype.createAuthCodeClient = function (serverTelemetryManager, authorityUrl, requestAzureCloudOptions) {
    return __awaiter$1(this, void 0, void 0, function () {
      var clientConfig;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.getClientConfiguration(serverTelemetryManager, authorityUrl, requestAzureCloudOptions)];

          case 1:
            clientConfig = _a.sent();
            return [2
            /*return*/
            , new AuthorizationCodeClient(clientConfig)];
        }
      });
    });
  };
  /**
   * Creates a Client Configuration object with the given request authority, or the default authority.
   * @param serverTelemetryManager
   * @param requestAuthority
   * @param requestCorrelationId
   */


  StandardInteractionClient.prototype.getClientConfiguration = function (serverTelemetryManager, requestAuthority, requestAzureCloudOptions) {
    return __awaiter$1(this, void 0, void 0, function () {
      var discoveredAuthority;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("getClientConfiguration called", this.correlationId);
            return [4
            /*yield*/
            , this.getDiscoveredAuthority(requestAuthority, requestAzureCloudOptions)];

          case 1:
            discoveredAuthority = _a.sent();
            return [2
            /*return*/
            , {
              authOptions: {
                clientId: this.config.auth.clientId,
                authority: discoveredAuthority,
                clientCapabilities: this.config.auth.clientCapabilities
              },
              systemOptions: {
                tokenRenewalOffsetSeconds: this.config.system.tokenRenewalOffsetSeconds,
                preventCorsPreflight: true
              },
              loggerOptions: {
                loggerCallback: this.config.system.loggerOptions.loggerCallback,
                piiLoggingEnabled: this.config.system.loggerOptions.piiLoggingEnabled,
                logLevel: this.config.system.loggerOptions.logLevel,
                correlationId: this.correlationId
              },
              cryptoInterface: this.browserCrypto,
              networkInterface: this.networkClient,
              storageInterface: this.browserStorage,
              serverTelemetryManager: serverTelemetryManager,
              libraryInfo: {
                sku: BrowserConstants.MSAL_SKU,
                version: version,
                cpu: Constants.EMPTY_STRING,
                os: Constants.EMPTY_STRING
              },
              telemetry: this.config.telemetry
            }];
        }
      });
    });
  };
  /**
   * @param hash
   * @param interactionType
   */


  StandardInteractionClient.prototype.validateAndExtractStateFromHash = function (serverParams, interactionType, requestCorrelationId) {
    this.logger.verbose("validateAndExtractStateFromHash called", requestCorrelationId);

    if (!serverParams.state) {
      throw BrowserAuthError.createHashDoesNotContainStateError();
    }

    var platformStateObj = BrowserProtocolUtils.extractBrowserRequestState(this.browserCrypto, serverParams.state);

    if (!platformStateObj) {
      throw BrowserAuthError.createUnableToParseStateError();
    }

    if (platformStateObj.interactionType !== interactionType) {
      throw BrowserAuthError.createStateInteractionTypeMismatchError();
    }

    this.logger.verbose("Returning state from hash", requestCorrelationId);
    return serverParams.state;
  };
  /**
   * Used to get a discovered version of the default authority.
   * @param requestAuthority
   * @param requestCorrelationId
   */


  StandardInteractionClient.prototype.getDiscoveredAuthority = function (requestAuthority, requestAzureCloudOptions) {
    return __awaiter$1(this, void 0, void 0, function () {
      var getAuthorityMeasurement, authorityOptions, userAuthority, builtAuthority;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("getDiscoveredAuthority called", this.correlationId);
            getAuthorityMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.StandardInteractionClientGetDiscoveredAuthority, this.correlationId);
            authorityOptions = {
              protocolMode: this.config.auth.protocolMode,
              knownAuthorities: this.config.auth.knownAuthorities,
              cloudDiscoveryMetadata: this.config.auth.cloudDiscoveryMetadata,
              authorityMetadata: this.config.auth.authorityMetadata
            };
            userAuthority = requestAuthority ? requestAuthority : this.config.auth.authority;
            builtAuthority = Authority.generateAuthority(userAuthority, requestAzureCloudOptions || this.config.auth.azureCloudOptions);
            this.logger.verbose("Creating discovered authority with configured authority", this.correlationId);
            return [4
            /*yield*/
            , AuthorityFactory.createDiscoveredInstance(builtAuthority, this.config.system.networkClient, this.browserStorage, authorityOptions).then(function (result) {
              getAuthorityMeasurement.endMeasurement({
                success: true
              });
              return result;
            }).catch(function (error) {
              getAuthorityMeasurement.endMeasurement({
                errorCode: error.errorCode,
                subErrorCode: error.subError,
                success: false
              });
              throw error;
            })];

          case 1:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };
  /**
   * Helper to initialize required request parameters for interactive APIs and ssoSilent()
   * @param request
   * @param interactionType
   */


  StandardInteractionClient.prototype.initializeAuthorizationRequest = function (request, interactionType) {
    return __awaiter$1(this, void 0, void 0, function () {
      var redirectUri, browserState, state, validatedRequest, _a, account, legacyLoginHint;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            this.logger.verbose("initializeAuthorizationRequest called", this.correlationId);
            redirectUri = this.getRedirectUri(request.redirectUri);
            browserState = {
              interactionType: interactionType
            };
            state = ProtocolUtils.setRequestState(this.browserCrypto, request && request.state || Constants.EMPTY_STRING, browserState);
            _a = [{}];
            return [4
            /*yield*/
            , this.initializeBaseRequest(request)];

          case 1:
            validatedRequest = __assign$1.apply(void 0, [__assign$1.apply(void 0, _a.concat([_b.sent()])), {
              redirectUri: redirectUri,
              state: state,
              nonce: request.nonce || this.browserCrypto.createNewGuid(),
              responseMode: ResponseMode.FRAGMENT
            }]);
            account = request.account || this.browserStorage.getActiveAccount();

            if (account) {
              this.logger.verbose("Setting validated request account", this.correlationId);
              this.logger.verbosePii("Setting validated request account: " + account.homeAccountId, this.correlationId);
              validatedRequest.account = account;
            } // Check for ADAL/MSAL v1 SSO


            if (StringUtils.isEmpty(validatedRequest.loginHint) && !account) {
              legacyLoginHint = this.browserStorage.getLegacyLoginHint();

              if (legacyLoginHint) {
                validatedRequest.loginHint = legacyLoginHint;
              }
            }

            return [2
            /*return*/
            , validatedRequest];
        }
      });
    });
  };

  return StandardInteractionClient;
}(BaseInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Abstract class which defines operations for a browser interaction handling class.
 */

var InteractionHandler =
/** @class */
function () {
  function InteractionHandler(authCodeModule, storageImpl, authCodeRequest, logger) {
    this.authModule = authCodeModule;
    this.browserStorage = storageImpl;
    this.authCodeRequest = authCodeRequest;
    this.logger = logger;
  }
  /**
   * Function to handle response parameters from hash.
   * @param locationHash
   */


  InteractionHandler.prototype.handleCodeResponseFromHash = function (locationHash, state, authority, networkModule) {
    return __awaiter$1(this, void 0, void 0, function () {
      var stateKey, requestState, authCodeResponse;
      return __generator$1(this, function (_a) {
        this.logger.verbose("InteractionHandler.handleCodeResponse called"); // Check that location hash isn't empty.

        if (StringUtils.isEmpty(locationHash)) {
          throw BrowserAuthError.createEmptyHashError(locationHash);
        }

        stateKey = this.browserStorage.generateStateKey(state);
        requestState = this.browserStorage.getTemporaryCache(stateKey);

        if (!requestState) {
          throw ClientAuthError.createStateNotFoundError("Cached State");
        }

        try {
          authCodeResponse = this.authModule.handleFragmentResponse(locationHash, requestState);
        } catch (e) {
          if (e instanceof ServerError && e.subError === BrowserAuthErrorMessage.userCancelledError.code) {
            // Translate server error caused by user closing native prompt to corresponding first class MSAL error
            throw BrowserAuthError.createUserCancelledError();
          } else {
            throw e;
          }
        }

        return [2
        /*return*/
        , this.handleCodeResponseFromServer(authCodeResponse, state, authority, networkModule)];
      });
    });
  };
  /**
   * Process auth code response from AAD
   * @param authCodeResponse
   * @param state
   * @param authority
   * @param networkModule
   * @returns
   */


  InteractionHandler.prototype.handleCodeResponseFromServer = function (authCodeResponse, state, authority, networkModule, validateNonce) {
    if (validateNonce === void 0) {
      validateNonce = true;
    }

    return __awaiter$1(this, void 0, void 0, function () {
      var stateKey, requestState, nonceKey, cachedNonce, cachedCcsCred, tokenResponse;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("InteractionHandler.handleCodeResponseFromServer called");
            stateKey = this.browserStorage.generateStateKey(state);
            requestState = this.browserStorage.getTemporaryCache(stateKey);

            if (!requestState) {
              throw ClientAuthError.createStateNotFoundError("Cached State");
            }

            nonceKey = this.browserStorage.generateNonceKey(requestState);
            cachedNonce = this.browserStorage.getTemporaryCache(nonceKey); // Assign code to request

            this.authCodeRequest.code = authCodeResponse.code;
            if (!authCodeResponse.cloud_instance_host_name) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , this.updateTokenEndpointAuthority(authCodeResponse.cloud_instance_host_name, authority, networkModule)];

          case 1:
            _a.sent();

            _a.label = 2;

          case 2:
            // Nonce validation not needed when redirect not involved (e.g. hybrid spa, renewing token via rt)
            if (validateNonce) {
              authCodeResponse.nonce = cachedNonce || undefined;
            }

            authCodeResponse.state = requestState; // Add CCS parameters if available

            if (authCodeResponse.client_info) {
              this.authCodeRequest.clientInfo = authCodeResponse.client_info;
            } else {
              cachedCcsCred = this.checkCcsCredentials();

              if (cachedCcsCred) {
                this.authCodeRequest.ccsCredential = cachedCcsCred;
              }
            }

            return [4
            /*yield*/
            , this.authModule.acquireToken(this.authCodeRequest, authCodeResponse)];

          case 3:
            tokenResponse = _a.sent();
            this.browserStorage.cleanRequestByState(state);
            return [2
            /*return*/
            , tokenResponse];
        }
      });
    });
  };
  /**
   * Updates authority based on cloudInstanceHostname
   * @param cloudInstanceHostname
   * @param authority
   * @param networkModule
   */


  InteractionHandler.prototype.updateTokenEndpointAuthority = function (cloudInstanceHostname, authority, networkModule) {
    return __awaiter$1(this, void 0, void 0, function () {
      var cloudInstanceAuthorityUri, cloudInstanceAuthority;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            cloudInstanceAuthorityUri = "https://" + cloudInstanceHostname + "/" + authority.tenant + "/";
            return [4
            /*yield*/
            , AuthorityFactory.createDiscoveredInstance(cloudInstanceAuthorityUri, networkModule, this.browserStorage, authority.options)];

          case 1:
            cloudInstanceAuthority = _a.sent();
            this.authModule.updateAuthority(cloudInstanceAuthority);
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Looks up ccs creds in the cache
   */


  InteractionHandler.prototype.checkCcsCredentials = function () {
    // Look up ccs credential in temp cache
    var cachedCcsCred = this.browserStorage.getTemporaryCache(TemporaryCacheKeys.CCS_CREDENTIAL, true);

    if (cachedCcsCred) {
      try {
        return JSON.parse(cachedCcsCred);
      } catch (e) {
        this.authModule.logger.error("Cache credential could not be parsed");
        this.authModule.logger.errorPii("Cache credential could not be parsed: " + cachedCcsCred);
      }
    }

    return null;
  };

  return InteractionHandler;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var RedirectHandler =
/** @class */
function (_super) {
  __extends$1(RedirectHandler, _super);

  function RedirectHandler(authCodeModule, storageImpl, authCodeRequest, logger, browserCrypto) {
    var _this = _super.call(this, authCodeModule, storageImpl, authCodeRequest, logger) || this;

    _this.browserCrypto = browserCrypto;
    return _this;
  }
  /**
   * Redirects window to given URL.
   * @param urlNavigate
   */


  RedirectHandler.prototype.initiateAuthRequest = function (requestUrl, params) {
    return __awaiter$1(this, void 0, void 0, function () {
      var navigationOptions, navigate;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("RedirectHandler.initiateAuthRequest called");
            if (!!StringUtils.isEmpty(requestUrl)) return [3
            /*break*/
            , 7]; // Cache start page, returns to this page after redirectUri if navigateToLoginRequestUrl is true

            if (params.redirectStartPage) {
              this.logger.verbose("RedirectHandler.initiateAuthRequest: redirectStartPage set, caching start page");
              this.browserStorage.setTemporaryCache(TemporaryCacheKeys.ORIGIN_URI, params.redirectStartPage, true);
            } // Set interaction status in the library.


            this.browserStorage.setTemporaryCache(TemporaryCacheKeys.CORRELATION_ID, this.authCodeRequest.correlationId, true);
            this.browserStorage.cacheCodeRequest(this.authCodeRequest, this.browserCrypto);
            this.logger.infoPii("RedirectHandler.initiateAuthRequest: Navigate to: " + requestUrl);
            navigationOptions = {
              apiId: ApiId.acquireTokenRedirect,
              timeout: params.redirectTimeout,
              noHistory: false
            };
            if (!(typeof params.onRedirectNavigate === "function")) return [3
            /*break*/
            , 4];
            this.logger.verbose("RedirectHandler.initiateAuthRequest: Invoking onRedirectNavigate callback");
            navigate = params.onRedirectNavigate(requestUrl);
            if (!(navigate !== false)) return [3
            /*break*/
            , 2];
            this.logger.verbose("RedirectHandler.initiateAuthRequest: onRedirectNavigate did not return false, navigating");
            return [4
            /*yield*/
            , params.navigationClient.navigateExternal(requestUrl, navigationOptions)];

          case 1:
            _a.sent();

            return [2
            /*return*/
            ];

          case 2:
            this.logger.verbose("RedirectHandler.initiateAuthRequest: onRedirectNavigate returned false, stopping navigation");
            return [2
            /*return*/
            ];

          case 3:
            return [3
            /*break*/
            , 6];

          case 4:
            // Navigate window to request URL
            this.logger.verbose("RedirectHandler.initiateAuthRequest: Navigating window to navigate url");
            return [4
            /*yield*/
            , params.navigationClient.navigateExternal(requestUrl, navigationOptions)];

          case 5:
            _a.sent();

            return [2
            /*return*/
            ];

          case 6:
            return [3
            /*break*/
            , 8];

          case 7:
            // Throw error if request URL is empty.
            this.logger.info("RedirectHandler.initiateAuthRequest: Navigate url is empty");
            throw BrowserAuthError.createEmptyNavigationUriError();

          case 8:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Handle authorization code response in the window.
   * @param hash
   */


  RedirectHandler.prototype.handleCodeResponseFromHash = function (locationHash, state, authority, networkModule) {
    return __awaiter$1(this, void 0, void 0, function () {
      var stateKey, requestState, authCodeResponse, nonceKey, cachedNonce, cachedCcsCred, tokenResponse;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("RedirectHandler.handleCodeResponse called"); // Check that location hash isn't empty.

            if (StringUtils.isEmpty(locationHash)) {
              throw BrowserAuthError.createEmptyHashError(locationHash);
            } // Interaction is completed - remove interaction status.


            this.browserStorage.setInteractionInProgress(false);
            stateKey = this.browserStorage.generateStateKey(state);
            requestState = this.browserStorage.getTemporaryCache(stateKey);

            if (!requestState) {
              throw ClientAuthError.createStateNotFoundError("Cached State");
            }

            try {
              authCodeResponse = this.authModule.handleFragmentResponse(locationHash, requestState);
            } catch (e) {
              if (e instanceof ServerError && e.subError === BrowserAuthErrorMessage.userCancelledError.code) {
                // Translate server error caused by user closing native prompt to corresponding first class MSAL error
                throw BrowserAuthError.createUserCancelledError();
              } else {
                throw e;
              }
            }

            nonceKey = this.browserStorage.generateNonceKey(requestState);
            cachedNonce = this.browserStorage.getTemporaryCache(nonceKey); // Assign code to request

            this.authCodeRequest.code = authCodeResponse.code;
            if (!authCodeResponse.cloud_instance_host_name) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , this.updateTokenEndpointAuthority(authCodeResponse.cloud_instance_host_name, authority, networkModule)];

          case 1:
            _a.sent();

            _a.label = 2;

          case 2:
            authCodeResponse.nonce = cachedNonce || undefined;
            authCodeResponse.state = requestState; // Add CCS parameters if available

            if (authCodeResponse.client_info) {
              this.authCodeRequest.clientInfo = authCodeResponse.client_info;
            } else {
              cachedCcsCred = this.checkCcsCredentials();

              if (cachedCcsCred) {
                this.authCodeRequest.ccsCredential = cachedCcsCred;
              }
            }

            return [4
            /*yield*/
            , this.authModule.acquireToken(this.authCodeRequest, authCodeResponse)];

          case 3:
            tokenResponse = _a.sent();
            this.browserStorage.cleanRequestByState(state);
            return [2
            /*return*/
            , tokenResponse];
        }
      });
    });
  };

  return RedirectHandler;
}(InteractionHandler);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var EventType;

(function (EventType) {
  EventType["INITIALIZE_START"] = "msal:initializeStart";
  EventType["INITIALIZE_END"] = "msal:initializeEnd";
  EventType["ACCOUNT_ADDED"] = "msal:accountAdded";
  EventType["ACCOUNT_REMOVED"] = "msal:accountRemoved";
  EventType["LOGIN_START"] = "msal:loginStart";
  EventType["LOGIN_SUCCESS"] = "msal:loginSuccess";
  EventType["LOGIN_FAILURE"] = "msal:loginFailure";
  EventType["ACQUIRE_TOKEN_START"] = "msal:acquireTokenStart";
  EventType["ACQUIRE_TOKEN_SUCCESS"] = "msal:acquireTokenSuccess";
  EventType["ACQUIRE_TOKEN_FAILURE"] = "msal:acquireTokenFailure";
  EventType["ACQUIRE_TOKEN_NETWORK_START"] = "msal:acquireTokenFromNetworkStart";
  EventType["SSO_SILENT_START"] = "msal:ssoSilentStart";
  EventType["SSO_SILENT_SUCCESS"] = "msal:ssoSilentSuccess";
  EventType["SSO_SILENT_FAILURE"] = "msal:ssoSilentFailure";
  EventType["ACQUIRE_TOKEN_BY_CODE_START"] = "msal:acquireTokenByCodeStart";
  EventType["ACQUIRE_TOKEN_BY_CODE_SUCCESS"] = "msal:acquireTokenByCodeSuccess";
  EventType["ACQUIRE_TOKEN_BY_CODE_FAILURE"] = "msal:acquireTokenByCodeFailure";
  EventType["HANDLE_REDIRECT_START"] = "msal:handleRedirectStart";
  EventType["HANDLE_REDIRECT_END"] = "msal:handleRedirectEnd";
  EventType["POPUP_OPENED"] = "msal:popupOpened";
  EventType["LOGOUT_START"] = "msal:logoutStart";
  EventType["LOGOUT_SUCCESS"] = "msal:logoutSuccess";
  EventType["LOGOUT_FAILURE"] = "msal:logoutFailure";
  EventType["LOGOUT_END"] = "msal:logoutEnd";
})(EventType || (EventType = {}));

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var NativeStatusCode;

(function (NativeStatusCode) {
  NativeStatusCode["USER_INTERACTION_REQUIRED"] = "USER_INTERACTION_REQUIRED";
  NativeStatusCode["USER_CANCEL"] = "USER_CANCEL";
  NativeStatusCode["NO_NETWORK"] = "NO_NETWORK";
  NativeStatusCode["TRANSIENT_ERROR"] = "TRANSIENT_ERROR";
  NativeStatusCode["PERSISTENT_ERROR"] = "PERSISTENT_ERROR";
  NativeStatusCode["DISABLED"] = "DISABLED";
})(NativeStatusCode || (NativeStatusCode = {}));

var NativeAuthErrorMessage = {
  extensionError: {
    code: "ContentError"
  },
  userSwitch: {
    code: "user_switch",
    desc: "User attempted to switch accounts in the native broker, which is not allowed. All new accounts must sign-in through the standard web flow first, please try again."
  }
};

var NativeAuthError =
/** @class */
function (_super) {
  __extends$1(NativeAuthError, _super);

  function NativeAuthError(errorCode, description, ext) {
    var _this = _super.call(this, errorCode, description) || this;

    Object.setPrototypeOf(_this, NativeAuthError.prototype);
    _this.name = "NativeAuthError";
    _this.ext = ext;
    return _this;
  }
  /**
   * These errors should result in a fallback to the 'standard' browser based auth flow.
   */


  NativeAuthError.prototype.isFatal = function () {
    if (this.ext && this.ext.status && (this.ext.status === NativeStatusCode.PERSISTENT_ERROR || this.ext.status === NativeStatusCode.DISABLED)) {
      return true;
    }

    switch (this.errorCode) {
      case NativeAuthErrorMessage.extensionError.code:
        return true;

      default:
        return false;
    }
  };
  /**
   * Create the appropriate error object based on the WAM status code.
   * @param code
   * @param description
   * @param ext
   * @returns
   */


  NativeAuthError.createError = function (code, description, ext) {
    if (ext && ext.status) {
      switch (ext.status) {
        case NativeStatusCode.USER_INTERACTION_REQUIRED:
          return new InteractionRequiredAuthError(code, description);

        case NativeStatusCode.USER_CANCEL:
          return BrowserAuthError.createUserCancelledError();

        case NativeStatusCode.NO_NETWORK:
          return BrowserAuthError.createNoNetworkConnectivityError();
      }
    }

    return new NativeAuthError(code, description, ext);
  };
  /**
   * Creates user switch error when the user chooses a different account in the native broker prompt
   * @returns
   */


  NativeAuthError.createUserSwitchError = function () {
    return new NativeAuthError(NativeAuthErrorMessage.userSwitch.code, NativeAuthErrorMessage.userSwitch.desc);
  };

  return NativeAuthError;
}(AuthError);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var NativeInteractionClient =
/** @class */
function (_super) {
  __extends$1(NativeInteractionClient, _super);

  function NativeInteractionClient(config, browserStorage, browserCrypto, logger, eventHandler, navigationClient, apiId, performanceClient, provider, accountId, correlationId) {
    var _this = _super.call(this, config, browserStorage, browserCrypto, logger, eventHandler, navigationClient, performanceClient, provider, correlationId) || this;

    _this.apiId = apiId;
    _this.accountId = accountId;
    _this.nativeMessageHandler = provider;
    return _this;
  }
  /**
   * Acquire token from native platform via browser extension
   * @param request
   */


  NativeInteractionClient.prototype.acquireToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var nativeATMeasurement, nativeRequest, messageBody, reqTimestamp, response, validatedResponse;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("NativeInteractionClient - acquireToken called.");
            nativeATMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.NativeInteractionClientAcquireToken, request.correlationId);
            return [4
            /*yield*/
            , this.initializeNativeRequest(request)];

          case 1:
            nativeRequest = _a.sent();
            messageBody = {
              method: NativeExtensionMethod.GetToken,
              request: nativeRequest
            };
            reqTimestamp = TimeUtils.nowSeconds();
            return [4
            /*yield*/
            , this.nativeMessageHandler.sendMessage(messageBody)];

          case 2:
            response = _a.sent();
            validatedResponse = this.validateNativeResponse(response);
            return [2
            /*return*/
            , this.handleNativeResponse(validatedResponse, nativeRequest, reqTimestamp).then(function (result) {
              nativeATMeasurement.endMeasurement({
                success: true,
                isNativeBroker: true
              });
              return result;
            }).catch(function (error) {
              nativeATMeasurement.endMeasurement({
                success: false,
                errorCode: error.errorCode,
                subErrorCode: error.subError,
                isNativeBroker: true
              });
              throw error;
            })];
        }
      });
    });
  };
  /**
   * Acquires a token from native platform then redirects to the redirectUri instead of returning the response
   * @param request
   */


  NativeInteractionClient.prototype.acquireTokenRedirect = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var nativeRequest, messageBody, response, e_1, navigationOptions, redirectUri;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("NativeInteractionClient - acquireTokenRedirect called.");
            return [4
            /*yield*/
            , this.initializeNativeRequest(request)];

          case 1:
            nativeRequest = _a.sent();
            messageBody = {
              method: NativeExtensionMethod.GetToken,
              request: nativeRequest
            };
            _a.label = 2;

          case 2:
            _a.trys.push([2, 4,, 5]);

            return [4
            /*yield*/
            , this.nativeMessageHandler.sendMessage(messageBody)];

          case 3:
            response = _a.sent();
            this.validateNativeResponse(response);
            return [3
            /*break*/
            , 5];

          case 4:
            e_1 = _a.sent(); // Only throw fatal errors here to allow application to fallback to regular redirect. Otherwise proceed and the error will be thrown in handleRedirectPromise

            if (e_1 instanceof NativeAuthError && e_1.isFatal()) {
              throw e_1;
            }

            return [3
            /*break*/
            , 5];

          case 5:
            this.browserStorage.setTemporaryCache(TemporaryCacheKeys.NATIVE_REQUEST, JSON.stringify(nativeRequest), true);
            navigationOptions = {
              apiId: ApiId.acquireTokenRedirect,
              timeout: this.config.system.redirectNavigationTimeout,
              noHistory: false
            };
            redirectUri = this.config.auth.navigateToLoginRequestUrl ? window.location.href : this.getRedirectUri(request.redirectUri);
            return [4
            /*yield*/
            , this.navigationClient.navigateExternal(redirectUri, navigationOptions)];

          case 6:
            _a.sent(); // Need to treat this as external to ensure handleRedirectPromise is run again


            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * If the previous page called native platform for a token using redirect APIs, send the same request again and return the response
   */


  NativeInteractionClient.prototype.handleRedirectPromise = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var cachedRequest, messageBody, reqTimestamp, response, result, e_2;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("NativeInteractionClient - handleRedirectPromise called.");

            if (!this.browserStorage.isInteractionInProgress(true)) {
              this.logger.info("handleRedirectPromise called but there is no interaction in progress, returning null.");
              return [2
              /*return*/
              , null];
            }

            cachedRequest = this.browserStorage.getCachedNativeRequest();

            if (!cachedRequest) {
              this.logger.verbose("NativeInteractionClient - handleRedirectPromise called but there is no cached request, returning null.");
              return [2
              /*return*/
              , null];
            }

            this.browserStorage.removeItem(this.browserStorage.generateCacheKey(TemporaryCacheKeys.NATIVE_REQUEST));
            messageBody = {
              method: NativeExtensionMethod.GetToken,
              request: cachedRequest
            };
            reqTimestamp = TimeUtils.nowSeconds();
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            this.logger.verbose("NativeInteractionClient - handleRedirectPromise sending message to native broker.");
            return [4
            /*yield*/
            , this.nativeMessageHandler.sendMessage(messageBody)];

          case 2:
            response = _a.sent();
            this.validateNativeResponse(response);
            result = this.handleNativeResponse(response, cachedRequest, reqTimestamp);
            this.browserStorage.setInteractionInProgress(false);
            return [2
            /*return*/
            , result];

          case 3:
            e_2 = _a.sent();
            this.browserStorage.setInteractionInProgress(false);
            throw e_2;

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Logout from native platform via browser extension
   * @param request
   */


  NativeInteractionClient.prototype.logout = function () {
    this.logger.trace("NativeInteractionClient - logout called.");
    return Promise.reject("Logout not implemented yet");
  };
  /**
   * Transform response from native platform into AuthenticationResult object which will be returned to the end user
   * @param response
   * @param request
   * @param reqTimestamp
   */


  NativeInteractionClient.prototype.handleNativeResponse = function (response, request, reqTimestamp) {
    return __awaiter$1(this, void 0, void 0, function () {
      var idTokenObj, authority, authorityPreferredCache, homeAccountIdentifier, accountEntity, responseScopes, accountProperties, uid, tid, responseAccessToken, responseTokenType, _a, popTokenGenerator, shrParameters, mats, result;

      var _this = this;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            this.logger.trace("NativeInteractionClient - handleNativeResponse called.");

            if (response.account.id !== request.accountId) {
              // User switch in native broker prompt is not supported. All users must first sign in through web flow to ensure server state is in sync
              throw NativeAuthError.createUserSwitchError();
            }

            idTokenObj = new AuthToken(response.id_token || Constants.EMPTY_STRING, this.browserCrypto);
            return [4
            /*yield*/
            , this.getDiscoveredAuthority(request.authority)];

          case 1:
            authority = _b.sent();
            authorityPreferredCache = authority.getPreferredCache();
            homeAccountIdentifier = AccountEntity.generateHomeAccountId(response.client_info || Constants.EMPTY_STRING, AuthorityType.Default, this.logger, this.browserCrypto, idTokenObj);
            accountEntity = AccountEntity.createAccount(response.client_info, homeAccountIdentifier, idTokenObj, undefined, undefined, undefined, authorityPreferredCache, response.account.id);
            this.browserStorage.setAccount(accountEntity);
            responseScopes = response.scopes ? ScopeSet.fromString(response.scopes) : ScopeSet.fromString(request.scopes);
            accountProperties = response.account.properties || {};
            uid = accountProperties["UID"] || idTokenObj.claims.oid || idTokenObj.claims.sub || Constants.EMPTY_STRING;
            tid = accountProperties["TenantId"] || idTokenObj.claims.tid || Constants.EMPTY_STRING;
            responseTokenType = AuthenticationScheme.BEARER;
            _a = request.tokenType;

            switch (_a) {
              case AuthenticationScheme.POP:
                return [3
                /*break*/
                , 2];
            }

            return [3
            /*break*/
            , 4];

          case 2:
            // Set the token type to POP in the response
            responseTokenType = AuthenticationScheme.POP; // Check if native layer returned an SHR token

            if (response.shr) {
              this.logger.trace("handleNativeServerResponse: SHR is enabled in native layer");
              responseAccessToken = response.shr;
              return [3
              /*break*/
              , 5];
            }

            popTokenGenerator = new PopTokenGenerator(this.browserCrypto);
            shrParameters = {
              resourceRequestMethod: request.resourceRequestMethod,
              resourceRequestUri: request.resourceRequestUri,
              shrClaims: request.shrClaims,
              shrNonce: request.shrNonce
            };
            /**
             * KeyID must be present in the native request from when the PoP key was generated in order for
             * PopTokenGenerator to query the full key for signing
             */

            if (!request.keyId) {
              throw ClientAuthError.createKeyIdMissingError();
            }

            return [4
            /*yield*/
            , popTokenGenerator.signPopToken(response.access_token, request.keyId, shrParameters)];

          case 3:
            responseAccessToken = _b.sent();
            return [3
            /*break*/
            , 5];

          case 4:
            {
              responseAccessToken = response.access_token;
            }
            _b.label = 5;

          case 5:
            mats = this.getMATSFromResponse(response);
            result = {
              authority: authority.canonicalAuthority,
              uniqueId: uid,
              tenantId: tid,
              scopes: responseScopes.asArray(),
              account: accountEntity.getAccountInfo(),
              idToken: response.id_token,
              idTokenClaims: idTokenObj.claims,
              accessToken: responseAccessToken,
              fromCache: mats ? this.isResponseFromCache(mats) : false,
              expiresOn: new Date(Number(reqTimestamp + response.expires_in) * 1000),
              tokenType: responseTokenType,
              correlationId: this.correlationId,
              state: response.state,
              fromNativeBroker: true
            }; // Remove any existing cached tokens for this account

            this.browserStorage.removeAccountContext(accountEntity).catch(function (e) {
              _this.logger.error("Error occurred while removing account context from browser storage. " + e);
            });
            return [2
            /*return*/
            , result];
        }
      });
    });
  };
  /**
   * Validates native platform response before processing
   * @param response
   */


  NativeInteractionClient.prototype.validateNativeResponse = function (response) {
    if (response.hasOwnProperty("access_token") && response.hasOwnProperty("id_token") && response.hasOwnProperty("client_info") && response.hasOwnProperty("account") && response.hasOwnProperty("scopes") && response.hasOwnProperty("expires_in")) {
      return response;
    } else {
      throw NativeAuthError.createUnexpectedError("Response missing expected properties.");
    }
  };
  /**
   * Gets MATS telemetry from native response
   * @param response
   * @returns
   */


  NativeInteractionClient.prototype.getMATSFromResponse = function (response) {
    if (response.properties.MATS) {
      try {
        return JSON.parse(response.properties.MATS);
      } catch (e) {
        this.logger.error("NativeInteractionClient - Error parsing MATS telemetry, returning null instead");
      }
    }

    return null;
  };
  /**
   * Returns whether or not response came from native cache
   * @param response
   * @returns
   */


  NativeInteractionClient.prototype.isResponseFromCache = function (mats) {
    if (typeof mats.is_cached === "undefined") {
      this.logger.verbose("NativeInteractionClient - MATS telemetry does not contain field indicating if response was served from cache. Returning false.");
      return false;
    }

    return !!mats.is_cached;
  };
  /**
   * Translates developer provided request object into NativeRequest object
   * @param request
   */


  NativeInteractionClient.prototype.initializeNativeRequest = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var authority, canonicalAuthority, scopes, scopeSet, validatedRequest, shrParameters, popTokenGenerator, reqCnfData;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("NativeInteractionClient - initializeNativeRequest called");
            authority = request.authority || this.config.auth.authority;
            canonicalAuthority = new UrlString(authority);
            canonicalAuthority.validateAsUri();
            scopes = request && request.scopes || [];
            scopeSet = new ScopeSet(scopes);
            scopeSet.appendScopes(OIDC_DEFAULT_SCOPES);

            if (request.prompt) {
              switch (request.prompt) {
                case PromptValue.NONE:
                case PromptValue.CONSENT:
                  this.logger.trace("initializeNativeRequest: prompt is compatible with native flow");
                  break;

                default:
                  this.logger.trace("initializeNativeRequest: prompt = " + request.prompt + " is not compatible with native flow");
                  throw BrowserAuthError.createNativePromptParameterNotSupportedError();
              }
            }

            validatedRequest = __assign$1(__assign$1({}, request), {
              accountId: this.accountId,
              clientId: this.config.auth.clientId,
              authority: canonicalAuthority.urlString,
              scopes: scopeSet.printScopes(),
              redirectUri: this.getRedirectUri(request.redirectUri),
              correlationId: this.correlationId,
              tokenType: request.authenticationScheme,
              windowTitleSubstring: document.title,
              extraParameters: __assign$1(__assign$1(__assign$1({}, request.extraQueryParameters), request.tokenQueryParameters), {
                telemetry: NativeConstants.MATS_TELEMETRY
              }),
              extendedExpiryToken: false // Make this configurable?

            });
            if (!(request.authenticationScheme === AuthenticationScheme.POP)) return [3
            /*break*/
            , 2];
            shrParameters = {
              resourceRequestUri: request.resourceRequestUri,
              resourceRequestMethod: request.resourceRequestMethod,
              shrClaims: request.shrClaims,
              shrNonce: request.shrNonce
            };
            popTokenGenerator = new PopTokenGenerator(this.browserCrypto);
            return [4
            /*yield*/
            , popTokenGenerator.generateCnf(shrParameters)];

          case 1:
            reqCnfData = _a.sent(); // to reduce the URL length, it is recommended to send the hash of the req_cnf instead of the whole string

            validatedRequest.reqCnf = reqCnfData.reqCnfHash;
            validatedRequest.keyId = reqCnfData.kid;
            _a.label = 2;

          case 2:
            if (this.apiId === ApiId.ssoSilent || this.apiId === ApiId.acquireTokenSilent_silentFlow) {
              validatedRequest.prompt = PromptValue.NONE;
            }

            return [2
            /*return*/
            , validatedRequest];
        }
      });
    });
  };

  return NativeInteractionClient;
}(BaseInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var NativeMessageHandler =
/** @class */
function () {
  function NativeMessageHandler(logger, handshakeTimeoutMs, extensionId) {
    this.logger = logger;
    this.handshakeTimeoutMs = handshakeTimeoutMs;
    this.extensionId = extensionId;
    this.resolvers = new Map(); // Used for non-handshake messages

    this.handshakeResolvers = new Map(); // Used for handshake messages

    this.responseId = 0;
    this.messageChannel = new MessageChannel();
    this.windowListener = this.onWindowMessage.bind(this); // Window event callback doesn't have access to 'this' unless it's bound
  }
  /**
   * Sends a given message to the extension and resolves with the extension response
   * @param body
   */


  NativeMessageHandler.prototype.sendMessage = function (body) {
    return __awaiter$1(this, void 0, void 0, function () {
      var req;

      var _this = this;

      return __generator$1(this, function (_a) {
        this.logger.trace("NativeMessageHandler - sendMessage called.");
        req = {
          channel: NativeConstants.CHANNEL_ID,
          extensionId: this.extensionId,
          responseId: this.responseId++,
          body: body
        };
        this.logger.trace("NativeMessageHandler - Sending request to browser extension");
        this.logger.tracePii("NativeMessageHandler - Sending request to browser extension: " + JSON.stringify(req));
        this.messageChannel.port1.postMessage(req);
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          _this.resolvers.set(req.responseId, {
            resolve: resolve,
            reject: reject
          });
        })];
      });
    });
  };
  /**
   * Returns an instance of the MessageHandler that has successfully established a connection with an extension
   * @param logger
   * @param handshakeTimeoutMs
   */


  NativeMessageHandler.createProvider = function (logger, handshakeTimeoutMs) {
    return __awaiter$1(this, void 0, void 0, function () {
      var preferredProvider, backupProvider;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            logger.trace("NativeMessageHandler - createProvider called.");
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 5]);

            preferredProvider = new NativeMessageHandler(logger, handshakeTimeoutMs, NativeConstants.PREFERRED_EXTENSION_ID);
            return [4
            /*yield*/
            , preferredProvider.sendHandshakeRequest()];

          case 2:
            _a.sent();

            return [2
            /*return*/
            , preferredProvider];

          case 3:
            _a.sent();

            backupProvider = new NativeMessageHandler(logger, handshakeTimeoutMs);
            return [4
            /*yield*/
            , backupProvider.sendHandshakeRequest()];

          case 4:
            _a.sent();

            return [2
            /*return*/
            , backupProvider];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Send handshake request helper.
   */


  NativeMessageHandler.prototype.sendHandshakeRequest = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var req;

      var _this = this;

      return __generator$1(this, function (_a) {
        this.logger.trace("NativeMessageHandler - sendHandshakeRequest called."); // Register this event listener before sending handshake

        window.addEventListener("message", this.windowListener, false); // false is important, because content script message processing should work first

        req = {
          channel: NativeConstants.CHANNEL_ID,
          extensionId: this.extensionId,
          responseId: this.responseId++,
          body: {
            method: NativeExtensionMethod.HandshakeRequest
          }
        };

        this.messageChannel.port1.onmessage = function (event) {
          _this.onChannelMessage(event);
        };

        window.postMessage(req, window.origin, [this.messageChannel.port2]);
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          _this.handshakeResolvers.set(req.responseId, {
            resolve: resolve,
            reject: reject
          });

          _this.timeoutId = window.setTimeout(function () {
            /*
             * Throw an error if neither HandshakeResponse nor original Handshake request are received in a reasonable timeframe.
             * This typically suggests an event handler stopped propagation of the Handshake request but did not respond to it on the MessageChannel port
             */
            window.removeEventListener("message", _this.windowListener, false);

            _this.messageChannel.port1.close();

            _this.messageChannel.port2.close();

            reject(BrowserAuthError.createNativeHandshakeTimeoutError());

            _this.handshakeResolvers.delete(req.responseId);
          }, _this.handshakeTimeoutMs); // Use a reasonable timeout in milliseconds here
        })];
      });
    });
  };
  /**
   * Invoked when a message is posted to the window. If a handshake request is received it means the extension is not installed.
   * @param event
   */


  NativeMessageHandler.prototype.onWindowMessage = function (event) {
    this.logger.trace("NativeMessageHandler - onWindowMessage called"); // We only accept messages from ourselves

    if (event.source !== window) {
      return;
    }

    var request = event.data;

    if (!request.channel || request.channel !== NativeConstants.CHANNEL_ID) {
      return;
    }

    if (request.extensionId && request.extensionId !== this.extensionId) {
      return;
    }

    if (request.body.method === NativeExtensionMethod.HandshakeRequest) {
      // If we receive this message back it means no extension intercepted the request, meaning no extension supporting handshake protocol is installed
      this.logger.verbose(request.extensionId ? "Extension with id: " + request.extensionId + " not installed" : "No extension installed");
      clearTimeout(this.timeoutId);
      this.messageChannel.port1.close();
      this.messageChannel.port2.close();
      window.removeEventListener("message", this.windowListener, false);
      var handshakeResolver = this.handshakeResolvers.get(request.responseId);

      if (handshakeResolver) {
        handshakeResolver.reject(BrowserAuthError.createNativeExtensionNotInstalledError());
      }
    }
  };
  /**
   * Invoked when a message is received from the extension on the MessageChannel port
   * @param event
   */


  NativeMessageHandler.prototype.onChannelMessage = function (event) {
    this.logger.trace("NativeMessageHandler - onChannelMessage called.");
    var request = event.data;
    var resolver = this.resolvers.get(request.responseId);
    var handshakeResolver = this.handshakeResolvers.get(request.responseId);

    try {
      var method = request.body.method;

      if (method === NativeExtensionMethod.Response) {
        if (!resolver) {
          return;
        }

        var response = request.body.response;
        this.logger.trace("NativeMessageHandler - Received response from browser extension");
        this.logger.tracePii("NativeMessageHandler - Received response from browser extension: " + JSON.stringify(response));

        if (response.status !== "Success") {
          resolver.reject(NativeAuthError.createError(response.code, response.description, response.ext));
        } else if (response.result) {
          if (response.result["code"] && response.result["description"]) {
            resolver.reject(NativeAuthError.createError(response.result["code"], response.result["description"], response.result["ext"]));
          } else {
            resolver.resolve(response.result);
          }
        } else {
          throw AuthError.createUnexpectedError("Event does not contain result.");
        }

        this.resolvers.delete(request.responseId);
      } else if (method === NativeExtensionMethod.HandshakeResponse) {
        if (!handshakeResolver) {
          return;
        }

        clearTimeout(this.timeoutId); // Clear setTimeout

        window.removeEventListener("message", this.windowListener, false); // Remove 'No extension' listener

        this.extensionId = request.extensionId;
        this.logger.verbose("NativeMessageHandler - Received HandshakeResponse from extension: " + this.extensionId);
        handshakeResolver.resolve();
        this.handshakeResolvers.delete(request.responseId);
      } // Do nothing if method is not Response or HandshakeResponse

    } catch (err) {
      this.logger.error("Error parsing response from WAM Extension");
      this.logger.errorPii("Error parsing response from WAM Extension: " + err.toString());
      this.logger.errorPii("Unable to parse " + event);

      if (resolver) {
        resolver.reject(err);
      } else if (handshakeResolver) {
        handshakeResolver.reject(err);
      }
    }
  };
  /**
   * Returns boolean indicating whether or not the request should attempt to use native broker
   * @param logger
   * @param config
   * @param nativeExtensionProvider
   * @param authenticationScheme
   */


  NativeMessageHandler.isNativeAvailable = function (config, logger, nativeExtensionProvider, authenticationScheme) {
    logger.trace("isNativeAvailable called");

    if (!config.system.allowNativeBroker) {
      logger.trace("isNativeAvailable: allowNativeBroker is not enabled, returning false"); // Developer disabled WAM

      return false;
    }

    if (!nativeExtensionProvider) {
      logger.trace("isNativeAvailable: WAM extension provider is not initialized, returning false"); // Extension is not available

      return false;
    }

    if (authenticationScheme) {
      switch (authenticationScheme) {
        case AuthenticationScheme.BEARER:
        case AuthenticationScheme.POP:
          logger.trace("isNativeAvailable: authenticationScheme is supported, returning true");
          return true;

        default:
          logger.trace("isNativeAvailable: authenticationScheme is not supported, returning false");
          return false;
      }
    }

    return true;
  };

  return NativeMessageHandler;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var RedirectClient =
/** @class */
function (_super) {
  __extends$1(RedirectClient, _super);

  function RedirectClient() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Redirects the page to the /authorize endpoint of the IDP
   * @param request
   */


  RedirectClient.prototype.acquireToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var validRequest, serverTelemetryManager, handleBackButton, authCodeRequest, authClient, interactionHandler, navigateUrl, redirectStartPage, e_1;

      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.initializeAuthorizationRequest(request, InteractionType.Redirect)];

          case 1:
            validRequest = _a.sent();
            this.browserStorage.updateCacheEntries(validRequest.state, validRequest.nonce, validRequest.authority, validRequest.loginHint || Constants.EMPTY_STRING, validRequest.account || null);
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.acquireTokenRedirect);

            handleBackButton = function (event) {
              // Clear temporary cache if the back button is clicked during the redirect flow.
              if (event.persisted) {
                _this.logger.verbose("Page was restored from back/forward cache. Clearing temporary cache.");

                _this.browserStorage.cleanRequestByState(validRequest.state);
              }
            };

            _a.label = 2;

          case 2:
            _a.trys.push([2, 7,, 8]);

            return [4
            /*yield*/
            , this.initializeAuthorizationCodeRequest(validRequest)];

          case 3:
            authCodeRequest = _a.sent();
            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, validRequest.authority, validRequest.azureCloudOptions)];

          case 4:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            interactionHandler = new RedirectHandler(authClient, this.browserStorage, authCodeRequest, this.logger, this.browserCrypto);
            return [4
            /*yield*/
            , authClient.getAuthCodeUrl(__assign$1(__assign$1({}, validRequest), {
              nativeBroker: NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeMessageHandler, request.authenticationScheme)
            }))];

          case 5:
            navigateUrl = _a.sent();
            redirectStartPage = this.getRedirectStartPage(request.redirectStartPage);
            this.logger.verbosePii("Redirect start page: " + redirectStartPage); // Clear temporary cache if the back button is clicked during the redirect flow.

            window.addEventListener("pageshow", handleBackButton);
            return [4
            /*yield*/
            , interactionHandler.initiateAuthRequest(navigateUrl, {
              navigationClient: this.navigationClient,
              redirectTimeout: this.config.system.redirectNavigationTimeout,
              redirectStartPage: redirectStartPage,
              onRedirectNavigate: request.onRedirectNavigate
            })];

          case 6:
            // Show the UI once the url has been created. Response will come back in the hash, which will be handled in the handleRedirectCallback function.
            return [2
            /*return*/
            , _a.sent()];

          case 7:
            e_1 = _a.sent();

            if (e_1 instanceof AuthError) {
              e_1.setCorrelationId(this.correlationId);
            }

            window.removeEventListener("pageshow", handleBackButton);
            serverTelemetryManager.cacheFailedRequest(e_1);
            this.browserStorage.cleanRequestByState(validRequest.state);
            throw e_1;

          case 8:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Checks if navigateToLoginRequestUrl is set, and:
   * - if true, performs logic to cache and navigate
   * - if false, handles hash string and parses response
   * @param hash
   */


  RedirectClient.prototype.handleRedirectPromise = function (hash) {
    return __awaiter$1(this, void 0, void 0, function () {
      var serverTelemetryManager, responseHash, state, serverParams, loginRequestUrl, loginRequestUrlNormalized, currentUrlNormalized, handleHashResult, navigationOptions, processHashOnRedirect, homepage, e_2;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.handleRedirectPromise);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 10,, 11]);

            if (!this.browserStorage.isInteractionInProgress(true)) {
              this.logger.info("handleRedirectPromise called but there is no interaction in progress, returning null.");
              return [2
              /*return*/
              , null];
            }

            responseHash = this.getRedirectResponseHash(hash || window.location.hash);

            if (!responseHash) {
              // Not a recognized server response hash or hash not associated with a redirect request
              this.logger.info("handleRedirectPromise did not detect a response hash as a result of a redirect. Cleaning temporary cache.");
              this.browserStorage.cleanRequestByInteractionType(InteractionType.Redirect);
              return [2
              /*return*/
              , null];
            }

            state = void 0;

            try {
              serverParams = UrlString.getDeserializedHash(responseHash);
              state = this.validateAndExtractStateFromHash(serverParams, InteractionType.Redirect);
              this.logger.verbose("State extracted from hash");
            } catch (e) {
              this.logger.info("handleRedirectPromise was unable to extract state due to: " + e);
              this.browserStorage.cleanRequestByInteractionType(InteractionType.Redirect);
              return [2
              /*return*/
              , null];
            }

            loginRequestUrl = this.browserStorage.getTemporaryCache(TemporaryCacheKeys.ORIGIN_URI, true) || Constants.EMPTY_STRING;
            loginRequestUrlNormalized = UrlString.removeHashFromUrl(loginRequestUrl);
            currentUrlNormalized = UrlString.removeHashFromUrl(window.location.href);
            if (!(loginRequestUrlNormalized === currentUrlNormalized && this.config.auth.navigateToLoginRequestUrl)) return [3
            /*break*/
            , 3]; // We are on the page we need to navigate to - handle hash

            this.logger.verbose("Current page is loginRequestUrl, handling hash");
            return [4
            /*yield*/
            , this.handleHash(responseHash, state, serverTelemetryManager)];

          case 2:
            handleHashResult = _a.sent();

            if (loginRequestUrl.indexOf("#") > -1) {
              // Replace current hash with non-msal hash, if present
              BrowserUtils.replaceHash(loginRequestUrl);
            }

            return [2
            /*return*/
            , handleHashResult];

          case 3:
            if (!!this.config.auth.navigateToLoginRequestUrl) return [3
            /*break*/
            , 4];
            this.logger.verbose("NavigateToLoginRequestUrl set to false, handling hash");
            return [2
            /*return*/
            , this.handleHash(responseHash, state, serverTelemetryManager)];

          case 4:
            if (!(!BrowserUtils.isInIframe() || this.config.system.allowRedirectInIframe)) return [3
            /*break*/
            , 9];
            /*
             * Returned from authority using redirect - need to perform navigation before processing response
             * Cache the hash to be retrieved after the next redirect
             */

            this.browserStorage.setTemporaryCache(TemporaryCacheKeys.URL_HASH, responseHash, true);
            navigationOptions = {
              apiId: ApiId.handleRedirectPromise,
              timeout: this.config.system.redirectNavigationTimeout,
              noHistory: true
            };
            processHashOnRedirect = true;
            if (!(!loginRequestUrl || loginRequestUrl === "null")) return [3
            /*break*/
            , 6];
            homepage = BrowserUtils.getHomepage(); // Cache the homepage under ORIGIN_URI to ensure cached hash is processed on homepage

            this.browserStorage.setTemporaryCache(TemporaryCacheKeys.ORIGIN_URI, homepage, true);
            this.logger.warning("Unable to get valid login request url from cache, redirecting to home page");
            return [4
            /*yield*/
            , this.navigationClient.navigateInternal(homepage, navigationOptions)];

          case 5:
            processHashOnRedirect = _a.sent();
            return [3
            /*break*/
            , 8];

          case 6:
            // Navigate to page that initiated the redirect request
            this.logger.verbose("Navigating to loginRequestUrl: " + loginRequestUrl);
            return [4
            /*yield*/
            , this.navigationClient.navigateInternal(loginRequestUrl, navigationOptions)];

          case 7:
            processHashOnRedirect = _a.sent();
            _a.label = 8;

          case 8:
            // If navigateInternal implementation returns false, handle the hash now
            if (!processHashOnRedirect) {
              return [2
              /*return*/
              , this.handleHash(responseHash, state, serverTelemetryManager)];
            }

            _a.label = 9;

          case 9:
            return [2
            /*return*/
            , null];

          case 10:
            e_2 = _a.sent();

            if (e_2 instanceof AuthError) {
              e_2.setCorrelationId(this.correlationId);
            }

            serverTelemetryManager.cacheFailedRequest(e_2);
            this.browserStorage.cleanRequestByInteractionType(InteractionType.Redirect);
            throw e_2;

          case 11:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Gets the response hash for a redirect request
   * Returns null if interactionType in the state value is not "redirect" or the hash does not contain known properties
   * @param hash
   */


  RedirectClient.prototype.getRedirectResponseHash = function (hash) {
    this.logger.verbose("getRedirectResponseHash called"); // Get current location hash from window or cache.

    var isResponseHash = UrlString.hashContainsKnownProperties(hash);

    if (isResponseHash) {
      BrowserUtils.clearHash(window);
      this.logger.verbose("Hash contains known properties, returning response hash");
      return hash;
    }

    var cachedHash = this.browserStorage.getTemporaryCache(TemporaryCacheKeys.URL_HASH, true);
    this.browserStorage.removeItem(this.browserStorage.generateCacheKey(TemporaryCacheKeys.URL_HASH));
    this.logger.verbose("Hash does not contain known properties, returning cached hash");
    return cachedHash;
  };
  /**
   * Checks if hash exists and handles in window.
   * @param hash
   * @param state
   */


  RedirectClient.prototype.handleHash = function (hash, state, serverTelemetryManager) {
    return __awaiter$1(this, void 0, void 0, function () {
      var cachedRequest, serverParams, nativeInteractionClient, userRequestState, currentAuthority, authClient, interactionHandler;

      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            cachedRequest = this.browserStorage.getCachedRequest(state, this.browserCrypto);
            this.logger.verbose("handleHash called, retrieved cached request");
            serverParams = UrlString.getDeserializedHash(hash);

            if (serverParams.accountId) {
              this.logger.verbose("Account id found in hash, calling WAM for token");

              if (!this.nativeMessageHandler) {
                throw BrowserAuthError.createNativeConnectionNotEstablishedError();
              }

              nativeInteractionClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.acquireTokenPopup, this.performanceClient, this.nativeMessageHandler, serverParams.accountId, cachedRequest.correlationId);
              userRequestState = ProtocolUtils.parseRequestState(this.browserCrypto, state).userRequestState;
              return [2
              /*return*/
              , nativeInteractionClient.acquireToken(__assign$1(__assign$1({}, cachedRequest), {
                state: userRequestState,
                prompt: undefined // Server should handle the prompt, ideally native broker can do this part silently

              })).finally(function () {
                _this.browserStorage.cleanRequestByState(state);
              })];
            }

            currentAuthority = this.browserStorage.getCachedAuthority(state);

            if (!currentAuthority) {
              throw BrowserAuthError.createNoCachedAuthorityError();
            }

            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, currentAuthority)];

          case 1:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            ThrottlingUtils.removeThrottle(this.browserStorage, this.config.auth.clientId, cachedRequest);
            interactionHandler = new RedirectHandler(authClient, this.browserStorage, cachedRequest, this.logger, this.browserCrypto);
            return [4
            /*yield*/
            , interactionHandler.handleCodeResponseFromHash(hash, state, authClient.authority, this.networkClient)];

          case 2:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };
  /**
   * Use to log out the current user, and redirect the user to the postLogoutRedirectUri.
   * Default behaviour is to redirect the user to `window.location.href`.
   * @param logoutRequest
   */


  RedirectClient.prototype.logout = function (logoutRequest) {
    return __awaiter$1(this, void 0, void 0, function () {
      var validLogoutRequest, serverTelemetryManager, navigationOptions, authClient, logoutUri, navigate, e_3;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("logoutRedirect called");
            validLogoutRequest = this.initializeLogoutRequest(logoutRequest);
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.logout);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 10,, 11]);

            this.eventHandler.emitEvent(EventType.LOGOUT_START, InteractionType.Redirect, logoutRequest); // Clear cache on logout

            return [4
            /*yield*/
            , this.clearCacheOnLogout(validLogoutRequest.account)];

          case 2:
            // Clear cache on logout
            _a.sent();

            navigationOptions = {
              apiId: ApiId.logout,
              timeout: this.config.system.redirectNavigationTimeout,
              noHistory: false
            };
            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, logoutRequest && logoutRequest.authority)];

          case 3:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            logoutUri = authClient.getLogoutUri(validLogoutRequest);
            this.eventHandler.emitEvent(EventType.LOGOUT_SUCCESS, InteractionType.Redirect, validLogoutRequest);
            if (!(logoutRequest && typeof logoutRequest.onRedirectNavigate === "function")) return [3
            /*break*/
            , 7];
            navigate = logoutRequest.onRedirectNavigate(logoutUri);
            if (!(navigate !== false)) return [3
            /*break*/
            , 5];
            this.logger.verbose("Logout onRedirectNavigate did not return false, navigating"); // Ensure interaction is in progress

            if (!this.browserStorage.getInteractionInProgress()) {
              this.browserStorage.setInteractionInProgress(true);
            }

            return [4
            /*yield*/
            , this.navigationClient.navigateExternal(logoutUri, navigationOptions)];

          case 4:
            _a.sent();

            return [2
            /*return*/
            ];

          case 5:
            // Ensure interaction is not in progress
            this.browserStorage.setInteractionInProgress(false);
            this.logger.verbose("Logout onRedirectNavigate returned false, stopping navigation");
            _a.label = 6;

          case 6:
            return [3
            /*break*/
            , 9];

          case 7:
            // Ensure interaction is in progress
            if (!this.browserStorage.getInteractionInProgress()) {
              this.browserStorage.setInteractionInProgress(true);
            }

            return [4
            /*yield*/
            , this.navigationClient.navigateExternal(logoutUri, navigationOptions)];

          case 8:
            _a.sent();

            return [2
            /*return*/
            ];

          case 9:
            return [3
            /*break*/
            , 11];

          case 10:
            e_3 = _a.sent();

            if (e_3 instanceof AuthError) {
              e_3.setCorrelationId(this.correlationId);
            }

            serverTelemetryManager.cacheFailedRequest(e_3);
            this.eventHandler.emitEvent(EventType.LOGOUT_FAILURE, InteractionType.Redirect, null, e_3);
            this.eventHandler.emitEvent(EventType.LOGOUT_END, InteractionType.Redirect);
            throw e_3;

          case 11:
            this.eventHandler.emitEvent(EventType.LOGOUT_END, InteractionType.Redirect);
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Use to get the redirectStartPage either from request or use current window
   * @param requestStartPage
   */


  RedirectClient.prototype.getRedirectStartPage = function (requestStartPage) {
    var redirectStartPage = requestStartPage || window.location.href;
    return UrlString.getAbsoluteUrl(redirectStartPage, BrowserUtils.getCurrentUri());
  };

  return RedirectClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var PopupClient =
/** @class */
function (_super) {
  __extends$1(PopupClient, _super);

  function PopupClient(config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, performanceClient, nativeMessageHandler, correlationId) {
    var _this = _super.call(this, config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, performanceClient, nativeMessageHandler, correlationId) || this; // Properly sets this reference for the unload event.


    _this.unloadWindow = _this.unloadWindow.bind(_this);
    return _this;
  }
  /**
   * Acquires tokens by opening a popup window to the /authorize endpoint of the authority
   * @param request
   */


  PopupClient.prototype.acquireToken = function (request) {
    try {
      var popupName = this.generatePopupName(request.scopes || OIDC_DEFAULT_SCOPES, request.authority || this.config.auth.authority);
      var popupWindowAttributes = request.popupWindowAttributes || {}; // asyncPopups flag is true. Acquires token without first opening popup. Popup will be opened later asynchronously.

      if (this.config.system.asyncPopups) {
        this.logger.verbose("asyncPopups set to true, acquiring token"); // Passes on popup position and dimensions if in request

        return this.acquireTokenPopupAsync(request, popupName, popupWindowAttributes);
      } else {
        // asyncPopups flag is set to false. Opens popup before acquiring token.
        this.logger.verbose("asyncPopup set to false, opening popup before acquiring token");
        var popup = this.openSizedPopup("about:blank", popupName, popupWindowAttributes);
        return this.acquireTokenPopupAsync(request, popupName, popupWindowAttributes, popup);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };
  /**
   * Clears local cache for the current user then opens a popup window prompting the user to sign-out of the server
   * @param logoutRequest
   */


  PopupClient.prototype.logout = function (logoutRequest) {
    try {
      this.logger.verbose("logoutPopup called");
      var validLogoutRequest = this.initializeLogoutRequest(logoutRequest);
      var popupName = this.generateLogoutPopupName(validLogoutRequest);
      var authority = logoutRequest && logoutRequest.authority;
      var mainWindowRedirectUri = logoutRequest && logoutRequest.mainWindowRedirectUri;
      var popupWindowAttributes = (logoutRequest === null || logoutRequest === void 0 ? void 0 : logoutRequest.popupWindowAttributes) || {}; // asyncPopups flag is true. Acquires token without first opening popup. Popup will be opened later asynchronously.

      if (this.config.system.asyncPopups) {
        this.logger.verbose("asyncPopups set to true"); // Passes on popup position and dimensions if in request

        return this.logoutPopupAsync(validLogoutRequest, popupName, popupWindowAttributes, authority, undefined, mainWindowRedirectUri);
      } else {
        // asyncPopups flag is set to false. Opens popup before logging out.
        this.logger.verbose("asyncPopup set to false, opening popup");
        var popup = this.openSizedPopup("about:blank", popupName, popupWindowAttributes);
        return this.logoutPopupAsync(validLogoutRequest, popupName, popupWindowAttributes, authority, popup, mainWindowRedirectUri);
      }
    } catch (e) {
      // Since this function is synchronous we need to reject
      return Promise.reject(e);
    }
  };
  /**
   * Helper which obtains an access_token for your API via opening a popup window in the user's browser
   * @param validRequest
   * @param popupName
   * @param popup
   * @param popupWindowAttributes
   *
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  PopupClient.prototype.acquireTokenPopupAsync = function (request, popupName, popupWindowAttributes, popup) {
    return __awaiter$1(this, void 0, void 0, function () {
      var serverTelemetryManager, validRequest, authCodeRequest, authClient, isNativeBroker, fetchNativeAccountIdMeasurement, navigateUrl, interactionHandler, popupParameters, popupWindow, hash, serverParams, state_1, nativeInteractionClient, userRequestState, result, e_1;

      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("acquireTokenPopupAsync called");
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.acquireTokenPopup);
            return [4
            /*yield*/
            , this.initializeAuthorizationRequest(request, InteractionType.Popup)];

          case 1:
            validRequest = _a.sent();
            this.browserStorage.updateCacheEntries(validRequest.state, validRequest.nonce, validRequest.authority, validRequest.loginHint || Constants.EMPTY_STRING, validRequest.account || null);
            _a.label = 2;

          case 2:
            _a.trys.push([2, 8,, 9]);

            return [4
            /*yield*/
            , this.initializeAuthorizationCodeRequest(validRequest)];

          case 3:
            authCodeRequest = _a.sent();
            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, validRequest.authority, validRequest.azureCloudOptions)];

          case 4:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            isNativeBroker = NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeMessageHandler, request.authenticationScheme);
            fetchNativeAccountIdMeasurement = void 0;

            if (isNativeBroker) {
              fetchNativeAccountIdMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.FetchAccountIdWithNativeBroker, request.correlationId);
            }

            return [4
            /*yield*/
            , authClient.getAuthCodeUrl(__assign$1(__assign$1({}, validRequest), {
              nativeBroker: isNativeBroker
            }))];

          case 5:
            navigateUrl = _a.sent();
            interactionHandler = new InteractionHandler(authClient, this.browserStorage, authCodeRequest, this.logger);
            popupParameters = {
              popup: popup,
              popupName: popupName,
              popupWindowAttributes: popupWindowAttributes
            };
            popupWindow = this.initiateAuthRequest(navigateUrl, popupParameters);
            this.eventHandler.emitEvent(EventType.POPUP_OPENED, InteractionType.Popup, {
              popupWindow: popupWindow
            }, null);
            return [4
            /*yield*/
            , this.monitorPopupForHash(popupWindow)];

          case 6:
            hash = _a.sent();
            serverParams = UrlString.getDeserializedHash(hash);
            state_1 = this.validateAndExtractStateFromHash(serverParams, InteractionType.Popup, validRequest.correlationId); // Remove throttle if it exists

            ThrottlingUtils.removeThrottle(this.browserStorage, this.config.auth.clientId, authCodeRequest);

            if (serverParams.accountId) {
              this.logger.verbose("Account id found in hash, calling WAM for token"); // end measurement for server call with native brokering enabled

              if (fetchNativeAccountIdMeasurement) {
                fetchNativeAccountIdMeasurement.endMeasurement({
                  success: true,
                  isNativeBroker: true
                });
              }

              if (!this.nativeMessageHandler) {
                throw BrowserAuthError.createNativeConnectionNotEstablishedError();
              }

              nativeInteractionClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.acquireTokenPopup, this.performanceClient, this.nativeMessageHandler, serverParams.accountId, validRequest.correlationId);
              userRequestState = ProtocolUtils.parseRequestState(this.browserCrypto, state_1).userRequestState;
              return [2
              /*return*/
              , nativeInteractionClient.acquireToken(__assign$1(__assign$1({}, validRequest), {
                state: userRequestState,
                prompt: undefined // Server should handle the prompt, ideally native broker can do this part silently

              })).finally(function () {
                _this.browserStorage.cleanRequestByState(state_1);
              })];
            }

            return [4
            /*yield*/
            , interactionHandler.handleCodeResponseFromHash(hash, state_1, authClient.authority, this.networkClient)];

          case 7:
            result = _a.sent();
            return [2
            /*return*/
            , result];

          case 8:
            e_1 = _a.sent();

            if (popup) {
              // Close the synchronous popup if an error is thrown before the window unload event is registered
              popup.close();
            }

            if (e_1 instanceof AuthError) {
              e_1.setCorrelationId(this.correlationId);
            }

            serverTelemetryManager.cacheFailedRequest(e_1);
            this.browserStorage.cleanRequestByState(validRequest.state);
            throw e_1;

          case 9:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   *
   * @param validRequest
   * @param popupName
   * @param requestAuthority
   * @param popup
   * @param mainWindowRedirectUri
   * @param popupWindowAttributes
   */


  PopupClient.prototype.logoutPopupAsync = function (validRequest, popupName, popupWindowAttributes, requestAuthority, popup, mainWindowRedirectUri) {
    return __awaiter$1(this, void 0, void 0, function () {
      var serverTelemetryManager, authClient, logoutUri, popupWindow, navigationOptions, absoluteUrl, e_2;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("logoutPopupAsync called");
            this.eventHandler.emitEvent(EventType.LOGOUT_START, InteractionType.Popup, validRequest);
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.logoutPopup);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 5,, 6]); // Clear cache on logout


            return [4
            /*yield*/
            , this.clearCacheOnLogout(validRequest.account)];

          case 2:
            // Clear cache on logout
            _a.sent();

            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, requestAuthority)];

          case 3:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            logoutUri = authClient.getLogoutUri(validRequest);
            this.eventHandler.emitEvent(EventType.LOGOUT_SUCCESS, InteractionType.Popup, validRequest);
            popupWindow = this.openPopup(logoutUri, {
              popupName: popupName,
              popupWindowAttributes: popupWindowAttributes,
              popup: popup
            });
            this.eventHandler.emitEvent(EventType.POPUP_OPENED, InteractionType.Popup, {
              popupWindow: popupWindow
            }, null);
            return [4
            /*yield*/
            , this.waitForLogoutPopup(popupWindow)];

          case 4:
            _a.sent();

            if (mainWindowRedirectUri) {
              navigationOptions = {
                apiId: ApiId.logoutPopup,
                timeout: this.config.system.redirectNavigationTimeout,
                noHistory: false
              };
              absoluteUrl = UrlString.getAbsoluteUrl(mainWindowRedirectUri, BrowserUtils.getCurrentUri());
              this.logger.verbose("Redirecting main window to url specified in the request");
              this.logger.verbosePii("Redirecting main window to: " + absoluteUrl);
              this.navigationClient.navigateInternal(absoluteUrl, navigationOptions);
            } else {
              this.logger.verbose("No main window navigation requested");
            }

            return [3
            /*break*/
            , 6];

          case 5:
            e_2 = _a.sent();

            if (popup) {
              // Close the synchronous popup if an error is thrown before the window unload event is registered
              popup.close();
            }

            if (e_2 instanceof AuthError) {
              e_2.setCorrelationId(this.correlationId);
            }

            this.browserStorage.setInteractionInProgress(false);
            this.eventHandler.emitEvent(EventType.LOGOUT_FAILURE, InteractionType.Popup, null, e_2);
            this.eventHandler.emitEvent(EventType.LOGOUT_END, InteractionType.Popup);
            serverTelemetryManager.cacheFailedRequest(e_2);
            throw e_2;

          case 6:
            this.eventHandler.emitEvent(EventType.LOGOUT_END, InteractionType.Popup);
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Opens a popup window with given request Url.
   * @param requestUrl
   */


  PopupClient.prototype.initiateAuthRequest = function (requestUrl, params) {
    // Check that request url is not empty.
    if (!StringUtils.isEmpty(requestUrl)) {
      this.logger.infoPii("Navigate to: " + requestUrl); // Open the popup window to requestUrl.

      return this.openPopup(requestUrl, params);
    } else {
      // Throw error if request URL is empty.
      this.logger.error("Navigate url is empty");
      throw BrowserAuthError.createEmptyNavigationUriError();
    }
  };
  /**
   * Monitors a window until it loads a url with the same origin.
   * @param popupWindow - window that is being monitored
   * @param timeout - timeout for processing hash once popup is redirected back to application
   */


  PopupClient.prototype.monitorPopupForHash = function (popupWindow) {
    var _this = this;

    return new Promise(function (resolve, reject) {
      /*
       * Polling for popups needs to be tick-based,
       * since a non-trivial amount of time can be spent on interaction (which should not count against the timeout).
       */
      var maxTicks = _this.config.system.windowHashTimeout / BrowserConstants.POLL_INTERVAL_MS;
      var ticks = 0;

      _this.logger.verbose("PopupHandler.monitorPopupForHash - polling started");

      var intervalId = setInterval(function () {
        // Window is closed
        if (popupWindow.closed) {
          _this.logger.error("PopupHandler.monitorPopupForHash - window closed");

          _this.cleanPopup();

          clearInterval(intervalId);
          reject(BrowserAuthError.createUserCancelledError());
          return;
        }

        var href = Constants.EMPTY_STRING;
        var hash = Constants.EMPTY_STRING;

        try {
          /*
           * Will throw if cross origin,
           * which should be caught and ignored
           * since we need the interval to keep running while on STS UI.
           */
          href = popupWindow.location.href;
          hash = popupWindow.location.hash;
        } catch (e) {} // Don't process blank pages or cross domain


        if (StringUtils.isEmpty(href) || href === "about:blank") {
          return;
        }

        _this.logger.verbose("PopupHandler.monitorPopupForHash - popup window is on same origin as caller");
        /*
         * Only run clock when we are on same domain for popups
         * as popup operations can take a long time.
         */


        ticks++;

        if (hash) {
          _this.logger.verbose("PopupHandler.monitorPopupForHash - found hash in url");

          clearInterval(intervalId);

          _this.cleanPopup(popupWindow);

          if (UrlString.hashContainsKnownProperties(hash)) {
            _this.logger.verbose("PopupHandler.monitorPopupForHash - hash contains known properties, returning.");

            resolve(hash);
          } else {
            _this.logger.error("PopupHandler.monitorPopupForHash - found hash in url but it does not contain known properties. Check that your router is not changing the hash prematurely.");

            _this.logger.errorPii("PopupHandler.monitorPopupForHash - hash found: " + hash);

            reject(BrowserAuthError.createHashDoesNotContainKnownPropertiesError());
          }
        } else if (ticks > maxTicks) {
          _this.logger.error("PopupHandler.monitorPopupForHash - unable to find hash in url, timing out");

          clearInterval(intervalId);
          reject(BrowserAuthError.createMonitorPopupTimeoutError());
        }
      }, BrowserConstants.POLL_INTERVAL_MS);
    });
  };
  /**
   * Waits for user interaction in logout popup window
   * @param popupWindow
   * @returns
   */


  PopupClient.prototype.waitForLogoutPopup = function (popupWindow) {
    var _this = this;

    return new Promise(function (resolve) {
      _this.logger.verbose("PopupHandler.waitForLogoutPopup - polling started");

      var intervalId = setInterval(function () {
        // Window is closed
        if (popupWindow.closed) {
          _this.logger.error("PopupHandler.waitForLogoutPopup - window closed");

          _this.cleanPopup();

          clearInterval(intervalId);
          resolve();
        }

        var href = Constants.EMPTY_STRING;

        try {
          /*
           * Will throw if cross origin,
           * which should be caught and ignored
           * since we need the interval to keep running while on STS UI.
           */
          href = popupWindow.location.href;
        } catch (e) {} // Don't process blank pages or cross domain


        if (StringUtils.isEmpty(href) || href === "about:blank") {
          return;
        }

        _this.logger.verbose("PopupHandler.waitForLogoutPopup - popup window is on same origin as caller, closing.");

        clearInterval(intervalId);

        _this.cleanPopup(popupWindow);

        resolve();
      }, BrowserConstants.POLL_INTERVAL_MS);
    });
  };
  /**
   * @hidden
   *
   * Configures popup window for login.
   *
   * @param urlNavigate
   * @param title
   * @param popUpWidth
   * @param popUpHeight
   * @param popupWindowAttributes
   * @ignore
   * @hidden
   */


  PopupClient.prototype.openPopup = function (urlNavigate, popupParams) {
    try {
      var popupWindow = void 0; // Popup window passed in, setting url to navigate to

      if (popupParams.popup) {
        popupWindow = popupParams.popup;
        this.logger.verbosePii("Navigating popup window to: " + urlNavigate);
        popupWindow.location.assign(urlNavigate);
      } else if (typeof popupParams.popup === "undefined") {
        // Popup will be undefined if it was not passed in
        this.logger.verbosePii("Opening popup window to: " + urlNavigate);
        popupWindow = this.openSizedPopup(urlNavigate, popupParams.popupName, popupParams.popupWindowAttributes);
      } // Popup will be null if popups are blocked


      if (!popupWindow) {
        throw BrowserAuthError.createEmptyWindowCreatedError();
      }

      if (popupWindow.focus) {
        popupWindow.focus();
      }

      this.currentWindow = popupWindow;
      window.addEventListener("beforeunload", this.unloadWindow);
      return popupWindow;
    } catch (e) {
      this.logger.error("error opening popup " + e.message);
      this.browserStorage.setInteractionInProgress(false);
      throw BrowserAuthError.createPopupWindowError(e.toString());
    }
  };
  /**
   * Helper function to set popup window dimensions and position
   * @param urlNavigate
   * @param popupName
   * @param popupWindowAttributes
   * @returns
   */


  PopupClient.prototype.openSizedPopup = function (urlNavigate, popupName, popupWindowAttributes) {
    var _a, _b, _c, _d;
    /**
     * adding winLeft and winTop to account for dual monitor
     * using screenLeft and screenTop for IE8 and earlier
     */


    var winLeft = window.screenLeft ? window.screenLeft : window.screenX;
    var winTop = window.screenTop ? window.screenTop : window.screenY;
    /**
     * window.innerWidth displays browser window"s height and width excluding toolbars
     * using document.documentElement.clientWidth for IE8 and earlier
     */

    var winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    var winHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    var width = (_a = popupWindowAttributes.popupSize) === null || _a === void 0 ? void 0 : _a.width;
    var height = (_b = popupWindowAttributes.popupSize) === null || _b === void 0 ? void 0 : _b.height;
    var top = (_c = popupWindowAttributes.popupPosition) === null || _c === void 0 ? void 0 : _c.top;
    var left = (_d = popupWindowAttributes.popupPosition) === null || _d === void 0 ? void 0 : _d.left;

    if (!width || width < 0 || width > winWidth) {
      this.logger.verbose("Default popup window width used. Window width not configured or invalid.");
      width = BrowserConstants.POPUP_WIDTH;
    }

    if (!height || height < 0 || height > winHeight) {
      this.logger.verbose("Default popup window height used. Window height not configured or invalid.");
      height = BrowserConstants.POPUP_HEIGHT;
    }

    if (!top || top < 0 || top > winHeight) {
      this.logger.verbose("Default popup window top position used. Window top not configured or invalid.");
      top = Math.max(0, winHeight / 2 - BrowserConstants.POPUP_HEIGHT / 2 + winTop);
    }

    if (!left || left < 0 || left > winWidth) {
      this.logger.verbose("Default popup window left position used. Window left not configured or invalid.");
      left = Math.max(0, winWidth / 2 - BrowserConstants.POPUP_WIDTH / 2 + winLeft);
    }

    return window.open(urlNavigate, popupName, "width=" + width + ", height=" + height + ", top=" + top + ", left=" + left + ", scrollbars=yes");
  };
  /**
   * Event callback to unload main window.
   */


  PopupClient.prototype.unloadWindow = function (e) {
    this.browserStorage.cleanRequestByInteractionType(InteractionType.Popup);

    if (this.currentWindow) {
      this.currentWindow.close();
    } // Guarantees browser unload will happen, so no other errors will be thrown.


    e.preventDefault();
  };
  /**
   * Closes popup, removes any state vars created during popup calls.
   * @param popupWindow
   */


  PopupClient.prototype.cleanPopup = function (popupWindow) {
    if (popupWindow) {
      // Close window.
      popupWindow.close();
    } // Remove window unload function


    window.removeEventListener("beforeunload", this.unloadWindow); // Interaction is completed - remove interaction status.

    this.browserStorage.setInteractionInProgress(false);
  };
  /**
   * Generates the name for the popup based on the client id and request
   * @param clientId
   * @param request
   */


  PopupClient.prototype.generatePopupName = function (scopes, authority) {
    return BrowserConstants.POPUP_NAME_PREFIX + "." + this.config.auth.clientId + "." + scopes.join("-") + "." + authority + "." + this.correlationId;
  };
  /**
   * Generates the name for the popup based on the client id and request for logouts
   * @param clientId
   * @param request
   */


  PopupClient.prototype.generateLogoutPopupName = function (request) {
    var homeAccountId = request.account && request.account.homeAccountId;
    return BrowserConstants.POPUP_NAME_PREFIX + "." + this.config.auth.clientId + "." + homeAccountId + "." + this.correlationId;
  };

  return PopupClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var NavigationClient =
/** @class */
function () {
  function NavigationClient() {}
  /**
   * Navigates to other pages within the same web application
   * @param url
   * @param options
   */


  NavigationClient.prototype.navigateInternal = function (url, options) {
    return NavigationClient.defaultNavigateWindow(url, options);
  };
  /**
   * Navigates to other pages outside the web application i.e. the Identity Provider
   * @param url
   * @param options
   */


  NavigationClient.prototype.navigateExternal = function (url, options) {
    return NavigationClient.defaultNavigateWindow(url, options);
  };
  /**
   * Default navigation implementation invoked by the internal and external functions
   * @param url
   * @param options
   */


  NavigationClient.defaultNavigateWindow = function (url, options) {
    if (options.noHistory) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }

    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(true);
      }, options.timeout);
    });
  };

  return NavigationClient;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
// Default timeout for popup windows and iframes in milliseconds

var DEFAULT_POPUP_TIMEOUT_MS = 60000;
var DEFAULT_IFRAME_TIMEOUT_MS = 6000;
var DEFAULT_REDIRECT_TIMEOUT_MS = 30000;
var DEFAULT_NATIVE_BROKER_HANDSHAKE_TIMEOUT_MS = 2000;
/**
 * MSAL function that sets the default options when not explicitly configured from app developer
 *
 * @param auth
 * @param cache
 * @param system
 *
 * @returns Configuration object
 */

function buildConfiguration(_a, isBrowserEnvironment) {
  var userInputAuth = _a.auth,
      userInputCache = _a.cache,
      userInputSystem = _a.system,
      userInputTelemetry = _a.telemetry; // Default auth options for browser

  var DEFAULT_AUTH_OPTIONS = {
    clientId: Constants.EMPTY_STRING,
    authority: "" + Constants.DEFAULT_AUTHORITY,
    knownAuthorities: [],
    cloudDiscoveryMetadata: Constants.EMPTY_STRING,
    authorityMetadata: Constants.EMPTY_STRING,
    redirectUri: Constants.EMPTY_STRING,
    postLogoutRedirectUri: Constants.EMPTY_STRING,
    navigateToLoginRequestUrl: true,
    clientCapabilities: [],
    protocolMode: ProtocolMode.AAD,
    azureCloudOptions: {
      azureCloudInstance: AzureCloudInstance.None,
      tenant: Constants.EMPTY_STRING
    }
  }; // Default cache options for browser

  var DEFAULT_CACHE_OPTIONS = {
    cacheLocation: BrowserCacheLocation.SessionStorage,
    storeAuthStateInCookie: false,
    secureCookies: false
  }; // Default logger options for browser

  var DEFAULT_LOGGER_OPTIONS = {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    loggerCallback: function () {},
    logLevel: LogLevel.Info,
    piiLoggingEnabled: false
  }; // Default system options for browser

  var DEFAULT_BROWSER_SYSTEM_OPTIONS = __assign$1(__assign$1({}, DEFAULT_SYSTEM_OPTIONS), {
    loggerOptions: DEFAULT_LOGGER_OPTIONS,
    networkClient: isBrowserEnvironment ? BrowserUtils.getBrowserNetworkClient() : StubbedNetworkModule,
    navigationClient: new NavigationClient(),
    loadFrameTimeout: 0,
    // If loadFrameTimeout is provided, use that as default.
    windowHashTimeout: (userInputSystem === null || userInputSystem === void 0 ? void 0 : userInputSystem.loadFrameTimeout) || DEFAULT_POPUP_TIMEOUT_MS,
    iframeHashTimeout: (userInputSystem === null || userInputSystem === void 0 ? void 0 : userInputSystem.loadFrameTimeout) || DEFAULT_IFRAME_TIMEOUT_MS,
    navigateFrameWait: isBrowserEnvironment && BrowserUtils.detectIEOrEdge() ? 500 : 0,
    redirectNavigationTimeout: DEFAULT_REDIRECT_TIMEOUT_MS,
    asyncPopups: false,
    allowRedirectInIframe: false,
    allowNativeBroker: false,
    nativeBrokerHandshakeTimeout: (userInputSystem === null || userInputSystem === void 0 ? void 0 : userInputSystem.nativeBrokerHandshakeTimeout) || DEFAULT_NATIVE_BROKER_HANDSHAKE_TIMEOUT_MS
  });

  var DEFAULT_TELEMETRY_OPTIONS = {
    application: {
      appName: Constants.EMPTY_STRING,
      appVersion: Constants.EMPTY_STRING
    }
  };
  var overlayedConfig = {
    auth: __assign$1(__assign$1({}, DEFAULT_AUTH_OPTIONS), userInputAuth),
    cache: __assign$1(__assign$1({}, DEFAULT_CACHE_OPTIONS), userInputCache),
    system: __assign$1(__assign$1({}, DEFAULT_BROWSER_SYSTEM_OPTIONS), userInputSystem),
    telemetry: __assign$1(__assign$1({}, DEFAULT_TELEMETRY_OPTIONS), userInputTelemetry)
  };
  return overlayedConfig;
}

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentHandler =
/** @class */
function (_super) {
  __extends$1(SilentHandler, _super);

  function SilentHandler(authCodeModule, storageImpl, authCodeRequest, logger, navigateFrameWait) {
    var _this = _super.call(this, authCodeModule, storageImpl, authCodeRequest, logger) || this;

    _this.navigateFrameWait = navigateFrameWait;
    return _this;
  }
  /**
   * Creates a hidden iframe to given URL using user-requested scopes as an id.
   * @param urlNavigate
   * @param userRequestScopes
   */


  SilentHandler.prototype.initiateAuthRequest = function (requestUrl) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _a;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            if (StringUtils.isEmpty(requestUrl)) {
              // Throw error if request URL is empty.
              this.logger.info("Navigate url is empty");
              throw BrowserAuthError.createEmptyNavigationUriError();
            }

            if (!this.navigateFrameWait) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , this.loadFrame(requestUrl)];

          case 1:
            _a = _b.sent();
            return [3
            /*break*/
            , 3];

          case 2:
            _a = this.loadFrameSync(requestUrl);
            _b.label = 3;

          case 3:
            return [2
            /*return*/
            , _a];
        }
      });
    });
  };
  /**
   * Monitors an iframe content window until it loads a url with a known hash, or hits a specified timeout.
   * @param iframe
   * @param timeout
   */


  SilentHandler.prototype.monitorIframeForHash = function (iframe, timeout) {
    var _this = this;

    return new Promise(function (resolve, reject) {
      if (timeout < DEFAULT_IFRAME_TIMEOUT_MS) {
        _this.logger.warning("system.loadFrameTimeout or system.iframeHashTimeout set to lower (" + timeout + "ms) than the default (" + DEFAULT_IFRAME_TIMEOUT_MS + "ms). This may result in timeouts.");
      }
      /*
       * Polling for iframes can be purely timing based,
       * since we don't need to account for interaction.
       */


      var nowMark = window.performance.now();
      var timeoutMark = nowMark + timeout;
      var intervalId = setInterval(function () {
        if (window.performance.now() > timeoutMark) {
          _this.removeHiddenIframe(iframe);

          clearInterval(intervalId);
          reject(BrowserAuthError.createMonitorIframeTimeoutError());
          return;
        }

        var href = Constants.EMPTY_STRING;
        var contentWindow = iframe.contentWindow;

        try {
          /*
           * Will throw if cross origin,
           * which should be caught and ignored
           * since we need the interval to keep running while on STS UI.
           */
          href = contentWindow ? contentWindow.location.href : Constants.EMPTY_STRING;
        } catch (e) {}

        if (StringUtils.isEmpty(href)) {
          return;
        }

        var contentHash = contentWindow ? contentWindow.location.hash : Constants.EMPTY_STRING;

        if (UrlString.hashContainsKnownProperties(contentHash)) {
          // Success case
          _this.removeHiddenIframe(iframe);

          clearInterval(intervalId);
          resolve(contentHash);
          return;
        }
      }, BrowserConstants.POLL_INTERVAL_MS);
    });
  };
  /**
   * @hidden
   * Loads iframe with authorization endpoint URL
   * @ignore
   */


  SilentHandler.prototype.loadFrame = function (urlNavigate) {
    /*
     * This trick overcomes iframe navigation in IE
     * IE does not load the page consistently in iframe
     */
    var _this = this;

    return new Promise(function (resolve, reject) {
      var frameHandle = _this.createHiddenIframe();

      setTimeout(function () {
        if (!frameHandle) {
          reject("Unable to load iframe");
          return;
        }

        frameHandle.src = urlNavigate;
        resolve(frameHandle);
      }, _this.navigateFrameWait);
    });
  };
  /**
   * @hidden
   * Loads the iframe synchronously when the navigateTimeFrame is set to `0`
   * @param urlNavigate
   * @param frameName
   * @param logger
   */


  SilentHandler.prototype.loadFrameSync = function (urlNavigate) {
    var frameHandle = this.createHiddenIframe();
    frameHandle.src = urlNavigate;
    return frameHandle;
  };
  /**
   * @hidden
   * Creates a new hidden iframe or gets an existing one for silent token renewal.
   * @ignore
   */


  SilentHandler.prototype.createHiddenIframe = function () {
    var authFrame = document.createElement("iframe");
    authFrame.style.visibility = "hidden";
    authFrame.style.position = "absolute";
    authFrame.style.width = authFrame.style.height = "0";
    authFrame.style.border = "0";
    authFrame.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms");
    document.getElementsByTagName("body")[0].appendChild(authFrame);
    return authFrame;
  };
  /**
   * @hidden
   * Removes a hidden iframe from the page.
   * @ignore
   */


  SilentHandler.prototype.removeHiddenIframe = function (iframe) {
    if (document.body === iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  return SilentHandler;
}(InteractionHandler);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentIframeClient =
/** @class */
function (_super) {
  __extends$1(SilentIframeClient, _super);

  function SilentIframeClient(config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, apiId, performanceClient, nativeMessageHandler, correlationId) {
    var _this = _super.call(this, config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, performanceClient, nativeMessageHandler, correlationId) || this;

    _this.apiId = apiId;
    return _this;
  }
  /**
   * Acquires a token silently by opening a hidden iframe to the /authorize endpoint with prompt=none
   * @param request
   */


  SilentIframeClient.prototype.acquireToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var acquireTokenMeasurement, silentRequest, serverTelemetryManager, authClient, e_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.verbose("acquireTokenByIframe called");
            acquireTokenMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.SilentIframeClientAcquireToken, request.correlationId); // Check that we have some SSO data

            if (StringUtils.isEmpty(request.loginHint) && StringUtils.isEmpty(request.sid) && (!request.account || StringUtils.isEmpty(request.account.username))) {
              this.logger.warning("No user hint provided. The authorization server may need more information to complete this request.");
            } // Check that prompt is set to none, throw error if it is set to anything else.


            if (request.prompt && request.prompt !== PromptValue.NONE) {
              acquireTokenMeasurement.endMeasurement({
                success: false
              });
              throw BrowserAuthError.createSilentPromptValueError(request.prompt);
            }

            return [4
            /*yield*/
            , this.initializeAuthorizationRequest(__assign$1(__assign$1({}, request), {
              prompt: PromptValue.NONE
            }), InteractionType.Silent)];

          case 1:
            silentRequest = _a.sent();
            this.browserStorage.updateCacheEntries(silentRequest.state, silentRequest.nonce, silentRequest.authority, silentRequest.loginHint || Constants.EMPTY_STRING, silentRequest.account || null);
            serverTelemetryManager = this.initializeServerTelemetryManager(this.apiId);
            _a.label = 2;

          case 2:
            _a.trys.push([2, 5,, 6]);

            return [4
            /*yield*/
            , this.createAuthCodeClient(serverTelemetryManager, silentRequest.authority, silentRequest.azureCloudOptions)];

          case 3:
            authClient = _a.sent();
            this.logger.verbose("Auth code client created");
            return [4
            /*yield*/
            , this.silentTokenHelper(authClient, silentRequest).then(function (result) {
              acquireTokenMeasurement.endMeasurement({
                success: true,
                fromCache: false
              });
              return result;
            })];

          case 4:
            return [2
            /*return*/
            , _a.sent()];

          case 5:
            e_1 = _a.sent();

            if (e_1 instanceof AuthError) {
              e_1.setCorrelationId(this.correlationId);
            }

            serverTelemetryManager.cacheFailedRequest(e_1);
            this.browserStorage.cleanRequestByState(silentRequest.state);
            acquireTokenMeasurement.endMeasurement({
              errorCode: e_1 instanceof AuthError && e_1.errorCode || undefined,
              subErrorCode: e_1 instanceof AuthError && e_1.subError || undefined,
              success: false
            });
            throw e_1;

          case 6:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Currently Unsupported
   */


  SilentIframeClient.prototype.logout = function () {
    // Synchronous so we must reject
    return Promise.reject(BrowserAuthError.createSilentLogoutUnsupportedError());
  };
  /**
   * Helper which acquires an authorization code silently using a hidden iframe from given url
   * using the scopes requested as part of the id, and exchanges the code for a set of OAuth tokens.
   * @param navigateUrl
   * @param userRequestScopes
   */


  SilentIframeClient.prototype.silentTokenHelper = function (authClient, silentRequest) {
    return __awaiter$1(this, void 0, void 0, function () {
      var authCodeRequest, navigateUrl, silentHandler, msalFrame, hash, serverParams, state, nativeInteractionClient, userRequestState;

      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.initializeAuthorizationCodeRequest(silentRequest)];

          case 1:
            authCodeRequest = _a.sent();
            return [4
            /*yield*/
            , authClient.getAuthCodeUrl(__assign$1(__assign$1({}, silentRequest), {
              nativeBroker: NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeMessageHandler, silentRequest.authenticationScheme)
            }))];

          case 2:
            navigateUrl = _a.sent();
            silentHandler = new SilentHandler(authClient, this.browserStorage, authCodeRequest, this.logger, this.config.system.navigateFrameWait);
            return [4
            /*yield*/
            , silentHandler.initiateAuthRequest(navigateUrl)];

          case 3:
            msalFrame = _a.sent();
            return [4
            /*yield*/
            , silentHandler.monitorIframeForHash(msalFrame, this.config.system.iframeHashTimeout)];

          case 4:
            hash = _a.sent();
            serverParams = UrlString.getDeserializedHash(hash);
            state = this.validateAndExtractStateFromHash(serverParams, InteractionType.Silent, authCodeRequest.correlationId);

            if (serverParams.accountId) {
              this.logger.verbose("Account id found in hash, calling WAM for token");

              if (!this.nativeMessageHandler) {
                throw BrowserAuthError.createNativeConnectionNotEstablishedError();
              }

              nativeInteractionClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, this.apiId, this.performanceClient, this.nativeMessageHandler, serverParams.accountId, this.correlationId);
              userRequestState = ProtocolUtils.parseRequestState(this.browserCrypto, state).userRequestState;
              return [2
              /*return*/
              , nativeInteractionClient.acquireToken(__assign$1(__assign$1({}, silentRequest), {
                state: userRequestState,
                prompt: PromptValue.NONE
              })).finally(function () {
                _this.browserStorage.cleanRequestByState(state);
              })];
            } // Handle response from hash string


            return [2
            /*return*/
            , silentHandler.handleCodeResponseFromHash(hash, state, authClient.authority, this.networkClient)];
        }
      });
    });
  };

  return SilentIframeClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentCacheClient =
/** @class */
function (_super) {
  __extends$1(SilentCacheClient, _super);

  function SilentCacheClient() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Returns unexpired tokens from the cache, if available
   * @param silentRequest
   */


  SilentCacheClient.prototype.acquireToken = function (silentRequest) {
    return __awaiter$1(this, void 0, void 0, function () {
      var acquireTokenMeasurement, serverTelemetryManager, silentAuthClient, cachedToken, error_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            acquireTokenMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.SilentCacheClientAcquireToken, silentRequest.correlationId);
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.acquireTokenSilent_silentFlow);
            return [4
            /*yield*/
            , this.createSilentFlowClient(serverTelemetryManager, silentRequest.authority, silentRequest.azureCloudOptions)];

          case 1:
            silentAuthClient = _a.sent();
            this.logger.verbose("Silent auth client created");
            _a.label = 2;

          case 2:
            _a.trys.push([2, 4,, 5]);

            return [4
            /*yield*/
            , silentAuthClient.acquireCachedToken(silentRequest)];

          case 3:
            cachedToken = _a.sent();
            acquireTokenMeasurement.endMeasurement({
              success: true,
              fromCache: true
            });
            return [2
            /*return*/
            , cachedToken];

          case 4:
            error_1 = _a.sent();

            if (error_1 instanceof BrowserAuthError && error_1.errorCode === BrowserAuthErrorMessage.signingKeyNotFoundInStorage.code) {
              this.logger.verbose("Signing keypair for bound access token not found. Refreshing bound access token and generating a new crypto keypair.");
            }

            acquireTokenMeasurement.endMeasurement({
              errorCode: error_1 instanceof AuthError && error_1.errorCode || undefined,
              subErrorCode: error_1 instanceof AuthError && error_1.subError || undefined,
              success: false
            });
            throw error_1;

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Currently Unsupported
   */


  SilentCacheClient.prototype.logout = function () {
    // Synchronous so we must reject
    return Promise.reject(BrowserAuthError.createSilentLogoutUnsupportedError());
  };
  /**
   * Creates an Silent Flow Client with the given authority, or the default authority.
   * @param serverTelemetryManager
   * @param authorityUrl
   */


  SilentCacheClient.prototype.createSilentFlowClient = function (serverTelemetryManager, authorityUrl, azureCloudOptions) {
    return __awaiter$1(this, void 0, void 0, function () {
      var clientConfig;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.getClientConfiguration(serverTelemetryManager, authorityUrl, azureCloudOptions)];

          case 1:
            clientConfig = _a.sent();
            return [2
            /*return*/
            , new SilentFlowClient(clientConfig)];
        }
      });
    });
  };

  SilentCacheClient.prototype.initializeSilentRequest = function (request, account) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _a;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = [__assign$1({}, request)];
            return [4
            /*yield*/
            , this.initializeBaseRequest(request)];

          case 1:
            return [2
            /*return*/
            , __assign$1.apply(void 0, [__assign$1.apply(void 0, _a.concat([_b.sent()])), {
              account: account,
              forceRefresh: request.forceRefresh || false
            }])];
        }
      });
    });
  };

  return SilentCacheClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentRefreshClient =
/** @class */
function (_super) {
  __extends$1(SilentRefreshClient, _super);

  function SilentRefreshClient() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /**
   * Exchanges the refresh token for new tokens
   * @param request
   */


  SilentRefreshClient.prototype.acquireToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var silentRequest, _a, acquireTokenMeasurement, serverTelemetryManager, refreshTokenClient;

      var _this = this;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = [__assign$1({}, request)];
            return [4
            /*yield*/
            , this.initializeBaseRequest(request)];

          case 1:
            silentRequest = __assign$1.apply(void 0, _a.concat([_b.sent()]));
            acquireTokenMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.SilentRefreshClientAcquireToken, silentRequest.correlationId);
            serverTelemetryManager = this.initializeServerTelemetryManager(ApiId.acquireTokenSilent_silentFlow);
            return [4
            /*yield*/
            , this.createRefreshTokenClient(serverTelemetryManager, silentRequest.authority, silentRequest.azureCloudOptions)];

          case 2:
            refreshTokenClient = _b.sent();
            this.logger.verbose("Refresh token client created"); // Send request to renew token. Auth module will throw errors if token cannot be renewed.

            return [2
            /*return*/
            , refreshTokenClient.acquireTokenByRefreshToken(silentRequest).then(function (result) {
              acquireTokenMeasurement.endMeasurement({
                success: true,
                fromCache: result.fromCache
              });
              return result;
            }).catch(function (e) {
              if (e instanceof AuthError) {
                e.setCorrelationId(_this.correlationId);
              }

              serverTelemetryManager.cacheFailedRequest(e);
              acquireTokenMeasurement.endMeasurement({
                errorCode: e.errorCode,
                subErrorCode: e.subError,
                success: false
              });
              throw e;
            })];
        }
      });
    });
  };
  /**
   * Currently Unsupported
   */


  SilentRefreshClient.prototype.logout = function () {
    // Synchronous so we must reject
    return Promise.reject(BrowserAuthError.createSilentLogoutUnsupportedError());
  };
  /**
   * Creates a Refresh Client with the given authority, or the default authority.
   * @param serverTelemetryManager
   * @param authorityUrl
   */


  SilentRefreshClient.prototype.createRefreshTokenClient = function (serverTelemetryManager, authorityUrl, azureCloudOptions) {
    return __awaiter$1(this, void 0, void 0, function () {
      var clientConfig;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.getClientConfiguration(serverTelemetryManager, authorityUrl, azureCloudOptions)];

          case 1:
            clientConfig = _a.sent();
            return [2
            /*return*/
            , new RefreshTokenClient(clientConfig)];
        }
      });
    });
  };

  return SilentRefreshClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var EventHandler =
/** @class */
function () {
  function EventHandler(logger, browserCrypto) {
    this.eventCallbacks = new Map();
    this.logger = logger;
    this.browserCrypto = browserCrypto;
    this.listeningToStorageEvents = false;
    this.handleAccountCacheChange = this.handleAccountCacheChange.bind(this);
  }
  /**
   * Adds event callbacks to array
   * @param callback
   */


  EventHandler.prototype.addEventCallback = function (callback) {
    if (typeof window !== "undefined") {
      var callbackId = this.browserCrypto.createNewGuid();
      this.eventCallbacks.set(callbackId, callback);
      this.logger.verbose("Event callback registered with id: " + callbackId);
      return callbackId;
    }

    return null;
  };
  /**
   * Removes callback with provided id from callback array
   * @param callbackId
   */


  EventHandler.prototype.removeEventCallback = function (callbackId) {
    this.eventCallbacks.delete(callbackId);
    this.logger.verbose("Event callback " + callbackId + " removed.");
  };
  /**
   * Adds event listener that emits an event when a user account is added or removed from localstorage in a different browser tab or window
   */


  EventHandler.prototype.enableAccountStorageEvents = function () {
    if (typeof window === "undefined") {
      return;
    }

    if (!this.listeningToStorageEvents) {
      this.logger.verbose("Adding account storage listener.");
      this.listeningToStorageEvents = true;
      window.addEventListener("storage", this.handleAccountCacheChange);
    } else {
      this.logger.verbose("Account storage listener already registered.");
    }
  };
  /**
   * Removes event listener that emits an event when a user account is added or removed from localstorage in a different browser tab or window
   */


  EventHandler.prototype.disableAccountStorageEvents = function () {
    if (typeof window === "undefined") {
      return;
    }

    if (this.listeningToStorageEvents) {
      this.logger.verbose("Removing account storage listener.");
      window.removeEventListener("storage", this.handleAccountCacheChange);
      this.listeningToStorageEvents = false;
    } else {
      this.logger.verbose("No account storage listener registered.");
    }
  };
  /**
   * Emits events by calling callback with event message
   * @param eventType
   * @param interactionType
   * @param payload
   * @param error
   */


  EventHandler.prototype.emitEvent = function (eventType, interactionType, payload, error) {
    var _this = this;

    if (typeof window !== "undefined") {
      var message_1 = {
        eventType: eventType,
        interactionType: interactionType || null,
        payload: payload || null,
        error: error || null,
        timestamp: Date.now()
      };
      this.logger.info("Emitting event: " + eventType);
      this.eventCallbacks.forEach(function (callback, callbackId) {
        _this.logger.verbose("Emitting event to callback " + callbackId + ": " + eventType);

        callback.apply(null, [message_1]);
      });
    }
  };
  /**
   * Emit account added/removed events when cached accounts are changed in a different tab or frame
   */


  EventHandler.prototype.handleAccountCacheChange = function (e) {
    try {
      var cacheValue = e.newValue || e.oldValue;

      if (!cacheValue) {
        return;
      }

      var parsedValue = JSON.parse(cacheValue);

      if (typeof parsedValue !== "object" || !AccountEntity.isAccountEntity(parsedValue)) {
        return;
      }

      var accountEntity = CacheManager.toObject(new AccountEntity(), parsedValue);
      var accountInfo = accountEntity.getAccountInfo();

      if (!e.oldValue && e.newValue) {
        this.logger.info("Account was added to cache in a different window");
        this.emitEvent(EventType.ACCOUNT_ADDED, undefined, accountInfo);
      } else if (!e.newValue && e.oldValue) {
        this.logger.info("Account was removed from cache in a different window");
        this.emitEvent(EventType.ACCOUNT_REMOVED, undefined, accountInfo);
      }
    } catch (e) {
      return;
    }
  };

  return EventHandler;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Utility class for math specific functions in browser.
 */

var MathUtils =
/** @class */
function () {
  function MathUtils() {}
  /**
   * Decimal to Hex
   *
   * @param num
   */


  MathUtils.decimalToHex = function (num) {
    var hex = num.toString(16);

    while (hex.length < 2) {
      hex = "0" + hex;
    }

    return hex;
  };

  return MathUtils;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var GuidGenerator =
/** @class */
function () {
  function GuidGenerator(cryptoObj) {
    this.cryptoObj = cryptoObj;
  }
  /*
   * RFC4122: The version 4 UUID is meant for generating UUIDs from truly-random or
   * pseudo-random numbers.
   * The algorithm is as follows:
   *     Set the two most significant bits (bits 6 and 7) of the
   *        clock_seq_hi_and_reserved to zero and one, respectively.
   *     Set the four most significant bits (bits 12 through 15) of the
   *        time_hi_and_version field to the 4-bit version number from
   *        Section 4.1.3. Version4
   *     Set all the other bits to randomly (or pseudo-randomly) chosen
   *     values.
   * UUID                   = time-low "-" time-mid "-"time-high-and-version "-"clock-seq-reserved and low(2hexOctet)"-" node
   * time-low               = 4hexOctet
   * time-mid               = 2hexOctet
   * time-high-and-version  = 2hexOctet
   * clock-seq-and-reserved = hexOctet:
   * clock-seq-low          = hexOctet
   * node                   = 6hexOctet
   * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   * y could be 1000, 1001, 1010, 1011 since most significant two bits needs to be 10
   * y values are 8, 9, A, B
   */


  GuidGenerator.prototype.generateGuid = function () {
    try {
      var buffer = new Uint8Array(16);
      this.cryptoObj.getRandomValues(buffer); // buffer[6] and buffer[7] represents the time_hi_and_version field. We will set the four most significant bits (4 through 7) of buffer[6] to represent decimal number 4 (UUID version number).

      buffer[6] |= 0x40; // buffer[6] | 01000000 will set the 6 bit to 1.

      buffer[6] &= 0x4f; // buffer[6] & 01001111 will set the 4, 5, and 7 bit to 0 such that bits 4-7 == 0100 = "4".
      // buffer[8] represents the clock_seq_hi_and_reserved field. We will set the two most significant bits (6 and 7) of the clock_seq_hi_and_reserved to zero and one, respectively.

      buffer[8] |= 0x80; // buffer[8] | 10000000 will set the 7 bit to 1.

      buffer[8] &= 0xbf; // buffer[8] & 10111111 will set the 6 bit to 0.

      return MathUtils.decimalToHex(buffer[0]) + MathUtils.decimalToHex(buffer[1]) + MathUtils.decimalToHex(buffer[2]) + MathUtils.decimalToHex(buffer[3]) + "-" + MathUtils.decimalToHex(buffer[4]) + MathUtils.decimalToHex(buffer[5]) + "-" + MathUtils.decimalToHex(buffer[6]) + MathUtils.decimalToHex(buffer[7]) + "-" + MathUtils.decimalToHex(buffer[8]) + MathUtils.decimalToHex(buffer[9]) + "-" + MathUtils.decimalToHex(buffer[10]) + MathUtils.decimalToHex(buffer[11]) + MathUtils.decimalToHex(buffer[12]) + MathUtils.decimalToHex(buffer[13]) + MathUtils.decimalToHex(buffer[14]) + MathUtils.decimalToHex(buffer[15]);
    } catch (err) {
      var guidHolder = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
      var hex = "0123456789abcdef";
      var r = 0;
      var guidResponse = Constants.EMPTY_STRING;

      for (var i = 0; i < 36; i++) {
        if (guidHolder[i] !== "-" && guidHolder[i] !== "4") {
          // each x and y needs to be random
          r = Math.random() * 16 | 0;
        }

        if (guidHolder[i] === "x") {
          guidResponse += hex[r];
        } else if (guidHolder[i] === "y") {
          // clock-seq-and-reserved first hex is filtered and remaining hex values are random
          r &= 0x3; // bit and with 0011 to set pos 2 to zero ?0??

          r |= 0x8; // set pos 3 to 1 as 1???

          guidResponse += hex[r];
        } else {
          guidResponse += guidHolder[i];
        }
      }

      return guidResponse;
    }
  };
  /**
   * verifies if a string is  GUID
   * @param guid
   */


  GuidGenerator.prototype.isGuid = function (guid) {
    var regexGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return regexGuid.test(guid);
  };

  return GuidGenerator;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Utility functions for strings in a browser. See here for implementation details:
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_2_%E2%80%93_JavaScript's_UTF-16_%3E_UTF-8_%3E_base64
 */

var BrowserStringUtils =
/** @class */
function () {
  function BrowserStringUtils() {}
  /**
   * Converts string to Uint8Array
   * @param sDOMStr
   */


  BrowserStringUtils.stringToUtf8Arr = function (sDOMStr) {
    var nChr;
    var nArrLen = 0;
    var nStrLen = sDOMStr.length;
    /* mapping... */

    for (var nMapIdx = 0; nMapIdx < nStrLen; nMapIdx++) {
      nChr = sDOMStr.charCodeAt(nMapIdx);
      nArrLen += nChr < 0x80 ? 1 : nChr < 0x800 ? 2 : nChr < 0x10000 ? 3 : nChr < 0x200000 ? 4 : nChr < 0x4000000 ? 5 : 6;
    }

    var aBytes = new Uint8Array(nArrLen);
    /* transcription... */

    for (var nIdx = 0, nChrIdx = 0; nIdx < nArrLen; nChrIdx++) {
      nChr = sDOMStr.charCodeAt(nChrIdx);

      if (nChr < 128) {
        /* one byte */
        aBytes[nIdx++] = nChr;
      } else if (nChr < 0x800) {
        /* two bytes */
        aBytes[nIdx++] = 192 + (nChr >>> 6);
        aBytes[nIdx++] = 128 + (nChr & 63);
      } else if (nChr < 0x10000) {
        /* three bytes */
        aBytes[nIdx++] = 224 + (nChr >>> 12);
        aBytes[nIdx++] = 128 + (nChr >>> 6 & 63);
        aBytes[nIdx++] = 128 + (nChr & 63);
      } else if (nChr < 0x200000) {
        /* four bytes */
        aBytes[nIdx++] = 240 + (nChr >>> 18);
        aBytes[nIdx++] = 128 + (nChr >>> 12 & 63);
        aBytes[nIdx++] = 128 + (nChr >>> 6 & 63);
        aBytes[nIdx++] = 128 + (nChr & 63);
      } else if (nChr < 0x4000000) {
        /* five bytes */
        aBytes[nIdx++] = 248 + (nChr >>> 24);
        aBytes[nIdx++] = 128 + (nChr >>> 18 & 63);
        aBytes[nIdx++] = 128 + (nChr >>> 12 & 63);
        aBytes[nIdx++] = 128 + (nChr >>> 6 & 63);
        aBytes[nIdx++] = 128 + (nChr & 63);
      } else
        /* if (nChr <= 0x7fffffff) */
        {
          /* six bytes */
          aBytes[nIdx++] = 252 + (nChr >>> 30);
          aBytes[nIdx++] = 128 + (nChr >>> 24 & 63);
          aBytes[nIdx++] = 128 + (nChr >>> 18 & 63);
          aBytes[nIdx++] = 128 + (nChr >>> 12 & 63);
          aBytes[nIdx++] = 128 + (nChr >>> 6 & 63);
          aBytes[nIdx++] = 128 + (nChr & 63);
        }
    }

    return aBytes;
  };
  /**
   * Converst string to ArrayBuffer
   * @param dataString
   */


  BrowserStringUtils.stringToArrayBuffer = function (dataString) {
    var data = new ArrayBuffer(dataString.length);
    var dataView = new Uint8Array(data);

    for (var i = 0; i < dataString.length; i++) {
      dataView[i] = dataString.charCodeAt(i);
    }

    return data;
  };
  /**
   * Converts Uint8Array to a string
   * @param aBytes
   */


  BrowserStringUtils.utf8ArrToString = function (aBytes) {
    var sView = Constants.EMPTY_STRING;

    for (var nPart = void 0, nLen = aBytes.length, nIdx = 0; nIdx < nLen; nIdx++) {
      nPart = aBytes[nIdx];
      sView += String.fromCharCode(nPart > 251 && nPart < 254 && nIdx + 5 < nLen ?
      /* six bytes */

      /* (nPart - 252 << 30) may be not so safe in ECMAScript! So...: */
      (nPart - 252) * 1073741824 + (aBytes[++nIdx] - 128 << 24) + (aBytes[++nIdx] - 128 << 18) + (aBytes[++nIdx] - 128 << 12) + (aBytes[++nIdx] - 128 << 6) + aBytes[++nIdx] - 128 : nPart > 247 && nPart < 252 && nIdx + 4 < nLen ?
      /* five bytes */
      (nPart - 248 << 24) + (aBytes[++nIdx] - 128 << 18) + (aBytes[++nIdx] - 128 << 12) + (aBytes[++nIdx] - 128 << 6) + aBytes[++nIdx] - 128 : nPart > 239 && nPart < 248 && nIdx + 3 < nLen ?
      /* four bytes */
      (nPart - 240 << 18) + (aBytes[++nIdx] - 128 << 12) + (aBytes[++nIdx] - 128 << 6) + aBytes[++nIdx] - 128 : nPart > 223 && nPart < 240 && nIdx + 2 < nLen ?
      /* three bytes */
      (nPart - 224 << 12) + (aBytes[++nIdx] - 128 << 6) + aBytes[++nIdx] - 128 : nPart > 191 && nPart < 224 && nIdx + 1 < nLen ?
      /* two bytes */
      (nPart - 192 << 6) + aBytes[++nIdx] - 128 :
      /* nPart < 127 ? */

      /* one byte */
      nPart);
    }

    return sView;
  };

  return BrowserStringUtils;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Class which exposes APIs to encode plaintext to base64 encoded string. See here for implementation details:
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_2_%E2%80%93_JavaScript's_UTF-16_%3E_UTF-8_%3E_base64
 */

var Base64Encode =
/** @class */
function () {
  function Base64Encode() {}
  /**
   * Returns URL Safe b64 encoded string from a plaintext string.
   * @param input
   */


  Base64Encode.prototype.urlEncode = function (input) {
    return encodeURIComponent(this.encode(input).replace(/=/g, Constants.EMPTY_STRING).replace(/\+/g, "-").replace(/\//g, "_"));
  };
  /**
   * Returns URL Safe b64 encoded string from an int8Array.
   * @param inputArr
   */


  Base64Encode.prototype.urlEncodeArr = function (inputArr) {
    return this.base64EncArr(inputArr).replace(/=/g, Constants.EMPTY_STRING).replace(/\+/g, "-").replace(/\//g, "_");
  };
  /**
   * Returns b64 encoded string from plaintext string.
   * @param input
   */


  Base64Encode.prototype.encode = function (input) {
    var inputUtf8Arr = BrowserStringUtils.stringToUtf8Arr(input);
    return this.base64EncArr(inputUtf8Arr);
  };
  /**
   * Base64 encode byte array
   * @param aBytes
   */


  Base64Encode.prototype.base64EncArr = function (aBytes) {
    var eqLen = (3 - aBytes.length % 3) % 3;
    var sB64Enc = Constants.EMPTY_STRING;

    for (var nMod3 = void 0, nLen = aBytes.length, nUint24 = 0, nIdx = 0; nIdx < nLen; nIdx++) {
      nMod3 = nIdx % 3;
      /* Uncomment the following line in order to split the output in lines 76-character long: */

      /*
       *if (nIdx > 0 && (nIdx * 4 / 3) % 76 === 0) { sB64Enc += "\r\n"; }
       */

      nUint24 |= aBytes[nIdx] << (16 >>> nMod3 & 24);

      if (nMod3 === 2 || aBytes.length - nIdx === 1) {
        sB64Enc += String.fromCharCode(this.uint6ToB64(nUint24 >>> 18 & 63), this.uint6ToB64(nUint24 >>> 12 & 63), this.uint6ToB64(nUint24 >>> 6 & 63), this.uint6ToB64(nUint24 & 63));
        nUint24 = 0;
      }
    }

    return eqLen === 0 ? sB64Enc : sB64Enc.substring(0, sB64Enc.length - eqLen) + (eqLen === 1 ? "=" : "==");
  };
  /**
   * Base64 string to array encoding helper
   * @param nUint6
   */


  Base64Encode.prototype.uint6ToB64 = function (nUint6) {
    return nUint6 < 26 ? nUint6 + 65 : nUint6 < 52 ? nUint6 + 71 : nUint6 < 62 ? nUint6 - 4 : nUint6 === 62 ? 43 : nUint6 === 63 ? 47 : 65;
  };

  return Base64Encode;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Class which exposes APIs to decode base64 strings to plaintext. See here for implementation details:
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_2_%E2%80%93_JavaScript's_UTF-16_%3E_UTF-8_%3E_base64
 */

var Base64Decode =
/** @class */
function () {
  function Base64Decode() {}
  /**
   * Returns a URL-safe plaintext decoded string from b64 encoded input.
   * @param input
   */


  Base64Decode.prototype.decode = function (input) {
    var encodedString = input.replace(/-/g, "+").replace(/_/g, "/");

    switch (encodedString.length % 4) {
      case 0:
        break;

      case 2:
        encodedString += "==";
        break;

      case 3:
        encodedString += "=";
        break;

      default:
        throw new Error("Invalid base64 string");
    }

    var inputUtf8Arr = this.base64DecToArr(encodedString);
    return BrowserStringUtils.utf8ArrToString(inputUtf8Arr);
  };
  /**
   * Decodes base64 into Uint8Array
   * @param base64String
   * @param nBlockSize
   */


  Base64Decode.prototype.base64DecToArr = function (base64String, nBlockSize) {
    var sB64Enc = base64String.replace(/[^A-Za-z0-9\+\/]/g, Constants.EMPTY_STRING);
    var nInLen = sB64Enc.length;
    var nOutLen = nBlockSize ? Math.ceil((nInLen * 3 + 1 >>> 2) / nBlockSize) * nBlockSize : nInLen * 3 + 1 >>> 2;
    var aBytes = new Uint8Array(nOutLen);

    for (var nMod3 = void 0, nMod4 = void 0, nUint24 = 0, nOutIdx = 0, nInIdx = 0; nInIdx < nInLen; nInIdx++) {
      nMod4 = nInIdx & 3;
      nUint24 |= this.b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << 18 - 6 * nMod4;

      if (nMod4 === 3 || nInLen - nInIdx === 1) {
        for (nMod3 = 0; nMod3 < 3 && nOutIdx < nOutLen; nMod3++, nOutIdx++) {
          aBytes[nOutIdx] = nUint24 >>> (16 >>> nMod3 & 24) & 255;
        }

        nUint24 = 0;
      }
    }

    return aBytes;
  };
  /**
   * Base64 string to array decoding helper
   * @param charNum
   */


  Base64Decode.prototype.b64ToUint6 = function (charNum) {
    return charNum > 64 && charNum < 91 ? charNum - 65 : charNum > 96 && charNum < 123 ? charNum - 71 : charNum > 47 && charNum < 58 ? charNum + 4 : charNum === 43 ? 62 : charNum === 47 ? 63 : 0;
  };

  return Base64Decode;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
// Constant byte array length

var RANDOM_BYTE_ARR_LENGTH = 32;
/**
 * Class which exposes APIs to generate PKCE codes and code verifiers.
 */

var PkceGenerator =
/** @class */
function () {
  function PkceGenerator(cryptoObj) {
    this.base64Encode = new Base64Encode();
    this.cryptoObj = cryptoObj;
  }
  /**
   * Generates PKCE Codes. See the RFC for more information: https://tools.ietf.org/html/rfc7636
   */


  PkceGenerator.prototype.generateCodes = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var codeVerifier, codeChallenge;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            codeVerifier = this.generateCodeVerifier();
            return [4
            /*yield*/
            , this.generateCodeChallengeFromVerifier(codeVerifier)];

          case 1:
            codeChallenge = _a.sent();
            return [2
            /*return*/
            , {
              verifier: codeVerifier,
              challenge: codeChallenge
            }];
        }
      });
    });
  };
  /**
   * Generates a random 32 byte buffer and returns the base64
   * encoded string to be used as a PKCE Code Verifier
   */


  PkceGenerator.prototype.generateCodeVerifier = function () {
    try {
      // Generate random values as utf-8
      var buffer = new Uint8Array(RANDOM_BYTE_ARR_LENGTH);
      this.cryptoObj.getRandomValues(buffer); // encode verifier as base64

      var pkceCodeVerifierB64 = this.base64Encode.urlEncodeArr(buffer);
      return pkceCodeVerifierB64;
    } catch (e) {
      throw BrowserAuthError.createPkceNotGeneratedError(e);
    }
  };
  /**
   * Creates a base64 encoded PKCE Code Challenge string from the
   * hash created from the PKCE Code Verifier supplied
   */


  PkceGenerator.prototype.generateCodeChallengeFromVerifier = function (pkceCodeVerifier) {
    return __awaiter$1(this, void 0, void 0, function () {
      var pkceHashedCodeVerifier, e_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            _a.trys.push([0, 2,, 3]);

            return [4
            /*yield*/
            , this.cryptoObj.sha256Digest(pkceCodeVerifier)];

          case 1:
            pkceHashedCodeVerifier = _a.sent(); // encode hash as base64

            return [2
            /*return*/
            , this.base64Encode.urlEncodeArr(new Uint8Array(pkceHashedCodeVerifier))];

          case 2:
            e_1 = _a.sent();
            throw BrowserAuthError.createPkceNotGeneratedError(e_1);

          case 3:
            return [2
            /*return*/
            ];
        }
      });
    });
  };

  return PkceGenerator;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * See here for more info on RsaHashedKeyGenParams: https://developer.mozilla.org/en-US/docs/Web/API/RsaHashedKeyGenParams
 */
// RSA KeyGen Algorithm

var PKCS1_V15_KEYGEN_ALG = "RSASSA-PKCS1-v1_5"; // SHA-256 hashing algorithm

var S256_HASH_ALG = "SHA-256"; // MOD length for PoP tokens

var MODULUS_LENGTH = 2048; // Public Exponent

var PUBLIC_EXPONENT = new Uint8Array([0x01, 0x00, 0x01]);
/**
 * This class implements functions used by the browser library to perform cryptography operations such as
 * hashing and encoding. It also has helper functions to validate the availability of specific APIs.
 */

var BrowserCrypto =
/** @class */
function () {
  function BrowserCrypto(logger) {
    this.logger = logger;

    if (!this.hasCryptoAPI()) {
      throw BrowserAuthError.createCryptoNotAvailableError("Browser crypto or msCrypto object not available.");
    }

    this._keygenAlgorithmOptions = {
      name: PKCS1_V15_KEYGEN_ALG,
      hash: S256_HASH_ALG,
      modulusLength: MODULUS_LENGTH,
      publicExponent: PUBLIC_EXPONENT
    };
  }
  /**
   * Returns a sha-256 hash of the given dataString as an ArrayBuffer.
   * @param dataString
   */


  BrowserCrypto.prototype.sha256Digest = function (dataString) {
    return __awaiter$1(this, void 0, void 0, function () {
      var data;
      return __generator$1(this, function (_a) {
        data = BrowserStringUtils.stringToUtf8Arr(dataString);
        return [2
        /*return*/
        , this.hasIECrypto() ? this.getMSCryptoDigest(S256_HASH_ALG, data) : this.getSubtleCryptoDigest(S256_HASH_ALG, data)];
      });
    });
  };
  /**
   * Populates buffer with cryptographically random values.
   * @param dataBuffer
   */


  BrowserCrypto.prototype.getRandomValues = function (dataBuffer) {
    var cryptoObj = window["msCrypto"] || window.crypto;

    if (!cryptoObj.getRandomValues) {
      throw BrowserAuthError.createCryptoNotAvailableError("getRandomValues does not exist.");
    }

    cryptoObj.getRandomValues(dataBuffer);
  };
  /**
   * Generates a keypair based on current keygen algorithm config.
   * @param extractable
   * @param usages
   */


  BrowserCrypto.prototype.generateKeyPair = function (extractable, usages) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.hasIECrypto() ? this.msCryptoGenerateKey(extractable, usages) : window.crypto.subtle.generateKey(this._keygenAlgorithmOptions, extractable, usages)];
      });
    });
  };
  /**
   * Export key as Json Web Key (JWK)
   * @param key
   * @param format
   */


  BrowserCrypto.prototype.exportJwk = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.hasIECrypto() ? this.msCryptoExportJwk(key) : window.crypto.subtle.exportKey(KEY_FORMAT_JWK, key)];
      });
    });
  };
  /**
   * Imports key as Json Web Key (JWK), can set extractable and usages.
   * @param key
   * @param format
   * @param extractable
   * @param usages
   */


  BrowserCrypto.prototype.importJwk = function (key, extractable, usages) {
    return __awaiter$1(this, void 0, void 0, function () {
      var keyString, keyBuffer;
      return __generator$1(this, function (_a) {
        keyString = BrowserCrypto.getJwkString(key);
        keyBuffer = BrowserStringUtils.stringToArrayBuffer(keyString);
        return [2
        /*return*/
        , this.hasIECrypto() ? this.msCryptoImportKey(keyBuffer, extractable, usages) : window.crypto.subtle.importKey(KEY_FORMAT_JWK, key, this._keygenAlgorithmOptions, extractable, usages)];
      });
    });
  };
  /**
   * Signs given data with given key
   * @param key
   * @param data
   */


  BrowserCrypto.prototype.sign = function (key, data) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.hasIECrypto() ? this.msCryptoSign(key, data) : window.crypto.subtle.sign(this._keygenAlgorithmOptions, key, data)];
      });
    });
  };
  /**
   * Check whether IE crypto or other browser cryptography is available.
   */


  BrowserCrypto.prototype.hasCryptoAPI = function () {
    return this.hasIECrypto() || this.hasBrowserCrypto();
  };
  /**
   * Checks whether IE crypto (AKA msCrypto) is available.
   */


  BrowserCrypto.prototype.hasIECrypto = function () {
    return "msCrypto" in window;
  };
  /**
   * Check whether browser crypto is available.
   */


  BrowserCrypto.prototype.hasBrowserCrypto = function () {
    return "crypto" in window;
  };
  /**
   * Helper function for SHA digest.
   * @param algorithm
   * @param data
   */


  BrowserCrypto.prototype.getSubtleCryptoDigest = function (algorithm, data) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , window.crypto.subtle.digest(algorithm, data)];
      });
    });
  };
  /**
   * IE Helper function for SHA digest.
   * @param algorithm
   * @param data
   */


  BrowserCrypto.prototype.getMSCryptoDigest = function (algorithm, data) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var digestOperation = window["msCrypto"].subtle.digest(algorithm, data.buffer);
          digestOperation.addEventListener("complete", function (e) {
            resolve(e.target.result);
          });
          digestOperation.addEventListener("error", function (error) {
            reject(error);
          });
        })];
      });
    });
  };
  /**
   * IE Helper function for generating a keypair
   * @param extractable
   * @param usages
   */


  BrowserCrypto.prototype.msCryptoGenerateKey = function (extractable, usages) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var msGenerateKey = window["msCrypto"].subtle.generateKey(_this._keygenAlgorithmOptions, extractable, usages);
          msGenerateKey.addEventListener("complete", function (e) {
            resolve(e.target.result);
          });
          msGenerateKey.addEventListener("error", function (error) {
            reject(error);
          });
        })];
      });
    });
  };
  /**
   * IE Helper function for exportKey
   * @param key
   * @param format
   */


  BrowserCrypto.prototype.msCryptoExportJwk = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var msExportKey = window["msCrypto"].subtle.exportKey(KEY_FORMAT_JWK, key);
          msExportKey.addEventListener("complete", function (e) {
            var resultBuffer = e.target.result;
            var resultString = BrowserStringUtils.utf8ArrToString(new Uint8Array(resultBuffer)).replace(/\r/g, Constants.EMPTY_STRING).replace(/\n/g, Constants.EMPTY_STRING).replace(/\t/g, Constants.EMPTY_STRING).split(" ").join(Constants.EMPTY_STRING).replace("\u0000", Constants.EMPTY_STRING);

            try {
              resolve(JSON.parse(resultString));
            } catch (e) {
              reject(e);
            }
          });
          msExportKey.addEventListener("error", function (error) {
            reject(error);
          });
        })];
      });
    });
  };
  /**
   * IE Helper function for importKey
   * @param key
   * @param format
   * @param extractable
   * @param usages
   */


  BrowserCrypto.prototype.msCryptoImportKey = function (keyBuffer, extractable, usages) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var msImportKey = window["msCrypto"].subtle.importKey(KEY_FORMAT_JWK, keyBuffer, _this._keygenAlgorithmOptions, extractable, usages);
          msImportKey.addEventListener("complete", function (e) {
            resolve(e.target.result);
          });
          msImportKey.addEventListener("error", function (error) {
            reject(error);
          });
        })];
      });
    });
  };
  /**
   * IE Helper function for sign JWT
   * @param key
   * @param data
   */


  BrowserCrypto.prototype.msCryptoSign = function (key, data) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var msSign = window["msCrypto"].subtle.sign(_this._keygenAlgorithmOptions, key, data);
          msSign.addEventListener("complete", function (e) {
            resolve(e.target.result);
          });
          msSign.addEventListener("error", function (error) {
            reject(error);
          });
        })];
      });
    });
  };
  /**
   * Returns stringified jwk.
   * @param jwk
   */


  BrowserCrypto.getJwkString = function (jwk) {
    return JSON.stringify(jwk, Object.keys(jwk).sort());
  };

  return BrowserCrypto;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Storage wrapper for IndexedDB storage in browsers: https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */

var DatabaseStorage =
/** @class */
function () {
  function DatabaseStorage() {
    this.dbName = DB_NAME;
    this.version = DB_VERSION;
    this.tableName = DB_TABLE_NAME;
    this.dbOpen = false;
  }
  /**
   * Opens IndexedDB instance.
   */


  DatabaseStorage.prototype.open = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , new Promise(function (resolve, reject) {
          var openDB = window.indexedDB.open(_this.dbName, _this.version);
          openDB.addEventListener("upgradeneeded", function (e) {
            var event = e;
            event.target.result.createObjectStore(_this.tableName);
          });
          openDB.addEventListener("success", function (e) {
            var event = e;
            _this.db = event.target.result;
            _this.dbOpen = true;
            resolve();
          });
          openDB.addEventListener("error", function () {
            return reject(BrowserAuthError.createDatabaseUnavailableError());
          });
        })];
      });
    });
  };
  /**
   * Closes the connection to IndexedDB database when all pending transactions
   * complete.
   */


  DatabaseStorage.prototype.closeConnection = function () {
    var db = this.db;

    if (db && this.dbOpen) {
      db.close();
      this.dbOpen = false;
    }
  };
  /**
   * Opens database if it's not already open
   */


  DatabaseStorage.prototype.validateDbIsOpen = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!!this.dbOpen) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , this.open()];

          case 1:
            return [2
            /*return*/
            , _a.sent()];

          case 2:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Retrieves item from IndexedDB instance.
   * @param key
   */


  DatabaseStorage.prototype.getItem = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.validateDbIsOpen()];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , new Promise(function (resolve, reject) {
              // TODO: Add timeouts?
              if (!_this.db) {
                return reject(BrowserAuthError.createDatabaseNotOpenError());
              }

              var transaction = _this.db.transaction([_this.tableName], "readonly");

              var objectStore = transaction.objectStore(_this.tableName);
              var dbGet = objectStore.get(key);
              dbGet.addEventListener("success", function (e) {
                var event = e;

                _this.closeConnection();

                resolve(event.target.result);
              });
              dbGet.addEventListener("error", function (e) {
                _this.closeConnection();

                reject(e);
              });
            })];
        }
      });
    });
  };
  /**
   * Adds item to IndexedDB under given key
   * @param key
   * @param payload
   */


  DatabaseStorage.prototype.setItem = function (key, payload) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.validateDbIsOpen()];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , new Promise(function (resolve, reject) {
              // TODO: Add timeouts?
              if (!_this.db) {
                return reject(BrowserAuthError.createDatabaseNotOpenError());
              }

              var transaction = _this.db.transaction([_this.tableName], "readwrite");

              var objectStore = transaction.objectStore(_this.tableName);
              var dbPut = objectStore.put(payload, key);
              dbPut.addEventListener("success", function () {
                _this.closeConnection();

                resolve();
              });
              dbPut.addEventListener("error", function (e) {
                _this.closeConnection();

                reject(e);
              });
            })];
        }
      });
    });
  };
  /**
   * Removes item from IndexedDB under given key
   * @param key
   */


  DatabaseStorage.prototype.removeItem = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.validateDbIsOpen()];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , new Promise(function (resolve, reject) {
              if (!_this.db) {
                return reject(BrowserAuthError.createDatabaseNotOpenError());
              }

              var transaction = _this.db.transaction([_this.tableName], "readwrite");

              var objectStore = transaction.objectStore(_this.tableName);
              var dbDelete = objectStore.delete(key);
              dbDelete.addEventListener("success", function () {
                _this.closeConnection();

                resolve();
              });
              dbDelete.addEventListener("error", function (e) {
                _this.closeConnection();

                reject(e);
              });
            })];
        }
      });
    });
  };
  /**
   * Get all the keys from the storage object as an iterable array of strings.
   */


  DatabaseStorage.prototype.getKeys = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.validateDbIsOpen()];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , new Promise(function (resolve, reject) {
              if (!_this.db) {
                return reject(BrowserAuthError.createDatabaseNotOpenError());
              }

              var transaction = _this.db.transaction([_this.tableName], "readonly");

              var objectStore = transaction.objectStore(_this.tableName);
              var dbGetKeys = objectStore.getAllKeys();
              dbGetKeys.addEventListener("success", function (e) {
                var event = e;

                _this.closeConnection();

                resolve(event.target.result);
              });
              dbGetKeys.addEventListener("error", function (e) {
                _this.closeConnection();

                reject(e);
              });
            })];
        }
      });
    });
  };
  /**
   *
   * Checks whether there is an object under the search key in the object store
   */


  DatabaseStorage.prototype.containsKey = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.validateDbIsOpen()];

          case 1:
            _a.sent();

            return [2
            /*return*/
            , new Promise(function (resolve, reject) {
              if (!_this.db) {
                return reject(BrowserAuthError.createDatabaseNotOpenError());
              }

              var transaction = _this.db.transaction([_this.tableName], "readonly");

              var objectStore = transaction.objectStore(_this.tableName);
              var dbContainsKey = objectStore.count(key);
              dbContainsKey.addEventListener("success", function (e) {
                var event = e;

                _this.closeConnection();

                resolve(event.target.result === 1);
              });
              dbContainsKey.addEventListener("error", function (e) {
                _this.closeConnection();

                reject(e);
              });
            })];
        }
      });
    });
  };
  /**
   * Deletes the MSAL database. The database is deleted rather than cleared to make it possible
   * for client applications to downgrade to a previous MSAL version without worrying about forward compatibility issues
   * with IndexedDB database versions.
   */


  DatabaseStorage.prototype.deleteDatabase = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var existingDatabases, database;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            // Check if database being deleted exists
            if (this.db && this.dbOpen) {
              this.closeConnection();
            }

            return [4
            /*yield*/
            , window.indexedDB.databases()];

          case 1:
            existingDatabases = _a.sent();
            database = existingDatabases.find(function (database) {
              return database.name === DB_NAME;
            }); // If database exists, delete it

            if (database) {
              return [2
              /*return*/
              , new Promise(function (resolve, reject) {
                var deleteDbRequest = window.indexedDB.deleteDatabase(DB_NAME);
                deleteDbRequest.addEventListener("success", function () {
                  return resolve(true);
                });
                deleteDbRequest.addEventListener("error", function () {
                  return reject(false);
                });
              })];
            } // Database doesn't exist, return true


            return [2
            /*return*/
            , true];
        }
      });
    });
  };

  return DatabaseStorage;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * This class allows MSAL to store artifacts asynchronously using the DatabaseStorage IndexedDB wrapper,
 * backed up with the more volatile MemoryStorage object for cases in which IndexedDB may be unavailable.
 */

var AsyncMemoryStorage =
/** @class */
function () {
  function AsyncMemoryStorage(logger, storeName) {
    this.inMemoryCache = new MemoryStorage();
    this.indexedDBCache = new DatabaseStorage();
    this.logger = logger;
    this.storeName = storeName;
  }

  AsyncMemoryStorage.prototype.handleDatabaseAccessError = function (error) {
    if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorMessage.databaseUnavailable.code) {
      this.logger.error("Could not access persistent storage. This may be caused by browser privacy features which block persistent storage in third-party contexts.");
    } else {
      throw error;
    }
  };
  /**
   * Get the item matching the given key. Tries in-memory cache first, then in the asynchronous
   * storage object if item isn't found in-memory.
   * @param key
   */


  AsyncMemoryStorage.prototype.getItem = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var item, e_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            item = this.inMemoryCache.getItem(key);
            if (!!item) return [3
            /*break*/
            , 4];
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            this.logger.verbose("Queried item not found in in-memory cache, now querying persistent storage.");
            return [4
            /*yield*/
            , this.indexedDBCache.getItem(key)];

          case 2:
            return [2
            /*return*/
            , _a.sent()];

          case 3:
            e_1 = _a.sent();
            this.handleDatabaseAccessError(e_1);
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            , item];
        }
      });
    });
  };
  /**
   * Sets the item in the in-memory cache and then tries to set it in the asynchronous
   * storage object with the given key.
   * @param key
   * @param value
   */


  AsyncMemoryStorage.prototype.setItem = function (key, value) {
    return __awaiter$1(this, void 0, void 0, function () {
      var e_2;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.inMemoryCache.setItem(key, value);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.indexedDBCache.setItem(key, value)];

          case 2:
            _a.sent();

            return [3
            /*break*/
            , 4];

          case 3:
            e_2 = _a.sent();
            this.handleDatabaseAccessError(e_2);
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Removes the item matching the key from the in-memory cache, then tries to remove it from the asynchronous storage object.
   * @param key
   */


  AsyncMemoryStorage.prototype.removeItem = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var e_3;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.inMemoryCache.removeItem(key);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.indexedDBCache.removeItem(key)];

          case 2:
            _a.sent();

            return [3
            /*break*/
            , 4];

          case 3:
            e_3 = _a.sent();
            this.handleDatabaseAccessError(e_3);
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Get all the keys from the in-memory cache as an iterable array of strings. If no keys are found, query the keys in the
   * asynchronous storage object.
   */


  AsyncMemoryStorage.prototype.getKeys = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var cacheKeys, e_4;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            cacheKeys = this.inMemoryCache.getKeys();
            if (!(cacheKeys.length === 0)) return [3
            /*break*/
            , 4];
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            this.logger.verbose("In-memory cache is empty, now querying persistent storage.");
            return [4
            /*yield*/
            , this.indexedDBCache.getKeys()];

          case 2:
            return [2
            /*return*/
            , _a.sent()];

          case 3:
            e_4 = _a.sent();
            this.handleDatabaseAccessError(e_4);
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            , cacheKeys];
        }
      });
    });
  };
  /**
   * Returns true or false if the given key is present in the cache.
   * @param key
   */


  AsyncMemoryStorage.prototype.containsKey = function (key) {
    return __awaiter$1(this, void 0, void 0, function () {
      var containsKey, e_5;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            containsKey = this.inMemoryCache.containsKey(key);
            if (!!containsKey) return [3
            /*break*/
            , 4];
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            this.logger.verbose("Key not found in in-memory cache, now querying persistent storage.");
            return [4
            /*yield*/
            , this.indexedDBCache.containsKey(key)];

          case 2:
            return [2
            /*return*/
            , _a.sent()];

          case 3:
            e_5 = _a.sent();
            this.handleDatabaseAccessError(e_5);
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            , containsKey];
        }
      });
    });
  };
  /**
   * Clears in-memory Map and tries to delete the IndexedDB database.
   */


  AsyncMemoryStorage.prototype.clear = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var dbDeleted, e_6;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            // InMemory cache is a Map instance, clear is straightforward
            this.logger.verbose("Deleting in-memory keystore " + this.storeName);
            this.inMemoryCache.clear();
            this.logger.verbose("In-memory keystore " + this.storeName + " deleted");
            this.logger.verbose("Deleting persistent keystore " + this.storeName);
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this.indexedDBCache.deleteDatabase()];

          case 2:
            dbDeleted = _a.sent();

            if (dbDeleted) {
              this.logger.verbose("Persistent keystore " + this.storeName + " deleted");
            }

            return [2
            /*return*/
            , dbDeleted];

          case 3:
            e_6 = _a.sent();
            this.handleDatabaseAccessError(e_6);
            return [2
            /*return*/
            , false];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };

  return AsyncMemoryStorage;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var CryptoKeyStoreNames;

(function (CryptoKeyStoreNames) {
  CryptoKeyStoreNames["asymmetricKeys"] = "asymmetricKeys";
  CryptoKeyStoreNames["symmetricKeys"] = "symmetricKeys";
})(CryptoKeyStoreNames || (CryptoKeyStoreNames = {}));
/**
 * This class implements MSAL's crypto interface, which allows it to perform base64 encoding and decoding, generating cryptographically random GUIDs and
 * implementing Proof Key for Code Exchange specs for the OAuth Authorization Code Flow using PKCE (rfc here: https://tools.ietf.org/html/rfc7636).
 */


var CryptoOps =
/** @class */
function () {
  function CryptoOps(logger, performanceClient) {
    this.logger = logger; // Browser crypto needs to be validated first before any other classes can be set.

    this.browserCrypto = new BrowserCrypto(this.logger);
    this.b64Encode = new Base64Encode();
    this.b64Decode = new Base64Decode();
    this.guidGenerator = new GuidGenerator(this.browserCrypto);
    this.pkceGenerator = new PkceGenerator(this.browserCrypto);
    this.cache = {
      asymmetricKeys: new AsyncMemoryStorage(this.logger, CryptoKeyStoreNames.asymmetricKeys),
      symmetricKeys: new AsyncMemoryStorage(this.logger, CryptoKeyStoreNames.symmetricKeys)
    };
    this.performanceClient = performanceClient;
  }
  /**
   * Creates a new random GUID - used to populate state and nonce.
   * @returns string (GUID)
   */


  CryptoOps.prototype.createNewGuid = function () {
    return this.guidGenerator.generateGuid();
  };
  /**
   * Encodes input string to base64.
   * @param input
   */


  CryptoOps.prototype.base64Encode = function (input) {
    return this.b64Encode.encode(input);
  };
  /**
   * Decodes input string from base64.
   * @param input
   */


  CryptoOps.prototype.base64Decode = function (input) {
    return this.b64Decode.decode(input);
  };
  /**
   * Generates PKCE codes used in Authorization Code Flow.
   */


  CryptoOps.prototype.generatePkceCodes = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      return __generator$1(this, function (_a) {
        return [2
        /*return*/
        , this.pkceGenerator.generateCodes()];
      });
    });
  };
  /**
   * Generates a keypair, stores it and returns a thumbprint
   * @param request
   */


  CryptoOps.prototype.getPublicKeyThumbprint = function (request) {
    var _a;

    return __awaiter$1(this, void 0, void 0, function () {
      var publicKeyThumbMeasurement, keyPair, publicKeyJwk, pubKeyThumprintObj, publicJwkString, publicJwkHash, privateKeyJwk, unextractablePrivateKey;
      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            publicKeyThumbMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.CryptoOptsGetPublicKeyThumbprint, request.correlationId);
            return [4
            /*yield*/
            , this.browserCrypto.generateKeyPair(CryptoOps.EXTRACTABLE, CryptoOps.POP_KEY_USAGES)];

          case 1:
            keyPair = _b.sent();
            return [4
            /*yield*/
            , this.browserCrypto.exportJwk(keyPair.publicKey)];

          case 2:
            publicKeyJwk = _b.sent();
            pubKeyThumprintObj = {
              e: publicKeyJwk.e,
              kty: publicKeyJwk.kty,
              n: publicKeyJwk.n
            };
            publicJwkString = BrowserCrypto.getJwkString(pubKeyThumprintObj);
            return [4
            /*yield*/
            , this.hashString(publicJwkString)];

          case 3:
            publicJwkHash = _b.sent();
            return [4
            /*yield*/
            , this.browserCrypto.exportJwk(keyPair.privateKey)];

          case 4:
            privateKeyJwk = _b.sent();
            return [4
            /*yield*/
            , this.browserCrypto.importJwk(privateKeyJwk, false, ["sign"])];

          case 5:
            unextractablePrivateKey = _b.sent(); // Store Keypair data in keystore

            return [4
            /*yield*/
            , this.cache.asymmetricKeys.setItem(publicJwkHash, {
              privateKey: unextractablePrivateKey,
              publicKey: keyPair.publicKey,
              requestMethod: request.resourceRequestMethod,
              requestUri: request.resourceRequestUri
            })];

          case 6:
            // Store Keypair data in keystore
            _b.sent();

            if (publicKeyThumbMeasurement) {
              publicKeyThumbMeasurement.endMeasurement({
                success: true
              });
            }

            return [2
            /*return*/
            , publicJwkHash];
        }
      });
    });
  };
  /**
   * Removes cryptographic keypair from key store matching the keyId passed in
   * @param kid
   */


  CryptoOps.prototype.removeTokenBindingKey = function (kid) {
    return __awaiter$1(this, void 0, void 0, function () {
      var keyFound;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.cache.asymmetricKeys.removeItem(kid)];

          case 1:
            _a.sent();

            return [4
            /*yield*/
            , this.cache.asymmetricKeys.containsKey(kid)];

          case 2:
            keyFound = _a.sent();
            return [2
            /*return*/
            , !keyFound];
        }
      });
    });
  };
  /**
   * Removes all cryptographic keys from IndexedDB storage
   */


  CryptoOps.prototype.clearKeystore = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var e_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            _a.trys.push([0, 3,, 4]);

            this.logger.verbose("Deleting in-memory and persistent asymmetric key stores");
            return [4
            /*yield*/
            , this.cache.asymmetricKeys.clear()];

          case 1:
            _a.sent();

            this.logger.verbose("Successfully deleted asymmetric key stores");
            this.logger.verbose("Deleting in-memory and persistent symmetric key stores");
            return [4
            /*yield*/
            , this.cache.symmetricKeys.clear()];

          case 2:
            _a.sent();

            this.logger.verbose("Successfully deleted symmetric key stores");
            return [2
            /*return*/
            , true];

          case 3:
            e_1 = _a.sent();

            if (e_1 instanceof Error) {
              this.logger.error("Clearing keystore failed with error: " + e_1.message);
            } else {
              this.logger.error("Clearing keystore failed with unknown error");
            }

            return [2
            /*return*/
            , false];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Signs the given object as a jwt payload with private key retrieved by given kid.
   * @param payload
   * @param kid
   */


  CryptoOps.prototype.signJwt = function (payload, kid, correlationId) {
    var _a;

    return __awaiter$1(this, void 0, void 0, function () {
      var signJwtMeasurement, cachedKeyPair, publicKeyJwk, publicKeyJwkString, encodedKeyIdThumbprint, shrHeader, encodedShrHeader, encodedPayload, tokenString, tokenBuffer, signatureBuffer, encodedSignature, signedJwt;
      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            signJwtMeasurement = (_a = this.performanceClient) === null || _a === void 0 ? void 0 : _a.startMeasurement(PerformanceEvents.CryptoOptsSignJwt, correlationId);
            return [4
            /*yield*/
            , this.cache.asymmetricKeys.getItem(kid)];

          case 1:
            cachedKeyPair = _b.sent();

            if (!cachedKeyPair) {
              throw BrowserAuthError.createSigningKeyNotFoundInStorageError(kid);
            }

            return [4
            /*yield*/
            , this.browserCrypto.exportJwk(cachedKeyPair.publicKey)];

          case 2:
            publicKeyJwk = _b.sent();
            publicKeyJwkString = BrowserCrypto.getJwkString(publicKeyJwk);
            encodedKeyIdThumbprint = this.b64Encode.urlEncode(JSON.stringify({
              kid: kid
            }));
            shrHeader = JoseHeader.getShrHeaderString({
              kid: encodedKeyIdThumbprint,
              alg: publicKeyJwk.alg
            });
            encodedShrHeader = this.b64Encode.urlEncode(shrHeader); // Generate payload

            payload.cnf = {
              jwk: JSON.parse(publicKeyJwkString)
            };
            encodedPayload = this.b64Encode.urlEncode(JSON.stringify(payload));
            tokenString = encodedShrHeader + "." + encodedPayload;
            tokenBuffer = BrowserStringUtils.stringToArrayBuffer(tokenString);
            return [4
            /*yield*/
            , this.browserCrypto.sign(cachedKeyPair.privateKey, tokenBuffer)];

          case 3:
            signatureBuffer = _b.sent();
            encodedSignature = this.b64Encode.urlEncodeArr(new Uint8Array(signatureBuffer));
            signedJwt = tokenString + "." + encodedSignature;

            if (signJwtMeasurement) {
              signJwtMeasurement.endMeasurement({
                success: true
              });
            }

            return [2
            /*return*/
            , signedJwt];
        }
      });
    });
  };
  /**
   * Returns the SHA-256 hash of an input string
   * @param plainText
   */


  CryptoOps.prototype.hashString = function (plainText) {
    return __awaiter$1(this, void 0, void 0, function () {
      var hashBuffer, hashBytes;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , this.browserCrypto.sha256Digest(plainText)];

          case 1:
            hashBuffer = _a.sent();
            hashBytes = new Uint8Array(hashBuffer);
            return [2
            /*return*/
            , this.b64Encode.urlEncodeArr(hashBytes)];
        }
      });
    });
  };

  CryptoOps.POP_KEY_USAGES = ["sign", "verify"];
  CryptoOps.EXTRACTABLE = true;
  return CryptoOps;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * Token cache manager
 */

var TokenCache =
/** @class */
function () {
  function TokenCache(configuration, storage, logger, cryptoObj) {
    this.isBrowserEnvironment = typeof window !== "undefined";
    this.config = configuration;
    this.storage = storage;
    this.logger = logger;
    this.cryptoObj = cryptoObj;
  } // Move getAllAccounts here and cache utility APIs

  /**
   * API to load tokens to msal-browser cache.
   * @param request
   * @param response
   * @param options
   */


  TokenCache.prototype.loadExternalTokens = function (request, response, options) {
    this.logger.info("TokenCache - loadExternalTokens called");

    if (!response.id_token) {
      throw BrowserAuthError.createUnableToLoadTokenError("Please ensure server response includes id token.");
    }

    if (request.account) {
      this.loadIdToken(response.id_token, request.account.homeAccountId, request.account.environment, request.account.tenantId, options);
      this.loadAccessToken(request, response, request.account.homeAccountId, request.account.environment, request.account.tenantId, options);
    } else if (request.authority) {
      var authorityUrl = Authority.generateAuthority(request.authority, request.azureCloudOptions);
      var authorityOptions = {
        protocolMode: this.config.auth.protocolMode,
        knownAuthorities: this.config.auth.knownAuthorities,
        cloudDiscoveryMetadata: this.config.auth.cloudDiscoveryMetadata,
        authorityMetadata: this.config.auth.authorityMetadata
      };
      var authority = new Authority(authorityUrl, this.config.system.networkClient, this.storage, authorityOptions); // "clientInfo" from options takes precedence over "clientInfo" in response

      if (options.clientInfo) {
        this.logger.trace("TokenCache - homeAccountId from options");
        this.loadIdToken(response.id_token, options.clientInfo, authority.hostnameAndPort, authority.tenant, options);
        this.loadAccessToken(request, response, options.clientInfo, authority.hostnameAndPort, authority.tenant, options);
      } else if (response.client_info) {
        this.logger.trace("TokenCache - homeAccountId from response");
        this.loadIdToken(response.id_token, response.client_info, authority.hostnameAndPort, authority.tenant, options);
        this.loadAccessToken(request, response, response.client_info, authority.hostnameAndPort, authority.tenant, options);
      } else {
        throw BrowserAuthError.createUnableToLoadTokenError("Please provide clientInfo in the response or options.");
      }
    } else {
      throw BrowserAuthError.createUnableToLoadTokenError("Please provide a request with an account or a request with authority.");
    }
  };
  /**
   * Helper function to load id tokens to msal-browser cache
   * @param idToken
   * @param homeAccountId
   * @param environment
   * @param tenantId
   * @param options
   */


  TokenCache.prototype.loadIdToken = function (idToken, homeAccountId, environment, tenantId, options) {
    var idTokenEntity = IdTokenEntity.createIdTokenEntity(homeAccountId, environment, idToken, this.config.auth.clientId, tenantId);
    var idAuthToken = new AuthToken(idToken, this.cryptoObj);
    var accountEntity = options.clientInfo ? AccountEntity.createAccount(options.clientInfo, homeAccountId, idAuthToken, undefined, undefined, undefined, environment) : AccountEntity.createGenericAccount(homeAccountId, idAuthToken, undefined, undefined, undefined, environment);

    if (this.isBrowserEnvironment) {
      this.logger.verbose("TokenCache - loading id token");
      this.storage.setAccount(accountEntity);
      this.storage.setIdTokenCredential(idTokenEntity);
    } else {
      throw BrowserAuthError.createUnableToLoadTokenError("loadExternalTokens is designed to work in browser environments only.");
    }
  };
  /**
   * Helper function to load access tokens to msal-browser cache
   * @param request
   * @param response
   * @param options
   * @param homeAccountId
   * @param environment
   * @param tenantId
   * @returns
   */


  TokenCache.prototype.loadAccessToken = function (request, response, homeAccountId, environment, tenantId, options) {
    if (!response.access_token) {
      this.logger.verbose("TokenCache - No access token provided for caching");
      return;
    }

    if (!response.expires_in) {
      throw BrowserAuthError.createUnableToLoadTokenError("Please ensure server response includes expires_in value.");
    }

    if (!options.extendedExpiresOn) {
      throw BrowserAuthError.createUnableToLoadTokenError("Please provide an extendedExpiresOn value in the options.");
    }

    var scopes = new ScopeSet(request.scopes).printScopes();
    var expiresOn = options.expiresOn || response.expires_in + new Date().getTime() / 1000;
    var extendedExpiresOn = options.extendedExpiresOn;
    var accessTokenEntity = AccessTokenEntity.createAccessTokenEntity(homeAccountId, environment, response.access_token, this.config.auth.clientId, tenantId, scopes, expiresOn, extendedExpiresOn, this.cryptoObj);

    if (this.isBrowserEnvironment) {
      this.logger.verbose("TokenCache - loading access token");
      this.storage.setAccessTokenCredential(accessTokenEntity);
    } else {
      throw BrowserAuthError.createUnableToLoadTokenError("loadExternalTokens is designed to work in browser environments only.");
    }
  };

  return TokenCache;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var HybridSpaAuthorizationCodeClient =
/** @class */
function (_super) {
  __extends$1(HybridSpaAuthorizationCodeClient, _super);

  function HybridSpaAuthorizationCodeClient(config) {
    var _this = _super.call(this, config) || this;

    _this.includeRedirectUri = false;
    return _this;
  }

  return HybridSpaAuthorizationCodeClient;
}(AuthorizationCodeClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var SilentAuthCodeClient =
/** @class */
function (_super) {
  __extends$1(SilentAuthCodeClient, _super);

  function SilentAuthCodeClient(config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, apiId, performanceClient, nativeMessageHandler, correlationId) {
    var _this = _super.call(this, config, storageImpl, browserCrypto, logger, eventHandler, navigationClient, performanceClient, nativeMessageHandler, correlationId) || this;

    _this.apiId = apiId;
    return _this;
  }
  /**
   * Acquires a token silently by redeeming an authorization code against the /token endpoint
   * @param request
   */


  SilentAuthCodeClient.prototype.acquireToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var silentRequest, serverTelemetryManager, authCodeRequest, clientConfig, authClient, silentHandler, e_1;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("SilentAuthCodeClient.acquireToken called"); // Auth code payload is required

            if (!request.code) {
              throw BrowserAuthError.createAuthCodeRequiredError();
            }

            return [4
            /*yield*/
            , this.initializeAuthorizationRequest(request, InteractionType.Silent)];

          case 1:
            silentRequest = _a.sent();
            this.browserStorage.updateCacheEntries(silentRequest.state, silentRequest.nonce, silentRequest.authority, silentRequest.loginHint || Constants.EMPTY_STRING, silentRequest.account || null);
            serverTelemetryManager = this.initializeServerTelemetryManager(this.apiId);
            _a.label = 2;

          case 2:
            _a.trys.push([2, 4,, 5]);

            authCodeRequest = __assign$1(__assign$1({}, silentRequest), {
              code: request.code
            });
            return [4
            /*yield*/
            , this.getClientConfiguration(serverTelemetryManager, silentRequest.authority)];

          case 3:
            clientConfig = _a.sent();
            authClient = new HybridSpaAuthorizationCodeClient(clientConfig);
            this.logger.verbose("Auth code client created");
            silentHandler = new SilentHandler(authClient, this.browserStorage, authCodeRequest, this.logger, this.config.system.navigateFrameWait); // Handle auth code parameters from request

            return [2
            /*return*/
            , silentHandler.handleCodeResponseFromServer({
              code: request.code,
              msgraph_host: request.msGraphHost,
              cloud_graph_host_name: request.cloudGraphHostName,
              cloud_instance_host_name: request.cloudInstanceHostName
            }, silentRequest.state, authClient.authority, this.networkClient, false)];

          case 4:
            e_1 = _a.sent();

            if (e_1 instanceof AuthError) {
              e_1.setCorrelationId(this.correlationId);
            }

            serverTelemetryManager.cacheFailedRequest(e_1);
            this.browserStorage.cleanRequestByState(silentRequest.state);
            throw e_1;

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * Currently Unsupported
   */


  SilentAuthCodeClient.prototype.logout = function () {
    // Synchronous so we must reject
    return Promise.reject(BrowserAuthError.createSilentLogoutUnsupportedError());
  };

  return SilentAuthCodeClient;
}(StandardInteractionClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var BrowserPerformanceMeasurement =
/** @class */
function () {
  function BrowserPerformanceMeasurement(name, correlationId) {
    this.correlationId = correlationId;
    this.measureName = "msal.measure." + name + "." + this.correlationId;
    this.startMark = "msal.start." + name + "." + this.correlationId;
    this.endMark = "msal.end." + name + "." + this.correlationId;
  }

  BrowserPerformanceMeasurement.supportsBrowserPerformance = function () {
    return typeof window !== "undefined" && typeof window.performance !== "undefined" && typeof window.performance.mark === "function" && typeof window.performance.measure === "function" && typeof window.performance.clearMarks === "function" && typeof window.performance.clearMeasures === "function" && typeof window.performance.getEntriesByName === "function";
  };

  BrowserPerformanceMeasurement.prototype.startMeasurement = function () {
    if (BrowserPerformanceMeasurement.supportsBrowserPerformance()) {
      try {
        window.performance.mark(this.startMark);
      } catch (e) {// Silently catch
      }
    }
  };

  BrowserPerformanceMeasurement.prototype.endMeasurement = function () {
    if (BrowserPerformanceMeasurement.supportsBrowserPerformance()) {
      try {
        window.performance.mark(this.endMark);
        window.performance.measure(this.measureName, this.startMark, this.endMark);
      } catch (e) {// Silently catch
      }
    }
  };

  BrowserPerformanceMeasurement.prototype.flushMeasurement = function () {
    if (BrowserPerformanceMeasurement.supportsBrowserPerformance()) {
      try {
        var entriesForMeasurement = window.performance.getEntriesByName(this.measureName, "measure");

        if (entriesForMeasurement.length > 0) {
          var durationMs = entriesForMeasurement[0].duration;
          window.performance.clearMeasures(this.measureName);
          window.performance.clearMarks(this.startMark);
          window.performance.clearMarks(this.endMark);
          return durationMs;
        }
      } catch (e) {// Silently catch and return null
      }
    }

    return null;
  };

  return BrowserPerformanceMeasurement;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var BrowserPerformanceClient =
/** @class */
function (_super) {
  __extends$1(BrowserPerformanceClient, _super);

  function BrowserPerformanceClient(clientId, authority, logger, libraryName, libraryVersion, applicationTelemetry) {
    var _this = _super.call(this, clientId, authority, logger, libraryName, libraryVersion, applicationTelemetry) || this;

    _this.browserCrypto = new BrowserCrypto(_this.logger);
    _this.guidGenerator = new GuidGenerator(_this.browserCrypto);
    return _this;
  }

  BrowserPerformanceClient.prototype.startPerformanceMeasuremeant = function (measureName, correlationId) {
    return new BrowserPerformanceMeasurement(measureName, correlationId);
  };

  BrowserPerformanceClient.prototype.generateId = function () {
    return this.guidGenerator.generateGuid();
  };

  BrowserPerformanceClient.prototype.getPageVisibility = function () {
    var _a;

    return ((_a = document.visibilityState) === null || _a === void 0 ? void 0 : _a.toString()) || null;
  };
  /**
   * Starts measuring performance for a given operation. Returns a function that should be used to end the measurement.
   * Also captures browser page visibilityState.
   *
   * @param {PerformanceEvents} measureName
   * @param {?string} [correlationId]
   * @returns {((event?: Partial<PerformanceEvent>) => PerformanceEvent| null)}
   */


  BrowserPerformanceClient.prototype.startMeasurement = function (measureName, correlationId) {
    var _this = this; // Capture page visibilityState and then invoke start/end measurement


    var startPageVisibility = this.getPageVisibility();

    var inProgressEvent = _super.prototype.startMeasurement.call(this, measureName, correlationId);

    return __assign$1(__assign$1({}, inProgressEvent), {
      endMeasurement: function (event) {
        return inProgressEvent.endMeasurement(__assign$1({
          startPageVisibility: startPageVisibility,
          endPageVisibility: _this.getPageVisibility()
        }, event));
      }
    });
  };

  return BrowserPerformanceClient;
}(PerformanceClient);

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

var ClientApplication =
/** @class */
function () {
  /**
   * @constructor
   * Constructor for the PublicClientApplication used to instantiate the PublicClientApplication object
   *
   * Important attributes in the Configuration object for auth are:
   * - clientID: the application ID of your application. You can obtain one by registering your application with our Application registration portal : https://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredAppsPreview
   * - authority: the authority URL for your application.
   * - redirect_uri: the uri of your application registered in the portal.
   *
   * In Azure AD, authority is a URL indicating the Azure active directory that MSAL uses to obtain tokens.
   * It is of the form https://login.microsoftonline.com/{Enter_the_Tenant_Info_Here}
   * If your application supports Accounts in one organizational directory, replace "Enter_the_Tenant_Info_Here" value with the Tenant Id or Tenant name (for example, contoso.microsoft.com).
   * If your application supports Accounts in any organizational directory, replace "Enter_the_Tenant_Info_Here" value with organizations.
   * If your application supports Accounts in any organizational directory and personal Microsoft accounts, replace "Enter_the_Tenant_Info_Here" value with common.
   * To restrict support to Personal Microsoft accounts only, replace "Enter_the_Tenant_Info_Here" value with consumers.
   *
   * In Azure B2C, authority is of the form https://{instance}/tfp/{tenant}/{policyName}/
   * Full B2C functionality will be available in this library in future versions.
   *
   * @param configuration Object for the MSAL PublicClientApplication instance
   */
  function ClientApplication(configuration) {
    /*
     * If loaded in an environment where window is not available,
     * set internal flag to false so that further requests fail.
     * This is to support server-side rendering environments.
     */
    this.isBrowserEnvironment = typeof window !== "undefined"; // Set the configuration.

    this.config = buildConfiguration(configuration, this.isBrowserEnvironment);
    this.initialized = false; // Initialize logger

    this.logger = new Logger(this.config.system.loggerOptions, name, version); // Initialize the network module class.

    this.networkClient = this.config.system.networkClient; // Initialize the navigation client class.

    this.navigationClient = this.config.system.navigationClient; // Initialize redirectResponse Map

    this.redirectResponse = new Map(); // Initial hybrid spa map

    this.hybridAuthCodeResponses = new Map(); // Initialize performance client

    this.performanceClient = this.isBrowserEnvironment ? new BrowserPerformanceClient(this.config.auth.clientId, this.config.auth.authority, this.logger, name, version, this.config.telemetry.application) : new StubPerformanceClient(this.config.auth.clientId, this.config.auth.authority, this.logger, name, version, this.config.telemetry.application); // Initialize the crypto class.

    this.browserCrypto = this.isBrowserEnvironment ? new CryptoOps(this.logger, this.performanceClient) : DEFAULT_CRYPTO_IMPLEMENTATION;
    this.eventHandler = new EventHandler(this.logger, this.browserCrypto); // Initialize the browser storage class.

    this.browserStorage = this.isBrowserEnvironment ? new BrowserCacheManager(this.config.auth.clientId, this.config.cache, this.browserCrypto, this.logger) : DEFAULT_BROWSER_CACHE_MANAGER(this.config.auth.clientId, this.logger); // Initialize the token cache

    this.tokenCache = new TokenCache(this.config, this.browserStorage, this.logger, this.browserCrypto);
  }
  /**
   * Initializer function to perform async startup tasks such as connecting to WAM extension
   */


  ClientApplication.prototype.initialize = function () {
    return __awaiter$1(this, void 0, void 0, function () {
      var _a, e_1;

      return __generator$1(this, function (_b) {
        switch (_b.label) {
          case 0:
            this.logger.trace("initialize called");

            if (this.initialized) {
              this.logger.info("initialize has already been called, exiting early.");
              return [2
              /*return*/
              ];
            }

            this.eventHandler.emitEvent(EventType.INITIALIZE_START);
            if (!this.config.system.allowNativeBroker) return [3
            /*break*/
            , 4];
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3,, 4]);

            _a = this;
            return [4
            /*yield*/
            , NativeMessageHandler.createProvider(this.logger, this.config.system.nativeBrokerHandshakeTimeout)];

          case 2:
            _a.nativeExtensionProvider = _b.sent();
            return [3
            /*break*/
            , 4];

          case 3:
            e_1 = _b.sent();
            this.logger.verbose(e_1);
            return [3
            /*break*/
            , 4];

          case 4:
            this.initialized = true;
            this.eventHandler.emitEvent(EventType.INITIALIZE_END);
            return [2
            /*return*/
            ];
        }
      });
    });
  }; // #region Redirect Flow

  /**
   * Event handler function which allows users to fire events after the PublicClientApplication object
   * has loaded during redirect flows. This should be invoked on all page loads involved in redirect
   * auth flows.
   * @param hash Hash to process. Defaults to the current value of window.location.hash. Only needs to be provided explicitly if the response to be handled is not contained in the current value.
   * @returns Token response or null. If the return value is null, then no auth redirect was detected.
   */


  ClientApplication.prototype.handleRedirectPromise = function (hash) {
    return __awaiter$1(this, void 0, void 0, function () {
      var loggedInAccounts, redirectResponseKey, response, request, redirectResponse, nativeClient, correlationId, redirectClient;

      var _this = this;

      return __generator$1(this, function (_a) {
        this.logger.verbose("handleRedirectPromise called"); // Block token acquisition before initialize has been called if native brokering is enabled

        BrowserUtils.blockNativeBrokerCalledBeforeInitialized(this.config.system.allowNativeBroker, this.initialized);
        loggedInAccounts = this.getAllAccounts();

        if (this.isBrowserEnvironment) {
          redirectResponseKey = hash || Constants.EMPTY_STRING;
          response = this.redirectResponse.get(redirectResponseKey);

          if (typeof response === "undefined") {
            this.eventHandler.emitEvent(EventType.HANDLE_REDIRECT_START, InteractionType.Redirect);
            this.logger.verbose("handleRedirectPromise has been called for the first time, storing the promise");
            request = this.browserStorage.getCachedNativeRequest();
            redirectResponse = void 0;

            if (request && NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeExtensionProvider) && this.nativeExtensionProvider && !hash) {
              this.logger.trace("handleRedirectPromise - acquiring token from native platform");
              nativeClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.handleRedirectPromise, this.performanceClient, this.nativeExtensionProvider, request.accountId, request.correlationId);
              redirectResponse = nativeClient.handleRedirectPromise();
            } else {
              this.logger.trace("handleRedirectPromise - acquiring token from web flow");
              correlationId = this.browserStorage.getTemporaryCache(TemporaryCacheKeys.CORRELATION_ID, true) || Constants.EMPTY_STRING;
              redirectClient = this.createRedirectClient(correlationId);
              redirectResponse = redirectClient.handleRedirectPromise(hash);
            }

            response = redirectResponse.then(function (result) {
              if (result) {
                // Emit login event if number of accounts change
                var isLoggingIn = loggedInAccounts.length < _this.getAllAccounts().length;

                if (isLoggingIn) {
                  _this.eventHandler.emitEvent(EventType.LOGIN_SUCCESS, InteractionType.Redirect, result);

                  _this.logger.verbose("handleRedirectResponse returned result, login success");
                } else {
                  _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_SUCCESS, InteractionType.Redirect, result);

                  _this.logger.verbose("handleRedirectResponse returned result, acquire token success");
                }
              }

              _this.eventHandler.emitEvent(EventType.HANDLE_REDIRECT_END, InteractionType.Redirect);

              return result;
            }).catch(function (e) {
              // Emit login event if there is an account
              if (loggedInAccounts.length > 0) {
                _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_FAILURE, InteractionType.Redirect, null, e);
              } else {
                _this.eventHandler.emitEvent(EventType.LOGIN_FAILURE, InteractionType.Redirect, null, e);
              }

              _this.eventHandler.emitEvent(EventType.HANDLE_REDIRECT_END, InteractionType.Redirect);

              throw e;
            });
            this.redirectResponse.set(redirectResponseKey, response);
          } else {
            this.logger.verbose("handleRedirectPromise has been called previously, returning the result from the first call");
          }

          return [2
          /*return*/
          , response];
        }

        this.logger.verbose("handleRedirectPromise returns null, not browser environment");
        return [2
        /*return*/
        , null];
      });
    });
  };
  /**
   * Use when you want to obtain an access_token for your API by redirecting the user's browser window to the authorization endpoint. This function redirects
   * the page, so any code that follows this function will not execute.
   *
   * IMPORTANT: It is NOT recommended to have code that is dependent on the resolution of the Promise. This function will navigate away from the current
   * browser window. It currently returns a Promise in order to reflect the asynchronous nature of the code running in this function.
   *
   * @param request
   */


  ClientApplication.prototype.acquireTokenRedirect = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId, isLoggedIn, result, nativeClient, redirectClient;

      var _this = this;

      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(request);
        this.logger.verbose("acquireTokenRedirect called", correlationId);
        this.preflightBrowserEnvironmentCheck(InteractionType.Redirect);
        isLoggedIn = this.getAllAccounts().length > 0;

        if (isLoggedIn) {
          this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_START, InteractionType.Redirect, request);
        } else {
          this.eventHandler.emitEvent(EventType.LOGIN_START, InteractionType.Redirect, request);
        }

        if (this.nativeExtensionProvider && this.canUseNative(request)) {
          nativeClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.acquireTokenRedirect, this.performanceClient, this.nativeExtensionProvider, this.getNativeAccountId(request), request.correlationId);
          result = nativeClient.acquireTokenRedirect(request).catch(function (e) {
            if (e instanceof NativeAuthError && e.isFatal()) {
              _this.nativeExtensionProvider = undefined; // If extension gets uninstalled during session prevent future requests from continuing to attempt

              var redirectClient = _this.createRedirectClient(request.correlationId);

              return redirectClient.acquireToken(request);
            }

            throw e;
          });
        } else {
          redirectClient = this.createRedirectClient(request.correlationId);
          result = redirectClient.acquireToken(request);
        }

        return [2
        /*return*/
        , result.catch(function (e) {
          // If logged in, emit acquire token events
          if (isLoggedIn) {
            _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_FAILURE, InteractionType.Redirect, null, e);
          } else {
            _this.eventHandler.emitEvent(EventType.LOGIN_FAILURE, InteractionType.Redirect, null, e);
          }

          throw e;
        })];
      });
    });
  }; // #endregion
  // #region Popup Flow

  /**
   * Use when you want to obtain an access_token for your API via opening a popup window in the user's browser
   *
   * @param request
   *
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  ClientApplication.prototype.acquireTokenPopup = function (request) {
    var _this = this;

    var correlationId = this.getRequestCorrelationId(request);
    var atPopupMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.AcquireTokenPopup, correlationId);

    try {
      this.logger.verbose("acquireTokenPopup called", correlationId);
      this.preflightBrowserEnvironmentCheck(InteractionType.Popup);
    } catch (e) {
      // Since this function is syncronous we need to reject
      return Promise.reject(e);
    } // If logged in, emit acquire token events


    var loggedInAccounts = this.getAllAccounts();

    if (loggedInAccounts.length > 0) {
      this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_START, InteractionType.Popup, request);
    } else {
      this.eventHandler.emitEvent(EventType.LOGIN_START, InteractionType.Popup, request);
    }

    var result;

    if (this.canUseNative(request)) {
      result = this.acquireTokenNative(request, ApiId.acquireTokenPopup).then(function (response) {
        _this.browserStorage.setInteractionInProgress(false);

        atPopupMeasurement.endMeasurement({
          success: true,
          isNativeBroker: true
        });
        atPopupMeasurement.flushMeasurement();
        return response;
      }).catch(function (e) {
        if (e instanceof NativeAuthError && e.isFatal()) {
          _this.nativeExtensionProvider = undefined; // If extension gets uninstalled during session prevent future requests from continuing to attempt

          var popupClient = _this.createPopupClient(request.correlationId);

          return popupClient.acquireToken(request);
        }

        throw e;
      });
    } else {
      var popupClient = this.createPopupClient(request.correlationId);
      result = popupClient.acquireToken(request);
    }

    return result.then(function (result) {
      // If logged in, emit acquire token events
      var isLoggingIn = loggedInAccounts.length < _this.getAllAccounts().length;

      if (isLoggingIn) {
        _this.eventHandler.emitEvent(EventType.LOGIN_SUCCESS, InteractionType.Popup, result);
      } else {
        _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_SUCCESS, InteractionType.Popup, result);
      }

      atPopupMeasurement.endMeasurement({
        success: true
      });
      atPopupMeasurement.flushMeasurement();
      return result;
    }).catch(function (e) {
      if (loggedInAccounts.length > 0) {
        _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_FAILURE, InteractionType.Popup, null, e);
      } else {
        _this.eventHandler.emitEvent(EventType.LOGIN_FAILURE, InteractionType.Popup, null, e);
      }

      atPopupMeasurement.endMeasurement({
        errorCode: e.errorCode,
        subErrorCode: e.subError,
        success: false
      });
      atPopupMeasurement.flushMeasurement(); // Since this function is syncronous we need to reject

      return Promise.reject(e);
    });
  }; // #endregion
  // #region Silent Flow

  /**
   * This function uses a hidden iframe to fetch an authorization code from the eSTS. There are cases where this may not work:
   * - Any browser using a form of Intelligent Tracking Prevention
   * - If there is not an established session with the service
   *
   * In these cases, the request must be done inside a popup or full frame redirect.
   *
   * For the cases where interaction is required, you cannot send a request with prompt=none.
   *
   * If your refresh token has expired, you can use this function to fetch a new set of tokens silently as long as
   * you session on the server still exists.
   * @param request {@link SsoSilentRequest}
   *
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  ClientApplication.prototype.ssoSilent = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId, validRequest, ssoSilentMeasurement, result, silentIframeClient;

      var _this = this;

      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(request);
        validRequest = __assign$1(__assign$1({}, request), {
          prompt: PromptValue.NONE,
          correlationId: correlationId
        });
        this.preflightBrowserEnvironmentCheck(InteractionType.Silent);
        ssoSilentMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.SsoSilent, correlationId);
        this.logger.verbose("ssoSilent called", correlationId);
        this.eventHandler.emitEvent(EventType.SSO_SILENT_START, InteractionType.Silent, validRequest);

        if (this.canUseNative(validRequest)) {
          result = this.acquireTokenNative(validRequest, ApiId.ssoSilent).catch(function (e) {
            // If native token acquisition fails for availability reasons fallback to standard flow
            if (e instanceof NativeAuthError && e.isFatal()) {
              _this.nativeExtensionProvider = undefined; // If extension gets uninstalled during session prevent future requests from continuing to attempt

              var silentIframeClient = _this.createSilentIframeClient(validRequest.correlationId);

              return silentIframeClient.acquireToken(validRequest);
            }

            throw e;
          });
        } else {
          silentIframeClient = this.createSilentIframeClient(validRequest.correlationId);
          result = silentIframeClient.acquireToken(validRequest);
        }

        return [2
        /*return*/
        , result.then(function (response) {
          _this.eventHandler.emitEvent(EventType.SSO_SILENT_SUCCESS, InteractionType.Silent, response);

          ssoSilentMeasurement.endMeasurement({
            success: true,
            isNativeBroker: response.fromNativeBroker
          });
          ssoSilentMeasurement.flushMeasurement();
          return response;
        }).catch(function (e) {
          _this.eventHandler.emitEvent(EventType.SSO_SILENT_FAILURE, InteractionType.Silent, null, e);

          ssoSilentMeasurement.endMeasurement({
            errorCode: e.errorCode,
            subErrorCode: e.subError,
            success: false
          });
          ssoSilentMeasurement.flushMeasurement();
          throw e;
        })];
      });
    });
  };
  /**
   * This function redeems an authorization code (passed as code) from the eSTS token endpoint.
   * This authorization code should be acquired server-side using a confidential client to acquire a spa_code.
   * This API is not indended for normal authorization code acquisition and redemption.
   *
   * Redemption of this authorization code will not require PKCE, as it was acquired by a confidential client.
   *
   * @param request {@link AuthorizationCodeRequest}
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  ClientApplication.prototype.acquireTokenByCode = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId, atbcMeasurement, hybridAuthCode_1, response;

      var _this = this;

      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(request);
        this.preflightBrowserEnvironmentCheck(InteractionType.Silent);
        this.logger.trace("acquireTokenByCode called", correlationId);
        this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_BY_CODE_START, InteractionType.Silent, request);
        atbcMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.AcquireTokenByCode, request.correlationId);

        try {
          if (request.code) {
            hybridAuthCode_1 = request.code;
            response = this.hybridAuthCodeResponses.get(hybridAuthCode_1);

            if (!response) {
              this.logger.verbose("Initiating new acquireTokenByCode request", correlationId);
              response = this.acquireTokenByCodeAsync(__assign$1(__assign$1({}, request), {
                correlationId: correlationId
              })).then(function (result) {
                _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS, InteractionType.Silent, result);

                _this.hybridAuthCodeResponses.delete(hybridAuthCode_1);

                atbcMeasurement.endMeasurement({
                  success: true,
                  isNativeBroker: result.fromNativeBroker
                });
                atbcMeasurement.flushMeasurement();
                return result;
              }).catch(function (error) {
                _this.hybridAuthCodeResponses.delete(hybridAuthCode_1);

                _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE, InteractionType.Silent, null, error);

                atbcMeasurement.endMeasurement({
                  errorCode: error.errorCode,
                  subErrorCode: error.subError,
                  success: false
                });
                atbcMeasurement.flushMeasurement();
                throw error;
              });
              this.hybridAuthCodeResponses.set(hybridAuthCode_1, response);
            } else {
              this.logger.verbose("Existing acquireTokenByCode request found", request.correlationId);
              atbcMeasurement.endMeasurement({
                success: true
              });
              atbcMeasurement.discardMeasurement();
            }

            return [2
            /*return*/
            , response];
          } else if (request.nativeAccountId) {
            if (this.canUseNative(request, request.nativeAccountId)) {
              return [2
              /*return*/
              , this.acquireTokenNative(request, ApiId.acquireTokenByCode, request.nativeAccountId).catch(function (e) {
                // If native token acquisition fails for availability reasons fallback to standard flow
                if (e instanceof NativeAuthError && e.isFatal()) {
                  _this.nativeExtensionProvider = undefined; // If extension gets uninstalled during session prevent future requests from continuing to attempt
                }

                throw e;
              })];
            } else {
              throw BrowserAuthError.createUnableToAcquireTokenFromNativePlatformError();
            }
          } else {
            throw BrowserAuthError.createAuthCodeOrNativeAccountIdRequiredError();
          }
        } catch (e) {
          this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE, InteractionType.Silent, null, e);
          atbcMeasurement.endMeasurement({
            errorCode: e instanceof AuthError && e.errorCode || undefined,
            subErrorCode: e instanceof AuthError && e.subError || undefined,
            success: false
          });
          throw e;
        }

        return [2
        /*return*/
        ];
      });
    });
  };
  /**
   * Creates a SilentAuthCodeClient to redeem an authorization code.
   * @param request
   * @returns Result of the operation to redeem the authorization code
   */


  ClientApplication.prototype.acquireTokenByCodeAsync = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var silentAuthCodeClient, silentTokenResult;
      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.logger.trace("acquireTokenByCodeAsync called", request.correlationId);
            silentAuthCodeClient = this.createSilentAuthCodeClient(request.correlationId);
            return [4
            /*yield*/
            , silentAuthCodeClient.acquireToken(request)];

          case 1:
            silentTokenResult = _a.sent();
            return [2
            /*return*/
            , silentTokenResult];
        }
      });
    });
  };
  /**
   * Use this function to obtain a token before every call to the API / resource provider
   *
   * MSAL return's a cached token when available
   * Or it send's a request to the STS to obtain a new token using a refresh token.
   *
   * @param {@link SilentRequest}
   *
   * To renew idToken, please pass clientId as the only scope in the Authentication Parameters
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  ClientApplication.prototype.acquireTokenByRefreshToken = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var atbrtMeasurement, silentRefreshClient;

      var _this = this;

      return __generator$1(this, function (_a) {
        // block the reload if it occurred inside a hidden iframe
        BrowserUtils.blockReloadInHiddenIframes();
        atbrtMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.AcquireTokenByRefreshToken, request.correlationId);
        this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_NETWORK_START, InteractionType.Silent, request);
        silentRefreshClient = this.createSilentRefreshClient(request.correlationId);
        return [2
        /*return*/
        , silentRefreshClient.acquireToken(request).then(function (result) {
          atbrtMeasurement.endMeasurement({
            success: true,
            fromCache: result.fromCache
          });
          return result;
        }).catch(function (e) {
          var isServerError = e instanceof ServerError;
          var isInteractionRequiredError = e instanceof InteractionRequiredAuthError;
          var isInvalidGrantError = e.errorCode === BrowserConstants.INVALID_GRANT_ERROR;

          if (isServerError && isInvalidGrantError && !isInteractionRequiredError) {
            _this.logger.verbose("Refresh token expired or invalid, attempting acquire token by iframe", request.correlationId);

            var silentIframeClient = _this.createSilentIframeClient(request.correlationId);

            return silentIframeClient.acquireToken(request).then(function (result) {
              atbrtMeasurement.endMeasurement({
                success: true,
                fromCache: result.fromCache
              });
              return result;
            }).catch(function (error) {
              atbrtMeasurement.endMeasurement({
                errorCode: error.errorCode,
                subErrorCode: error.subError,
                success: false
              });
              throw error;
            });
          }

          atbrtMeasurement.endMeasurement({
            success: false
          });
          throw e;
        })];
      });
    });
  }; // #endregion
  // #region Logout

  /**
   * Deprecated logout function. Use logoutRedirect or logoutPopup instead
   * @param logoutRequest
   * @deprecated
   */


  ClientApplication.prototype.logout = function (logoutRequest) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId;
      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(logoutRequest);
        this.logger.warning("logout API is deprecated and will be removed in msal-browser v3.0.0. Use logoutRedirect instead.", correlationId);
        return [2
        /*return*/
        , this.logoutRedirect(__assign$1({
          correlationId: correlationId
        }, logoutRequest))];
      });
    });
  };
  /**
   * Use to log out the current user, and redirect the user to the postLogoutRedirectUri.
   * Default behaviour is to redirect the user to `window.location.href`.
   * @param logoutRequest
   */


  ClientApplication.prototype.logoutRedirect = function (logoutRequest) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId, redirectClient;
      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(logoutRequest);
        this.preflightBrowserEnvironmentCheck(InteractionType.Redirect);
        redirectClient = this.createRedirectClient(correlationId);
        return [2
        /*return*/
        , redirectClient.logout(logoutRequest)];
      });
    });
  };
  /**
   * Clears local cache for the current user then opens a popup window prompting the user to sign-out of the server
   * @param logoutRequest
   */


  ClientApplication.prototype.logoutPopup = function (logoutRequest) {
    try {
      var correlationId = this.getRequestCorrelationId(logoutRequest);
      this.preflightBrowserEnvironmentCheck(InteractionType.Popup);
      var popupClient = this.createPopupClient(correlationId);
      return popupClient.logout(logoutRequest);
    } catch (e) {
      // Since this function is syncronous we need to reject
      return Promise.reject(e);
    }
  }; // #endregion
  // #region Account APIs

  /**
   * Returns all accounts that MSAL currently has data for.
   * (the account object is created at the time of successful login)
   * or empty array when no accounts are found
   * @returns Array of account objects in cache
   */


  ClientApplication.prototype.getAllAccounts = function () {
    this.logger.verbose("getAllAccounts called");
    return this.isBrowserEnvironment ? this.browserStorage.getAllAccounts() : [];
  };
  /**
   * Returns the signed in account matching username.
   * (the account object is created at the time of successful login)
   * or null when no matching account is found.
   * This API is provided for convenience but getAccountById should be used for best reliability
   * @param userName
   * @returns The account object stored in MSAL
   */


  ClientApplication.prototype.getAccountByUsername = function (userName) {
    var allAccounts = this.getAllAccounts();

    if (!StringUtils.isEmpty(userName) && allAccounts && allAccounts.length) {
      this.logger.verbose("Account matching username found, returning");
      this.logger.verbosePii("Returning signed-in accounts matching username: " + userName);
      return allAccounts.filter(function (accountObj) {
        return accountObj.username.toLowerCase() === userName.toLowerCase();
      })[0] || null;
    } else {
      this.logger.verbose("getAccountByUsername: No matching account found, returning null");
      return null;
    }
  };
  /**
   * Returns the signed in account matching homeAccountId.
   * (the account object is created at the time of successful login)
   * or null when no matching account is found
   * @param homeAccountId
   * @returns The account object stored in MSAL
   */


  ClientApplication.prototype.getAccountByHomeId = function (homeAccountId) {
    var allAccounts = this.getAllAccounts();

    if (!StringUtils.isEmpty(homeAccountId) && allAccounts && allAccounts.length) {
      this.logger.verbose("Account matching homeAccountId found, returning");
      this.logger.verbosePii("Returning signed-in accounts matching homeAccountId: " + homeAccountId);
      return allAccounts.filter(function (accountObj) {
        return accountObj.homeAccountId === homeAccountId;
      })[0] || null;
    } else {
      this.logger.verbose("getAccountByHomeId: No matching account found, returning null");
      return null;
    }
  };
  /**
   * Returns the signed in account matching localAccountId.
   * (the account object is created at the time of successful login)
   * or null when no matching account is found
   * @param localAccountId
   * @returns The account object stored in MSAL
   */


  ClientApplication.prototype.getAccountByLocalId = function (localAccountId) {
    var allAccounts = this.getAllAccounts();

    if (!StringUtils.isEmpty(localAccountId) && allAccounts && allAccounts.length) {
      this.logger.verbose("Account matching localAccountId found, returning");
      this.logger.verbosePii("Returning signed-in accounts matching localAccountId: " + localAccountId);
      return allAccounts.filter(function (accountObj) {
        return accountObj.localAccountId === localAccountId;
      })[0] || null;
    } else {
      this.logger.verbose("getAccountByLocalId: No matching account found, returning null");
      return null;
    }
  };
  /**
   * Sets the account to use as the active account. If no account is passed to the acquireToken APIs, then MSAL will use this active account.
   * @param account
   */


  ClientApplication.prototype.setActiveAccount = function (account) {
    this.browserStorage.setActiveAccount(account);
  };
  /**
   * Gets the currently active account
   */


  ClientApplication.prototype.getActiveAccount = function () {
    return this.browserStorage.getActiveAccount();
  }; // #endregion
  // #region Helpers

  /**
   * Helper to validate app environment before making an auth request
   *
   * @protected
   * @param {InteractionType} interactionType What kind of interaction is being used
   * @param {boolean} [setInteractionInProgress=true] Whether to set interaction in progress temp cache flag
   */


  ClientApplication.prototype.preflightBrowserEnvironmentCheck = function (interactionType, setInteractionInProgress) {
    if (setInteractionInProgress === void 0) {
      setInteractionInProgress = true;
    }

    this.logger.verbose("preflightBrowserEnvironmentCheck started"); // Block request if not in browser environment

    BrowserUtils.blockNonBrowserEnvironment(this.isBrowserEnvironment); // Block redirects if in an iframe

    BrowserUtils.blockRedirectInIframe(interactionType, this.config.system.allowRedirectInIframe); // Block auth requests inside a hidden iframe

    BrowserUtils.blockReloadInHiddenIframes(); // Block redirectUri opened in a popup from calling MSAL APIs

    BrowserUtils.blockAcquireTokenInPopups(); // Block token acquisition before initialize has been called if native brokering is enabled

    BrowserUtils.blockNativeBrokerCalledBeforeInitialized(this.config.system.allowNativeBroker, this.initialized); // Block redirects if memory storage is enabled but storeAuthStateInCookie is not

    if (interactionType === InteractionType.Redirect && this.config.cache.cacheLocation === BrowserCacheLocation.MemoryStorage && !this.config.cache.storeAuthStateInCookie) {
      throw BrowserConfigurationAuthError.createInMemoryRedirectUnavailableError();
    }

    if (interactionType === InteractionType.Redirect || interactionType === InteractionType.Popup) {
      this.preflightInteractiveRequest(setInteractionInProgress);
    }
  };
  /**
   * Preflight check for interactive requests
   *
   * @protected
   * @param {boolean} setInteractionInProgress Whether to set interaction in progress temp cache flag
   */


  ClientApplication.prototype.preflightInteractiveRequest = function (setInteractionInProgress) {
    this.logger.verbose("preflightInteractiveRequest called, validating app environment"); // block the reload if it occurred inside a hidden iframe

    BrowserUtils.blockReloadInHiddenIframes(); // Set interaction in progress temporary cache or throw if alread set.

    if (setInteractionInProgress) {
      this.browserStorage.setInteractionInProgress(true);
    }
  };
  /**
   * Acquire a token from native device (e.g. WAM)
   * @param request
   */


  ClientApplication.prototype.acquireTokenNative = function (request, apiId, accountId) {
    return __awaiter$1(this, void 0, void 0, function () {
      var nativeClient;
      return __generator$1(this, function (_a) {
        this.logger.trace("acquireTokenNative called");

        if (!this.nativeExtensionProvider) {
          throw BrowserAuthError.createNativeConnectionNotEstablishedError();
        }

        nativeClient = new NativeInteractionClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, apiId, this.performanceClient, this.nativeExtensionProvider, accountId || this.getNativeAccountId(request), request.correlationId);
        return [2
        /*return*/
        , nativeClient.acquireToken(request)];
      });
    });
  };
  /**
   * Returns boolean indicating if this request can use the native broker
   * @param request
   */


  ClientApplication.prototype.canUseNative = function (request, accountId) {
    this.logger.trace("canUseNative called");

    if (!NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeExtensionProvider, request.authenticationScheme)) {
      this.logger.trace("canUseNative: isNativeAvailable returned false, returning false");
      return false;
    }

    if (request.prompt) {
      switch (request.prompt) {
        case PromptValue.NONE:
        case PromptValue.CONSENT:
          this.logger.trace("canUseNative: prompt is compatible with native flow");
          break;

        default:
          this.logger.trace("canUseNative: prompt = " + request.prompt + " is not compatible with native flow, returning false");
          return false;
      }
    }

    if (!accountId && !this.getNativeAccountId(request)) {
      this.logger.trace("canUseNative: nativeAccountId is not available, returning false");
      return false;
    }

    return true;
  };
  /**
   * Get the native accountId from the account
   * @param request
   * @returns
   */


  ClientApplication.prototype.getNativeAccountId = function (request) {
    var account = request.account || this.browserStorage.getAccountInfoByHints(request.loginHint, request.sid) || this.getActiveAccount();
    return account && account.nativeAccountId || "";
  };
  /**
   * Returns new instance of the Popup Interaction Client
   * @param correlationId
   */


  ClientApplication.prototype.createPopupClient = function (correlationId) {
    return new PopupClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Returns new instance of the Popup Interaction Client
   * @param correlationId
   */


  ClientApplication.prototype.createRedirectClient = function (correlationId) {
    return new RedirectClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Returns new instance of the Silent Iframe Interaction Client
   * @param correlationId
   */


  ClientApplication.prototype.createSilentIframeClient = function (correlationId) {
    return new SilentIframeClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.ssoSilent, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Returns new instance of the Silent Cache Interaction Client
   */


  ClientApplication.prototype.createSilentCacheClient = function (correlationId) {
    return new SilentCacheClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Returns new instance of the Silent Refresh Interaction Client
   */


  ClientApplication.prototype.createSilentRefreshClient = function (correlationId) {
    return new SilentRefreshClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Returns new instance of the Silent AuthCode Interaction Client
   */


  ClientApplication.prototype.createSilentAuthCodeClient = function (correlationId) {
    return new SilentAuthCodeClient(this.config, this.browserStorage, this.browserCrypto, this.logger, this.eventHandler, this.navigationClient, ApiId.acquireTokenByCode, this.performanceClient, this.nativeExtensionProvider, correlationId);
  };
  /**
   * Adds event callbacks to array
   * @param callback
   */


  ClientApplication.prototype.addEventCallback = function (callback) {
    return this.eventHandler.addEventCallback(callback);
  };
  /**
   * Removes callback with provided id from callback array
   * @param callbackId
   */


  ClientApplication.prototype.removeEventCallback = function (callbackId) {
    this.eventHandler.removeEventCallback(callbackId);
  };
  /**
   * Registers a callback to receive performance events.
   *
   * @param {PerformanceCallbackFunction} callback
   * @returns {string}
   */


  ClientApplication.prototype.addPerformanceCallback = function (callback) {
    return this.performanceClient.addPerformanceCallback(callback);
  };
  /**
   * Removes a callback registered with addPerformanceCallback.
   *
   * @param {string} callbackId
   * @returns {boolean}
   */


  ClientApplication.prototype.removePerformanceCallback = function (callbackId) {
    return this.performanceClient.removePerformanceCallback(callbackId);
  };
  /**
   * Adds event listener that emits an event when a user account is added or removed from localstorage in a different browser tab or window
   */


  ClientApplication.prototype.enableAccountStorageEvents = function () {
    this.eventHandler.enableAccountStorageEvents();
  };
  /**
   * Removes event listener that emits an event when a user account is added or removed from localstorage in a different browser tab or window
   */


  ClientApplication.prototype.disableAccountStorageEvents = function () {
    this.eventHandler.disableAccountStorageEvents();
  };
  /**
   * Gets the token cache for the application.
   */


  ClientApplication.prototype.getTokenCache = function () {
    return this.tokenCache;
  };
  /**
   * Returns the logger instance
   */


  ClientApplication.prototype.getLogger = function () {
    return this.logger;
  };
  /**
   * Replaces the default logger set in configurations with new Logger with new configurations
   * @param logger Logger instance
   */


  ClientApplication.prototype.setLogger = function (logger) {
    this.logger = logger;
  };
  /**
   * Called by wrapper libraries (Angular & React) to set SKU and Version passed down to telemetry, logger, etc.
   * @param sku
   * @param version
   */


  ClientApplication.prototype.initializeWrapperLibrary = function (sku, version) {
    // Validate the SKU passed in is one we expect
    this.browserStorage.setWrapperMetadata(sku, version);
  };
  /**
   * Sets navigation client
   * @param navigationClient
   */


  ClientApplication.prototype.setNavigationClient = function (navigationClient) {
    this.navigationClient = navigationClient;
  };
  /**
   * Returns the configuration object
   */


  ClientApplication.prototype.getConfiguration = function () {
    return this.config;
  };
  /**
   * Generates a correlation id for a request if none is provided.
   *
   * @protected
   * @param {?Partial<BaseAuthRequest>} [request]
   * @returns {string}
   */


  ClientApplication.prototype.getRequestCorrelationId = function (request) {
    if (request === null || request === void 0 ? void 0 : request.correlationId) {
      return request.correlationId;
    }

    if (this.isBrowserEnvironment) {
      return this.browserCrypto.createNewGuid();
    }
    /*
     * Included for fallback for non-browser environments,
     * and to ensure this method always returns a string.
     */


    return Constants.EMPTY_STRING;
  };

  return ClientApplication;
}();

/*! @azure/msal-browser v2.26.0 2022-06-13 */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/**
 * The PublicClientApplication class is the object exposed by the library to perform authentication and authorization functions in Single Page Applications
 * to obtain JWT tokens as described in the OAuth 2.0 Authorization Code Flow with PKCE specification.
 */

var PublicClientApplication =
/** @class */
function (_super) {
  __extends$1(PublicClientApplication, _super);
  /**
   * @constructor
   * Constructor for the PublicClientApplication used to instantiate the PublicClientApplication object
   *
   * Important attributes in the Configuration object for auth are:
   * - clientID: the application ID of your application. You can obtain one by registering your application with our Application registration portal : https://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredAppsPreview
   * - authority: the authority URL for your application.
   * - redirect_uri: the uri of your application registered in the portal.
   *
   * In Azure AD, authority is a URL indicating the Azure active directory that MSAL uses to obtain tokens.
   * It is of the form https://login.microsoftonline.com/{Enter_the_Tenant_Info_Here}
   * If your application supports Accounts in one organizational directory, replace "Enter_the_Tenant_Info_Here" value with the Tenant Id or Tenant name (for example, contoso.microsoft.com).
   * If your application supports Accounts in any organizational directory, replace "Enter_the_Tenant_Info_Here" value with organizations.
   * If your application supports Accounts in any organizational directory and personal Microsoft accounts, replace "Enter_the_Tenant_Info_Here" value with common.
   * To restrict support to Personal Microsoft accounts only, replace "Enter_the_Tenant_Info_Here" value with consumers.
   *
   * In Azure B2C, authority is of the form https://{instance}/tfp/{tenant}/{policyName}/
   * Full B2C functionality will be available in this library in future versions.
   *
   * @param configuration object for the MSAL PublicClientApplication instance
   */


  function PublicClientApplication(configuration) {
    var _this = _super.call(this, configuration) || this;

    _this.activeSilentTokenRequests = new Map();
    return _this;
  }
  /**
   * Use when initiating the login process by redirecting the user's browser to the authorization endpoint. This function redirects the page, so
   * any code that follows this function will not execute.
   *
   * IMPORTANT: It is NOT recommended to have code that is dependent on the resolution of the Promise. This function will navigate away from the current
   * browser window. It currently returns a Promise in order to reflect the asynchronous nature of the code running in this function.
   *
   * @param request
   */


  PublicClientApplication.prototype.loginRedirect = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId;
      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(request);
        this.logger.verbose("loginRedirect called", correlationId);
        return [2
        /*return*/
        , this.acquireTokenRedirect(__assign$1({
          correlationId: correlationId
        }, request || DEFAULT_REQUEST))];
      });
    });
  };
  /**
   * Use when initiating the login process via opening a popup window in the user's browser
   *
   * @param request
   *
   * @returns A promise that is fulfilled when this function has completed, or rejected if an error was raised.
   */


  PublicClientApplication.prototype.loginPopup = function (request) {
    var correlationId = this.getRequestCorrelationId(request);
    this.logger.verbose("loginPopup called", correlationId);
    return this.acquireTokenPopup(__assign$1({
      correlationId: correlationId
    }, request || DEFAULT_REQUEST));
  };
  /**
   * Silently acquire an access token for a given set of scopes. Returns currently processing promise if parallel requests are made.
   *
   * @param {@link (SilentRequest:type)}
   * @returns {Promise.<AuthenticationResult>} - a promise that is fulfilled when this function has completed, or rejected if an error was raised. Returns the {@link AuthResponse} object
   */


  PublicClientApplication.prototype.acquireTokenSilent = function (request) {
    return __awaiter$1(this, void 0, void 0, function () {
      var correlationId, atsMeasurement, account, thumbprint, silentRequestKey, cachedResponse, response;

      var _this = this;

      return __generator$1(this, function (_a) {
        correlationId = this.getRequestCorrelationId(request);
        atsMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.AcquireTokenSilent, correlationId);
        this.preflightBrowserEnvironmentCheck(InteractionType.Silent);
        this.logger.verbose("acquireTokenSilent called", correlationId);
        account = request.account || this.getActiveAccount();

        if (!account) {
          throw BrowserAuthError.createNoAccountError();
        }

        thumbprint = {
          clientId: this.config.auth.clientId,
          authority: request.authority || Constants.EMPTY_STRING,
          scopes: request.scopes,
          homeAccountIdentifier: account.homeAccountId,
          claims: request.claims,
          authenticationScheme: request.authenticationScheme,
          resourceRequestMethod: request.resourceRequestMethod,
          resourceRequestUri: request.resourceRequestUri,
          shrClaims: request.shrClaims,
          sshKid: request.sshKid
        };
        silentRequestKey = JSON.stringify(thumbprint);
        cachedResponse = this.activeSilentTokenRequests.get(silentRequestKey);

        if (typeof cachedResponse === "undefined") {
          this.logger.verbose("acquireTokenSilent called for the first time, storing active request", correlationId);
          response = this.acquireTokenSilentAsync(__assign$1(__assign$1({}, request), {
            correlationId: correlationId
          }), account).then(function (result) {
            _this.activeSilentTokenRequests.delete(silentRequestKey);

            atsMeasurement.endMeasurement({
              success: true,
              fromCache: result.fromCache,
              isNativeBroker: result.fromNativeBroker
            });
            atsMeasurement.flushMeasurement();
            return result;
          }).catch(function (error) {
            _this.activeSilentTokenRequests.delete(silentRequestKey);

            atsMeasurement.endMeasurement({
              errorCode: error.errorCode,
              subErrorCode: error.subError,
              success: false
            });
            atsMeasurement.flushMeasurement();
            throw error;
          });
          this.activeSilentTokenRequests.set(silentRequestKey, response);
          return [2
          /*return*/
          , response];
        } else {
          this.logger.verbose("acquireTokenSilent has been called previously, returning the result from the first call", correlationId);
          atsMeasurement.endMeasurement({
            success: true
          }); // Discard measurements for memoized calls, as they are usually only a couple of ms and will artificially deflate metrics

          atsMeasurement.discardMeasurement();
          return [2
          /*return*/
          , cachedResponse];
        }
      });
    });
  };
  /**
   * Silently acquire an access token for a given set of scopes. Will use cached token if available, otherwise will attempt to acquire a new token from the network via refresh token.
   * @param {@link (SilentRequest:type)}
   * @param {@link (AccountInfo:type)}
   * @returns {Promise.<AuthenticationResult>} - a promise that is fulfilled when this function has completed, or rejected if an error was raised. Returns the {@link AuthResponse}
   */


  PublicClientApplication.prototype.acquireTokenSilentAsync = function (request, account) {
    return __awaiter$1(this, void 0, void 0, function () {
      var astsAsyncMeasurement, result, silentRequest, silentCacheClient, silentRequest_1;

      var _this = this;

      return __generator$1(this, function (_a) {
        switch (_a.label) {
          case 0:
            this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_START, InteractionType.Silent, request);
            astsAsyncMeasurement = this.performanceClient.startMeasurement(PerformanceEvents.AcquireTokenSilentAsync, request.correlationId);
            if (!(NativeMessageHandler.isNativeAvailable(this.config, this.logger, this.nativeExtensionProvider, request.authenticationScheme) && account.nativeAccountId)) return [3
            /*break*/
            , 1];
            this.logger.verbose("acquireTokenSilent - attempting to acquire token from native platform");
            silentRequest = __assign$1(__assign$1({}, request), {
              account: account
            });
            result = this.acquireTokenNative(silentRequest, ApiId.acquireTokenSilent_silentFlow).catch(function (e) {
              return __awaiter$1(_this, void 0, void 0, function () {
                var silentIframeClient;
                return __generator$1(this, function (_a) {
                  // If native token acquisition fails for availability reasons fallback to web flow
                  if (e instanceof NativeAuthError && e.isFatal()) {
                    this.logger.verbose("acquireTokenSilent - native platform unavailable, falling back to web flow");
                    this.nativeExtensionProvider = undefined; // Prevent future requests from continuing to attempt

                    silentIframeClient = this.createSilentIframeClient(request.correlationId);
                    return [2
                    /*return*/
                    , silentIframeClient.acquireToken(request)];
                  }

                  throw e;
                });
              });
            });
            return [3
            /*break*/
            , 3];

          case 1:
            this.logger.verbose("acquireTokenSilent - attempting to acquire token from web flow");
            silentCacheClient = this.createSilentCacheClient(request.correlationId);
            return [4
            /*yield*/
            , silentCacheClient.initializeSilentRequest(request, account)];

          case 2:
            silentRequest_1 = _a.sent();
            result = silentCacheClient.acquireToken(silentRequest_1).catch(function () {
              return __awaiter$1(_this, void 0, void 0, function () {
                return __generator$1(this, function (_a) {
                  return [2
                  /*return*/
                  , this.acquireTokenByRefreshToken(silentRequest_1)];
                });
              });
            });
            _a.label = 3;

          case 3:
            return [2
            /*return*/
            , result.then(function (response) {
              _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_SUCCESS, InteractionType.Silent, response);

              astsAsyncMeasurement.endMeasurement({
                success: true,
                fromCache: response.fromCache,
                isNativeBroker: response.fromNativeBroker
              });
              return response;
            }).catch(function (tokenRenewalError) {
              _this.eventHandler.emitEvent(EventType.ACQUIRE_TOKEN_FAILURE, InteractionType.Silent, null, tokenRenewalError);

              astsAsyncMeasurement.endMeasurement({
                errorCode: tokenRenewalError.errorCode,
                subErrorCode: tokenRenewalError.subError,
                success: false
              });
              throw tokenRenewalError;
            })];
        }
      });
    });
  };

  return PublicClientApplication;
}(ClientApplication);

/**
 * @returns {string | null}
 */

function getToken() {
  return window[_setting.tokenLocation].getItem(_setting.tokenKey);
}
function setToken(token) {
  return window[_setting.tokenLocation].setItem(_setting.tokenKey, token);
}
function removeToken() {
  return window[_setting.tokenLocation].removeItem(_setting.tokenKey);
}
/**
 * 判断是否登陆过
 * @returns {boolean}
 */

function isLogin() {
  if (_setting.isESO) {
    return localStorage.getExpire('msal.authorization') !== null;
  } else {
    return getToken() !== null;
  }
}
function logOut() {
  removeToken();
  window.location.href = _setting.loginUrl;
  setTimeout(function () {
    location.reload();
  }, 100);
}

var GRAPH_SCOPES = {
  OPENID: "openid",
  PROFILE: "profile",
  USER_READ: "User.Read",
  MAIL_READ: "Mail.Read",
  OBOAPI_DEFAULT: 'OboApi/.default',
  OFFLINE_ACCESS: 'offline_access'
};
var GRAPH_REQUESTS = {
  LOGIN: {
    scopes: [GRAPH_SCOPES.OPENID, GRAPH_SCOPES.PROFILE, GRAPH_SCOPES.USER_READ]
  },
  EMAIL: {
    scopes: [GRAPH_SCOPES.MAIL_READ]
  }
};

var isIE = function isIE() {
  var ua = window.navigator.userAgent;
  var msie = ua.indexOf("MSIE ") > -1;
  var msie11 = ua.indexOf("Trident/") > -1; // If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
  // const isEdge = ua.indexOf("Edge/") > -1;

  return msie || msie11;
};

var provider = /*#__PURE__*/function () {
  function provider() {
    _classCallCheck(this, provider);
  }

  _createClass(provider, [{
    key: "getToken",
    value: function () {
      var _getToken = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                throw new Error('在此处实现你的获取token逻辑');

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      function getToken() {
        return _getToken.apply(this, arguments);
      }

      return getToken;
    }()
  }, {
    key: "signIn",
    value: function signIn() {
      throw new Error('在此处实现登录逻辑');
    }
  }, {
    key: "signOut",
    value: function signOut() {
      throw new Error('在此处实现登出逻辑');
    }
  }]);

  return provider;
}();

var AuthProvider = /*#__PURE__*/function (_provider) {
  _inherits(AuthProvider, _provider);

  var _super = _createSuper(AuthProvider);

  function AuthProvider(opt) {
    var _this;

    _classCallCheck(this, AuthProvider);

    _this = _super.call(this);
    _this.opt = opt;
    _this.msalApp = null;
    return _this;
  }

  _createClass(AuthProvider, [{
    key: "initialize",
    value: function () {
      var _initialize = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
        var resp, account, errorMessage;
        return _regeneratorRuntime().wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                //如何客户端设置eso参数，覆盖默认参数
                this.opt = location.href.indexOf('dev.myops.cdc.accenture') > -1 ? _setting.eso.stage : _setting.eso.prod;

                if (!(this.msalApp === null)) {
                  _context2.next = 33;
                  break;
                }

                this.msalApp = new PublicClientApplication({
                  auth: {
                    clientId: this.opt.clientId,
                    authority: "https://login.microsoftonline.com/" + this.opt.tenantId,
                    redirectUri: this.opt.redirectUri,
                    validateAuthority: true,
                    navigateToLoginRequestUrl: false
                  },
                  cache: {
                    cacheLocation: "localStorage",
                    storeAuthStateInCookie: isIE()
                  }
                });
                _context2.prev = 3;
                _context2.next = 6;
                return this.msalApp.handleRedirectPromise();

              case 6:
                resp = _context2.sent;

                if (!(resp !== null)) {
                  _context2.next = 17;
                  break;
                }

                this.msalApp.setActiveAccount(resp.account);

                if (!resp.state) {
                  _context2.next = 14;
                  break;
                }

                location.replace(resp.state);
                return _context2.abrupt("return", true);

              case 14:
                return _context2.abrupt("return", true);

              case 15:
                _context2.next = 26;
                break;

              case 17:
                account = this.msalApp.getActiveAccount();

                if (account) {
                  _context2.next = 25;
                  break;
                }

                console.log('没有账户开始走eso的login了');
                _context2.next = 22;
                return this.msalApp.loginRedirect(_objectSpread2(_objectSpread2({}, GRAPH_REQUESTS.LOGIN), {}, {
                  state: location.href
                }));

              case 22:
                return _context2.abrupt("return", false);

              case 25:
                return _context2.abrupt("return", true);

              case 26:
                _context2.next = 31;
                break;

              case 28:
                _context2.prev = 28;
                _context2.t0 = _context2["catch"](3);

                if (_context2.t0) {
                  errorMessage = _context2.t0.errorMessage ? _context2.t0.errorMessage : "Unable to acquire access token.";
                  console.error(errorMessage);
                  console.log('msalApp.handleRedirectCallback.error:' + _context2.t0.errorMessage);
                } else {
                  console.log('msalApp.handleRedirectCallback.success');
                }

              case 31:
                _context2.next = 34;
                break;

              case 33:
                return _context2.abrupt("return", true);

              case 34:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this, [[3, 28]]);
      }));

      function initialize() {
        return _initialize.apply(this, arguments);
      }

      return initialize;
    }()
  }, {
    key: "requiresInteraction",
    value: function requiresInteraction(errorMessage) {
      if (!errorMessage || !errorMessage.length) {
        return false;
      }

      return errorMessage.indexOf("consent_required") > -1 || errorMessage.indexOf("interaction_required") > -1 || errorMessage.indexOf("login_required") > -1 || errorMessage.indexOf("monitor_window_timeout") > -1;
    }
  }, {
    key: "acquireToken",
    value: function () {
      var _acquireToken = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4() {
        var _this2 = this;

        var request,
            tokenResponse,
            _args4 = arguments;
        return _regeneratorRuntime().wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                request = _args4.length > 0 && _args4[0] !== undefined ? _args4[0] : GRAPH_REQUESTS.LOGIN;
                request.account = this.msalApp.getActiveAccount();
                _context4.next = 4;
                return this.msalApp.acquireTokenSilent(request)["catch"]( /*#__PURE__*/function () {
                  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(error) {
                    return _regeneratorRuntime().wrap(function _callee3$(_context3) {
                      while (1) {
                        switch (_context3.prev = _context3.next) {
                          case 0:
                            if (!_this2.requiresInteraction(error.errorCode)) {
                              _context3.next = 4;
                              break;
                            }

                            return _context3.abrupt("return", _this2.msalApp.acquireTokenRedirect(_objectSpread2(_objectSpread2({}, request), {}, {
                              state: location.href
                            })));

                          case 4:
                            console.error('Non-interactive error:', error.errorCode);
                            return _context3.abrupt("return", false);

                          case 6:
                          case "end":
                            return _context3.stop();
                        }
                      }
                    }, _callee3);
                  }));

                  return function (_x) {
                    return _ref.apply(this, arguments);
                  };
                }());

              case 4:
                tokenResponse = _context4.sent;
                return _context4.abrupt("return", tokenResponse);

              case 6:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      function acquireToken() {
        return _acquireToken.apply(this, arguments);
      }

      return acquireToken;
    }()
  }, {
    key: "signIn",
    value: function signIn() {
      if (!isLogin()) {
        eventBus.$emit('authorization');
        return false;
      } else {
        return true;
      }
    }
  }, {
    key: "signOut",
    value: function signOut() {
      this.msalApp.logout();
    }
  }, {
    key: "getToken",
    value: function () {
      var _getToken2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee5() {
        var tokenResponse;
        return _regeneratorRuntime().wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return this.acquireToken();

              case 2:
                tokenResponse = _context5.sent;

                if (!tokenResponse) {
                  _context5.next = 7;
                  break;
                }

                return _context5.abrupt("return", tokenResponse.accessToken);

              case 7:
                return _context5.abrupt("return", null);

              case 8:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      function getToken() {
        return _getToken2.apply(this, arguments);
      }

      return getToken;
    }()
  }]);

  return AuthProvider;
}(provider);

var setting = location.href.indexOf('dev.myops.cdc.accenture') > -1 ? _setting.eso.stage : _setting.eso.prod;
var authV2 = new AuthProvider(setting);

function setAuthProvide(auth) {
  authV2 = auth;
}

/**
 * 根据权限id判断是否有对应权限
 * @param permissionId
 * @returns boolean
 */

function hasPermission(permissionId) {
  var permission = store.state.user.permission;
  return permission && permission.find(function (item) {
    return item.repositoryId === permissionId;
  });
}
/**
 * 判断用户是否用对应角色
 * @param filter 过滤函数 参数：role  return：bool
 * @returns {boolean}
 */

function hasRole() {
  var filter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : function () {};
  var roles = store.state.user.user.roles;
  return roles.filter(function (t) {
    return filter(t);
  }).length > 0;
}

function routerLog(route) {
  var data = {
    businessId: route.path,
    //路由的全名
    createByName: store.state.user.user.eid,
    //eid
    createBy: store.state.user.user.personnelNumber,
    // personalNumber
    businessType: '访问记录',
    //常量：访问记录
    businessAction: route.meta && typeof route.meta.title === 'function' ? route.meta.title(route.query) : route.meta.title,
    appType: _setting.appName,
    // 所属系统名称
    actionData: {
      query: route.query
    }
  };
  log(data);
}

var moduleLoadStatus = new Set();
function judgePermission(_x, _x2) {
  return _judgePermission.apply(this, arguments);
}

function _judgePermission() {
  _judgePermission = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(to, next) {
    var hasAccount, _to$path$replace$spli, _to$path$replace$spli2, moduleName, user, dispatch;

    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!_setting.isESO) {
              _context.next = 6;
              break;
            }

            _context.next = 3;
            return authV2.initialize();

          case 3:
            hasAccount = _context.sent;

            if (hasAccount) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return");

          case 6:
            _to$path$replace$spli = to.path.replace(/^\//g, '').split('/'), _to$path$replace$spli2 = _slicedToArray(_to$path$replace$spli, 1), moduleName = _to$path$replace$spli2[0];
            user = store.state.user.user, dispatch = store.dispatch;

            if (user.eid) {
              _context.next = 11;
              break;
            }

            _context.next = 11;
            return dispatch('user/getUserInfo');

          case 11:
            if (!moduleLoadStatus.has(moduleName)) {
              _context.next = 19;
              break;
            }

            if (to.matched.filter(function (t) {
              return t.meta && t.meta.permissionId;
            }).every(function (item) {
              return hasPermission(item.meta && item.meta.permissionId);
            })) {
              _context.next = 15;
              break;
            }

            if (_setting.noPermissionUrl) next(_setting.noPermissionUrl);else alert('您暂时没权限访问该功能!');
            return _context.abrupt("return");

          case 15:
            _setting.routeLog && routerLog(to);
            next();
            _context.next = 24;
            break;

          case 19:
            _context.next = 21;
            return dispatch('user/getPermissions', {
              moduleName: moduleName
            });

          case 21:
            moduleLoadStatus.add(moduleName);
            _context.next = 24;
            return judgePermission(to, next);

          case 24:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _judgePermission.apply(this, arguments);
}

var _service$interceptors;

var service = axios.create({
  withCredentials: false // 请求是否带cookie

});

function onError(response) {
  switch (response.data.code) {
    case 401:
      eventBus.$emit('authorization');
      break;
  }
}

var responseHandler = [function (response) {
  onError(response);
  return response;
}, function (error) {
  if (!error.response) {
    console.error(error);
    return Promise.reject(error);
  }

  switch (error.response.status) {
    /**
     * 未验证
     * 1. 用户未登录, 没有token
     * 2. 已有token失效
     */
    case 401:
      // 如果login 401
      eventBus.$emit('authorization');
      break;

    default:
      eventBus.$emit("requestError", error.response);
      console.error(error);
      break;
  }

  return Promise.reject(error);
}];

(_service$interceptors = service.interceptors.response).use.apply(_service$interceptors, responseHandler);

service.interceptors.request.use( /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(config) {
    var accessToken, idToken, tokenResponse;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            accessToken = '';
            idToken = '';

            if (!_setting.isESO) {
              _context.next = 14;
              break;
            }

            _context.next = 5;
            return authV2.acquireToken();

          case 5:
            tokenResponse = _context.sent;

            if (!tokenResponse) {
              _context.next = 11;
              break;
            }

            accessToken = tokenResponse.accessToken;
            idToken = "Bearer ".concat(tokenResponse.idToken);
            _context.next = 12;
            break;

          case 11:
            return _context.abrupt("return", null);

          case 12:
            _context.next = 15;
            break;

          case 14:
            accessToken = getToken();

          case 15:
            config.timeout = _setting.request.timeout;
            config.baseURL = _setting.request.base_url; // 为get请求添加时间戳,为了避免ie下get请求缓存

            if (config.method === 'get') {
              config.params = config.params || {};

              if (typeof config.params === 'string') {
                config.url += '?t=' + new Date().getTime();
              } else {
                config.params['t'] = new Date().getTime();
              }
            } // 发送request之前拦截请求,并添加token


            config.headers['Authorization'] = idToken;
            config.headers[_setting.request.tokenId] = accessToken;
            config.headers['Token-Type'] = _setting.request.tokenType;
            config.headers['language'] = localStorage.getItem('language') || 'en'; // 客户端IP

            config.headers['common'] = {
              Pragma: 'no-cache',
              'Cache-Control': 'no-cache'
            }; // config.startTime = new Date()

            return _context.abrupt("return", config);

          case 24:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x) {
    return _ref.apply(this, arguments);
  };
}(), function (error) {
  // 请求错误处理
  console.error(error); // for debug

  Promise.reject(error);
});

var baseProxy = /*#__PURE__*/function () {
  function baseProxy() {
    var req = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : service;

    _classCallCheck(this, baseProxy);

    this.request = req;
  }
  /**
   * 开发环境获取token
   * @param eid
   * @param password
   * @returns {Promise<{token:string}>}
   */


  _createClass(baseProxy, [{
    key: "login",
    value: function () {
      var _login = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _ref.eid, _ref.password;
                throw new Error('require to implement login api');

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      function login(_x) {
        return _login.apply(this, arguments);
      }

      return login;
    }()
    /**
     *通过代理获取用户信息
     * @returns {Promise<{fullname:string,eid:string,personnelNumber:string}>}
     */

  }, {
    key: "getUserInfo",
    value: function () {
      var _getUserInfo = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
        return _regeneratorRuntime().wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                throw new Error('require to implement getUserInfo api');

              case 1:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      function getUserInfo() {
        return _getUserInfo.apply(this, arguments);
      }

      return getUserInfo;
    }()
  }, {
    key: "getDicByArray",
    value: function () {
      var _getDicByArray = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(catagroyArray) {
        return _regeneratorRuntime().wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                throw new Error('require to implement getDicByArray api');

              case 1:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      function getDicByArray(_x2) {
        return _getDicByArray.apply(this, arguments);
      }

      return getDicByArray;
    }()
    /**
     *
     * @param moduleName
     * @returns {Promise<Array<{repositoryId:string}>>}
     */

  }, {
    key: "getPermission",
    value: function () {
      var _getPermission = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4(moduleName) {
        return _regeneratorRuntime().wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                throw new Error('require to implement getPermission api');

              case 1:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      function getPermission(_x3) {
        return _getPermission.apply(this, arguments);
      }

      return getPermission;
    }()
  }]);

  return baseProxy;
}();

var dataProxy$1 = /*#__PURE__*/function (_baseProxy) {
  _inherits(dataProxy, _baseProxy);

  var _super = _createSuper(dataProxy);

  function dataProxy(request) {
    _classCallCheck(this, dataProxy);

    return _super.call(this, request);
  }

  _createClass(dataProxy, [{
    key: "login",
    value: function () {
      var _login = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
        var eid, password, _yield$this$request, _yield$this$request$d, code, data, msg;

        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                eid = _ref.eid, password = _ref.password;
                _context.next = 3;
                return this.request({
                  url: "api/login",
                  method: "post",
                  data: {
                    eid: eid,
                    password: password
                  }
                });

              case 3:
                _yield$this$request = _context.sent;
                _yield$this$request$d = _yield$this$request.data;
                code = _yield$this$request$d.code;
                data = _yield$this$request$d.data;
                msg = _yield$this$request$d.msg;

                if (!(code === '0')) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return", data);

              case 12:
                throw new Error(msg);

              case 13:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      function login(_x) {
        return _login.apply(this, arguments);
      }

      return login;
    }()
  }, {
    key: "getUserInfo",
    value: function () {
      var _getUserInfo = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
        var _yield$this$request2, _yield$this$request2$, code, data, msg;

        return _regeneratorRuntime().wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.request({
                  url: "api/user/info",
                  method: 'get'
                });

              case 2:
                _yield$this$request2 = _context2.sent;
                _yield$this$request2$ = _yield$this$request2.data;
                code = _yield$this$request2$.code;
                data = _yield$this$request2$.data;
                msg = _yield$this$request2$.msg;

                if (!(code === '0')) {
                  _context2.next = 11;
                  break;
                }

                return _context2.abrupt("return", data);

              case 11:
                throw new Error(msg);

              case 12:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function getUserInfo() {
        return _getUserInfo.apply(this, arguments);
      }

      return getUserInfo;
    }()
  }, {
    key: "getDicByArray",
    value: function () {
      var _getDicByArray = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(catagroyArray) {
        var _yield$this$request3, _yield$this$request3$, code, data, msg;

        return _regeneratorRuntime().wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.request({
                  url: "api/dictionary",
                  data: catagroyArray,
                  method: 'post'
                });

              case 2:
                _yield$this$request3 = _context3.sent;
                _yield$this$request3$ = _yield$this$request3.data;
                code = _yield$this$request3$.code;
                data = _yield$this$request3$.data;
                msg = _yield$this$request3$.msg;

                if (!(code === '0')) {
                  _context3.next = 11;
                  break;
                }

                return _context3.abrupt("return", data);

              case 11:
                throw new Error(msg);

              case 12:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      function getDicByArray(_x2) {
        return _getDicByArray.apply(this, arguments);
      }

      return getDicByArray;
    }()
  }, {
    key: "getPermission",
    value: function () {
      var _getPermission = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4(moduleName) {
        var _yield$this$request4, _yield$this$request4$, code, data, msg;

        return _regeneratorRuntime().wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.request({
                  url: "api/user/myPageAndButton",
                  data: {
                    moduleName: moduleName
                  },
                  method: 'post'
                });

              case 2:
                _yield$this$request4 = _context4.sent;
                _yield$this$request4$ = _yield$this$request4.data;
                code = _yield$this$request4$.code;
                data = _yield$this$request4$.data;
                msg = _yield$this$request4$.msg;

                if (!(code === '0')) {
                  _context4.next = 11;
                  break;
                }

                return _context4.abrupt("return", data);

              case 11:
                throw new Error(msg);

              case 12:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      function getPermission(_x3) {
        return _getPermission.apply(this, arguments);
      }

      return getPermission;
    }()
  }]);

  return dataProxy;
}(baseProxy);

var dataProxy = new dataProxy$1();
function setDataProxy(proxy) {
  dataProxy = proxy;
}
function getDataProxy() {
  return dataProxy;
}

var required = function required(value) {
  return value && value.length ? true : 'This field is required';
};
var emailValidator = function emailValidator(value) {
  if (isEmpty(value)) {
    return true;
  } // eslint-disable-next-line


  var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (Array.isArray(value)) {
    return value.every(function (val) {
      return re.test(String(val));
    });
  }

  return re.test(String(value)) || 'The Email field must be a valid email';
};
var passwordValidator = function passwordValidator(password) {
  /* eslint-disable no-useless-escape */
  var regExp = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/;
  /* eslint-enable no-useless-escape */

  var validPassword = regExp.test(password);
  return (// eslint-disable-next-line operator-linebreak
    validPassword || 'Field must contain at least one uppercase, lowercase, special character and digit with min 8 chars'
  );
};
var confirmedValidator = function confirmedValidator(value, target) {
  return (// eslint-disable-next-line implicit-arrow-linebreak
    value === target || 'The Confirm Password field confirmation does not match'
  );
};
var between = function between(value, min, max) {
  return function () {
    var valueAsNumber = Number(value);
    return Number(min) <= valueAsNumber && Number(max) >= valueAsNumber || "Enter number between ".concat(min, " and ").concat(max);
  };
};
var integerValidator = function integerValidator(value) {
  if (isEmpty(value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(function (val) {
      return /^-?[0-9]+$/.test(String(val));
    });
  }

  return /^-?[0-9]+$/.test(String(value)) || 'This field must be an integer';
};
var regexValidator = function regexValidator(value, regex) {
  if (isEmpty(value)) {
    return true;
  }

  var regeX = regex;

  if (typeof regeX === 'string') {
    regeX = new RegExp(regeX);
  }

  if (Array.isArray(value)) {
    return value.every(function (val) {
      return regexValidator(val, {
        regeX: regeX
      });
    });
  }

  return regeX.test(String(value)) || 'The Regex field format is invalid';
};
var alphaValidator = function alphaValidator(value) {
  if (isEmpty(value)) {
    return true;
  } // const valueAsString = String(value)


  return /^[A-Z]*$/i.test(String(value)) || 'The Alpha field may only contain alphabetic characters';
};
var urlValidator = function urlValidator(value) {
  if (value === undefined || value === null || value.length === 0) {
    return true;
  }
  /* eslint-disable no-useless-escape */


  var re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;
  return re.test(value) || 'URL is invalid';
};
var lengthValidator = function lengthValidator(value, length) {
  if (isEmpty(value)) {
    return true;
  }

  return value.length === length || "The Min Character field must be at least ".concat(length, " characters");
};
var alphaDashValidator = function alphaDashValidator(value) {
  if (isEmpty(value)) {
    return true;
  }

  var valueAsString = String(value);
  return /^[0-9A-Z_-]*$/i.test(valueAsString) || 'All Character is not valid';
};

var useDynamicVh = (function () {
  var setVh = function setVh() {
    document.documentElement.style.setProperty('--vh', "".concat(window.innerHeight * 0.01, "px"));
  };

  var _useWindowSize = useWindowSize(),
      windowWidth = _useWindowSize.width;

  watch(windowWidth, setVh, {
    immediate: true
  });
  return {};
});

var kFormatter = function kFormatter(num) {
  var fraction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  return num > 999 ? "".concat((num / 1000).toFixed(fraction), "k") : num;
};
var title = function title(value) {
  var replacer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ' ';
  if (!value) return '';
  var str = value.toString();
  var arr = str.split(replacer);
  var capitalizedArray = [];
  arr.forEach(function (word) {
    var capitalized = word.charAt(0).toUpperCase() + word.slice(1);
    capitalizedArray.push(capitalized);
  });
  return capitalizedArray.join(' ');
};
var avatarText = function avatarText(value) {
  if (!value) return '';
  var nameArray = value.split(' ');
  return nameArray.map(function (word) {
    return word.charAt(0).toUpperCase();
  }).join('');
};
/**
 * Format and return date in Humanize format
 * Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 * Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */

var formatDate = function formatDate(value) {
  var formatting = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  if (!value) return value;
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};
/**
 * Return short human friendly month representation of date
 * Can also convert date to only time if date is of today (Better UX)
 * @param {String} value date to format
 * @param {Boolean} toTimeForCurrentDay Shall convert to time if day is today/current
 */

var formatDateToMonthShort = function formatDateToMonthShort(value) {
  var toTimeForCurrentDay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  var date = new Date(value);
  var formatting = {
    month: 'short',
    day: 'numeric'
  };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = {
      hour: 'numeric',
      minute: 'numeric'
    };
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
}; // Strip all the tags from markup and return plain text

var filterTags = function filterTags(value) {
  return value.replace(/<\/?[^>]+(>|$)/g, '');
};

var filter = /*#__PURE__*/Object.freeze({
	__proto__: null,
	kFormatter: kFormatter,
	title: title,
	avatarText: avatarText,
	formatDate: formatDate,
	formatDateToMonthShort: formatDateToMonthShort,
	filterTags: filterTags
});

var DEFAULT_TIME_FORMAT = 'YYYY/MM/DD';
function hexToRgba(hex) {
  var opacity = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  return "rgba(" + parseInt("0x" + hex.slice(1, 3)) + "," + parseInt("0x" + hex.slice(3, 5)) + "," + parseInt("0x" + hex.slice(5, 7)) + "," + opacity + ")";
} // export function rgbToHex(r, g, b) {
//     return ((r << 16) | (g << 8) | b).toString(16);
// }

/**
 * 通过moment.js来格式化时间
 * @param date {any} 传入时间
 * @param format {String} (可选)格式化格式,默认为'YYYY/MM/DD'
 * @param option {Object} (可选)其他设置,目前只是预留位,留作之后扩展
 * @return {String, Error}
 */

function dateFilter(date, format) {
  moment(date).isSameOrBefore();
  return moment(date).isValid() ? moment(date).format(format || DEFAULT_TIME_FORMAT) : '';
}
/**
 * js小数计算误差处理函数
 * @param f:需要转化的number型小数
 * @param digit:保留精度
 * @returns {number}
 */

function formatNum(f, digit) {
  var m = Math.pow(10, digit);
  return parseInt((f * m).toFixed(), 10) / m;
}
/**
 * 用逗号以千分隔数字
 * @param num {String, Number} 待转换数字
 * @return {string} 转换后的字符串数字
 */

function moneyFormat(num) {
  return (+num || 0).toFixed(2).replace(/^-?\d+/g, function (m) {
    return m.replace(/(?=(?!\b)(\d{3})+$)/g, ',');
  });
}
function isVNode(node) {
  return node !== null && _typeof(node) === 'object' && node.hasOwnProperty('componentOptions');
}
function merge(target) {
  for (var i = 1, j = arguments.length; i < j; i++) {
    var source = arguments[i] || {};

    for (var prop in source) {
      if (source.hasOwnProperty(prop)) {
        var value = source[prop];

        if (value !== undefined) {
          target[prop] = value;
        }
      }
    }
  }

  return target;
}
/*
获取带有过期时间得localStorage
 */

Storage.prototype.getExpire = function (key) {
  var val = localStorage.getItem(key);

  if (!val) {
    return val;
  }

  val = JSON.parse(val);

  if (Date.now() - val.time > val.expire) {
    localStorage.removeItem(key);
    return null;
  }

  return val.data;
};
/*
设置带有过期时间得localStorage
 */


Storage.prototype.setExpire = function (key, value, expire) {
  var obj = {
    data: value,
    time: Date.now(),
    expire: expire
  };
  localStorage.setItem(key, JSON.stringify(obj));
};
/**
 * 防抖函数
 * @param fn 防抖目标函数
 * @param wait 防抖事件间隔
 * @param immediate 是否立即执行一次
 * @returns {function(): void}
 */


var debounce = function debounce(fn, wait) {
  var immediate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var timer,
      startTimeStamp = 0;
  var context, args;

  var run = function run(timerInterval) {
    timer = setTimeout(function () {
      var now = new Date().getTime();
      var interval = now - startTimeStamp;

      if (interval < timerInterval) {
        // the timer start time has been reset，so the interval is less than timerInterval
        // console.log('debounce reset',timerInterval-interval);
        startTimeStamp = now;
        run(wait - interval); // reset timer for left time
      } else {
        if (!immediate) {
          fn.apply(context, args);
        }

        clearTimeout(timer);
        timer = null;
      }
    }, timerInterval);
  };

  return function () {
    context = this;
    args = arguments;
    var now = new Date().getTime();
    startTimeStamp = now; // set timer start time

    if (!timer) {
      // console.log('debounce set',wait);
      if (immediate) {
        fn.apply(context, args);
      }

      run(wait); // last timer alreay executed, set a new timer
    }
  };
};
function log() {
  var info = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var businessId = info.businessId,
      createByName = info.createByName,
      createBy = info.createBy,
      businessType = info.businessType,
      appType = info.appType,
      _info$actionData = info.actionData,
      actionData = _info$actionData === void 0 ? {} : _info$actionData;
  var data = {
    businessId: businessId,
    createByName: createByName,
    createBy: createBy,
    businessType: businessType,
    appType: appType,
    actionData: JSON.stringify(_objectSpread2(_objectSpread2({}, actionData), {}, {
      userAgent: navigator.userAgent
    }))
  };
  return axios.post('/myops-admin/open-api/writeLog', data);
}
var isObject = function isObject(obj) {
  return _typeof(obj) === 'object' && obj !== null;
};
var isToday = function isToday(date) {
  var today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
    /* eslint-enable */

  );
};
var dateInPast = function dateInPast(firstDate, secondDate) {
  if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
    return true;
  }

  return false;
};
var getVuetify = function getVuetify() {
  var _getCurrentInstance;

  var ins = (_getCurrentInstance = getCurrentInstance()) === null || _getCurrentInstance === void 0 ? void 0 : _getCurrentInstance.proxy;
  return ins && ins.$vuetify ? ins.$vuetify : null;
};
var useRouter = function useRouter() {
  var vm = getCurrentInstance().proxy;
  var state = reactive({
    route: vm.$route
  });
  watch(function () {
    return vm.$route;
  }, function (r) {
    state.route = r;
  });
  return _objectSpread2(_objectSpread2({}, toRefs(state)), {}, {
    router: vm.$router
  });
};
var isEmpty = function isEmpty(value) {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  return false;
}; // ——— Get Initial Text from name ——————— //

var getInitialName = function getInitialName(fullName) {
  return (// eslint-disable-next-line implicit-arrow-linebreak
    fullName.split(' ').map(function (n) {
      return n[0];
    }).join('')
  );
}; // ——— Add Alpha To color ——————— //

var addAlpha = function addAlpha(color, opacity) {
  var opacityLocal = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + opacityLocal.toString(16).toUpperCase();
}; // ——— Perfect Scrollbar Scroll to bottom ——————— //

var psScrollToBottom = function psScrollToBottom(psRef) {
  return function () {
    var scrollEl = psRef.value.$el || psRef.value;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  };
}; // ——— Perfect Scrollbar Scroll to bottom ——————— //

var psScrollToTop = function psScrollToTop(psRef) {
  return function () {
    var scrollEl = psRef.value.$el || psRef.value;
    scrollEl.scrollTop = 0;
  };
}; // ————————————————————————————————————
//* ——— Color Manipulations
// ————————————————————————————————————
// Thanks: https://stackoverflow.com/a/5624139/10796681

var rgbToHex = function rgbToHex(r, g, b) {
  var componentToHex = function componentToHex(c) {
    var hex = c.toString(16);
    return hex.length === 1 ? "0".concat(hex) : hex;
  };

  return "#".concat(componentToHex(r)).concat(componentToHex(g)).concat(componentToHex(b));
}; // Thanks: https://stackoverflow.com/a/5624139/10796681

var hexToRgb = function hexToRgb(hex) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i; // eslint-disable-next-line no-param-reassign

  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  /* eslint-disable indent */

  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
  /* eslint-enable */
};

var state = {
  user: {
    fullname: '',
    eid: '',
    personnelNumber: ''
  },
  permission: null,
  dictionary: {}
};
var mutations = {
  SET_PERMISSION: function SET_PERMISSION(state, permission) {
    state.permission = [].concat(_toConsumableArray(state.permission || []), _toConsumableArray(permission));
  }
};
var actions = {
  login: function login(_ref, loginInfo) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
      var token;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _objectDestructuringEmpty(_ref);

              _context.next = 3;
              return getDataProxy().login(loginInfo);

            case 3:
              token = _context.sent;
              setToken(token);
              return _context.abrupt("return", token);

            case 6:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }))();
  },
  getUserInfo: function getUserInfo(_ref2) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
      var commit, user;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              commit = _ref2.commit;
              _context2.next = 3;
              return getDataProxy().getUserInfo();

            case 3:
              user = _context2.sent;
              commit('SET_USER', user);

            case 5:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }))();
  },
  getPermissions: function getPermissions(_ref3, _ref4) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3() {
      var commit, moduleName, permission;
      return _regeneratorRuntime().wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              commit = _ref3.commit;
              moduleName = _ref4.moduleName;
              _context3.next = 4;
              return getDataProxy().getPermission(moduleName);

            case 4:
              permission = _context3.sent;
              commit('SET_PERMISSION', permission);

            case 6:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }))();
  },
  loadDictionary: function loadDictionary(_ref5, _ref6) {
    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4() {
      var commit, state, catagroyArray, force, queryArray, dic, dicObj, retVal;
      return _regeneratorRuntime().wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              commit = _ref5.commit, state = _ref5.state;
              catagroyArray = _ref6.catagroyArray, force = _ref6.force;
              queryArray = [];

              if (force) {
                queryArray = catagroyArray;
              } else {
                queryArray = catagroyArray.filter(function (t) {
                  return !state.dictionary.hasOwnProperty(t);
                });
              }

              if (!(queryArray.length > 0)) {
                _context4.next = 12;
                break;
              }

              dic = {}; //初始化字典，防止重复获取接口

              queryArray.forEach(function (t) {
                dic[t] = [];
              });
              commit('SET_DICTIONARY', _objectSpread2(_objectSpread2({}, state.dictionary), dic));
              _context4.next = 10;
              return getDataProxy().getDicByArray(queryArray);

            case 10:
              dicObj = _context4.sent;
              commit('SET_DICTIONARY', _objectSpread2(_objectSpread2({}, state.dictionary), dicObj));

            case 12:
              retVal = {};
              catagroyArray.forEach(function (key) {
                retVal[key] = state.dictionary[key];
              });
              return _context4.abrupt("return", retVal);

            case 15:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }))();
  },

  /**
   * 根据字典分类及字典code 转换成字典text
   * @param state
   * @param catagroy
   * @param code
   * @returns {Promise<void>}
   */
  convertDictionary: function convertDictionary(_ref7, _ref8) {
    var state = _ref7.state;
    var catagroy = _ref8.catagroy,
        code = _ref8.code;
    var list = state.dictionary[catagroy];

    if (list.length > 0) {
      var item = list.find(function (t) {
        return t.subCategoryCode === code;
      }) || {};
      return item.categoryName;
    } else {
      return "";
    }
  }
};
var user = _objectSpread2({}, createModule({
  state: state,
  mutations: mutations,
  actions: actions
}));

Vue.use(Vuex);
var store = new Vuex.Store({
  modules: {
    app: app,
    user: user
  }
});
function addStore(name, module) {
  if (module) {
    store.registerModule(name, module);
  }
}
/**
 * 快速创建store module 对象
 * @param config
 * @returns {{mutation: *, getter: *}}
 */

function createModule(config) {
  var state = config.state,
      mutations = config.mutations,
      getters = config.getters,
      _config$namespaced = config.namespaced,
      namespaced = _config$namespaced === void 0 ? true : _config$namespaced;

  _objectSpread2({}, state);

  var stateMutation = function () {
    var _mutation = {};

    var _loop = function _loop(key) {
      _mutation['SET_' + (key || '').toUpperCase()] = function (_state, val) {
        if (state[key] instanceof Array) {
          _state[key] = _toConsumableArray(val);
        } else if (state[key] instanceof Object) {
          _state[key] = _objectSpread2(_objectSpread2({}, _state[key]), val);
        } else {
          _state[key] = val;
        }
      };
    };

    for (var key in state) {
      _loop(key);
    }

    return _objectSpread2(_objectSpread2({}, _mutation), mutations);
  }();

  stateMutation['SET_INIT'] = function (_state) {
  };

  var stateGetter = function () {
    var _getter = {};

    var _loop2 = function _loop2(key) {
      _getter[key] = function (state) {
        return state[key];
      };
    };

    for (var key in state) {
      _loop2(key);
    }

    return _objectSpread2(_objectSpread2({}, _getter), getters);
  }();

  return _objectSpread2(_objectSpread2(_objectSpread2({}, config), {
    mutations: stateMutation,
    getters: stateGetter
  }), {}, {
    namespaced: namespaced
  });
}

Vue.use(vueRouter);
var commit = store.commit;
var router = new vueRouter({
  routes: [],
  scrollBehavior: function scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          resolve(savedPosition);
        }, 0);
      });
    } else {
      return {
        x: 0,
        y: 0
      };
    }
  }
});

function getStrWithLastChart() {
  var path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var chart = arguments.length > 1 ? arguments[1] : undefined;
  return "".concat(path).concat(chart).replace(/[/]*$/, '/');
}

function pathJoin(paths, split) {
  return paths.reduce(function (prev, cur) {
    if (cur) {
      return getStrWithLastChart(prev, split) + cur.replace(/^[/]*/g, '');
    }

    return prev;
  }, '');
}

function buildRouteAndMenuNested(current) {
  var parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    to: '',
    path: ''
  };
  var title = current.title,
      icon = current.icon,
      _current$name = current.name,
      name = _current$name === void 0 ? '' : _current$name,
      children = current.children,
      path = current.path,
      component = current.component,
      meta = current.meta,
      redirect = current.redirect;
  var currentPath = path || pathJoin([parent.path, name], '/') || '/';
  var currentName = parent.to ? "".concat(parent.to, "-").concat(name || 'index') : name || 'index';
  var route;
  var menu = {
    title: title,
    icon: icon,
    permissionId: meta === null || meta === void 0 ? void 0 : meta.permissionId
  };

  if (!children) {
    menu.to = currentName;
    route = {
      name: menu.to,
      path: currentPath,
      component: component,
      meta: meta
    };
    redirect && (route.redirect = redirect);
  } else {
    menu.children = [];
    route = {
      path: currentPath,
      component: component || {
        render: function render(h) {
          return h('router-view');
        }
      },
      children: [],
      meta: meta
    };
    redirect && (route.redirect = redirect);

    var _iterator = _createForOfIteratorHelper(children),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var child = _step.value;
        var childRouteAndMenu = buildRouteAndMenuNested(child, {
          to: currentName,
          path: currentPath
        });

        if (!child.hidden) {
          menu.children.push(childRouteAndMenu.menu);
        }

        route.children.push(childRouteAndMenu.route);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  return {
    menu: menu,
    route: route
  };
}

function createMenu(menu) {
  var _menu$bundle = menu.bundle,
      bundle = _menu$bundle === void 0 ? '' : _menu$bundle,
      key = menu.key;

  if (key.toUpperCase() === 'INDEX') {
    // eslint-disable-next-line no-param-reassign
    menu.path = "/".concat(bundle);
  } else {
    // eslint-disable-next-line no-param-reassign
    menu.path = bundle ? "/".concat(bundle, "/").concat(key) : "/".concat(key);
  }

  var RouterAndMenu = buildRouteAndMenuNested(menu, {
    to: key,
    path: menu.path
  });
  router.addRoute(RouterAndMenu.route);

  if (!menu.hidden) {
    commit('app/ADD_MENU', RouterAndMenu.menu);
  }
}

/**
 * Returns translated string if i18n package is available to Vue
 * If i18n is not configured then it will simply return what is being passed
 * Useful if you don't know if i18n is configured or not
 * Used in @core files to handle absence of i18n without errors
 * @param {String} key i18n key to use for translation
 */

var t = function t(key) {
  var vm = getCurrentInstance().proxy;
  return vm.$i18n && vm.$t ? vm.$t(key) : key;
};
var _ = null;

var utils$1 = /*#__PURE__*/Object.freeze({
	__proto__: null,
	t: t,
	_: _
});

Vue.use(VueI18n);
var i18n = new VueI18n({
  locale: localStorage.language || 'en',
  // set locale
  fallbackLocale: 'en' // set locale messages

});
var loadedLanguages = []; // our default language that is preloaded

function setI18nLanguage(lang) {
  i18n.locale = lang;
  return lang;
}

function loadLanguage(lang, msgs) {
  // If the same language
  if (loadedLanguages.includes(lang)) {
    return setI18nLanguage(lang);
  }

  i18n.setLocaleMessage(lang, msgs);
  loadedLanguages.push(lang);
  return setI18nLanguage(lang);
}
var useI18nUtils = function useI18nUtils() {
  return _objectSpread2({}, utils$1);
};

/**
 * Returns ability result if ACL is configured or else just return true
 * Useful if you don't know if ACL is configured or not
 * Used in @core files to handle absence of ACL without errors
 * @param {String} action CASL Actions // https://casl.js.org/v4/en/guide/intro#basics
 * @param {String} subject CASL Subject // https://casl.js.org/v4/en/guide/intro#basics
 */

var can = function can(permissionId) {
  return permissionId ? hasPermission(permissionId) : true;
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */

var canViewVerticalNavMenuLink = function canViewVerticalNavMenuLink(item) {
  return can(item.permissionId);
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style

var canViewVerticalNavMenuGroup = function canViewVerticalNavMenuGroup(item) {
  // ! This same logic is used in canViewHorizontalNavMenuGroup and canViewHorizontalNavMenuHeaderGroup. So make sure to update logic in them as well
  var hasAnyVisibleChild = item.children.some(function (i) {
    return can(i.permissionId);
  });
  return can(item.permissionId) && hasAnyVisibleChild;
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */

var canViewVerticalNavMenuHeader = function canViewVerticalNavMenuHeader(item) {
  return can(item.permissionId);
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */

var canViewHorizontalNavMenuLink = function canViewHorizontalNavMenuLink(item) {
  return can(item.permissionId);
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */

var canViewHorizontalNavMenuHeaderLink = function canViewHorizontalNavMenuHeaderLink(item) {
  return can(item.permissionId);
};
/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style

var canViewHorizontalNavMenuGroup = function canViewHorizontalNavMenuGroup(item) {
  // ? Same logic as canViewVerticalNavMenuGroup
  var hasAnyVisibleChild = item.children.some(function (i) {
    return can(i.permissionId);
  }); // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child

  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }

  return can(item.permissionId) && hasAnyVisibleChild;
}; // eslint-disable-next-line arrow-body-style

var canViewHorizontalNavMenuHeaderGroup = function canViewHorizontalNavMenuHeaderGroup(item) {
  // ? Same logic as canViewVerticalNavMenuGroup but with extra content
  // eslint-disable-next-line arrow-body-style
  var hasAnyVisibleChild = item.children.some(function (grpOrItem) {
    // If it have children => It's grp
    // Call ACL function based on grp/link
    return grpOrItem.children ? canViewHorizontalNavMenuGroup(grpOrItem) : canViewHorizontalNavMenuLink(grpOrItem);
  });
  return can(item.permissionId) && hasAnyVisibleChild;
};

var utils = /*#__PURE__*/Object.freeze({
	__proto__: null,
	can: can,
	canViewVerticalNavMenuLink: canViewVerticalNavMenuLink,
	canViewVerticalNavMenuGroup: canViewVerticalNavMenuGroup,
	canViewVerticalNavMenuHeader: canViewVerticalNavMenuHeader,
	canViewHorizontalNavMenuLink: canViewHorizontalNavMenuLink,
	canViewHorizontalNavMenuHeaderLink: canViewHorizontalNavMenuHeaderLink,
	canViewHorizontalNavMenuGroup: canViewHorizontalNavMenuGroup,
	canViewHorizontalNavMenuHeaderGroup: canViewHorizontalNavMenuHeaderGroup
});

var useAclUtils = function useAclUtils() {
  return _objectSpread2({}, utils);
};

export { addAlpha, addStore, alphaDashValidator, alphaValidator, authV2, baseProxy, between, confirmedValidator, createMenu, createModule, dateFilter, dateInPast, debounce, emailValidator, eventBus as event, filter, formatNum, getDataProxy, getInitialName, getToken, getVuetify, hasPermission, hasRole, hexToRgb, hexToRgba, i18n, integerValidator, isEmpty, isLogin, isObject, isToday, isVNode, judgePermission, lengthValidator, loadLanguage, log, logOut, merge, module, moment, moneyFormat, paramsUtil, passwordValidator, provider, psScrollToBottom, psScrollToTop, regexValidator, removeToken, service as request, required, rgbToHex, router, setAuthProvide, setDataProxy, setToken, _setting as settings, store, urlValidator, useAclUtils, useDynamicVh, useI18nUtils, useRouter };
